import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { supabase } from "../../lib/supabase";
import { adminService } from "../../services/adminService";
const db = supabase;
import { callEdgeFunction } from "./callEdgeFunction";
import LocationPickerMap from "../../components/LocationPickerMap";
import { calculateDistance } from "../../services/locationService";
import { getCurrentPosition, isGeolocationSupported } from "../../utils/location";
import { getWorkloadLabelAr, getDeliveredPhotosLabelAr, getShootingMethodLabelAr, getPhotographyTypeLabelAr, getShootingPlaceTypeLabelAr, getProductPhysicalSizeLabelAr } from "../../utils/bookingLabels";
import { formatNumber } from "../../utils/formatNumber";
import { formatIbanForDisplay, hasValidSaudiIban, cleanIban } from "../../utils/iban";
import { normalizePhone } from "../../sendOtp";
import BrandSettingsTab from "./components/BrandSettingsTab";
import PagesTab from "./components/PagesTab";
import ExifChip from "./components/ExifChip";
import {
  getDaysUntilExpiry as getDroneDaysUntilExpiry,
  getDroneExpiryBadgeClass,
  getDroneExpiryStatus,
  getDroneExpiryStatusLabel,
  getDronePermitStatus,
  isIdentityVerificationFile,
  matchesDroneFilter,
  photographerUsesDrone,
  sortByDroneExpiry,
} from "../../services/dronePermitService";
import {
  getDaysUntilExpiry,
  getFreelanceExpiryBadgeClass,
  getFreelanceExpiryStatus,
  getFreelanceExpiryStatusLabel,
  getFreelancePermitStatus,
  getFreelancePermitStatusLabel,
  matchesFreelanceFilter,
  photographerIsIndividual,
  sortByFreelanceExpiry,
  validateFreelancePermitFile,
} from "../../services/freelancePermitService";
import {
  getDaysUntilExpiry as getIdentityDaysUntilExpiry,
  getIdentityActivationBlockReason,
  getIdentityExpiryBadgeClass,
  getIdentityExpiryStatus,
  getIdentityExpiryStatusLabel,
  getIdentityVerificationStatus,
  matchesIdentityFilter,
  photographerAccountActivationRequirementsMet,
  photographerHasIdentityDocument,
  sortByIdentityExpiry,
} from "../../services/identityDocumentService";

// ===== Experience level mappings (DB <-> UI <-> Arabic) =====
const DB_TO_UI_EXPERIENCE_LEVEL = {
  beginner: "certified",
  intermediate: "distinguished",
  advanced: "distinguished",
  expert: "elite",
};

const UI_TO_DB_EXPERIENCE_LEVEL = {
  certified: "beginner",
  distinguished: "intermediate",
  elite: "expert",
};

const UI_EXPERIENCE_LEVEL_LABELS_AR = {
  certified: "معتمد",
  distinguished: "مميز",
  elite: "نخبة",
};

// نفس قيم الويزارد العام — تُحفظ كـ labels في قاعدة البيانات
const ADMIN_WIZARD_SPECIALIZATIONS = [
  { value: "event", label: "تصوير المناسبات" },
  { value: "product", label: "تصوير المنتجات" },
  { value: "portrait", label: "التصوير الشخصي" },
  { value: "food", label: "تصوير الطعام" },
  { value: "real-estate", label: "تصوير العقارات" },
  { value: "wedding", label: "حفلات الزفاف" },
];

const ADMIN_WIZARD_EQUIPMENT_LEVELS = [
  { value: "basic-editing", label: "تعديل أساسي (تصحيح إضاءة وألوان)", desc: "تصحيح الإضاءة والألوان" },
  { value: "professional-editing", label: "تعديل احترافي متقدم", desc: "معالجة متقدمة باستخدام Lr/Ps" },
];

const mapAdminSpecializationsToLabels = (values) =>
  (values || []).map((v) => ADMIN_WIZARD_SPECIALIZATIONS.find((s) => s.value === v)?.label || v);

const mapAdminEquipmentToLabels = (values) =>
  (values || []).map((v) => ADMIN_WIZARD_EQUIPMENT_LEVELS.find((e) => e.value === v)?.label || v);

// Unified helper function to manage photographer actions
async function managePhotographer(photographerId, action, value) {
  return await callEdgeFunction('admin_manage_photographer', {
    photographerId,
    action,
    value,
  });
}

// Helper function to manage customer actions
async function manageCustomer(customerId, action, value) {
  return await callEdgeFunction('admin_manage_photographer', {
    photographerId: customerId, // Reuse same Edge Function
    action,
    value,
  });
}

function getBookingCommissionBreakdown(booking, commissionSettings) {
  const amount = Number(booking?.final_price_sar || booking?.price || booking?.pricing_snapshot?.total || 0);
  const rate = commissionSettings?.platformCommissionRate ?? 15;
  const min = Number(commissionSettings?.minCommission ?? 0);
  const raw = Math.round(amount * (rate / 100));
  const commission = amount > 0 ? Math.max(raw, min) : 0;
  return { amount, rate, commission, photographerNet: amount - commission };
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const TABS = [
  { key: "overview", label: "نظرة عامة", icon: "LayoutDashboard" },
  { key: "bookings", label: "الحجوزات", icon: "Calendar" },
  { key: "deliveries", label: "التسليمات", icon: "Upload" },
  { key: "photographers", label: "المصورين", icon: "Camera" },
  { key: "verification", label: "التحقق", icon: "ShieldCheck" },
  { key: "reviews", label: "التقييمات", icon: "Star" },
  { key: "customers", label: "العملاء", icon: "Users" },
  { key: "support", label: "الدعم والشكاوى", icon: "Headphones" },
  { key: "payments", label: "المدفوعات", icon: "Wallet" },
  { key: "pricing", label: "التسعير", icon: "BadgeDollarSign" },
  { key: "email-templates", label: "قوالب البريد", icon: "Mail" },
  { key: "bulk-notifications", label: "إشعارات جماعية", icon: "Bell" },
  { key: "brand-settings", label: "الهوية البصرية", icon: "Palette" },
  { key: "pages", label: "الصفحات", icon: "FileText" },
  { key: "settings", label: "الإعدادات", icon: "Settings" },
];

// Support Ticket Categories
const TICKET_CATEGORIES = [
  { key: "booking", label: "مشكلة في الحجز", icon: "Calendar" },
  { key: "payment", label: "مشكلة في الدفع", icon: "CreditCard" },
  { key: "technical", label: "مشكلة تقنية", icon: "Settings" },
  { key: "account", label: "مشكلة في الحساب", icon: "User" },
  { key: "photographer", label: "شكوى على مصور", icon: "Camera" },
  { key: "complaint", label: "شكوى عامة", icon: "AlertCircle" },
  { key: "suggestion", label: "اقتراح", icon: "Lightbulb" },
  { key: "other", label: "أخرى", icon: "MoreHorizontal" },
];

const TICKET_STATUSES = [
  { key: "open", label: "مفتوحة", color: "blue" },
  { key: "in_progress", label: "قيد المعالجة", color: "amber" },
  { key: "waiting_user", label: "بانتظار الرد", color: "purple" },
  { key: "waiting_admin", label: "بانتظار المشرف", color: "orange" },
  { key: "resolved", label: "تم الحل", color: "green" },
  { key: "closed", label: "مغلقة", color: "slate" },
];

const TICKET_PRIORITIES = [
  { key: "low", label: "منخفضة", color: "slate" },
  { key: "medium", label: "متوسطة", color: "blue" },
  { key: "high", label: "عالية", color: "amber" },
  { key: "urgent", label: "عاجلة", color: "red" },
];

// Recipient Filter Options
const RECIPIENT_FILTERS = [
  { key: "all", label: "جميع المستخدمين", icon: "Users", description: "كل المسجلين في المنصة" },
  { key: "customers", label: "العملاء فقط", icon: "User", description: "جميع العملاء" },
  { key: "photographers", label: "المصورين فقط", icon: "Camera", description: "جميع المصورين" },
  { key: "verified_photographers", label: "المصورين الموثقين", icon: "BadgeCheck", description: "المصورين الذين تم التحقق منهم" },
  { key: "active_customers", label: "العملاء النشطين", icon: "Activity", description: "لديهم حجز خلال آخر 30 يوم" },
  { key: "new_users", label: "المستخدمين الجدد", icon: "UserPlus", description: "سجلوا خلال آخر 7 أيام" },
  { key: "custom", label: "اختيار مخصص", icon: "Filter", description: "تحديد المستلمين يدوياً" },
];

// Email Templates Configuration
const EMAIL_TEMPLATES_CONFIG = {
  customer: {
    label: "قوالب العميل",
    icon: "User",
    templates: [
      { key: "customer_welcome", label: "ترحيب بالعميل الجديد", description: "يُرسل عند تسجيل عميل جديد" },
      { key: "customer_email_verification", label: "التحقق من البريد", description: "رمز التحقق من البريد الإلكتروني" },
      { key: "customer_password_reset", label: "استعادة كلمة المرور", description: "رابط استعادة كلمة المرور" },
      { key: "customer_booking_created", label: "تأكيد إنشاء الحجز", description: "يُرسل بعد إنشاء حجز جديد" },
      { key: "customer_booking_confirmed", label: "تأكيد الحجز (تم تعيين مصور)", description: "يُرسل عند قبول مصور للحجز" },
      { key: "customer_booking_cancelled", label: "إلغاء الحجز", description: "يُرسل عند إلغاء حجز مدفوع" },
      { key: "customer_booking_cancelled_unpaid", label: "إلغاء قبل الدفع", description: "يُرسل عند إلغاء العميل قبل الدفع أو قبل قبول المصور" },
      { key: "customer_booking_completed", label: "اكتمال الحجز", description: "يُرسل بعد انتهاء جلسة التصوير" },
      { key: "customer_payment_success", label: "تأكيد الدفع", description: "يُرسل بعد نجاح عملية الدفع" },
      { key: "customer_photos_delivered", label: "تسليم الصور", description: "يُرسل عند رفع المصور للصور" },
      { key: "customer_review_request", label: "طلب تقييم", description: "يُرسل بعد اكتمال الحجز لطلب تقييم" },
    ]
  },
  photographer: {
    label: "قوالب المصور",
    icon: "Camera",
    templates: [
      { key: "photographer_welcome", label: "ترحيب بالمصور الجديد", description: "يُرسل عند تسجيل مصور جديد" },
      { key: "photographer_email_verification", label: "التحقق من البريد", description: "رمز التحقق من البريد الإلكتروني" },
      { key: "photographer_password_reset", label: "استعادة كلمة المرور", description: "رابط استعادة كلمة المرور" },
      { key: "photographer_account_approved", label: "قبول الحساب", description: "يُرسل عند الموافقة على حساب المصور" },
      { key: "photographer_account_rejected", label: "رفض الحساب", description: "يُرسل عند رفض حساب المصور" },
      { key: "photographer_new_booking", label: "طلب حجز جديد", description: "يُرسل عند استلام طلب حجز جديد" },
      { key: "photographer_booking_confirmed", label: "تأكيد الحجز", description: "يُرسل عند قبول المصور للحجز" },
      { key: "photographer_booking_cancelled", label: "إلغاء الحجز", description: "يُرسل عند إلغاء العميل لحجز مدفوع" },
      { key: "photographer_booking_cancelled_unpaid", label: "إلغاء طلب قبل التأكيد", description: "يُرسل للمصور عندما يلغي العميل الطلب قبل القبول" },
      { key: "photographer_payment_received", label: "استلام دفعة", description: "يُرسل عند استلام دفعة من حجز" },
      { key: "photographer_payout_processed", label: "تحويل الأرباح", description: "يُرسل عند تحويل أرباح للمصور" },
      { key: "photographer_verification_approved", label: "قبول التحقق", description: "يُرسل عند قبول وثائق الهوية" },
      { key: "photographer_verification_rejected", label: "رفض التحقق", description: "يُرسل عند رفض وثائق الهوية" },
    ]
  },
  admin: {
    label: "إشعارات المشرف",
    icon: "Shield",
    templates: [
      { key: "admin_new_customer", label: "عميل جديد", description: "إشعار بتسجيل عميل جديد" },
      { key: "admin_new_photographer", label: "مصور جديد", description: "إشعار بتسجيل مصور جديد يحتاج مراجعة" },
      { key: "admin_customer_cancellation", label: "طلب إلغاء من عميل", description: "إشعار عند طلب العميل إلغاء حجز مدفوع" },
      { key: "admin_photographer_withdrawal", label: "انسحاب مصور", description: "إشعار عند انسحاب مصور من حجز مؤكد" },
      { key: "admin_stuck_booking", label: "حجز يحتاج تدخل", description: "إشعار عند مرور يوم الجلسة دون توثيق الحضور" },
      { key: "admin_customer_support", label: "تذكرة دعم من عميل", description: "إشعار عند إرسال عميل تذكرة دعم أو شكوى" },
      { key: "admin_photographer_support", label: "تذكرة دعم من مصور", description: "إشعار عند إرسال مصور تذكرة دعم أو شكوى" },
      { key: "admin_new_booking", label: "حجز جديد", description: "إشعار بحجز جديد" },
      { key: "admin_verification_request", label: "طلب تحقق", description: "إشعار بطلب تحقق من الهوية" },
      { key: "admin_payment_issue", label: "مشكلة في الدفع", description: "إشعار بمشكلة في عملية دفع" },
      { key: "admin_customer_complaint", label: "شكوى عميل (قديم)", description: "إشعار بشكوى من عميل" },
    ]
  }
};

// أسباب رفض طلب التوثيق — يختار المشرف واحداً أو أكثر
const REJECTION_REASONS = [
  'وثائق الهوية غير واضحة أو منتهية الصلاحية',
  'معرض الأعمال لا يعكس المستوى المطلوب (3 صور معتمدة على الأقل)',
  'الشهادات المرفوعة غير معتمدة أو لا يمكن التحقق منها',
  'الصورة الشخصية غير واضحة أو غير مناسبة',
  'البيانات الشخصية غير مكتملة أو غير صحيحة',
];

// Default email template content
const getDefaultTemplateContent = (templateKey) => {
  const defaults = {
    // Customer templates
    customer_welcome: {
      subject: "مرحباً بك في منصتنا! 📸",
      body: `مرحباً {{customer_name}}،

يسعدنا انضمامك إلى منصتنا للتصوير الفوتوغرافي!

يمكنك الآن:
• تصفح المصورين المحترفين
• حجز جلسات تصوير
• استلام صورك بجودة عالية

إذا كان لديك أي استفسار، لا تتردد في التواصل معنا.

مع أطيب التحيات،
فريق المنصة`
    },
    customer_email_verification: {
      subject: "تأكيد بريدك الإلكتروني",
      body: `مرحباً {{customer_name}}،

رمز التحقق الخاص بك هو: {{verification_code}}

أو يمكنك النقر على الرابط التالي:
{{verification_link}}

صالح لمدة 24 ساعة.

مع أطيب التحيات،
فريق المنصة`
    },
    customer_password_reset: {
      subject: "استعادة كلمة المرور",
      body: `مرحباً {{customer_name}}،

تلقينا طلباً لاستعادة كلمة المرور الخاصة بحسابك.

انقر على الرابط التالي لإعادة تعيين كلمة المرور:
{{reset_link}}

إذا لم تطلب هذا، يمكنك تجاهل هذا البريد.

مع أطيب التحيات،
فريق المنصة`
    },
    customer_booking_created: {
      subject: "تم إنشاء حجزك بنجاح #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

تم إنشاء حجزك بنجاح!

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}
• نوع التصوير: {{photography_type}}
• التاريخ: {{booking_date}}
• الوقت: {{booking_time}}
• المبلغ: {{total_amount}} ر.س

نحن نبحث عن أفضل مصور لك. سنرسل لك إشعاراً عند تأكيد الحجز.

مع أطيب التحيات،
فريق المنصة`
    },
    customer_booking_confirmed: {
      subject: "تم تأكيد حجزك! 🎉 #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

أخبار رائعة! تم تأكيد حجزك.

تفاصيل المصور:
• الاسم: {{photographer_name}}
• التقييم: {{photographer_rating}} ⭐
• الهاتف: {{photographer_phone}}

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}
• التاريخ: {{booking_date}}
• الوقت: {{booking_time}}
• الموقع: {{booking_location}}

نتمنى لك جلسة تصوير ممتعة!

مع أطيب التحيات،
فريق المنصة`
    },
    customer_booking_cancelled: {
      subject: "تم إلغاء الحجز #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

نأسف لإعلامك بأنه تم إلغاء الحجز #{{booking_id}}.

سبب الإلغاء: {{cancellation_reason}}

إذا تم استرداد المبلغ، سيصلك خلال 3-5 أيام عمل.

يمكنك إنشاء حجز جديد في أي وقت.

مع أطيب التحيات،
فريق المنصة`
    },
    customer_booking_cancelled_unpaid: {
      subject: "تم إلغاء الطلب #{{booking_id}}",
      body: `هلا {{customer_name}}،

تم إلغاء طلبك بناءً على طلبك. لم يُخصم أي مبلغ.

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}

يمكنك إنشاء حجز جديد في أي وقت من خلال المنصة.

مع أطيب التحيات، فريق مشهد`
    },
    customer_booking_completed: {
      subject: "اكتملت جلسة التصوير! 📷 #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

نأمل أن تكون قد استمتعت بجلسة التصوير مع {{photographer_name}}!

سيقوم المصور برفع صورك قريباً. سنرسل لك إشعاراً عند جاهزيتها.

نود أن نسمع رأيك! يرجى تقييم تجربتك:
{{review_link}}

مع أطيب التحيات،
فريق المنصة`
    },
    customer_payment_success: {
      subject: "تم الدفع بنجاح ✅ #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

تم استلام دفعتك بنجاح وتأكيد حجزك!

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}
• المبلغ الإجمالي: {{total_amount}} ر.س
• نوع التصوير: {{photography_type}}
• التاريخ: {{booking_date}}
• الوقت: {{booking_time}}
• الموقع: {{booking_location}}
• المصور: {{photographer_name}}
• رقم العملية: {{transaction_id}}

شكراً لثقتك بنا!

مع أطيب التحيات،
فريق المنصة`
    },
    customer_photos_delivered: {
      subject: "صورك جاهزة للتحميل! 🖼️ #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

أخبار رائعة! قام {{photographer_name}} برفع صورك.

عدد الصور: {{photos_count}} صورة

يمكنك تحميل صورك من لوحة التحكم:
{{download_link}}

ملاحظة: الصور متاحة للتحميل لمدة 30 يوماً.

مع أطيب التحيات،
فريق المنصة`
    },
    customer_review_request: {
      subject: "شاركنا رأيك! ⭐ #{{booking_id}}",
      body: `مرحباً {{customer_name}}،

نأمل أن تكون قد استمتعت بتجربتك مع {{photographer_name}}.

تقييمك يساعدنا على تحسين خدماتنا ويساعد المستخدمين الآخرين.

قيّم تجربتك الآن:
{{review_link}}

شكراً لوقتك!

مع أطيب التحيات،
فريق المنصة`
    },
    
    // Photographer templates
    photographer_welcome: {
      subject: "مرحباً بك كمصور في منصتنا! 📸",
      body: `مرحباً {{photographer_name}}،

يسعدنا انضمامك إلى فريق المصورين في منصتنا!

الخطوات التالية:
1. أكمل ملفك الشخصي
2. ارفع معرض أعمالك
3. حدد أسعارك ومناطق خدمتك
4. ارفع وثائق التحقق من الهوية

بمجرد الموافقة على حسابك، ستبدأ باستلام طلبات الحجز.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_email_verification: {
      subject: "تأكيد بريدك الإلكتروني",
      body: `مرحباً {{photographer_name}}،

رمز التحقق الخاص بك هو: {{verification_code}}

أو يمكنك النقر على الرابط التالي:
{{verification_link}}

صالح لمدة 24 ساعة.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_password_reset: {
      subject: "استعادة كلمة المرور",
      body: `مرحباً {{photographer_name}}،

تلقينا طلباً لاستعادة كلمة المرور الخاصة بحسابك.

انقر على الرابط التالي لإعادة تعيين كلمة المرور:
{{reset_link}}

إذا لم تطلب هذا، يمكنك تجاهل هذا البريد.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_account_approved: {
      subject: "تهانينا! تمت الموافقة على حسابك ✅",
      body: `مرحباً {{photographer_name}}،

يسعدنا إبلاغك بأنه تمت الموافقة على حسابك!

يمكنك الآن:
• استلام طلبات الحجز
• إدارة حجوزاتك
• تحميل صور للعملاء

ابدأ الآن وانتظر أول حجز لك!

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_account_rejected: {
      subject: "حالة طلب التسجيل",
      body: `مرحباً {{photographer_name}}،

نأسف لإعلامك بأنه لم تتم الموافقة على طلب تسجيلك حالياً.

السبب: {{rejection_reason}}

يمكنك تحديث معلوماتك وإعادة التقديم.

إذا كان لديك أي استفسار، تواصل معنا.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_new_booking: {
      subject: "لديك طلب حجز جديد! 📅 #{{booking_id}}",
      body: `مرحباً {{photographer_name}}،

لديك طلب حجز جديد!

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}
• العميل: {{customer_name}}
• نوع التصوير: {{photography_type}}
• التاريخ: {{booking_date}}
• الوقت: {{booking_time}}
• الموقع: {{booking_location}}
• المبلغ: {{total_amount}} ر.س

يرجى الرد خلال 3 دقائق:
{{accept_link}} | {{reject_link}}

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_booking_confirmed: {
      subject: "تم تأكيد الحجز! ✅ #{{booking_id}}",
      body: `مرحباً {{photographer_name}}،

تم تأكيد الحجز بنجاح!

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}
• العميل: {{customer_name}}
• هاتف العميل: {{customer_phone}}
• التاريخ: {{booking_date}}
• الوقت: {{booking_time}}
• الموقع: {{booking_location}}

نتمنى لك جلسة تصوير ممتعة!

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_booking_cancelled: {
      subject: "تم إلغاء الحجز #{{booking_id}}",
      body: `مرحباً {{photographer_name}}،

نود إعلامك بأنه تم إلغاء الحجز #{{booking_id}}.

السبب: {{cancellation_reason}}

لا تقلق، سيتم إعادة الموعد لجدولك تلقائياً.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_booking_cancelled_unpaid: {
      subject: "ألغى العميل الطلب #{{booking_id}}",
      body: `مرحبا {{photographer_name}}،

العميل ألغى الطلب قبل تأكيدك. لا حاجة للرد.

تفاصيل الحجز:
• رقم الحجز: #{{booking_id}}

مع أطيب التحيات،
فريق مشهد`
    },
    photographer_payment_received: {
      subject: "تم استلام دفعة جديدة! 💰 #{{booking_id}}",
      body: `مرحباً {{photographer_name}}،

تم استلام دفعة من الحجز #{{booking_id}}.

التفاصيل:
• المبلغ الإجمالي: {{total_amount}} ر.س
• عمولة المنصة: {{platform_commission}} ر.س
• صافي أرباحك: {{photographer_earnings}} ر.س

رصيدك الحالي: {{current_balance}} ر.س

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_payout_processed: {
      subject: "تم تحويل أرباحك! 🎉",
      body: `مرحباً {{photographer_name}}،

تم تحويل أرباحك بنجاح!

التفاصيل:
• المبلغ المحول: {{payout_amount}} ر.س
• طريقة التحويل: {{payout_method}}
• رقم العملية: {{transaction_id}}

سيصل المبلغ خلال 1-3 أيام عمل.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_verification_approved: {
      subject: "تم قبول وثائق التحقق ✅",
      body: `مرحباً {{photographer_name}}،

تم قبول وثائق التحقق من الهوية الخاصة بك.

حسابك الآن موثق بالكامل! ستظهر علامة التوثيق بجانب اسمك.

مع أطيب التحيات،
فريق المنصة`
    },
    photographer_verification_rejected: {
      subject: "وثائق التحقق — يلزم إعادة الرفع",
      body: `مرحبًا {{photographer_name}}،

وصلتنا وثائقك وشكرًا على تسجيلك 🙏
راجعنا الوثائق واحتجنا بعض التعديلات:

{{rejection_reason}}

عند رفع الوثائق من جديد مع مراعاة:
• وضوح الصورة
• ظهور جميع البيانات
• صلاحية الوثيقة

نكمّل التوثيق لك في أقرب وقت ممكن.

مع التحيات،
فريق مشهد`
    },
    
    // Admin templates
    admin_new_photographer: {
      subject: "مصور جديد يحتاج للمراجعة",
      body: `مصور جديد سجل في المنصة:

الاسم: {{photographer_name}}
البريد: {{photographer_email}}
الهاتف: {{photographer_phone}}
المدينة: {{photographer_city}}

يرجى مراجعة الطلب:
{{review_link}}`
    },
    admin_new_booking: {
      subject: "حجز جديد #{{booking_id}}",
      body: `حجز جديد في المنصة:

رقم الحجز: #{{booking_id}}
العميل: {{customer_name}}
المصور: {{photographer_name}}
المبلغ: {{total_amount}} ر.س
التاريخ: {{booking_date}}`
    },
    admin_verification_request: {
      subject: "طلب تحقق جديد",
      body: `طلب تحقق من الهوية جديد:

المصور: {{photographer_name}}
نوع الوثيقة: {{document_type}}

يرجى المراجعة:
{{review_link}}`
    },
    admin_payment_issue: {
      subject: "⚠️ مشكلة في الدفع #{{booking_id}}",
      body: `مشكلة في عملية دفع:

رقم الحجز: #{{booking_id}}
العميل: {{customer_name}}
المبلغ: {{total_amount}} ر.س
الخطأ: {{error_message}}

يرجى المراجعة فوراً.`
    },
    admin_customer_complaint: {
      subject: "🚨 شكوى عميل جديدة",
      body: `شكوى جديدة من عميل:

العميل: {{customer_name}}
رقم الحجز: {{booking_id}}
المصور: {{photographer_name}}

نص الشكوى:
{{complaint_text}}

يرجى المراجعة والتواصل مع العميل.`
    }
  };
  
  return defaults[templateKey] || { subject: "", body: "" };
};

function Badge({ children, tone = "slate" }) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-green-100 text-green-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    purple: "bg-blue-100 text-blue-800",
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${map[tone] || map.slate}`}>
      {children}
    </span>
  );
}

function StatCard({ title, value, icon, hint, tone = "purple" }) {
  const tones = {
    purple: { bg: "bg-blue-50", icon: "#7c3aed" },
    blue: { bg: "bg-blue-50", icon: "#2563eb" },
    green: { bg: "bg-green-50", icon: "#16a34a" },
    amber: { bg: "bg-amber-50", icon: "#d97706" },
    slate: { bg: "bg-slate-50", icon: "#475569" },
  };
  const t = tones[tone] || tones.purple;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-slate-600">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
            {typeof value === "number" ? value.toLocaleString("ar-SA") : value}
          </p>
          {hint ? <p className="text-xs text-slate-500 mt-1">{hint}</p> : null}
        </div>
        <div className={`w-12 h-12 rounded-2xl ${t.bg} flex items-center justify-center shrink-0`}>
          <Icon name={icon} size={22} color={t.icon} />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ title, desc, action }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-50 mx-auto flex items-center justify-center mb-3">
        <Icon name="Inbox" size={26} color="#64748b" />
      </div>
      <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-6" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{desc}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

function Table({ columns, rows, rowKey }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-right font-semibold text-slate-700 whitespace-nowrap" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={typeof rowKey === "function" ? rowKey(r) : r[rowKey]} className="hover:bg-slate-50/60">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-800 whitespace-nowrap align-middle">
                    {typeof c.render === "function" ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminHome() {
  const navigate = useNavigate();
  const q = useQuery();
  const tab = q.get("tab") || "overview";
  const active = TABS.find((t) => t.key === tab) ? tab : "overview";

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    activePhotographers: 0,
    totalCustomers: 0,
  });
  const [bookings, setBookings] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [pricingTable, setPricingTable] = useState(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingForm, setPricingForm] = useState({ id: null, name: '', city: '', photography_type: '', base_price: '', multiplier: '1', is_active: true });

  // Admin Delivery Override States
  const [adminDeliveryModal, setAdminDeliveryModal] = useState({ open: false, mode: null, bookingId: null, _deliveryMeta: null });
  const [pendingDeliveryBookings, setPendingDeliveryBookings] = useState([]);

  // Payments & Commission States
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [commissionSettings, setCommissionSettings] = useState({
    platformCommissionRate: 15, // Default 15%
    minCommission: 50, // Minimum commission in SAR
    maxCommission: null, // No max limit
    payoutThreshold: 500, // Minimum for photographer payout
  });
  const [savingCommission, setSavingCommission] = useState(false);
  
  const [paymentStats, setPaymentStats] = useState({
    totalRevenue: 0,
    totalCommissions: 0,
    photographerPayouts: 0,
    pendingPayouts: 0,
    thisMonthRevenue: 0,
    thisMonthCommissions: 0,
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentsSubTab, setPaymentsSubTab] = useState('payouts');
  const [payoutConfirmModal, setPayoutConfirmModal] = useState(null);
  const [payoutTransferRef, setPayoutTransferRef] = useState('');
  const [expandedPayoutGroups, setExpandedPayoutGroups] = useState({});
  const [paymentDateFilter, setPaymentDateFilter] = useState('all'); // all, today, week, month
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // all, completed, pending, refunded
  const [adminUploadFiles, setAdminUploadFiles] = useState([]);
  const [adminUploading, setAdminUploading] = useState(false);

  // Email Templates States
  const [emailTemplates, setEmailTemplates] = useState({});
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState('customer');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState({ subject: '', body: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templatePreviewMode, setTemplatePreviewMode] = useState(false);
  const [templateSaveStatus, setTemplateSaveStatus] = useState(null); // 'success' | 'error' | null
  const [templateSaveMessage, setTemplateSaveMessage] = useState('');
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState(null); // 'success' | 'error' | null

  // Bulk Notifications States
  const [bulkNotification, setBulkNotification] = useState({
    title: '',
    message: '',
    recipientFilter: 'all',
    channels: { inApp: true, push: false, email: false, whatsapp: false },
    scheduleType: 'immediate', // immediate or scheduled
    scheduledDate: '',
    scheduledTime: '',
  });
  const [bulkNotificationLoading, setBulkNotificationLoading] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [recipientsList, setRecipientsList] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [notificationHistoryLoading, setNotificationHistoryLoading] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationPreview, setNotificationPreview] = useState(false);
  const [showCustomRecipientsModal, setShowCustomRecipientsModal] = useState(false);
  const [customRecipientsSearch, setCustomRecipientsSearch] = useState('');
  const [allUsersForCustom, setAllUsersForCustom] = useState([]);
  const [loadingCustomUsers, setLoadingCustomUsers] = useState(false);
  const [tempSelectedIds, setTempSelectedIds] = useState([]);

  // Support Tickets States
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketBookingDetails, setTicketBookingDetails] = useState(null);
  const [ticketBookingLoading, setTicketBookingLoading] = useState(false);
  const [disputeActionLoading, setDisputeActionLoading] = useState(false);
  const [ticketReplies, setTicketReplies] = useState([]);
  const [ticketReplyText, setTicketReplyText] = useState('');
  const [ticketReplyInternal, setTicketReplyInternal] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    waitingUser: 0,
    waitingAdmin: 0,
    resolved: 0,
    avgResponseTime: 0,
  });
  const [ticketFilter, setTicketFilter] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    userRole: 'all',
    ticketType: 'all',
    search: '',
  });
  const [showTicketModal, setShowTicketModal] = useState(false);

  // App Settings / API Keys
  const [appSettings, setAppSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editingSettings, setEditingSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [adminUploadProgress, setAdminUploadProgress] = useState(0);
  const adminFileInputRef = useRef(null);

  const [error, setError] = useState("");

  const canMutate = true; // المشرف مصادق عليه عبر AdminGuard
  const [mutating, setMutating] = useState(false);
  const [notice, setNotice] = useState("");

  const [bookingEdits, setBookingEdits] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [updatingPhotographer, setUpdatingPhotographer] = useState(null); // Track which photographer is being updated
  const [updatingCustomer, setUpdatingCustomer] = useState(null); // Track which customer is being updated
  const [showAddPhotographerModal, setShowAddPhotographerModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: ''
  });
  const [refreshKey, setRefreshKey] = useState(0);
  
  // ── Add photographer wizard state ────────────────────────────────────────
  const [photographerWizardStep, setPhotographerWizardStep] = useState(1); // 1=info, 2=media, 3=map, 4=identity
  const [photographerWizardUserId, setPhotographerWizardUserId] = useState(null);
  const [photographerForm, setPhotographerForm] = useState({
    full_name: '', email: '', phone: '',
    entity_type: 'individual', business_name: '',
    city: '', specializations: [],
    shooting_methods: [], shooting_method_models: {},
    equipment_available: [], languages: [],
  });
  // Step 2: avatar + portfolio
  const [adminAvatarFile,    setAdminAvatarFile]    = useState(null);
  const [adminAvatarPreview, setAdminAvatarPreview] = useState(null);
  const [adminPortfolioItems, setAdminPortfolioItems] = useState([]);
  const [adminUploadingFiles, setAdminUploadingFiles] = useState(false);
  const adminAvatarInputRef    = useRef(null);
  const adminPortfolioInputRef = useRef(null);
  // Step 3: service area
  const [adminServiceCenter,  setAdminServiceCenter]  = useState({ lat: null, lng: null });
  const [adminServiceRadius,  setAdminServiceRadius]  = useState(20);
  const [adminServiceLabel,   setAdminServiceLabel]   = useState('');
  const [adminSavingService,  setAdminSavingService]  = useState(false);
  const [adminMapVisible,     setAdminMapVisible]     = useState(false);
  const adminServiceCenterRef = useRef({ lat: null, lng: null });
  // Step 4: identity + drone permit
  const [adminIdFile,       setAdminIdFile]       = useState(null);
  const [adminIdUploading,  setAdminIdUploading]  = useState(false);
  const [adminIdUploaded,   setAdminIdUploaded]   = useState(false);
  const [adminDroneFile,    setAdminDroneFile]    = useState(null);
  const [adminDroneUploading, setAdminDroneUploading] = useState(false);
  const [adminDroneUploaded,  setAdminDroneUploaded]  = useState(false);
  const [adminFreelanceFile, setAdminFreelanceFile] = useState(null);
  const [adminFreelanceUploading, setAdminFreelanceUploading] = useState(false);
  const [adminFreelancePermitUrl, setAdminFreelancePermitUrl] = useState(null);
  const [adminFreelancePermitStatus, setAdminFreelancePermitStatus] = useState('not_uploaded');
  const adminIdInputRef    = useRef(null);
  const adminDroneInputRef = useRef(null);
  const adminFreelanceInputRef = useRef(null);

  const ADMIN_CITY_COORDS = {
    'الرياض': { lat: 24.7136, lng: 46.6753 }, 'جدة': { lat: 21.4858, lng: 39.1925 },
    'الدمام': { lat: 26.4207, lng: 50.0888 }, 'الخبر': { lat: 26.2172, lng: 50.1971 },
    'المدينة المنورة': { lat: 24.5247, lng: 39.5692 }, 'مكة المكرمة': { lat: 21.3891, lng: 39.8579 },
    'الطائف': { lat: 21.2703, lng: 40.4158 }, 'أبها': { lat: 18.2164, lng: 42.5042 },
    'خميس مشيط': { lat: 18.3000, lng: 42.7333 }, 'جازان': { lat: 16.8894, lng: 42.5706 },
    'نجران': { lat: 17.4924, lng: 44.1277 }, 'الباحة': { lat: 20.0129, lng: 41.4677 },
    'تبوك': { lat: 28.3998, lng: 36.5700 }, 'حائل': { lat: 27.5114, lng: 41.7208 },
    'بريدة': { lat: 26.3260, lng: 43.9750 }, 'سكاكا': { lat: 29.9697, lng: 40.2064 },
  };

  const resetPhotographerWizard = () => {
    setPhotographerWizardStep(1);
    setPhotographerWizardUserId(null);
    setPhotographerForm({ full_name: '', email: '', phone: '', entity_type: 'individual', business_name: '', city: '', specializations: [], shooting_methods: [], shooting_method_models: {}, equipment_available: [], languages: [] });
    setAdminAvatarFile(null); setAdminAvatarPreview(null); setAdminPortfolioItems([]);
    setAdminServiceCenter({ lat: null, lng: null }); setAdminServiceLabel(''); setAdminServiceRadius(20);
    setAdminMapVisible(false);
    adminServiceCenterRef.current = { lat: null, lng: null };
    setAdminIdFile(null); setAdminIdUploaded(false); setAdminIdUploading(false);
    setAdminDroneFile(null); setAdminDroneUploaded(false); setAdminDroneUploading(false);
    setAdminFreelanceFile(null); setAdminFreelanceUploading(false);
    setAdminFreelancePermitUrl(null); setAdminFreelancePermitStatus('not_uploaded');
  };

  const adminFinishPhotographerWizard = () => {
    const name = photographerForm.full_name?.trim() || 'المصور';
    setShowAddPhotographerModal(false);
    resetPhotographerWizard();
    refreshData();
    setNotice(adminIdUploaded
      ? `تم تسجيل ${name} بنجاح مع رفع مستند الهوية`
      : `تم تسجيل ${name} بنجاح — يمكن رفع الهوية لاحقاً من قسم التحقق`);
  };

  const adminUploadIdDocument = async (file) => {
    const uid = photographerWizardUserId;
    if (!file || !uid) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) { setNotice('يُقبل JPEG, PNG, WebP أو PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { setNotice('الحجم الأقصى 10MB'); return; }
    setAdminIdUploading(true);
    try {
      await adminService.uploadPhotographerWizardIdDocument({
        userId: uid,
        file,
        photographerName: photographerForm.full_name || 'مصور',
      });
      setAdminIdUploaded(true);
      setNotice('تم رفع الهوية بنجاح');
    } catch (err) {
      setNotice(err?.message || 'فشل رفع الهوية');
    } finally {
      setAdminIdUploading(false);
    }
  };

  const adminUploadDronePermit = async (file) => {
    const uid = photographerWizardUserId;
    if (!file || !uid) return;
    setAdminDroneUploading(true);
    try {
      await adminService.uploadPhotographerWizardDronePermit({
        userId: uid,
        file,
        photographerName: photographerForm.full_name || 'مصور',
      });
      setAdminDroneUploaded(true);
      setNotice('تم رفع تصريح الدرون بنجاح');
    } catch (err) {
      setNotice(err?.message || 'فشل رفع التصريح');
    } finally {
      setAdminDroneUploading(false);
    }
  };

  const adminUploadFreelancePermit = async (file) => {
    const uid = photographerWizardUserId;
    if (!file || !uid) return;
    const validationError = validateFreelancePermitFile(file);
    if (validationError) { setNotice(validationError); return; }
    setAdminFreelanceUploading(true);
    try {
      await adminService.uploadPhotographerWizardFreelancePermit({
        userId: uid,
        file,
        photographerName: photographerForm.full_name || 'مصور',
      });
      setAdminFreelancePermitUrl('uploaded');
      setAdminFreelancePermitStatus('submitted');
      setNotice('تم رفع وثيقة العمل الحر وإرسالها للمراجعة');
    } catch (err) {
      setNotice(err?.message || 'فشل رفع الوثيقة');
    } finally {
      setAdminFreelanceUploading(false);
    }
  };

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Reviews Management States
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Verification Requests States
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [updatingVerificationUserId, setUpdatingVerificationUserId] = useState(null);
  const [previewPortfolioImage, setPreviewPortfolioImage] = useState(null);
  // SAFE MODE: Certificates feature - reject reason for certificates
  const [certificatesRejectReason, setCertificatesRejectReason] = useState('');
  const [showCertificatesRejectInput, setShowCertificatesRejectInput] = useState(false);
  const [droneRejectReason, setDroneRejectReason] = useState('');
  const [showDroneRejectInput, setShowDroneRejectInput] = useState(false);
  const [droneExpiryInput, setDroneExpiryInput] = useState('');
  const [freelanceRejectReason, setFreelanceRejectReason] = useState('');
  const [showFreelanceRejectInput, setShowFreelanceRejectInput] = useState(false);
  const [freelanceExpiryInput, setFreelanceExpiryInput] = useState('');
  const [identityExpiryInput, setIdentityExpiryInput] = useState('');
  const [identityRejectReason, setIdentityRejectReason] = useState('');
  const [showIdentityRejectInput, setShowIdentityRejectInput] = useState(false);
  // عرض ملفات التسليم للمشرف
  const [showDeliveryFilesModal, setShowDeliveryFilesModal] = useState(false);
  const [selectedDeliveryForFiles, setSelectedDeliveryForFiles] = useState(null);
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [loadingDeliveryFiles, setLoadingDeliveryFiles] = useState(false);
  const [lightboxFile, setLightboxFile] = useState(null);
  // فلاتر تبويب التسليمات
  const [deliverySearch, setDeliverySearch] = useState('');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all');
  // فلتر صفحة التحقق: بحث (رقم المصور / الاسم / البريد) وحالة الطلب
  const [verificationFilterSearch, setVerificationFilterSearch] = useState('');
  const [verificationFilterStatus, setVerificationFilterStatus] = useState('all');
  // فلتر صفحة المصورين: بحث (الاسم / الهاتف / البريد / المدينة) والحالة
  const [photographerFilterSearch, setPhotographerFilterSearch] = useState('');
  const [photographerFilterStatus, setPhotographerFilterStatus] = useState('all');
  // فلتر صفحة العملاء: بحث (الاسم / الهاتف / البريد) والحالة
  const [customerFilterSearch, setCustomerFilterSearch] = useState('');
  const [customerFilterStatus, setCustomerFilterStatus] = useState('all');
  // نافذة الرفض الموحّدة بأسباب متعددة
  const [showRejectionPanel, setShowRejectionPanel] = useState(false);
  const [rejectionTargetId, setRejectionTargetId] = useState(null);
  const [selectedRejectionReasons, setSelectedRejectionReasons] = useState([]);
  const [customRejectionNote, setCustomRejectionNote] = useState('');

  // القائمة الجانبية قابلة للطي لتوسيع المحتوى
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Admin Notifications States
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [pendingVerificationsCount, setPendingVerificationsCount] = useState(0);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);

  // Badge counts for all sections
  const [badgeCounts, setBadgeCounts] = useState({
    bookings: 0,      // pending bookings
    deliveries: 0,    // pending deliveries
    photographers: 0, // pending approval
    verification: 0,  // pending verification
    reviews: 0,       // unmoderated reviews
    support: 0,       // open/waiting tickets
    payments: 0,      // pending payouts
    refunds: 0,       // pending customer refunds
    stuckBookings: 0, // paid_confirmed but session passed
  });

  // Cancellation requests
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [cancellationMutating, setCancellationMutating] = useState(null); // bookingId being processed
  const [pendingRefunds, setPendingRefunds] = useState([]); // photographer withdrawals needing manual refund

  // Stuck bookings (paid_confirmed but session date passed)
  const [stuckBookings, setStuckBookings] = useState([]);
  const [stuckBookingsLoading, setStuckBookingsLoading] = useState(false);
  const [resolvingStuck, setResolvingStuck] = useState(null); // bookingId being resolved

  const loadCancellationRequests = async () => {
    setCancellationLoading(true);
    try {
      // Step 1: fetch bookings with cancellation_requested status
      const { data: bookingsData, error: bookingsErr } = await supabase
        .from('bookings')
        .select('id, booking_date, booking_time, price, final_price_sar, customer_id, assigned_photographer_id, cancellation_requested_by, cancellation_requested_at, cancellation_fee_pct, cancellation_reason')
        .eq('status', 'cancellation_requested')
        .order('cancellation_requested_at', { ascending: false });

      if (bookingsErr || !bookingsData) {
        console.error('loadCancellationRequests bookings error:', bookingsErr);
        setCancellationRequests([]);
        return;
      }

      if (bookingsData.length === 0) {
        setCancellationRequests([]);
        return;
      }

      // Step 2: enrich with user/photographer names
      const customerIds = [...new Set(bookingsData.map(b => b.customer_id).filter(Boolean))];
      const photographerIds = [...new Set(bookingsData.map(b => b.assigned_photographer_id).filter(Boolean))];

      const [customersRes, photographersRes] = await Promise.all([
        customerIds.length > 0
          ? supabase.from('user_profiles').select('id, full_name').in('id', customerIds)
          : Promise.resolve({ data: [] }),
        photographerIds.length > 0
          ? supabase.from('photographer_profiles').select('user_id, display_name').in('user_id', photographerIds)
          : Promise.resolve({ data: [] }),
      ]);

      const customerMap = Object.fromEntries((customersRes.data || []).map(u => [u.id, u.full_name]));
      const photographerMap = Object.fromEntries((photographersRes.data || []).map(p => [p.user_id, p.display_name]));

      const enriched = bookingsData.map(b => ({
        ...b,
        total_price: b.final_price_sar || b.price || 0,
        customer_name: customerMap[b.customer_id] || '—',
        photographer_name: photographerMap[b.assigned_photographer_id] || '—',
      }));

      setCancellationRequests(enriched);
    } catch (e) {
      console.error('loadCancellationRequests error:', e);
      setCancellationRequests([]);
    } finally {
      setCancellationLoading(false);
    }
  };

  const loadPendingRefunds = async () => {
    try {
      const { data: bookingsData } = await supabase
        .from('bookings')
        .select('id, booking_date, booking_time, price, final_price_sar, customer_id, assigned_photographer_id, cancellation_reason, cancellation_requested_at')
        .eq('status', 'cancelled_by_photographer')
        .eq('payment_status', 'refund_pending')
        .order('cancellation_requested_at', { ascending: false });

      if (!bookingsData || bookingsData.length === 0) { setPendingRefunds([]); return; }

      const customerIds = [...new Set(bookingsData.map(b => b.customer_id).filter(Boolean))];
      const photographerIds = [...new Set(bookingsData.map(b => b.assigned_photographer_id).filter(Boolean))];

      const [customersRes, photographersRes] = await Promise.all([
        customerIds.length > 0 ? supabase.from('user_profiles').select('id, full_name').in('id', customerIds) : Promise.resolve({ data: [] }),
        photographerIds.length > 0 ? supabase.from('photographer_profiles').select('user_id, display_name').in('user_id', photographerIds) : Promise.resolve({ data: [] }),
      ]);

      const customerMap = Object.fromEntries((customersRes.data || []).map(u => [u.id, u.full_name]));
      const photographerMap = Object.fromEntries((photographersRes.data || []).map(p => [p.user_id, p.display_name]));

      setPendingRefunds(bookingsData.map(b => ({
        ...b,
        total_price: b.final_price_sar || b.price || 0,
        customer_name: customerMap[b.customer_id] || '—',
        photographer_name: photographerMap[b.assigned_photographer_id] || '—',
      })));
    } catch (e) {
      console.error('loadPendingRefunds error:', e);
    }
  };

  const loadStuckBookings = async () => {
    setStuckBookingsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('bookings')
        .select('id, booking_date, booking_time, city, photography_type, final_price_sar, price, customer_id, photographer_id, assigned_photographer_id')
        .eq('status', 'paid_confirmed')
        .lt('booking_date', today)
        .order('booking_date', { ascending: false });
      if (error) throw error;
      if (!data || data.length === 0) { setStuckBookings([]); return; }

      const custIds = [...new Set(data.map(b => b.customer_id).filter(Boolean))];
      const photIds = [...new Set(data.map(b => b.photographer_id || b.assigned_photographer_id).filter(Boolean))];

      const [{ data: custs }, { data: phots }] = await Promise.all([
        supabase.from('user_profiles').select('id, full_name').in('id', custIds),
        supabase.from('user_profiles').select('id, full_name').in('id', photIds),
      ]);
      const cMap = Object.fromEntries((custs || []).map(u => [u.id, u.full_name]));
      const pMap = Object.fromEntries((phots || []).map(u => [u.id, u.full_name]));

      setStuckBookings(data.map(b => ({
        ...b,
        customer_name: cMap[b.customer_id] || '—',
        photographer_name: pMap[b.photographer_id || b.assigned_photographer_id] || '—',
        amount: b.final_price_sar || b.price || 0,
      })));
    } catch (e) {
      console.error('loadStuckBookings error:', e);
    } finally {
      setStuckBookingsLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  // ─── No-Show Refund: Admin marks photographer absent → sends to refunds queue ─
  const handleAdminNoShowRefund = async (booking) => {
    if (!window.confirm(`هل تريد إرسال حجز #${String(booking.id).slice(0, 8).toUpperCase()} إلى قائمة الاستردادات بسبب غياب المصور؟`)) return;
    try {
      setMutating(true);
      const { error } = await supabase
        .from('bookings')
        .update({
          status: 'cancelled_by_photographer',
          payment_status: 'refund_pending',
          cancellation_reason: 'غياب المصور عن الجلسة — تم الإبلاغ من العميل',
          cancellation_requested_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', booking.id);
      if (error) throw error;
      setNotice(`✅ تم إرسال الحجز #${String(booking.id).slice(0, 8).toUpperCase()} إلى صفحة إدارة الاستردادات`);
      refreshData();
      navigate('/admin-refunds');
    } catch (e) {
      setNotice(e?.message || 'فشل العملية، يرجى المحاولة مرة أخرى');
    } finally {
      setMutating(false);
    }
  };

  // Load all badge counts for sections
  const loadBadgeCounts = async () => {
    try {
      const counts = { ...badgeCounts };
      
      // 1. Pending bookings (status = pending or assigned)
      const { count: pendingBookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .in('status', ['pending', 'assigned', 'hold']);
      counts.bookings = pendingBookings || 0;
      
      // 2. Pending deliveries (delivery_status = pending or not delivered)
      const { count: pendingDeliveries } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .or('delivery_status.is.null,delivery_status.eq.pending');
      counts.deliveries = pendingDeliveries || 0;
      
      // 3. Photographers pending approval
      const { count: pendingPhotographers } = await supabase
        .from('photographer_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      counts.photographers = pendingPhotographers || 0;
      
      // 4. Pending verification (verification_status = submitted)
      const { count: pendingVerification } = await supabase
        .from('photographer_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('verification_status', 'submitted');
      counts.verification = pendingVerification || 0;
      setPendingVerificationsCount(pendingVerification || 0);
      
      // 5. Unmoderated reviews (if there's a moderation field)
      const { count: pendingReviews } = await supabase
        .from('reviews')
        .select('*', { count: 'exact', head: true })
        .or('moderated.is.null,moderated.eq.false');
      counts.reviews = pendingReviews || 0;
      
      // 6. Open support tickets
      const { count: openTickets } = await supabase
        .from('support_tickets')
        .select('*', { count: 'exact', head: true })
        .in('status', ['open', 'waiting_admin', 'in_progress']);
      counts.support = openTickets || 0;
      
      // 7. Pending payouts (future implementation)
      counts.payments = 0;

      // 8. Cancellation requests pending admin review
      const { count: pendingCancellations } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'cancellation_requested');
      counts.cancellations = pendingCancellations || 0;

      // 9. Pending customer refunds
      const { count: pendingRefundCount } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'cancelled_by_photographer')
        .eq('payment_status', 'refund_pending');
      counts.refunds = pendingRefundCount || 0;

      // 10. Stuck bookings (paid_confirmed, session date passed)
      const todayStr = new Date().toISOString().split('T')[0];
      const { count: stuckCount } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'paid_confirmed')
        .lt('booking_date', todayStr);
      counts.stuckBookings = stuckCount || 0;
      
      console.log('📊 Badge counts loaded:', counts);
      setBadgeCounts(counts);
      
      // Show notification banner if there are pending verifications
      if (counts.verification > 0) {
        setShowNotificationBanner(true);
      }
      
    } catch (error) {
      console.error('Error loading badge counts:', error);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // Load badge counts first
        await loadBadgeCounts();
        loadCancellationRequests();
        loadPendingRefunds();
        loadStuckBookings();
        
        const platformStats = await adminService.getPlatformStats();
        if (!alive) return;
        setStats(platformStats);

        // Latest bookings with full details
        const { data: bookingsData, error: bookingsError } = await supabase
          .from("bookings")
          .select(`
            id,
            created_at,
            updated_at,
            status,
            city,
            photography_type,
            price,
            final_price_sar,
            customer_id,
            photographer_id,
            assigned_photographer_id,
            booking_date,
            booking_time,
            scheduled_at,
            duration,
            location,
            location_details,
            pricing_snapshot,
            attempt_count,
            hold_expires_at,
            payment_expires_at,
            payment_transaction_id,
            photographer_response_at,
            payment_status,
            no_show_reported_at,
            session_started_at,
            session_ended_at
          `)
          .order("created_at", { ascending: false })
          .limit(200);
        
        // SAFE MODE: Enhanced error handling and logging for booking visibility (data flow only)
        if (bookingsError) {
          console.error('❌ Error loading bookings:', bookingsError);
          console.error('❌ Error details:', {
            code: bookingsError.code,
            message: bookingsError.message,
            details: bookingsError.details,
            hint: bookingsError.hint
          });
          if (alive) {
            setError('تعذّر تحميل الحجوزات. حدّث الصفحة أو تواصل مع الدعم.');
            setBookings([]);
          }
        } else {
          console.log('✅ Bookings query successful:', {
            count: bookingsData?.length || 0,
            sampleIds: bookingsData?.slice(0, 3).map(b => b.id) || []
          });
        }
        
        if (!alive) return;
        
        // SAFE MODE: Ensure bookings are set even if empty (data flow fix)
        // Enrich bookings with customer and photographer info
        if (bookingsData && bookingsData.length > 0) {
          const customerIds = [...new Set(bookingsData.map(b => b.customer_id).filter(Boolean))];
          const photographerIds = [...new Set([
            ...bookingsData.map(b => b.photographer_id).filter(Boolean),
            ...bookingsData.map(b => b.assigned_photographer_id).filter(Boolean)
          ].filter(Boolean))];
          
          const [customersResult, photographersResult] = await Promise.all([
            customerIds.length > 0 
              ? db.from('user_profiles').select('id, full_name, email, phone, avatar_url').in('id', customerIds)
              : Promise.resolve({ data: [], error: null }),
            photographerIds.length > 0
              ? db.from('user_profiles').select('id, full_name, email, phone, avatar_url').in('id', photographerIds)
              : Promise.resolve({ data: [], error: null })
          ]);
          
          const customersMap = {};
          const photographersMap = {};
          
          if (customersResult.data) {
            customersResult.data.forEach(c => { customersMap[c.id] = c; });
          }
          if (photographersResult.data) {
            photographersResult.data.forEach(p => { photographersMap[p.id] = p; });
          }
          
          const enrichedBookings = bookingsData.map(booking => ({
            ...booking,
            customer: customersMap[booking.customer_id] || null,
            photographer: photographersMap[booking.photographer_id || booking.assigned_photographer_id] || null
          }));
          
          console.log('✅ Bookings loaded and enriched:', {
            total: enrichedBookings.length,
            sample: enrichedBookings[0] ? {
              id: enrichedBookings[0].id,
              customer: enrichedBookings[0].customer?.full_name,
              photographer: enrichedBookings[0].photographer?.full_name,
              status: enrichedBookings[0].status
            } : null
          });
          
          setBookings(enrichedBookings);
          console.log('✅ Bookings set in state:', enrichedBookings.length);
        } else {
          console.log('⚠️ No bookings data found - setting empty array');
          // SAFE MODE: Explicitly set empty array to ensure state is updated
          setBookings([]);
        }

        // Load Photographers from photographer_profiles with user_profiles and photographers table
        const loadPhotographers = async () => {
          try {
            // Get all photographer profiles (with cache busting to ensure fresh data)
            const { data: photographerProfiles, error: ppError } = await supabase
              .from('photographer_profiles')
              .select('user_id, city, available_for_booking, experience_years, experience_level, created_at')
              .order('created_at', { ascending: false })
              .limit(200);

            if (ppError) {
              console.error('❌ Error loading photographer_profiles:', ppError);
              console.error('Error details:', {
                message: ppError.message,
                code: ppError.code,
                details: ppError.details,
                hint: ppError.hint
              });
              return [];
            }

            console.log('📸 Found photographer_profiles:', photographerProfiles?.length || 0);

            if (!photographerProfiles || photographerProfiles.length === 0) {
              console.warn('⚠️ No photographer_profiles found in database');
              return [];
            }

            // Get user_ids
            const userIds = photographerProfiles.map(p => p.user_id).filter(Boolean);
            console.log('👤 User IDs to fetch:', userIds.length);
            
            if (userIds.length === 0) {
              console.warn('⚠️ No valid user_ids found in photographer_profiles');
              return [];
            }

            // Get user profiles (note: user_profiles doesn't have city column)
            let userProfiles;
            let upError;
            // First try with deleted_by_user (new column)
            let usersResult = await supabase
              .from('user_profiles')
              .select('id, full_name, phone, email, avatar_url, created_at, role, is_active, deleted_by_user')
              .in('id', userIds);

            userProfiles = usersResult.data;
            upError = usersResult.error;

            // Fallback if deleted_by_user column does not exist (older DB schema)
            if (
              upError &&
              (
                upError.code === '42703' ||
                upError.message?.includes('deleted_by_user') ||
                upError.message?.includes('does not exist') ||
                upError.message?.toLowerCase().includes('column')
              )
            ) {
              console.warn('⚠️ user_profiles.deleted_by_user is missing, falling back without this column');
              const fallback = await supabase
                .from('user_profiles')
                .select('id, full_name, phone, email, avatar_url, created_at, role, is_active')
                .in('id', userIds);
              userProfiles = fallback.data;
              upError = fallback.error;
            }

            if (upError) {
              console.error('❌ Error loading user_profiles:', upError);
              console.error('Error details:', {
                message: upError.message,
                code: upError.code,
                details: upError.details,
                hint: upError.hint
              });
              return [];
            }

            console.log('👥 Found user_profiles:', userProfiles?.length || 0);

            // Filter for photographers manually
            const photographerUsers = (userProfiles || []).filter(u => u.role === 'photographer');
            console.log('📷 Filtered photographers:', photographerUsers.length);

            // Get status, experience_level and deleted_by_user from photographer_profiles table
            const photographerUserIds = photographerUsers.map(u => u.id);
            let photographersStatusMap = {};
            let photographersExpLevelMap = {};
            let photographersDeletedByUserMap = {};
            
            try {
              // Force fresh query without cache to get latest experience_level
              // NOTE: نستخدم created_at للترتيب لضمان التوافق حتى إذا لم يكن عمود updated_at متوفراً في بعض البيئات
              const { data: photographerProfilesData, error: ppError } = await supabase
                .from('photographer_profiles')
                .select('user_id, status, experience_level, deleted_by_user')
                .in('user_id', photographerUserIds)
                .order('created_at', { ascending: false });
              
              if (ppError) {
                console.error('❌ Error loading photographer_profiles status/experience_level:', ppError);
              } else if (photographerProfilesData) {
                console.log('📊 Loaded photographer_profiles data:', photographerProfilesData.length, 'records');
                photographerProfilesData.forEach(pp => {
                  photographersStatusMap[pp.user_id] = pp.status || 'pending';
                  // Convert DB value to UI value
                  const dbLevel = pp.experience_level || 'beginner';
                  photographersExpLevelMap[pp.user_id] = DB_TO_UI_EXPERIENCE_LEVEL[dbLevel] || 'certified';
                  photographersDeletedByUserMap[pp.user_id] = pp.deleted_by_user === true;
                  console.log(`  - User ${pp.user_id}: status=${pp.status}, db_level=${dbLevel}, ui_level=${photographersExpLevelMap[pp.user_id]}`);
                });
              }
            } catch (e) {
              console.warn('⚠️ Could not load photographer_profiles status:', e);
            }

            // Combine data
            const photographerMap = {};
            photographerProfiles.forEach(pp => {
              photographerMap[pp.user_id] = pp;
            });

            const result = photographerUsers
              .map(user => {
              // Priority: photographersExpLevelMap (from second query) > photographerMap (from first query) > default
              // Always normalize fallback to UI value so table buttons match (certified/distinguished/elite)
              const rawFromMap = photographerMap[user.id]?.experience_level;
              const fallbackUi = rawFromMap ? (DB_TO_UI_EXPERIENCE_LEVEL[rawFromMap] || 'certified') : 'certified';
              const finalExperienceLevel = photographersExpLevelMap[user.id] || fallbackUi;
              
              return {
              ...user,
              city: photographerMap[user.id]?.city || null, // city comes from photographer_profiles, not user_profiles
              available_for_booking: photographerMap[user.id]?.available_for_booking !== false,
              photographer_status: photographerMap[user.id]?.status || 'active',
              experience_years: photographerMap[user.id]?.experience_years || 0,
                // Use experience_level from the second query (most up-to-date) or fallback to photographerMap
                experience_level: finalExperienceLevel,
              status: photographersStatusMap[user.id] || 'pending', // Add status from photographers table
              deleted_by_user: photographersDeletedByUserMap[user.id] || user.deleted_by_user === true
              };
            });

            console.log('✅ Final photographers list:', result.length);
            result.forEach(p => {
              console.log(`  - ${p.full_name || p.id}: experience_level=${p.experience_level}, status=${p.status}`);
            });
            return result;
          } catch (error) {
            console.error('❌ Exception loading photographers:', error);
            return [];
          }
        };

        // Load Customers from user_profiles
        const loadCustomers = async () => {
          try {
            console.log('🔍 Loading customers from user_profiles...');
            const { data: customers, error } = await supabase
              .from('user_profiles')
              .select('id, full_name, phone, email, avatar_url, created_at, role, is_active')
              .eq('role', 'customer')
              .order('created_at', { ascending: false })
              .limit(200);

            if (error) {
              console.error('❌ Error loading customers:', error);
              console.error('Error details:', {
                message: error.message,
                code: error.code,
                details: error.details,
                hint: error.hint
              });
              return [];
            }

            console.log('✅ Found customers:', customers?.length || 0);
            return customers || [];
          } catch (error) {
            console.error('❌ Exception loading customers:', error);
            return [];
          }
        };

        // Load Deliveries
        const loadDeliveries = async () => {
          try {
            // First get deliveries with booking info
            const { data: deliveriesData, error: deliveriesError } = await supabase
              .from('booking_deliveries')
              .select(`
                *,
                booking:booking_id (
                  id,
                  booking_date,
                  photography_type,
                  city
                )
              `)
              .order('created_at', { ascending: false })
              .limit(100);
            
            if (deliveriesError) {
              console.error('❌ Error loading deliveries:', deliveriesError);
              return [];
            }

            if (!deliveriesData || deliveriesData.length === 0) {
              console.log('✅ No deliveries found');
              return [];
            }

            // Get unique user IDs
            const photographerIds = [...new Set(deliveriesData.map(d => d.photographer_id).filter(Boolean))];
            const customerIds = [...new Set(deliveriesData.map(d => d.customer_id).filter(Boolean))];
            const allUserIds = [...new Set([...photographerIds, ...customerIds])];

            // Fetch user profiles
            let usersMap = {};
            if (allUserIds.length > 0) {
              const { data: usersData } = await supabase
                .from('user_profiles')
                .select('id, full_name, avatar_url')
                .in('id', allUserIds);
              
              usersData?.forEach(u => { usersMap[u.id] = u; });
            }

            // Fetch ratings for deliveries
            const bookingIds = deliveriesData.map(d => d.booking_id).filter(Boolean);
            let ratingsMap = {};
            let disputesMap = {};
            if (bookingIds.length > 0) {
              const { data: ratingsData } = await supabase
                .from('reviews')
                .select('booking_id, rating, comment')
                .in('booking_id', bookingIds);
              ratingsData?.forEach(r => { ratingsMap[r.booking_id] = r; });

              const { data: disputeData } = await supabase
                .from('support_tickets')
                .select('related_booking_id, status, ticket_number, ticket_type')
                .in('related_booking_id', bookingIds)
                .eq('ticket_type', 'complaint');
              disputeData?.forEach(d => { disputesMap[d.related_booking_id] = d; });
            }

            // Enrich deliveries
            const enrichedDeliveries = deliveriesData.map(d => ({
              ...d,
              photographer: usersMap[d.photographer_id] || null,
              customer: usersMap[d.customer_id] || null,
              review: ratingsMap[d.booking_id] || null,
              dispute: disputesMap[d.booking_id] || null,
            }));

            console.log('✅ Found deliveries:', enrichedDeliveries.length);
            return enrichedDeliveries;
          } catch (error) {
            console.error('❌ Exception loading deliveries:', error);
            return [];
          }
        };

        const [ph, cu, dl] = await Promise.all([loadPhotographers(), loadCustomers(), loadDeliveries()]);
        if (!alive) return;
        console.log('📊 Setting photographers:', ph?.length || 0);
        console.log('📊 Setting customers:', cu?.length || 0);
        console.log('📊 Setting deliveries:', dl?.length || 0);
        setPhotographers(ph || []);
        setCustomers(cu || []);
        setDeliveries(dl || []);

        // Pricing rules will be loaded on-demand when user navigates to pricing tab
        // This prevents unnecessary HTTP requests and console errors on initial load
        setPricingTable(null);
        setPricingRules([]);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || "حدث خطأ أثناء تحميل لوحة التحكم");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  // ============ Admin Notifications Functions ============
  const loadAdminNotifications = async () => {
    try {
      // Load unread admin notifications
      const { data: notifications, error: notifError } = await supabase
        .from('admin_notifications')
        .select('*')
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!notifError && notifications) {
        setAdminNotifications(notifications);
        // Count verification requests
        const verificationCount = notifications.filter(n => n.type === 'verification_request').length;
        setPendingVerificationsCount(verificationCount);
        setShowNotificationBanner(notifications.length > 0);
      }

      // Also count pending verifications from photographer_profiles
      const { data: pendingPhotographers, error: ppError } = await supabase
        .from('photographer_profiles')
        .select('user_id')
        .eq('verification_status', 'submitted');

      if (!ppError && pendingPhotographers) {
        setPendingVerificationsCount(pendingPhotographers.length);
        if (pendingPhotographers.length > 0) {
          setShowNotificationBanner(true);
        }
      }
    } catch (error) {
      console.error('Error loading admin notifications:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await supabase
        .from('admin_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
      
      setAdminNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const dismissAllNotifications = async () => {
    try {
      await supabase
        .from('admin_notifications')
        .update({ is_read: true })
        .eq('is_read', false);
      
      setAdminNotifications([]);
      setShowNotificationBanner(false);
    } catch (error) {
      console.error('Error dismissing notifications:', error);
    }
  };

  // Load notifications on mount and periodically
  useEffect(() => {
    loadAdminNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(loadAdminNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // ============ App Settings / API Keys Functions ============
  const loadAppSettings = async () => {
    setSettingsLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setAppSettings(data || []);
      
      // Initialize editing state
      const editState = {};
      data?.forEach(setting => {
        editState[setting.key] = setting.value || '';
      });
      setEditingSettings(editState);
    } catch (error) {
      console.error('Error loading app settings:', error);
      setNotice('فشل تحميل الإعدادات');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      // Update each changed setting
      for (const setting of appSettings) {
        const newValue = editingSettings[setting.key];
        if (newValue !== setting.value) {
          const { error } = await supabase
            .from('app_settings')
            .update({ 
              value: newValue, 
              updated_at: new Date().toISOString() 
            })
            .eq('key', setting.key);
          
          if (error) throw error;
        }
      }
      
      setNotice('✅ تم حفظ الإعدادات بنجاح');
      loadAppSettings(); // Reload to confirm
    } catch (error) {
      console.error('Error saving settings:', error);
      setNotice('❌ فشل حفظ الإعدادات: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  // Load settings when settings tab is active
  useEffect(() => {
    if (active === 'settings' && appSettings.length === 0) {
      loadAppSettings();
    }
  }, [active]);

  // ============ Reviews Functions ============
  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          customer:customer_id (id, full_name, avatar_url, email),
          booking:booking_id (id, booking_date, photography_type, city)
        `)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;

      // Get photographer info separately
      const photographerIds = [...new Set(data?.map(r => r.photographer_id).filter(Boolean) || [])];
      let photographersMap = {};
      
      if (photographerIds.length > 0) {
        const { data: photographers } = await supabase
          .from('user_profiles')
          .select('id, full_name, avatar_url')
          .in('id', photographerIds);
        
        photographers?.forEach(p => { photographersMap[p.id] = p; });
      }

      const enrichedReviews = (data || []).map(review => ({
        ...review,
        photographer: photographersMap[review.photographer_id] || null
      }));

      setReviews(enrichedReviews);
      console.log('✅ Loaded reviews:', enrichedReviews.length);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setNotice('❌ فشل تحميل التقييمات');
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleToggleReviewVisibility = async (reviewId, currentVisibility) => {
    try {
      setMutating(true);
      const { error } = await supabase
        .from('reviews')
        .update({ is_visible: !currentVisibility, updated_at: new Date().toISOString() })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(prev => prev.map(r => 
        r.id === reviewId ? { ...r, is_visible: !currentVisibility } : r
      ));
      setNotice(`${!currentVisibility ? '✅ تم إظهار' : '⚠️ تم إخفاء'} التقييم`);
    } catch (error) {
      console.error('Error toggling review visibility:', error);
      setNotice('❌ فشل تحديث التقييم');
    } finally {
      setMutating(false);
    }
  };

  const handleFlagReview = async (reviewId, flagReason) => {
    try {
      setMutating(true);
      const { error } = await supabase
        .from('reviews')
        .update({ 
          is_flagged: true, 
          flag_reason: flagReason,
          updated_at: new Date().toISOString() 
        })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(prev => prev.map(r => 
        r.id === reviewId ? { ...r, is_flagged: true, flag_reason: flagReason } : r
      ));
      setNotice('⚠️ تم الإبلاغ عن التقييم');
    } catch (error) {
      console.error('Error flagging review:', error);
      setNotice('❌ فشل الإبلاغ عن التقييم');
    } finally {
      setMutating(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التقييم نهائياً؟')) return;
    
    try {
      setMutating(true);
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(prev => prev.filter(r => r.id !== reviewId));
      setNotice('🗑️ تم حذف التقييم نهائياً');
      if (selectedReview?.id === reviewId) {
        setShowReviewModal(false);
        setSelectedReview(null);
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      setNotice('❌ فشل حذف التقييم');
    } finally {
      setMutating(false);
    }
  };

  // Load reviews when reviews tab is active
  useEffect(() => {
    if (active === 'reviews' && reviews.length === 0) {
      loadReviews();
    }
  }, [active]);

  // ============ Payments & Commission Functions ============
  const loadPayments = async () => {
    setPaymentsLoading(true);
    try {
      // Fetch bookings that have been paid
      const { data: paidBookings, error } = await supabase
        .from('bookings')
        .select(`
          id,
          customer_id,
          photographer_id,
          assigned_photographer_id,
          final_price_sar,
          price,
          pricing_snapshot,
          status,
          created_at,
          booking_date,
          photography_type,
          city,
          photographer_payout_status,
          photographer_paid_at,
          photographer_payout_transfer_ref
        `)
        .in('status', ['paid_confirmed', 'completed', 'in_progress'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get customer and photographer details
      const customerIds = [...new Set(paidBookings?.map(b => b.customer_id).filter(Boolean))];
      const photographerIds = [...new Set(paidBookings?.map(b => b.photographer_id || b.assigned_photographer_id).filter(Boolean))];

      let customersMap = {};
      let photographersMap = {};

      if (customerIds.length > 0) {
        const { data: customerProfiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, avatar_url')
          .in('id', customerIds);
        customerProfiles?.forEach(c => { customersMap[c.id] = c; });
      }

      if (photographerIds.length > 0) {
        const { data: photographerProfiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, avatar_url')
          .in('id', photographerIds);
        photographerProfiles?.forEach(p => { photographersMap[p.id] = p; });
      }

      // بيانات المصورين البنكية (لتحويل الأرباح) — عبر Edge Function لتجاوز RLS
      let photographerBankMap = {};
      if (photographerIds.length > 0) {
        try {
          const bankRes = await callEdgeFunction('admin-db-action', {
            operation: 'get_photographer_banks',
            params: { userIds: photographerIds },
          });
          (bankRes?.data || []).forEach((p) => { photographerBankMap[p.user_id] = p; });
        } catch (bankErr) {
          console.warn('⚠️ Bank profiles via edge function failed, trying direct query:', bankErr);
          const { data: bankProfiles, error: bankError } = await supabase
            .from('photographer_profiles')
            .select('user_id, bank_name, bank_account_holder_name, iban')
            .in('user_id', photographerIds);
          if (bankError) {
            console.error('❌ Failed to load photographer bank profiles:', bankError);
            setNotice(`تعذّر تحميل البيانات البنكية: ${bankError.message}`);
          } else {
            bankProfiles?.forEach((p) => { photographerBankMap[p.user_id] = p; });
          }
        }
      }

      // Calculate payments with commission
      const paymentsData = paidBookings?.map(booking => {
        const { amount, commission, photographerNet } = getBookingCommissionBreakdown(booking, commissionSettings);
        const photographerId = booking.photographer_id || booking.assigned_photographer_id;
        
        return {
          id: booking.id,
          bookingId: booking.id,
          customerId: booking.customer_id,
          photographerId: photographerId,
          customer: customersMap[booking.customer_id] || null,
          photographer: photographersMap[photographerId] || null,
          photographerBank: photographerBankMap[photographerId] || null,
          amount: amount,
          commission,
          photographerAmount: photographerNet,
          status: booking.status === 'completed' ? 'completed' : 'pending',
          payoutStatus: booking.photographer_payout_status === 'paid' ? 'paid' : 'pending',
          paidAt: booking.photographer_paid_at,
          transferRef: booking.photographer_payout_transfer_ref || null,
          date: booking.created_at,
          bookingDate: booking.booking_date,
          photographyType: booking.photography_type,
          city: booking.city,
        };
      }) || [];

      setPayments(paymentsData);

      // Calculate stats
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const totalRevenue = paymentsData.reduce((sum, p) => sum + p.amount, 0);
      const totalCommissions = paymentsData.reduce((sum, p) => sum + p.commission, 0);
      const photographerPayouts = paymentsData.filter(p => p.payoutStatus === 'paid').reduce((sum, p) => sum + p.photographerAmount, 0);
      const pendingPayouts = paymentsData.filter(p => p.payoutStatus === 'pending').reduce((sum, p) => sum + p.photographerAmount, 0);
      
      const thisMonthPayments = paymentsData.filter(p => new Date(p.date) >= thisMonthStart);
      const thisMonthRevenue = thisMonthPayments.reduce((sum, p) => sum + p.amount, 0);
      const thisMonthCommissions = thisMonthPayments.reduce((sum, p) => sum + p.commission, 0);

      setPaymentStats({
        totalRevenue,
        totalCommissions,
        photographerPayouts,
        pendingPayouts,
        thisMonthRevenue,
        thisMonthCommissions,
      });

      console.log('✅ Payments loaded:', paymentsData.length);
    } catch (error) {
      console.error('❌ Error loading payments:', error);
      const msg = error?.message || error?.toString() || 'خطأ غير معروف';
      setNotice(`فشل تحميل سجل المدفوعات: ${msg}`);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadCommissionSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('key', 'commission_settings');

      // Handle case where no record exists
      if (error) {
        console.log('Error loading commission settings, using defaults:', error.message);
        return;
      }

      // Check if we got any results
      if (data && data.length > 0 && data[0]?.value) {
        try {
          const settings = JSON.parse(data[0].value);
          setCommissionSettings(settings);
          console.log('✅ Commission settings loaded:', settings);
        } catch (e) {
          console.log('Failed to parse commission settings, using defaults');
        }
      } else {
        console.log('No commission settings found, using defaults');
      }
    } catch (error) {
      console.log('Using default commission settings:', error);
    }
  };

  const saveCommissionSettings = async () => {
    setSavingCommission(true);
    try {
      // First check if record exists
      const { data: existing, error: selectError } = await supabase
        .from('app_settings')
        .select('id')
        .eq('key', 'commission_settings');

      const settingsValue = JSON.stringify(commissionSettings);

      if (!selectError && existing && existing.length > 0) {
        // Update existing record
        const { error } = await supabase
          .from('app_settings')
          .update({ value: settingsValue, updated_at: new Date().toISOString() })
          .eq('key', 'commission_settings');
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('app_settings')
          .insert({ 
            key: 'commission_settings', 
            value: settingsValue,
            description: 'إعدادات العمولة - نسبة العمولة والحدود',
            category: 'payments'
          });
        if (error) throw error;
      }

      setNotice('✅ تم حفظ إعدادات العمولة بنجاح');
      // Reload payments to recalculate
      loadPayments();
    } catch (error) {
      console.error('Error saving commission settings:', error);
      setNotice('❌ فشل حفظ إعدادات العمولة: ' + error.message);
    } finally {
      setSavingCommission(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`✅ تم نسخ ${label}`);
    } catch {
      setNotice('❌ فشل النسخ — حاول نسخ النص يدوياً');
    }
  };

  const buildPayoutCopyText = (group) => {
    const bank = group.bank || {};
    const iban = formatIbanForDisplay(bank.iban || '');
    return [
      `المصور: ${group.photographer?.full_name || '—'}`,
      bank.bank_account_holder_name ? `صاحب الحساب: ${bank.bank_account_holder_name}` : null,
      bank.bank_name ? `البنك: ${bank.bank_name}` : null,
      iban ? `IBAN: ${cleanIban(bank.iban)}` : null,
      `المبلغ: ${formatNumber(group.totalDue)} ر.س`,
      `عدد الحجوزات: ${group.bookings.length}`,
    ].filter(Boolean).join('\n');
  };

  const sendPayoutNotification = async (photographerId, totalAmount) => {
    if (!photographerId) return;
    const notifTitle = 'تم تحويل أرباحك 💰';
    const notifMsg = `تم تحويل ${formatNumber(totalAmount)} ر.س إلى حسابك البنكي.`;
    try {
      await db.from('notifications').insert({
        user_id: photographerId,
        title: notifTitle,
        message: notifMsg,
        type: 'payment',
        is_read: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      callEdgeFunction('send-fcm-notification', {
        user_id: photographerId,
        title: notifTitle,
        body: notifMsg,
        data: { type: 'PAYOUT' },
      }).catch((e) => console.warn('⚠️ FCM payout notification failed:', e));
    } catch (e) {
      console.warn('⚠️ Failed to send payout notification:', e);
    }
  };

  const pendingPayoutGroups = useMemo(() => {
    const map = new Map();
    payments
      .filter((p) => p.payoutStatus === 'pending' && p.photographerId)
      .forEach((p) => {
        const pid = p.photographerId;
        if (!map.has(pid)) {
          map.set(pid, {
            photographerId: pid,
            photographer: p.photographer,
            bank: p.photographerBank,
            bookings: [],
            totalDue: 0,
          });
        }
        const group = map.get(pid);
        group.bookings.push(p);
        group.totalDue += p.photographerAmount || 0;
        if (p.photographerBank && !group.bank) group.bank = p.photographerBank;
      });
    return Array.from(map.values()).sort((a, b) => b.totalDue - a.totalDue);
  }, [payments]);

  const openPayoutConfirm = (payload) => {
    if (payload.bank && !hasValidSaudiIban(payload.bank?.iban)) {
      setNotice('❌ لا يمكن تأكيد الصرف — المصور لم يُدخل IBAN صالحاً');
      return;
    }
    setPayoutTransferRef('');
    setPayoutConfirmModal(payload);
  };

  const executePayoutConfirm = async () => {
    if (!payoutConfirmModal) return;
    const { bookingIds, photographerId, totalAmount, bank } = payoutConfirmModal;
    if (bank && !hasValidSaudiIban(bank?.iban)) {
      setNotice('❌ لا يمكن تأكيد الصرف — IBAN غير متوفر أو غير صالح');
      return;
    }

    setMutating(true);
    try {
      await callEdgeFunction('admin-db-action', {
        operation: 'set_photographer_paid_batch',
        params: {
          bookingIds,
          transferRef: payoutTransferRef.trim() || null,
        },
      });

      const now = new Date().toISOString();
      const ref = payoutTransferRef.trim() || null;
      setPayments((prev) =>
        prev.map((p) =>
          bookingIds.includes(p.id)
            ? { ...p, payoutStatus: 'paid', paidAt: now, transferRef: ref }
            : p
        )
      );

      await sendPayoutNotification(photographerId, totalAmount);

      setNotice(`✅ تم تأكيد صرف ${formatNumber(totalAmount)} ر.س للمصور`);
      setPayoutConfirmModal(null);
      setPayoutTransferRef('');
      if (showPaymentModal) {
        setShowPaymentModal(false);
        setSelectedPayment(null);
      }
    } catch (error) {
      console.error('Error processing payout:', error);
      setNotice('❌ فشل في عملية الصرف: ' + (error?.message || ''));
    } finally {
      setMutating(false);
    }
  };

  const handlePayoutPhotographer = (payment) => {
    openPayoutConfirm({
      mode: 'single',
      photographerId: payment.photographerId,
      photographer: payment.photographer,
      bank: payment.photographerBank,
      bookingIds: [payment.id],
      totalAmount: payment.photographerAmount,
      bookings: [payment],
    });
  };

  const handleBatchPayoutPhotographer = (group) => {
    openPayoutConfirm({
      mode: 'batch',
      photographerId: group.photographerId,
      photographer: group.photographer,
      bank: group.bank,
      bookingIds: group.bookings.map((b) => b.id),
      totalAmount: group.totalDue,
      bookings: group.bookings,
    });
  };

  const togglePayoutGroupExpanded = (photographerId) => {
    setExpandedPayoutGroups((prev) => ({ ...prev, [photographerId]: !prev[photographerId] }));
  };

  const getFilteredPayments = () => {
    let filtered = [...payments];
    
    // Date filter
    const now = new Date();
    if (paymentDateFilter === 'today') {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filtered = filtered.filter(p => new Date(p.date) >= todayStart);
    } else if (paymentDateFilter === 'week') {
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(p => new Date(p.date) >= weekStart);
    } else if (paymentDateFilter === 'month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      filtered = filtered.filter(p => new Date(p.date) >= monthStart);
    }
    
    // Status filter
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === paymentStatusFilter || p.payoutStatus === paymentStatusFilter);
    }
    
    return filtered;
  };

  // Load payments when tab is active (reload each visit)
  useEffect(() => {
    if (active === 'payments') {
      loadPayments();
      loadCommissionSettings();
    }
  }, [active]);

  useEffect(() => {
    if (active === 'bookings') {
      loadCommissionSettings();
    }
  }, [active]);

  // ============ Email Templates Functions ============
  const loadEmailTemplates = async () => {
    setEmailTemplatesLoading(true);
    try {
      // Load all email templates from app_settings
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('key', 'email_templates');
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setEmailTemplates(JSON.parse(data[0].value));
      } else {
        // Initialize with default templates
        const defaultTemplates = {};
        Object.keys(EMAIL_TEMPLATES_CONFIG).forEach(category => {
          EMAIL_TEMPLATES_CONFIG[category].templates.forEach(template => {
            defaultTemplates[template.key] = getDefaultTemplateContent(template.key);
          });
        });
        setEmailTemplates(defaultTemplates);
      }
    } catch (err) {
      console.error('Error loading email templates:', err);
      // Use defaults on error
      const defaultTemplates = {};
      Object.keys(EMAIL_TEMPLATES_CONFIG).forEach(category => {
        EMAIL_TEMPLATES_CONFIG[category].templates.forEach(template => {
          defaultTemplates[template.key] = getDefaultTemplateContent(template.key);
        });
      });
      setEmailTemplates(defaultTemplates);
    } finally {
      setEmailTemplatesLoading(false);
    }
  };

  const saveEmailTemplate = async (templateKey, templateData) => {
    setSavingTemplate(true);
    try {
      console.log('📧 Saving email template:', templateKey, templateData);
      
      // Update the template in state
      const updatedTemplates = {
        ...emailTemplates,
        [templateKey]: templateData
      };
      
      const settingsValue = JSON.stringify(updatedTemplates);
      console.log('📧 Templates JSON size:', settingsValue.length);
      
      // Use upsert with onConflict for cleaner operation
      const { data: upsertData, error: upsertError } = await supabase
        .from('app_settings')
        .upsert({
          key: 'email_templates',
          value: settingsValue,
          description: 'قوالب البريد الإلكتروني للعملاء والمصورين',
          category: 'notifications',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key',
          ignoreDuplicates: false
        })
        .select();
      
      console.log('📧 Upsert result:', { upsertData, upsertError });
      
      if (upsertError) {
        console.error('❌ Upsert error:', upsertError);
        throw upsertError;
      }
      
      setEmailTemplates(updatedTemplates);
      setTemplateSaveStatus('success');
      setTemplateSaveMessage('تم حفظ القالب بنجاح!');
      setTimeout(() => setTemplateSaveStatus(null), 4000);
    } catch (err) {
      console.error('❌ Error saving email template:', err);
      setTemplateSaveStatus('error');
      setTemplateSaveMessage('فشل حفظ القالب: ' + (err.message || 'خطأ غير معروف'));
      setTimeout(() => setTemplateSaveStatus(null), 6000);
    } finally {
      setSavingTemplate(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress.trim()) return;
    setSendingTestEmail(true);
    setTestEmailStatus(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: testEmailAddress.trim(),
          template_key: selectedTemplate,
          template_data: {},
        },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'فشل الإرسال');
      setTestEmailStatus('success');
      setTimeout(() => { setTestEmailStatus(null); setTestEmailModalOpen(false); }, 3000);
    } catch (err) {
      setTestEmailStatus('error');
      console.error('Test email failed:', err);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const resetTemplateToDefault = (templateKey) => {
    const defaultContent = getDefaultTemplateContent(templateKey);
    setEditingTemplate(defaultContent);
  };

  const selectTemplate = (templateKey) => {
    setSelectedTemplate(templateKey);
    const content = emailTemplates[templateKey] || getDefaultTemplateContent(templateKey);
    setEditingTemplate({ ...content });
    setTemplatePreviewMode(false);
  };

  // Get template preview with sample data
  const getTemplatePreview = (text) => {
    const sampleData = {
      customer_name: 'أحمد محمد',
      photographer_name: 'سارة أحمد',
      booking_id: 'BK123456',
      booking_date: '2026-02-10',
      booking_time: '10:00 صباحاً',
      booking_location: 'الرياض، حي العليا',
      photography_type: 'تصوير مناسبات',
      total_amount: '1,500',
      photographer_rating: '4.8',
      photographer_phone: '0501234567',
      customer_phone: '0551234567',
      photographer_email: 'photographer@example.com',
      customer_email: 'customer@example.com',
      verification_code: '123456',
      verification_link: 'https://example.com/verify/abc123',
      reset_link: 'https://example.com/reset/abc123',
      review_link: 'https://example.com/review/abc123',
      download_link: 'https://example.com/download/abc123',
      accept_link: '[قبول]',
      reject_link: '[رفض]',
      photos_count: '25',
      cancellation_reason: 'طلب العميل',
      rejection_reason: 'الوثائق غير واضحة',
      payment_method: 'بطاقة ائتمان',
      transaction_id: 'TXN789012',
      platform_commission: '225',
      photographer_earnings: '1,275',
      current_balance: '5,000',
      payout_amount: '5,000',
      payout_method: 'تحويل بنكي',
      document_type: 'هوية وطنية',
      error_message: 'فشل في معالجة الدفع',
      complaint_text: 'لم أكن راضياً عن جودة الصور.',
      photographer_city: 'الرياض',
    };
    
    let preview = text || '';
    Object.keys(sampleData).forEach(key => {
      preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), sampleData[key]);
    });
    return preview;
  };

  // Load email templates when tab is active
  useEffect(() => {
    if (active === 'email-templates' && Object.keys(emailTemplates).length === 0) {
      loadEmailTemplates();
    }
  }, [active]);

  // ============ Bulk Notifications Functions ============
  const loadRecipientCount = async (filter) => {
    try {
      let query;
      const now = new Date();
      
      switch (filter) {
        case 'all':
          // Count all users
          const { count: allCount } = await supabase
            .from('user_profiles')
            .select('*', { count: 'exact', head: true });
          return allCount || 0;
          
        case 'customers':
          const { count: customerCount } = await supabase
            .from('user_profiles')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'customer');
          return customerCount || 0;
          
        case 'photographers':
          const { count: photographerCount } = await supabase
            .from('photographer_profiles')
            .select('*', { count: 'exact', head: true });
          return photographerCount || 0;
          
        case 'verified_photographers':
          const { count: verifiedCount } = await supabase
            .from('photographer_profiles')
            .select('*', { count: 'exact', head: true })
            .eq('identity_verified', true);
          return verifiedCount || 0;
          
        case 'active_customers':
          // Customers with booking in last 30 days
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
          const { data: activeCustomers } = await supabase
            .from('bookings')
            .select('customer_id')
            .gte('created_at', thirtyDaysAgo);
          const uniqueActive = [...new Set(activeCustomers?.map(b => b.customer_id) || [])];
          return uniqueActive.length;
          
        case 'new_users':
          // Users registered in last 7 days
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
          const { count: newCount } = await supabase
            .from('user_profiles')
            .select('*', { count: 'exact', head: true })
            .gte('created_at', sevenDaysAgo);
          return newCount || 0;
          
        case 'custom':
          return selectedRecipients.length;
          
        default:
          return 0;
      }
    } catch (error) {
      console.error('Error loading recipient count:', error);
      return 0;
    }
  };

  const loadRecipientsList = async (filter) => {
    setBulkNotificationLoading(true);
    try {
      let recipients = [];
      const now = new Date();
      
      switch (filter) {
        case 'all':
          const { data: allUsers, error: allError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, phone, role, created_at')
            .order('created_at', { ascending: false });
          console.log('📢 All users loaded:', allUsers?.length, allError);
          recipients = allUsers || [];
          break;
          
        case 'customers':
          const { data: customers, error: custError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, phone, role, created_at')
            .eq('role', 'customer')
            .order('created_at', { ascending: false });
          console.log('📢 Customers loaded:', customers?.length, custError);
          recipients = customers || [];
          break;
          
        case 'photographers':
          // First get photographer profiles
          const { data: photographerProfiles, error: photoError } = await supabase
            .from('photographer_profiles')
            .select('user_id, created_at')
            .order('created_at', { ascending: false });
          console.log('📢 Photographer profiles:', photographerProfiles?.length, photoError);
          
          if (photographerProfiles && photographerProfiles.length > 0) {
            const photographerIds = photographerProfiles.map(p => p.user_id).filter(Boolean);
            const { data: photographerUsers } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, phone, role, created_at')
              .in('id', photographerIds);
            recipients = photographerUsers || [];
          }
          break;
          
        case 'verified_photographers':
          const { data: verifiedProfiles, error: verifiedError } = await supabase
            .from('photographer_profiles')
            .select('user_id, created_at')
            .eq('identity_verified', true)
            .order('created_at', { ascending: false });
          console.log('📢 Verified photographers:', verifiedProfiles?.length, verifiedError);
          
          if (verifiedProfiles && verifiedProfiles.length > 0) {
            const verifiedIds = verifiedProfiles.map(p => p.user_id).filter(Boolean);
            const { data: verifiedUsers } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, phone, role, created_at')
              .in('id', verifiedIds);
            recipients = verifiedUsers || [];
          }
          break;
          
        case 'active_customers':
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
          const { data: bookings, error: bookError } = await supabase
            .from('bookings')
            .select('customer_id')
            .gte('created_at', thirtyDaysAgo);
          console.log('📢 Active bookings:', bookings?.length, bookError);
          
          const uniqueIds = [...new Set(bookings?.map(b => b.customer_id).filter(Boolean) || [])];
          if (uniqueIds.length > 0) {
            const { data: activeUsers } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, phone, role, created_at')
              .in('id', uniqueIds);
            recipients = activeUsers || [];
          }
          break;
          
        case 'new_users':
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
          const { data: newUsers, error: newError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, phone, role, created_at')
            .gte('created_at', sevenDaysAgo)
            .order('created_at', { ascending: false });
          console.log('📢 New users:', newUsers?.length, newError);
          recipients = newUsers || [];
          break;
          
        default:
          recipients = [];
      }
      
      console.log('📢 Total recipients loaded:', recipients.length);
      setRecipientsList(recipients);
      setRecipientCount(recipients.length);
    } catch (error) {
      console.error('❌ Error loading recipients:', error);
      setRecipientsList([]);
      setRecipientCount(0);
    } finally {
      setBulkNotificationLoading(false);
    }
  };

  const openCustomRecipientsModal = async () => {
    setShowCustomRecipientsModal(true);
    setTempSelectedIds(selectedRecipients.map(r => r.id));
    if (allUsersForCustom.length > 0) return;
    setLoadingCustomUsers(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, role, created_at')
        .order('full_name', { ascending: true });
      setAllUsersForCustom(data || []);
    } catch (err) {
      console.error('Error loading users for custom selection:', err);
    } finally {
      setLoadingCustomUsers(false);
    }
  };

  const confirmCustomRecipients = () => {
    const chosen = allUsersForCustom.filter(u => tempSelectedIds.includes(u.id));
    setSelectedRecipients(chosen);
    setRecipientCount(chosen.length);
    setShowCustomRecipientsModal(false);
  };

  const loadNotificationHistory = async () => {
    setNotificationHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('bulk_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      setNotificationHistory(data || []);
    } catch (error) {
      console.error('Error loading notification history:', error);
      setNotificationHistory([]);
    } finally {
      setNotificationHistoryLoading(false);
    }
  };

  const sendBulkNotification = async () => {
    if (!bulkNotification.title.trim() || !bulkNotification.message.trim()) {
      alert('يرجى إدخال العنوان ونص الرسالة');
      return;
    }
    
    if (recipientCount === 0) {
      alert('لا يوجد مستلمين لهذا الإشعار');
      return;
    }
    
    if (!bulkNotification.channels.inApp && !bulkNotification.channels.push && !bulkNotification.channels.email && !bulkNotification.channels.whatsapp) {
      alert('يرجى اختيار قناة إرسال واحدة على الأقل');
      return;
    }
    
    // Confirm before sending
    const confirmSend = confirm(`هل أنت متأكد من إرسال الإشعار إلى ${recipientCount} مستلم؟`);
    if (!confirmSend) return;
    
    setSendingNotification(true);
    try {
      // Get recipients based on filter
      const recipients = bulkNotification.recipientFilter === 'custom' 
        ? selectedRecipients 
        : recipientsList;
      
      console.log('📢 Sending bulk notification to', recipients.length, 'recipients');
      console.log('📢 Channels:', bulkNotification.channels);
      
      // Get current user
      const { data: userData } = await db.auth.getUser();
      const currentUserId = userData?.user?.id;
      
      // Save notification record first
      const { data: notificationRecord, error: recordError } = await supabase
        .from('bulk_notifications')
        .insert({
          title: bulkNotification.title,
          message: bulkNotification.message,
          recipient_filter: bulkNotification.recipientFilter,
          recipient_count: recipients.length,
          channels: bulkNotification.channels,
          status: bulkNotification.scheduleType === 'scheduled' ? 'scheduled' : 'sending',
          scheduled_at: bulkNotification.scheduleType === 'scheduled' 
            ? `${bulkNotification.scheduledDate}T${bulkNotification.scheduledTime}:00` 
            : null,
          sent_by: currentUserId
        })
        .select()
        .single();
      
      if (recordError) {
        console.error('❌ Error saving notification record:', recordError);
        throw recordError;
      }
      
      console.log('✅ Notification record saved:', notificationRecord.id);
      
      let sentCount = 0;
      let failedCount = 0;
      const errors = [];
      
      // Send in-app notifications
      if (bulkNotification.channels.inApp) {
        console.log('📢 Sending in-app notifications...');
        
        // Batch insert for better performance
        const notificationsToInsert = recipients.map(recipient => ({
          user_id: recipient.id,
          title: bulkNotification.title,
          message: bulkNotification.message,
          type: 'announcement',
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        // Insert in batches of 100
        const batchSize = 100;
        for (let i = 0; i < notificationsToInsert.length; i += batchSize) {
          const batch = notificationsToInsert.slice(i, i + batchSize);
          const { data: insertedData, error: insertError } = await supabase
            .from('notifications')
            .insert(batch);
          
          if (insertError) {
            console.error('❌ Batch insert error:', insertError);
            errors.push(`خطأ في الدفعة ${Math.floor(i/batchSize) + 1}: ${insertError.message}`);
            failedCount += batch.length;
          } else {
            sentCount += batch.length;
            console.log(`✅ Batch ${Math.floor(i/batchSize) + 1} sent: ${batch.length} notifications`);
          }
        }
      }
      
      // Send emails (using Edge Function if available)
      if (bulkNotification.channels.email) {
        const emailRecipients = recipients.filter(r => r.email);
        console.log('📧 Sending emails to', emailRecipients.length, 'recipients');
        
        if (emailRecipients.length > 0) {
          try {
            // Try to send via Edge Function
            const { data: emailResult, error: emailError } = await db.functions.invoke('send-bulk-email', {
              body: {
                recipients: emailRecipients.map(r => ({ email: r.email, name: r.full_name })),
                subject: bulkNotification.title,
                message: bulkNotification.message
              }
            });
            
            if (emailError) {
              console.warn('⚠️ Email sending via Edge Function failed:', emailError);
              errors.push('تعذر إرسال البريد الإلكتروني - قد تحتاج لإعداد خدمة البريد');
            } else {
              console.log('✅ Emails queued:', emailResult);
            }
          } catch (emailErr) {
            console.warn('⚠️ Email Edge Function not available:', emailErr);
            errors.push('خدمة البريد الإلكتروني غير متوفرة حالياً');
          }
        }
      }
      
      // Send WhatsApp messages
      if (bulkNotification.channels.whatsapp) {
        const whatsappRecipients = recipients.filter(r => r.phone);
        console.log('📱 Sending WhatsApp to', whatsappRecipients.length, 'recipients');
        
        if (whatsappRecipients.length > 0) {
          try {
            // Try to send via Edge Function
            const { data: waResult, error: waError } = await db.functions.invoke('send-bulk-whatsapp', {
              body: {
                recipients: whatsappRecipients.map(r => ({ phone: r.phone, name: r.full_name })),
                message: `*${bulkNotification.title}*\n\n${bulkNotification.message}`
              }
            });
            
            if (waError) {
              console.warn('⚠️ WhatsApp sending failed:', waError);
              errors.push('تعذر إرسال رسائل واتساب - تحقق من إعدادات Twilio');
            } else {
              console.log('✅ WhatsApp messages queued:', waResult);
            }
          } catch (waErr) {
            console.warn('⚠️ WhatsApp Edge Function not available:', waErr);
            errors.push('خدمة واتساب غير متوفرة حالياً');
          }
        }
      }
      
      // Send Push Notifications (FCM)
      if (bulkNotification.channels.push) {
        console.log('🔔 Sending Push Notifications to', recipients.length, 'recipients');
        let pushSent = 0;
        let pushFailed = 0;

        for (const recipient of recipients) {
          try {
            const { error: pushError } = await db.functions.invoke('send-fcm-notification', {
              body: {
                user_id: recipient.id,
                title: bulkNotification.title,
                body: bulkNotification.message,
                data: { type: 'announcement' },
              },
            });
            if (pushError) {
              pushFailed++;
            } else {
              pushSent++;
            }
          } catch (_) {
            pushFailed++;
          }
        }

        console.log(`🔔 Push: sent=${pushSent}, failed=${pushFailed}`);
        if (pushSent > 0) sentCount += pushSent;
        if (pushFailed > 0) errors.push(`Push: فشل إرسال ${pushFailed} إشعار (قد لا يكون المستخدم مسجّل الجهاز)`);
      }

      // Update notification record with results
      const finalStatus = failedCount === 0 ? 'sent' : (sentCount > 0 ? 'sent' : 'failed');
      const { error: updateError } = await supabase
        .from('bulk_notifications')
        .update({
          status: finalStatus,
          sent_count: sentCount,
          failed_count: failedCount,
          sent_at: new Date().toISOString()
        })
        .eq('id', notificationRecord.id);
      
      if (updateError) {
        console.error('⚠️ Error updating notification record:', updateError);
      }
      
      // Show result message
      let resultMessage = `✅ تم إرسال الإشعار!\n\n`;
      resultMessage += `📊 النتائج:\n`;
      resultMessage += `• تم الإرسال: ${sentCount} إشعار\n`;
      if (failedCount > 0) {
        resultMessage += `• فشل: ${failedCount} إشعار\n`;
      }
      if (errors.length > 0) {
        resultMessage += `\n⚠️ ملاحظات:\n${errors.join('\n')}`;
      }
      
      alert(resultMessage);
      
      // Reset form
      setBulkNotification({
        title: '',
        message: '',
        recipientFilter: 'all',
        channels: { inApp: true, push: false, email: false, whatsapp: false },
        scheduleType: 'immediate',
        scheduledDate: '',
        scheduledTime: '',
      });
      
      // Reload history and recipients
      loadNotificationHistory();
      loadRecipientsList('all');
      
    } catch (error) {
      console.error('❌ Error sending bulk notification:', error);
      alert('فشل إرسال الإشعار: ' + (error.message || JSON.stringify(error)));
    } finally {
      setSendingNotification(false);
    }
  };

  // Load recipients when filter changes
  useEffect(() => {
    if (active === 'bulk-notifications' && bulkNotification.recipientFilter !== 'custom') {
      loadRecipientsList(bulkNotification.recipientFilter);
    }
  }, [active, bulkNotification.recipientFilter]);

  // Load notification history when tab is active
  useEffect(() => {
    if (active === 'bulk-notifications' && notificationHistory.length === 0) {
      loadNotificationHistory();
    }
  }, [active]);

  // ============ Support Tickets Functions ============
  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      console.log('🎫 Loading support tickets...');

      // 1. Load tickets (no JOIN — avoids auth.users FK issue)
      const { data: ticketsData, error: ticketsError } = await supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (ticketsError) {
        console.error('❌ Error loading tickets:', ticketsError);
        throw ticketsError;
      }

      // 2. Enrich with user_profiles (separate query)
      const userIds = [...new Set(
        (ticketsData || []).map(t => t.user_id).filter(Boolean)
      )];
      let profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, role')
          .in('id', userIds);
        (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      }

      const enriched = (ticketsData || []).map(t => ({
        ...t,
        user: profilesMap[t.user_id] || null,
      }));

      console.log('✅ Tickets loaded:', enriched.length);
      setTickets(enriched);
      
      // Calculate stats
      const stats = {
        total: ticketsData?.length || 0,
        open: ticketsData?.filter(t => t.status === 'open').length || 0,
        inProgress: ticketsData?.filter(t => t.status === 'in_progress').length || 0,
        waitingUser: ticketsData?.filter(t => t.status === 'waiting_user').length || 0,
        waitingAdmin: ticketsData?.filter(t => t.status === 'waiting_admin').length || 0,
        resolved: ticketsData?.filter(t => t.status === 'resolved' || t.status === 'closed').length || 0,
        avgResponseTime: 0,
      };
      
      // Calculate average response time
      const ticketsWithResponse = ticketsData?.filter(t => t.first_response_at) || [];
      if (ticketsWithResponse.length > 0) {
        const totalMinutes = ticketsWithResponse.reduce((acc, t) => {
          const created = new Date(t.created_at);
          const responded = new Date(t.first_response_at);
          return acc + (responded - created) / (1000 * 60);
        }, 0);
        stats.avgResponseTime = Math.round(totalMinutes / ticketsWithResponse.length);
      }
      
      setTicketStats(stats);
      
    } catch (error) {
      console.error('❌ Error loading tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };

  const loadTicketBookingDetails = async (bookingId) => {
    if (!bookingId) { setTicketBookingDetails(null); return; }
    setTicketBookingLoading(true);
    try {
      const { data: booking, error } = await supabase
        .from('bookings')
        .select('id, status, final_price_sar, price, photography_type, scheduled_at, booking_date, created_at, customer_id, assigned_photographer_id')
        .eq('id', bookingId)
        .maybeSingle();

      if (error) throw error;
      if (!booking) { setTicketBookingDetails(null); return; }

      // Enrich customer & photographer separately (safe — no FK JOIN)
      const profileIds = [booking.customer_id, booking.assigned_photographer_id].filter(Boolean);
      let profilesMap = {};
      if (profileIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, phone, email')
          .in('id', profileIds);
        (profiles || []).forEach((p) => { profilesMap[p.id] = p; });
      }

      const { data: delivery } = await supabase
        .from('booking_deliveries')
        .select('id, delivered_at, total_photos, total_videos')
        .eq('booking_id', bookingId)
        .maybeSingle();

      const { data: review } = await supabase
        .from('reviews')
        .select('rating, comment, created_at')
        .eq('booking_id', bookingId)
        .maybeSingle();

      setTicketBookingDetails({
        ...booking,
        total_price: booking.final_price_sar || booking.price,
        customer: profilesMap[booking.customer_id] || null,
        photographer: profilesMap[booking.assigned_photographer_id] || null,
        delivery: delivery || null,
        review: review || null,
      });
    } catch (err) {
      console.error('[Admin] loadTicketBookingDetails error:', err);
      setTicketBookingDetails(null);
    } finally {
      setTicketBookingLoading(false);
    }
  };

  const handleDisputeAction = async (action) => {
    if (!selectedTicket) return;
    const labels = {
      full_refund: 'استرداد كامل للعميل',
      partial_refund: 'استرداد جزئي',
      pay_photographer: 'صرف المستحقات للمصور',
    };
    if (!window.confirm(`هل تريد تنفيذ: ${labels[action]}؟`)) return;
    setDisputeActionLoading(true);
    try {
      const note = `[قرار النزاع: ${labels[action]}]`;
      const bookingId = selectedTicket.related_booking_id;

      // ── ① صرف المستحقات للمصور ──────────────────────────────────────────────
      if (action === 'pay_photographer' && bookingId) {
        // تحديث photographer_payout_status → paid في قاعدة البيانات
        const { error: payoutErr } = await supabase
          .from('bookings')
          .update({
            photographer_payout_status: 'paid',
            photographer_paid_at: new Date().toISOString(),
          })
          .eq('id', bookingId);
        if (payoutErr) throw new Error('فشل تحديث حالة مدفوعات المصور: ' + payoutErr.message);
      }

      // ── ② استرداد كامل للعميل عبر ميسر ────────────────────────────────────
      if (action === 'full_refund' && bookingId) {
        const refundResult = await callEdgeFunction('process-refund', {
          bookingId,
          adminNote: note,
        });
        if (!refundResult?.success) {
          throw new Error(refundResult?.error || 'فشل استرداد المبلغ عبر ميسر');
        }
        // منع ظهور الحجز كمستحقات مصور بعد الاسترداد
        await supabase
          .from('bookings')
          .update({ photographer_payout_status: 'refunded' })
          .eq('id', bookingId);
      }

      // ── تحديث التذكرة في جميع الحالات ─────────────────────────────────────
      await supabase
        .from('support_tickets')
        .update({ status: 'resolved', admin_notes: note })
        .eq('id', selectedTicket.id);

      // ── إشعار داخلي للعميل ─────────────────────────────────────────────────
      if (selectedTicket.user_id) {
        const msgs = {
          full_refund: 'تمت الموافقة على استرداد المبلغ كاملاً. سيُحوَّل إليك خلال 3-5 أيام عمل.',
          partial_refund: 'تمت الموافقة على استرداد جزء من المبلغ. سيتواصل معك فريق الدعم.',
          pay_photographer: 'تم مراجعة شكواك وقرار فريق الدعم هو صرف المستحقات للمصور. للاستفسار تواصل معنا.',
        };
        await supabase.from('notifications').insert({
          user_id: selectedTicket.user_id,
          type: 'booking',
          title: '⚖️ قرار فريق الدعم',
          message: msgs[action],
          related_booking_id: bookingId || null,
        });
      }

      // ── إشعار داخلي للمصور ─────────────────────────────────────────────────
      if (ticketBookingDetails?.photographer?.id) {
        const photographerMsgs = {
          full_refund: 'تم اتخاذ قرار باسترداد مبلغ الحجز كاملاً للعميل بعد مراجعة الشكوى.',
          partial_refund: 'تم اتخاذ قرار باسترداد جزء من مبلغ الحجز للعميل.',
          pay_photographer: 'تمت مراجعة الشكوى وقرار فريق الدعم هو صرف مستحقاتك. سيتم التحويل إليك قريباً.',
        };
        await supabase.from('notifications').insert({
          user_id: ticketBookingDetails.photographer.id,
          type: 'booking',
          title: '⚖️ قرار فريق الدعم على نزاع',
          message: photographerMsgs[action],
          related_booking_id: bookingId || null,
        });
      }

      await loadTickets();
      setSelectedTicket((prev) => prev ? { ...prev, status: 'resolved', admin_notes: note } : prev);
      alert(`✅ تم تنفيذ: ${labels[action]}`);
    } catch (err) {
      alert('فشل تنفيذ الإجراء: ' + (err.message || 'حاول مرة أخرى'));
    } finally {
      setDisputeActionLoading(false);
    }
  };

  const loadTicketReplies = async (ticketId) => {
    try {
      // Fetch replies without JOIN (avoids auth.users FK issue)
      const { data: replies, error } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Enrich with user_profiles separately
      const userIds = [...new Set((replies || []).map(r => r.user_id).filter(Boolean))];
      let profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, role')
          .in('id', userIds);
        (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      }

      setTicketReplies(
        (replies || []).map(r => ({ ...r, user: profilesMap[r.user_id] || null }))
      );
    } catch (error) {
      console.error('Error loading replies:', error);
      setTicketReplies([]);
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReplyText.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const { data: userData } = await db.auth.getUser();
      
      // Insert reply
      const { data: reply, error: replyError } = await supabase
        .from('ticket_replies')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: userData?.user?.id,
          message: ticketReplyText.trim(),
          is_internal: ticketReplyInternal,
        })
        .select()
        .single();
      
      if (replyError) throw replyError;
      
      console.log('✅ Reply sent:', reply.id);
      
      // Clear input
      setTicketReplyText('');
      setTicketReplyInternal(false);
      
      // Reload replies
      await loadTicketReplies(selectedTicket.id);
      
      // Send notification to user if not internal
      if (!ticketReplyInternal && selectedTicket.user_id) {
        const ticketNotifTitle = 'رد جديد على تذكرتك';
        const ticketNotifMsg = `تم الرد على التذكرة رقم ${selectedTicket.ticket_number}`;
        await db.from('notifications').insert({
          user_id: selectedTicket.user_id,
          title: ticketNotifTitle,
          message: ticketNotifMsg,
          type: 'support',
          is_read: false,
        });
        callEdgeFunction('send-fcm-notification', {
          user_id: selectedTicket.user_id,
          title: ticketNotifTitle,
          body: ticketNotifMsg,
          data: { type: 'SUPPORT' },
        }).catch((e) => console.warn('⚠️ FCM push failed:', e));

        // Email to user
        const userEmail = selectedTicket.user?.email;
        if (userEmail) {
          supabase.functions.invoke('send-email', {
            body: {
              to: userEmail,
              template_key: 'ticket_reply_user',
              template_data: {
                user_name: selectedTicket.user?.full_name || '',
                ticket_number: selectedTicket.ticket_number || '',
                subject: selectedTicket.subject || '',
                reply_message: ticketReplyText.trim(),
              },
            },
          }).catch((e) => console.warn('⚠️ Ticket reply email failed:', e));
        }
      }
      
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('فشل إرسال الرد: ' + error.message);
    } finally {
      setSendingReply(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const { data: userData } = await db.auth.getUser();
      
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };
      
      // If resolving, add resolution info
      if (newStatus === 'resolved' || newStatus === 'closed') {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = userData?.user?.id;
      }
      
      const { error } = await supabase
        .from('support_tickets')
        .update(updateData)
        .eq('id', ticketId);
      
      if (error) throw error;
      
      // Reload tickets
      await loadTickets();
      
      // Update selected ticket if viewing
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus, ...updateData }));
      }
      
      // Notify user
      const ticket = tickets.find(t => t.id === ticketId);
      if (ticket?.user_id) {
        const statusLabels = {
          'in_progress': 'قيد المعالجة',
          'resolved': 'تم حلها',
          'closed': 'مغلقة',
        };
        const statusNotifTitle = 'تحديث حالة التذكرة';
        const statusLabelAr = statusLabels[newStatus] || newStatus;
        const statusNotifMsg = `تم تحديث حالة التذكرة ${ticket.ticket_number} إلى: ${statusLabelAr}`;
        await db.from('notifications').insert({
          user_id: ticket.user_id,
          title: statusNotifTitle,
          message: statusNotifMsg,
          type: 'support',
          is_read: false,
        });
        callEdgeFunction('send-fcm-notification', {
          user_id: ticket.user_id,
          title: statusNotifTitle,
          body: statusNotifMsg,
          data: { type: 'SUPPORT' },
        }).catch((e) => console.warn('⚠️ FCM push failed:', e));

        // Email to user
        const userEmail = ticket.user?.email;
        if (userEmail) {
          supabase.functions.invoke('send-email', {
            body: {
              to: userEmail,
              template_key: 'ticket_status_update',
              template_data: {
                user_name: ticket.user?.full_name || '',
                ticket_number: ticket.ticket_number || '',
                subject: ticket.subject || '',
                new_status: statusLabelAr,
              },
            },
          }).catch((e) => console.warn('⚠️ Ticket status email failed:', e));
        }
      }
      
      console.log('✅ Ticket status updated');
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('فشل تحديث الحالة: ' + error.message);
    }
  };

  const updateTicketPriority = async (ticketId, newPriority) => {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({
          priority: newPriority,
          updated_at: new Date().toISOString(),
        })
        .eq('id', ticketId);
      
      if (error) throw error;
      
      await loadTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, priority: newPriority }));
      }
      
      console.log('✅ Ticket priority updated');
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const assignTicket = async (ticketId, adminId) => {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({
          assigned_to: adminId,
          status: 'in_progress',
          updated_at: new Date().toISOString(),
        })
        .eq('id', ticketId);
      
      if (error) throw error;
      
      await loadTickets();
      console.log('✅ Ticket assigned');
    } catch (error) {
      console.error('Error assigning ticket:', error);
    }
  };

  const getFilteredTickets = () => {
    let filtered = [...tickets];

    if (ticketFilter.status !== 'all') {
      filtered = filtered.filter(t => t.status === ticketFilter.status);
    }
    if (ticketFilter.priority !== 'all') {
      filtered = filtered.filter(t => t.priority === ticketFilter.priority);
    }
    if (ticketFilter.category !== 'all') {
      filtered = filtered.filter(t => t.category === ticketFilter.category);
    }
    if (ticketFilter.userRole !== 'all') {
      filtered = filtered.filter(t => {
        const role = t.user_role || t.user?.role || 'customer';
        return role === ticketFilter.userRole;
      });
    }
    if (ticketFilter.ticketType !== 'all') {
      filtered = filtered.filter(t => (t.ticket_type || 'support') === ticketFilter.ticketType);
    }
    if (ticketFilter.search) {
      const search = ticketFilter.search.toLowerCase();
      filtered = filtered.filter(t =>
        t.ticket_number?.toLowerCase().includes(search) ||
        t.subject?.toLowerCase().includes(search) ||
        t.user?.full_name?.toLowerCase().includes(search) ||
        t.user?.email?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  };

  const openTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setTicketBookingDetails(null);
    setShowTicketModal(true);
    await loadTicketReplies(ticket.id);
    if (ticket.related_booking_id) {
      await loadTicketBookingDetails(ticket.related_booking_id);
    }
  };

  // Realtime: reload replies when a new reply arrives for the open ticket
  useEffect(() => {
    if (!showTicketModal || !selectedTicket?.id) return;
    const channel = supabase
      .channel(`ticket-replies-${selectedTicket.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_replies', filter: `ticket_id=eq.${selectedTicket.id}` },
        () => { loadTicketReplies(selectedTicket.id); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [showTicketModal, selectedTicket?.id]);

  // Load tickets when tab is active
  useEffect(() => {
    if (active === 'support' && tickets.length === 0) {
      loadTickets();
    }
  }, [active]);

  useEffect(() => {
    if (active === 'customers' && tickets.length === 0) {
      loadTickets();
    }
  }, [active]);

  // ============ Verification Functions ============
  const loadVerificationRequests = async () => {
    setVerificationLoading(true);
    try {
      try {
        await db.rpc('revoke_expired_identity_verifications');
      } catch (e) {
        console.warn('Could not revoke expired identity verifications:', e);
      }

      const selectWithCertReview = `
          user_id,
          status,
          verification_status,
          id_document_url,
          id_document_type,
          verification_submitted_at,
          verification_reviewed_at,
          verification_notes,
          rejection_reason,
          portfolio_verified,
          identity_verified,
          phone_verified,
          experience_years,
          experience_level,
          available_for_booking,
          city,
          display_id,
          entity_type,
          specializations,
          business_name,
          equipment_available,
          shooting_methods,
          shooting_method_models,
          languages,
          created_at,
          certificates,
          certificates_verified,
          certificates_reviewed_at,
          certificates_rejection_reason,
          drone_permit_url,
          drone_permit_verified,
          drone_permit_submitted_at,
          drone_permit_reviewed_at,
          drone_permit_rejection_reason,
          drone_permit_expires_at,
          freelance_permit_url,
          freelance_permit_verified,
          freelance_permit_submitted_at,
          freelance_permit_reviewed_at,
          freelance_permit_rejection_reason,
          freelance_permit_expires_at,
          identity_document_expires_at,
          identity_reviewed_at,
          identity_rejection_reason,
          withdrawal_count,
          withdrawal_suspended_until,
          visibility_penalty_until,
          profile_completion_pct,
          updated_at
        `;
      const selectWithoutCertReview = `
          user_id,
          status,
          verification_status,
          id_document_url,
          id_document_type,
          verification_submitted_at,
          verification_reviewed_at,
          verification_notes,
          rejection_reason,
          portfolio_verified,
          identity_verified,
          phone_verified,
          experience_years,
          experience_level,
          available_for_booking,
          city,
          display_id,
          entity_type,
          specializations,
          business_name,
          equipment_available,
          shooting_methods,
          shooting_method_models,
          languages,
          created_at,
          certificates,
          withdrawal_count,
          withdrawal_suspended_until,
          visibility_penalty_until,
          profile_completion_pct,
          updated_at
        `;
      const selectWithoutDisplayId = `
          user_id,
          status,
          verification_status,
          id_document_url,
          id_document_type,
          verification_submitted_at,
          verification_reviewed_at,
          verification_notes,
          rejection_reason,
          portfolio_verified,
          identity_verified,
          phone_verified,
          experience_years,
          experience_level,
          available_for_booking,
          city,
          entity_type,
          specializations,
          business_name,
          equipment_available,
          shooting_methods,
          shooting_method_models,
          languages,
          created_at,
          certificates,
          withdrawal_count,
          withdrawal_suspended_until,
          visibility_penalty_until,
          profile_completion_pct,
          updated_at
        `;
      let result = await db.from('photographer_profiles').select(selectWithCertReview).order('created_at', { ascending: false });
      let pendingPhotographers = result.data;
      let ppError = result.error;
      if (ppError && (ppError.code === '42703' || ppError.message?.includes('does not exist') || ppError.message?.includes('400'))) {
        result = await db.from('photographer_profiles').select(selectWithoutCertReview).order('created_at', { ascending: false });
        pendingPhotographers = result.data;
        ppError = result.error;
      }
      if (ppError && (ppError.code === '42703' || ppError.message?.includes('does not exist') || ppError.message?.includes('display_id'))) {
        result = await db.from('photographer_profiles').select(selectWithoutDisplayId).order('created_at', { ascending: false });
        pendingPhotographers = result.data;
        ppError = result.error;
      }
      if (ppError) throw ppError;

      // Get user profiles for names
      const userIds = pendingPhotographers?.map(p => p.user_id).filter(Boolean) || [];
      let usersMap = {};
      
      if (userIds.length > 0) {
        // Try to load user profiles with deleted_by_user; fallback if column is missing
        let users;
        let usersError;

        let usersResult = await supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, avatar_url, is_active, deleted_by_user')
          .in('id', userIds);

        users = usersResult.data;
        usersError = usersResult.error;

        if (
          usersError &&
          (
            usersError.code === '42703' ||
            usersError.message?.includes('deleted_by_user') ||
            usersError.message?.includes('does not exist') ||
            usersError.message?.toLowerCase().includes('column')
          )
        ) {
          console.warn('⚠️ user_profiles.deleted_by_user is missing (verification), falling back without this column');
          const fallback = await supabase
            .from('user_profiles')
            .select('id, full_name, email, phone, avatar_url, is_active')
            .in('id', userIds);
          users = fallback.data;
          usersError = fallback.error;
        }

        if (usersError) {
          console.error('❌ Error loading user_profiles for verification:', usersError);
        } else {
          users?.forEach(u => { usersMap[u.id] = u; });
        }
      }

      // Get verification requests
      const { data: requests } = await supabase
        .from('photographer_verification_requests')
        .select('*')
        .order('submitted_at', { ascending: false });

      // Enrich photographers with user data and also load documents from storage
      const enrichedPhotographers = await Promise.all((pendingPhotographers || []).map(async (p) => {
        // Try to get documents from storage for this photographer
        let storageDocuments = [];
        let identityDocuments = [];
        let dronePermitDocuments = [];
        try {
          const { data: files } = await db.storage
            .from('verification-docs')
            .list(p.user_id, { limit: 10 });
          
          if (files && files.length > 0) {
            storageDocuments = files.map(file => {
              const { data: urlData } = db.storage
                .from('verification-docs')
                .getPublicUrl(`${p.user_id}/${file.name}`);
              return {
                name: file.name,
                url: urlData?.publicUrl,
                type: file.metadata?.mimetype || (file.name.endsWith('.pdf') ? 'application/pdf' : 'image'),
                uploadedAt: file.created_at
              };
            });
          }

          identityDocuments = storageDocuments.filter((doc) => isIdentityVerificationFile(doc.name));
          dronePermitDocuments = storageDocuments.filter((doc) => !isIdentityVerificationFile(doc.name));
        } catch (e) {
          console.warn('Could not load storage documents for', p.user_id, e);
        }

        // Load portfolio images for this photographer (for moderation)
        let portfolioImages = [];
        try {
          const { data: portfolioData } = await supabase
            .from('portfolio_images')
            .select('id, image_url, title, approval_status, created_at')
            .eq('photographer_id', p.user_id)
            .order('created_at', { ascending: false });
          portfolioImages = portfolioData || [];
        } catch (e) {
          console.warn('Could not load portfolio images for', p.user_id, e);
        }

        return {
          ...p,
          user: usersMap[p.user_id] || null,
          verification_requests: (requests || []).filter(r => r.photographer_id === p.user_id),
          storageDocuments,
          identityDocuments,
          dronePermitDocuments,
          portfolioImages
        };
      }));

      setVerificationRequests(enrichedPhotographers);
      console.log('✅ Loaded verification requests:', enrichedPhotographers.length);
      
      // Update pending count and show notification if needed
      const submittedCount = enrichedPhotographers.filter(p => p.verification_status === 'submitted').length;
      setPendingVerificationsCount(submittedCount);
      if (submittedCount > 0) {
        setShowNotificationBanner(true);
      }
    } catch (error) {
      console.error('Error loading verification requests:', error);
      setNotice('❌ فشل تحميل طلبات التحقق');
    } finally {
      setVerificationLoading(false);
    }
  };

  // تهيئة حقول تاريخ الانتهاء عند فتح نافذة التحقق — كل قسم مستقل حتى لا يُمسح تاريخ الدرون عند تحديث العمل الحر
  useEffect(() => {
    if (!selectedVerification) {
      setIdentityExpiryInput('');
      setIdentityRejectReason('');
      setShowIdentityRejectInput(false);
      return;
    }
    const identityRaw = selectedVerification.identity_document_expires_at;
    setIdentityExpiryInput(identityRaw ? String(identityRaw).split('T')[0] : '');
    setIdentityRejectReason('');
    setShowIdentityRejectInput(false);
  }, [
    selectedVerification?.user_id,
    selectedVerification?.identity_document_expires_at,
  ]);

  useEffect(() => {
    if (!selectedVerification) {
      setFreelanceExpiryInput('');
      setFreelanceRejectReason('');
      setShowFreelanceRejectInput(false);
      return;
    }
    const freelanceRaw = selectedVerification.freelance_permit_expires_at;
    setFreelanceExpiryInput(freelanceRaw ? String(freelanceRaw).split('T')[0] : '');
    setFreelanceRejectReason('');
    setShowFreelanceRejectInput(false);
  }, [
    selectedVerification?.user_id,
    selectedVerification?.freelance_permit_expires_at,
  ]);

  useEffect(() => {
    if (!selectedVerification) {
      setDroneExpiryInput('');
      setDroneRejectReason('');
      setShowDroneRejectInput(false);
      return;
    }
    const droneRaw = selectedVerification.drone_permit_expires_at;
    setDroneExpiryInput(droneRaw ? String(droneRaw).split('T')[0] : '');
    setDroneRejectReason('');
    setShowDroneRejectInput(false);
  }, [
    selectedVerification?.user_id,
    selectedVerification?.drone_permit_expires_at,
  ]);

  // قائمة التحقق المفلترة حسب البحث (رقم المصور، الاسم، البريد) والحالة
  const filteredVerificationRequests = useMemo(() => {
    let list = verificationRequests || [];
    const search = (verificationFilterSearch || '').trim().toLowerCase();
    if (search) {
      // تطبيع البحث الرقمي إلى 6 خانات لضمان مطابقة "000001" و "1"
      const searchAsPaddedId = /^\d+$/.test(search) ? String(Number(search)).padStart(6, '0') : null;
      list = list.filter((p) => {
        const rawId = p.display_id ?? p.displayId;
        const idStr = (rawId != null && rawId !== '') ? String(Number(rawId)).padStart(6, '0').toLowerCase() : '';
        const name = (p.user?.full_name || '').toLowerCase();
        const email = (p.user?.email || '').toLowerCase();
        const idMatch = idStr && (idStr.includes(search) || (searchAsPaddedId && idStr === searchAsPaddedId));
        return idMatch || name.includes(search) || email.includes(search);
      });
    }
    const status = verificationFilterStatus || 'all';
    const FREELANCE_EXPIRY_FILTERS = [
      'freelance_expiring_soon',
      'freelance_critical',
      'freelance_expired',
      'freelance_valid',
      'freelance_no_expiry',
    ];
    const DRONE_EXPIRY_FILTERS = [
      'drone_expiring_soon',
      'drone_critical',
      'drone_expired',
      'drone_valid',
      'drone_no_expiry',
    ];
    const IDENTITY_EXPIRY_FILTERS = [
      'identity_expiring_soon',
      'identity_critical',
      'identity_expired',
      'identity_valid',
      'identity_no_expiry',
    ];

    if (status !== 'all') {
      if (status === 'pending') {
        list = list.filter((p) => p.status === 'pending' || !p.status);
      } else if (status === 'submitted') {
        list = list.filter((p) => p.verification_status === 'submitted');
      } else if (status === 'approved') {
        list = list.filter((p) => p.status === 'approved');
      } else if (status === 'rejected') {
        list = list.filter((p) => p.verification_status === 'rejected');
      } else if (status === 'needs_review') {
        list = list.filter((p) => {
          const pendingPortfolio = p.portfolioImages?.some(i => i.approval_status === 'pending');
          const profileUpdated = p.updated_at && p.verification_reviewed_at
            ? new Date(p.updated_at) > new Date(p.verification_reviewed_at)
            : p.updated_at && p.status === 'approved'
              ? new Date(p.updated_at) > new Date(p.created_at)
              : false;
          return pendingPortfolio || profileUpdated;
        });
      } else if (status === 'freelance_pending') {
        list = list.filter((p) => matchesFreelanceFilter(p, 'freelance_pending'));
      } else if (FREELANCE_EXPIRY_FILTERS.includes(status)) {
        list = list.filter((p) => matchesFreelanceFilter(p, status));
        list = sortByFreelanceExpiry(list, true);
      } else if (status === 'drone_pending') {
        list = list.filter((p) => matchesDroneFilter(p, 'drone_pending'));
      } else if (DRONE_EXPIRY_FILTERS.includes(status)) {
        list = list.filter((p) => matchesDroneFilter(p, status));
        list = sortByDroneExpiry(list, true);
      } else if (status === 'identity_pending') {
        list = list.filter((p) => matchesIdentityFilter(p, 'identity_pending'));
      } else if (IDENTITY_EXPIRY_FILTERS.includes(status)) {
        list = list.filter((p) => matchesIdentityFilter(p, status));
        list = sortByIdentityExpiry(list, true);
      }
    }
    return list;
  }, [verificationRequests, verificationFilterSearch, verificationFilterStatus]);

  const filteredPhotographers = useMemo(() => {
    let list = photographers || [];
    const search = (photographerFilterSearch || '').trim().toLowerCase();
    if (search) {
      const searchPhone = search.replace(/\s/g, '');
      list = list.filter((p) => {
        const name = (p.full_name || '').toLowerCase();
        const email = (p.email || '').toLowerCase();
        const phone = (p.phone || '').replace(/\s/g, '');
        const city = (p.city || '').toLowerCase();
        return name.includes(search) || email.includes(search) || phone.includes(searchPhone) || city.includes(search);
      });
    }
    const status = photographerFilterStatus || 'all';
    if (status === 'approved') {
      list = list.filter((p) => (p.status || 'pending') === 'approved');
    } else if (status === 'pending') {
      list = list.filter((p) => (p.status || 'pending') === 'pending');
    } else if (status === 'suspended') {
      list = list.filter((p) => p.status === 'suspended');
    } else if (status === 'inactive') {
      list = list.filter((p) => p.is_active === false || p.deleted_by_user);
    } else if (status === 'unavailable') {
      list = list.filter((p) => p.available_for_booking === false);
    }
    return list;
  }, [photographers, photographerFilterSearch, photographerFilterStatus]);

  const customerActivityById = useMemo(() => {
    const map = {};
    const ever = new Set();
    const active30d = new Set();
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const paidSpendStatuses = new Set([
      'paid_confirmed', 'completed', 'in_progress', 'awaiting_delivery',
      'cancellation_requested', 'cancelled_by_customer', 'cancelled_by_admin', 'cancelled_by_photographer',
    ]);

    (bookings || []).forEach((b) => {
      if (!b.customer_id) return;
      const id = b.customer_id;
      ever.add(id);
      if (b.created_at && new Date(b.created_at).getTime() >= thirtyDaysAgo) {
        active30d.add(id);
      }

      if (!map[id]) {
        map[id] = { count: 0, totalSpend: 0, lastBookingAt: null };
      }
      map[id].count += 1;

      const amount = Number(b.final_price_sar ?? b.price ?? 0);
      if (paidSpendStatuses.has(b.status) && amount > 0) {
        map[id].totalSpend += amount;
      }

      const bookingTime = new Date(b.booking_date || b.created_at || 0).getTime();
      if (bookingTime && (!map[id].lastBookingAt || bookingTime > map[id].lastBookingAt)) {
        map[id].lastBookingAt = bookingTime;
      }
    });

    return { map, ever, active30d };
  }, [bookings]);

  const getCustomerActivity = useCallback((customerId) => {
    return customerActivityById.map[customerId] || { count: 0, totalSpend: 0, lastBookingAt: null };
  }, [customerActivityById]);

  const getCustomerBookingsList = useCallback((customerId) => {
    return (bookings || [])
      .filter((b) => b.customer_id === customerId)
      .sort((a, b) => {
        const ta = new Date(a.booking_date || a.created_at || 0).getTime();
        const tb = new Date(b.booking_date || b.created_at || 0).getTime();
        return tb - ta;
      });
  }, [bookings]);

  const getCustomerTicketsList = useCallback((customerId) => {
    return (tickets || [])
      .filter((t) => t.user_id === customerId)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [tickets]);

  const customerStats = useMemo(() => {
    const list = customers || [];
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return {
      total: list.length,
      active30d: list.filter((c) => customerActivityById.active30d.has(c.id)).length,
      new7d: list.filter((c) => c.created_at && new Date(c.created_at).getTime() >= sevenDaysAgo).length,
      suspended: list.filter((c) => c.is_active === false).length,
      noBookings: list.filter((c) => !customerActivityById.ever.has(c.id)).length,
    };
  }, [customers, customerActivityById]);

  const filteredCustomers = useMemo(() => {
    let list = customers || [];
    const search = (customerFilterSearch || '').trim().toLowerCase();
    if (search) {
      const searchPhone = search.replace(/\s/g, '');
      list = list.filter((c) => {
        const name = (c.full_name || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const phone = (c.phone || '').replace(/\s/g, '');
        return name.includes(search) || email.includes(search) || phone.includes(searchPhone);
      });
    }
    const status = customerFilterStatus || 'all';
    if (status === 'active') {
      list = list.filter((c) => customerActivityById.active30d.has(c.id));
    } else if (status === 'new') {
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      list = list.filter((c) => c.created_at && new Date(c.created_at).getTime() >= sevenDaysAgo);
    } else if (status === 'suspended') {
      list = list.filter((c) => c.is_active === false);
    } else if (status === 'no_bookings') {
      list = list.filter((c) => !customerActivityById.ever.has(c.id));
    }
    return list;
  }, [customers, customerFilterSearch, customerFilterStatus, customerActivityById]);

  // Download file helper for verification documents
  const handleDownloadDocument = async (url, filename) => {
    try {
      setNotice('جاري تحميل المستند...');
      const response = await fetch(url);
      if (!response.ok) throw new Error('فشل تحميل الملف');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
      setNotice('✅ تم تحميل المستند بنجاح');
    } catch (error) {
      console.error('Error downloading document:', error);
      setNotice('❌ فشل تحميل المستند. جرب فتحه في تاب جديد ثم حفظه.');
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  const handleIdentityExpirySave = async (photographerId, expiresAt) => {
    if (!expiresAt) {
      setNotice('❌ يرجى إدخال تاريخ انتهاء مستند الهوية');
      return;
    }
    try {
      setMutating(true);
      const updates = {
        identity_document_expires_at: expiresAt,
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setNotice('✅ تم تحديث تاريخ انتهاء مستند الهوية');
    } catch (error) {
      console.error('Error saving identity expiry:', error);
      setNotice('❌ فشل تحديث تاريخ الانتهاء');
    } finally {
      setMutating(false);
    }
  };

  const handleIdentityApprove = async (photographerId, expiresAt) => {
    if (!expiresAt) {
      setNotice('❌ يرجى إدخال تاريخ انتهاء مستند الهوية قبل القبول');
      return;
    }
    try {
      setMutating(true);
      const updates = {
        identity_verified: true,
        identity_reviewed_at: new Date().toISOString(),
        identity_rejection_reason: null,
        identity_document_expires_at: expiresAt,
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setShowIdentityRejectInput(false);
      setIdentityRejectReason('');
      setNotice('✅ تم قبول مستند الهوية');
      try {
        const notifTitle = '✅ تم قبول مستند الهوية';
        const notifMsg = `تم التحقق من مستند الهوية واعتماده. ينتهي في ${new Date(expiresAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}.`;
        await db.from('notifications').insert({
          user_id: photographerId,
          type: 'verification',
          title: notifTitle,
          message: notifMsg,
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } catch (e) {
        console.warn('Failed to send identity approval notification:', e);
      }
    } catch (error) {
      console.error('Error approving identity document:', error);
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة مراجعة الهوية في Supabase أولاً.');
      } else {
        setNotice('❌ فشل قبول مستند الهوية');
      }
    } finally {
      setMutating(false);
    }
  };

  const handleIdentityReject = async (photographerId, reason) => {
    try {
      setMutating(true);
      const updates = {
        identity_verified: false,
        identity_rejection_reason: reason || null,
        identity_reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setShowIdentityRejectInput(false);
      setIdentityRejectReason('');
      setNotice('❌ تم رفض مستند الهوية');
      try {
        const notifTitle = '❌ رفض مستند الهوية';
        const notifMsg = reason ? `تم رفض مستند الهوية. السبب: ${reason}` : 'تم رفض مستند الهوية. يرجى رفع مستند صالح.';
        await db.from('notifications').insert({
          user_id: photographerId,
          type: 'verification',
          title: notifTitle,
          message: notifMsg,
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        callEdgeFunction('send-fcm-notification', {
          user_id: photographerId,
          title: notifTitle,
          body: notifMsg,
          data: { type: 'VERIFICATION' },
        }).catch((e) => console.warn('⚠️ FCM push failed:', e));
      } catch (e) {
        console.warn('Failed to send identity rejection notification:', e);
      }
    } catch (error) {
      console.error('Error rejecting identity document:', error);
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة مراجعة الهوية في Supabase أولاً.');
      } else {
        setNotice('❌ فشل رفض مستند الهوية');
      }
    } finally {
      setMutating(false);
    }
  };

  const handleVerificationAction = async (photographerId, action, notes = '') => {
    if (action === 'approve') {
      const record = verificationRequests.find((p) => p.user_id === photographerId) || selectedVerification;
      const block = getIdentityActivationBlockReason(record);
      if (block) {
        setNotice(`❌ ${block}`);
        return;
      }
    }
    try {
      setMutating(true);
      
      const updates = {
        verification_reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (action === 'approve') {
        updates.status = 'approved';
        updates.verification_status = 'approved';
      } else if (action === 'reject') {
        updates.status = 'pending';
        updates.verification_status = 'rejected';
        updates.rejection_reason = notes;
      } else if (action === 'request_docs') {
        updates.verification_status = 'pending';
        updates.verification_notes = notes;
      }

      const { error } = await supabase
        .from('photographer_profiles')
        .update(updates)
        .eq('user_id', photographerId);

      if (error) throw error;

      // Also update photographers table status if approved
      if (action === 'approve') {
        await supabase
          .from('photographers')
          .update({ status: 'approved' })
          .eq('id', photographerId);
      }

      setVerificationRequests(prev => prev.map(p => 
        p.user_id === photographerId ? { ...p, ...updates } : p
      ));
      
      const actionMessages = {
        'approve': '✅ تم تفعيل حساب المصور',
        'reject': '❌ تم رفض المصور',
        'request_docs': '📄 تم طلب مستندات إضافية'
      };
      setNotice(actionMessages[action] || 'تم التحديث');
      
      // Close modal if open
      if (showVerificationModal) {
        setShowVerificationModal(false);
        setSelectedVerification(null);
        setShowCertificatesRejectInput(false);
        setCertificatesRejectReason('');
        setShowDroneRejectInput(false);
        setDroneRejectReason('');
        setShowFreelanceRejectInput(false);
        setFreelanceRejectReason('');
        setShowIdentityRejectInput(false);
        setIdentityRejectReason('');
        setFreelanceExpiryInput('');
        setDroneExpiryInput('');
        setIdentityExpiryInput('');
      }

      // 🔔 Send in-app notification to the photographer about the decision
      try {
        const notifMessages = {
          'approve': {
            title: '✅ تم تفعيل حسابك',
            message: 'مبروك! تم تفعيل حسابك على منصة مشهد 🎉\nأضف صور لمعرض أعمالك وابدأ استقبال الحجوزات',
            type: 'verification'
          },
          'reject': { title: '📋 وثائقك تحتاج مراجعة', message: `هلا! وصلتنا وثائقك، لكن ما اكتملت المراجعة.${notes ? ' ' + notes + '.' : ''} إذا أعدت رفعها نكمل لك التوثيق فور مراجعتها 🙏`, type: 'verification' },
          'request_docs': { title: '📄 مطلوب مستندات إضافية', message: `يرجى رفع مستندات إضافية لإتمام عملية التوثيق.${notes ? ' الملاحظة: ' + notes : ''}`, type: 'verification' }
        };
        const notifData = notifMessages[action];
        if (notifData) {
          await supabase
            .from('notifications')
            .insert({
              user_id: photographerId,
              type: notifData.type,
              title: notifData.title,
              message: notifData.message,
              is_read: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          console.log('✅ Photographer notification created for verification action:', action);

          // FCM Push notification
          callEdgeFunction('send-fcm-notification', {
            user_id: photographerId,
            title: notifData.title,
            body: notifData.message,
            data: { type: notifData.type.toUpperCase() },
          }).catch((e) => console.warn('⚠️ FCM push failed:', e));

          // 📧 بريد إلكتروني عند الموافقة أو الرفض
          if (action === 'approve' || action === 'reject') {
            const photographerRecord = verificationRequests.find(p => p.user_id === photographerId);
            const photographerEmail  = photographerRecord?.user?.email;
            const photographerName   = photographerRecord?.user?.full_name || photographerRecord?.business_name || '';
            if (photographerEmail) {
              const emailTemplate = action === 'approve'
                ? { key: 'photographer_verification_approved', data: { photographer_name: photographerName } }
                : { key: 'photographer_verification_rejected',  data: { photographer_name: photographerName, rejection_reason: notes || 'لم يُحدَّد سبب' } };
              supabase.functions.invoke('send-email', {
                body: {
                  to: photographerEmail,
                  template_key: emailTemplate.key,
                  template_data: emailTemplate.data,
                },
              }).catch((e) => console.warn('⚠️ Verification action email failed:', e));
            }
          }
        }
      } catch (notifError) {
        console.warn('⚠️ Failed to create photographer notification:', notifError);
      }
      
      // Refresh data and notifications
      refreshData();
      loadAdminNotifications();
      loadVerificationRequests();
      
      // Mark related notifications as read
      const relatedNotifs = adminNotifications.filter(n => 
        n.type === 'verification_request' && n.related_user_id === photographerId
      );
      for (const notif of relatedNotifs) {
        await markNotificationAsRead(notif.id);
      }
    } catch (error) {
      console.error('Error processing verification:', error);
      setNotice('❌ فشل تحديث حالة التحقق');
    } finally {
      setMutating(false);
    }
  };

  const handleVerifySection = async (photographerId, section, verified) => {
    try {
      setMutating(true);
      
      const updates = {
        [`${section}_verified`]: verified,
        updated_at: new Date().toISOString()
      };

      // When identity is verified, also update verification_status
      if (section === 'identity') {
        if (verified) {
          updates.verification_status = 'approved';
          updates.verification_reviewed_at = new Date().toISOString();
        } else {
          updates.verification_status = 'pending';
        }
      }
      // SAFE MODE: Certificates feature - set certificates_reviewed_at on approve/un-approve
      if (section === 'certificates') {
        updates.certificates_reviewed_at = new Date().toISOString();
        if (!verified) updates.certificates_rejection_reason = null;
      }

      const { error, count } = await supabase
        .from('photographer_profiles')
        .update(updates)
        .eq('user_id', photographerId);

      if (error) throw error;

      // Update local state
      setVerificationRequests(prev => prev.map(p => 
        p.user_id === photographerId ? { ...p, ...updates } : p
      ));
      
      // Also update selected verification if modal is open
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification(prev => ({ ...prev, ...updates }));
      }

      const sectionLabels = { identity: 'الهوية', portfolio: 'معرض الأعمال', phone: 'رقم الهاتف', certificates: 'شهادات التصوير' };
      setNotice(`${verified ? '✅ تم التحقق من' : '❌ تم إلغاء التحقق من'} ${sectionLabels[section] || section}`);

      try {
        let notifTitle;
        let notifMessage;

        if (verified) {
          if (section === 'identity') {
            notifTitle = '✅ تم توثيق حسابك';
            notifMessage = 'مبروك تم توثيق حسابك 🎉\nأضف صور لمعرض أعمالك وابدأ استقبال الحجوزات';
          } else if (section === 'certificates') {
            notifTitle = '✅ تم التحقق من شهادات التصوير';
            notifMessage = 'تم التحقق من شهادات التصوير واعتمادها من الإدارة.';
          } else {
            notifTitle = `✅ تم التحقق من ${sectionLabels[section]}`;
            notifMessage = `تم التحقق من ${sectionLabels[section]} بنجاح. شكراً لتعاونك.`;
          }
        } else {
          notifTitle = `❌ تم إلغاء التحقق من ${sectionLabels[section]}`;
          notifMessage = `تم إلغاء التحقق من ${sectionLabels[section]}. يرجى التواصل مع الدعم لمزيد من التفاصيل.`;
        }

        await supabase
          .from('notifications')
          .insert({
            user_id: photographerId,
            type: 'verification',
            title: notifTitle,
            message: notifMessage,
            is_read: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
      } catch (notifError) {
        console.warn('⚠️ Failed to send photographer notification:', notifError);
      }

      if (section === 'identity' && verified) {
        try { await db.from('photographers').update({ status: 'approved' }).eq('id', photographerId); } catch (e) { console.warn('⚠️ Could not update photographers table:', e); }

        // 📧 بريد إلكتروني عند قبول التحقق من الهوية
        try {
          const photographerRecord = verificationRequests.find(p => p.user_id === photographerId);
          const photographerEmail = photographerRecord?.user?.email;
          const photographerName  = photographerRecord?.user?.full_name || photographerRecord?.business_name || '';
          if (photographerEmail) {
            supabase.functions.invoke('send-email', {
              body: {
                to: photographerEmail,
                template_key: 'photographer_verification_approved',
                template_data: { photographer_name: photographerName },
              },
            }).catch((e) => console.warn('⚠️ Verification approval email failed:', e));
          }
        } catch (e) { console.warn('⚠️ Could not send verification approval email:', e); }
      }
      try {
        const relatedNotifs = adminNotifications.filter(n => n.type === 'verification_request' && n.related_user_id === photographerId);
        for (const notif of relatedNotifs) await markNotificationAsRead(notif.id);
      } catch (e) { console.warn('⚠️ Could not mark notifications as read:', e); }
    } catch (error) {
      console.error('Error verifying section:', error);
      const is400 = error?.status === 400 || error?.code === '400' || error?.message?.includes('400');
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist') || error?.message?.includes('column');
      if (section === 'certificates' && (is400 || isMissingColumn)) {
        setNotice('❌ يرجى تشغيل هجرة مراجعة الشهادات في Supabase (SQL Editor). انظر RUN_CERTIFICATES_MIGRATION.md — القسم «قبول/رفض الشهادات».');
      } else {
        setNotice('❌ فشل التحديث: ' + (error?.message || 'خطأ غير معروف'));
      }
    } finally {
      setMutating(false);
    }
  };

  const handleDronePermitApprove = async (photographerId, expiresAt) => {
    if (!expiresAt) {
      setNotice('❌ يرجى إدخال تاريخ انتهاء التصريح قبل القبول');
      return;
    }
    try {
      setMutating(true);
      const updates = {
        drone_permit_verified: true,
        drone_permit_reviewed_at: new Date().toISOString(),
        drone_permit_rejection_reason: null,
        drone_permit_expires_at: expiresAt,
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setNotice('✅ تم قبول تصريح الدرون');
      try {
        const notifTitle = '✅ تم قبول تصريح الدرون';
        const notifMsg = `تم التحقق من تصريح الدرون واعتماده. ينتهي في ${new Date(expiresAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}.`;
        await db.from('notifications').insert({
          user_id: photographerId,
          type: 'verification',
          title: notifTitle,
          message: notifMsg,
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } catch (e) {
        console.warn('Failed to send drone permit approval notification:', e);
      }
      try {
        const relatedNotifs = adminNotifications.filter(
          (n) => n.type === 'drone_permit_request' && n.related_user_id === photographerId
        );
        for (const notif of relatedNotifs) await markNotificationAsRead(notif.id);
      } catch (e) {
        console.warn('Could not mark drone permit notifications as read:', e);
      }
    } catch (error) {
      console.error('Error approving drone permit:', error);
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة تصريح الدرون في Supabase أولاً.');
      } else {
        setNotice('❌ فشل قبول تصريح الدرون');
      }
    } finally {
      setMutating(false);
    }
  };

  const handleDroneExpirySave = async (photographerId, expiresAt) => {
    if (!expiresAt) {
      setNotice('❌ يرجى إدخال تاريخ الانتهاء');
      return;
    }
    try {
      setMutating(true);
      const updates = {
        drone_permit_expires_at: expiresAt,
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setNotice('✅ تم تحديث تاريخ انتهاء تصريح الدرون');
    } catch (error) {
      console.error('Error saving drone expiry:', error);
      setNotice('❌ فشل تحديث تاريخ الانتهاء');
    } finally {
      setMutating(false);
    }
  };

  const handleDronePermitReject = async (photographerId, reason) => {
    try {
      setMutating(true);
      const updates = {
        drone_permit_verified: false,
        drone_permit_rejection_reason: reason || null,
        drone_permit_reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setShowDroneRejectInput(false);
      setDroneRejectReason('');
      setNotice('❌ تم رفض تصريح الدرون');
      try {
        const notifTitle = '❌ رفض تصريح الدرون';
        const notifMsg = reason ? `تم رفض تصريح الدرون. السبب: ${reason}` : 'تم رفض تصريح الدرون. يرجى رفع تصريح صالح.';
        await db.from('notifications').insert({
          user_id: photographerId,
          type: 'verification',
          title: notifTitle,
          message: notifMsg,
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        callEdgeFunction('send-fcm-notification', {
          user_id: photographerId,
          title: notifTitle,
          body: notifMsg,
          data: { type: 'VERIFICATION' },
        }).catch((e) => console.warn('⚠️ FCM push failed:', e));
      } catch (e) {
        console.warn('Failed to send drone permit rejection notification:', e);
      }
      try {
        const relatedNotifs = adminNotifications.filter(
          (n) => n.type === 'drone_permit_request' && n.related_user_id === photographerId
        );
        for (const notif of relatedNotifs) await markNotificationAsRead(notif.id);
      } catch (e) {
        console.warn('Could not mark drone permit notifications as read:', e);
      }
    } catch (error) {
      console.error('Error rejecting drone permit:', error);
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة تصريح الدرون في Supabase أولاً.');
      } else {
        setNotice('❌ فشل رفض تصريح الدرون');
      }
    } finally {
      setMutating(false);
    }
  };

  const handleFreelancePermitApprove = async (photographerId, expiresAt) => {
    if (!expiresAt) {
      setNotice('❌ يرجى إدخال تاريخ انتهاء الوثيقة قبل القبول');
      return;
    }
    try {
      setMutating(true);
      const updates = {
        freelance_permit_verified: true,
        freelance_permit_reviewed_at: new Date().toISOString(),
        freelance_permit_rejection_reason: null,
        freelance_permit_expires_at: expiresAt,
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setNotice('✅ تم قبول وثيقة العمل الحر');
      try {
        const notifTitle = '✅ تم قبول وثيقة العمل الحر';
        const notifMsg = `تم التحقق من وثيقة العمل الحر واعتمادها. تنتهي في ${new Date(expiresAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}.`;
        await db.from('notifications').insert({
          user_id: photographerId,
          type: 'verification',
          title: notifTitle,
          message: notifMsg,
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } catch (e) {
        console.warn('Failed to send freelance permit approval notification:', e);
      }
      try {
        const relatedNotifs = adminNotifications.filter(
          (n) => n.type === 'freelance_permit_request' && n.related_user_id === photographerId
        );
        for (const notif of relatedNotifs) await markNotificationAsRead(notif.id);
      } catch (e) {
        console.warn('Could not mark freelance permit notifications as read:', e);
      }
    } catch (error) {
      console.error('Error approving freelance permit:', error);
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة وثيقة العمل الحر في Supabase أولاً.');
      } else {
        setNotice('❌ فشل قبول وثيقة العمل الحر');
      }
    } finally {
      setMutating(false);
    }
  };

  const handleFreelanceExpirySave = async (photographerId, expiresAt) => {
    if (!expiresAt) {
      setNotice('❌ يرجى إدخال تاريخ الانتهاء');
      return;
    }
    try {
      setMutating(true);
      const updates = {
        freelance_permit_expires_at: expiresAt,
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setNotice('✅ تم تحديث تاريخ انتهاء الوثيقة');
    } catch (error) {
      console.error('Error saving freelance expiry:', error);
      setNotice('❌ فشل تحديث تاريخ الانتهاء');
    } finally {
      setMutating(false);
    }
  };

  const handleFreelancePermitReject = async (photographerId, reason) => {
    try {
      setMutating(true);
      const updates = {
        freelance_permit_verified: false,
        freelance_permit_rejection_reason: reason || null,
        freelance_permit_reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests((prev) => prev.map((p) => (p.user_id === photographerId ? { ...p, ...updates } : p)));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification((prev) => ({ ...prev, ...updates }));
      }
      setShowFreelanceRejectInput(false);
      setFreelanceRejectReason('');
      setNotice('❌ تم رفض وثيقة العمل الحر');
      try {
        const notifTitle = '❌ رفض وثيقة العمل الحر';
        const notifMsg = reason ? `تم رفض وثيقة العمل الحر. السبب: ${reason}` : 'تم رفض وثيقة العمل الحر. يرجى رفع وثيقة صالحة.';
        await db.from('notifications').insert({
          user_id: photographerId,
          type: 'verification',
          title: notifTitle,
          message: notifMsg,
          is_read: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        callEdgeFunction('send-fcm-notification', {
          user_id: photographerId,
          title: notifTitle,
          body: notifMsg,
          data: { type: 'VERIFICATION' },
        }).catch((e) => console.warn('⚠️ FCM push failed:', e));
      } catch (e) {
        console.warn('Failed to send freelance permit rejection notification:', e);
      }
      try {
        const relatedNotifs = adminNotifications.filter(
          (n) => n.type === 'freelance_permit_request' && n.related_user_id === photographerId
        );
        for (const notif of relatedNotifs) await markNotificationAsRead(notif.id);
      } catch (e) {
        console.warn('Could not mark freelance permit notifications as read:', e);
      }
    } catch (error) {
      console.error('Error rejecting freelance permit:', error);
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة وثيقة العمل الحر في Supabase أولاً.');
      } else {
        setNotice('❌ فشل رفض وثيقة العمل الحر');
      }
    } finally {
      setMutating(false);
    }
  };

  // SAFE MODE: Certificates feature - reject certificates with reason
  const handleCertificatesReject = async (photographerId, reason) => {
    try {
      setMutating(true);
      const updates = {
        certificates_verified: false,
        certificates_rejection_reason: reason || null,
        certificates_reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      const { error } = await db.from('photographer_profiles').update(updates).eq('user_id', photographerId);
      if (error) throw error;
      setVerificationRequests(prev => prev.map(p => p.user_id === photographerId ? { ...p, ...updates } : p));
      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification(prev => ({ ...prev, ...updates }));
      }
      setShowCertificatesRejectInput(false);
      setCertificatesRejectReason('');
      setNotice('❌ تم رفض شهادات التصوير');
      try {
        const certNotifTitle = '❌ رفض شهادات التصوير';
        const certNotifMsg = reason ? `تم رفض شهادات التصوير. السبب: ${reason}` : 'تم رفض شهادات التصوير. يرجى التواصل مع الدعم.';
        await db.from('notifications').insert({ user_id: photographerId, type: 'verification', title: certNotifTitle, message: certNotifMsg, is_read: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
        callEdgeFunction('send-fcm-notification', {
          user_id: photographerId,
          title: certNotifTitle,
          body: certNotifMsg,
          data: { type: 'VERIFICATION' },
        }).catch((e) => console.warn('⚠️ FCM push failed:', e));
      } catch (e) { console.warn('Failed to send notification:', e); }
    } catch (error) {
      console.error('Error rejecting certificates:', error);
      const is400 = error?.status === 400 || error?.code === '400' || error?.message?.includes('400');
      const isMissingColumn = error?.code === '42703' || error?.message?.includes('does not exist');
      if (is400 || isMissingColumn) {
        setNotice('❌ يرجى تشغيل هجرة مراجعة الشهادات في Supabase (SQL Editor). انظر RUN_CERTIFICATES_MIGRATION.md — القسم «قبول/رفض الشهادات».');
      } else {
        setNotice('❌ فشل تحديث حالة الشهادات');
      }
    } finally {
      setMutating(false);
    }
  };

  // ── نافذة الرفض الموحّدة ──────────────────────────────────────────────────
  const openRejectionPanel = (photographerId) => {
    setRejectionTargetId(photographerId);
    setSelectedRejectionReasons([]);
    setCustomRejectionNote('');
    setShowRejectionPanel(true);
  };

  const toggleRejectionReason = (reason) => {
    setSelectedRejectionReasons(prev =>
      prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
    );
  };

  const submitRejection = () => {
    const reasons = [
      ...selectedRejectionReasons,
      ...(customRejectionNote.trim() ? [customRejectionNote.trim()] : []),
    ];
    const rejectionText = reasons.length > 0
      ? reasons.map((r, i) => `${i + 1}. ${r}`).join('\n')
      : 'لم يُحدَّد سبب';
    handleVerificationAction(rejectionTargetId, 'reject', rejectionText);
    setShowRejectionPanel(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handlePortfolioImageApproval = async (photographerId, imageId, status) => {
    if (!imageId || !['approved', 'rejected'].includes(status)) return;
    try {
      setMutating(true);

      // 1. تحديث حالة الصورة
      const { error } = await supabase
        .from('portfolio_images')
        .update({ approval_status: status })
        .eq('id', imageId);

      if (error) throw error;

      // 2. حساب الصور المعتمدة بعد التحديث
      const { count: approvedCount } = await supabase
        .from('portfolio_images')
        .select('id', { count: 'exact', head: true })
        .eq('photographer_id', photographerId)
        .eq('approval_status', 'approved');

      // portfolio_verified = true إذا كان هناك صورة واحدة على الأقل معتمدة
      const portfolioVerified = (approvedCount ?? 0) >= 1;

      // 3. تحديث portfolio_verified في الملف الشخصي
      await supabase
        .from('photographer_profiles')
        .update({ portfolio_verified: portfolioVerified, updated_at: new Date().toISOString() })
        .eq('user_id', photographerId);

      // 4. تحديث الحالة المحلية
      const updatedImages = (imgList) =>
        (imgList || []).map(img => img.id === imageId ? { ...img, approval_status: status } : img);

      setVerificationRequests(prev => prev.map(p => {
        if (p.user_id !== photographerId) return p;
        return { ...p, portfolioImages: updatedImages(p.portfolioImages), portfolio_verified: portfolioVerified };
      }));

      if (selectedVerification && selectedVerification.user_id === photographerId) {
        setSelectedVerification(prev => ({
          ...prev,
          portfolioImages: updatedImages(prev.portfolioImages),
          portfolio_verified: portfolioVerified,
        }));
      }

      // إشعار المصور عند رفض صورته
      if (status === 'rejected') {
        try {
          await supabase.from('notifications').insert({
            user_id:    photographerId,
            type:       'portfolio',
            title:      '📸 تم رفض صورة من معرض أعمالك',
            message:    'تم رفض إحدى صور معرض أعمالك من قِبل المشرف. يرجى الدخول على معرضك واستبدال الصورة المرفوضة بأخرى ملائمة.',
            is_read:    false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        } catch (_) {}
      }

      setNotice(status === 'approved' ? '✅ تمت الموافقة على الصورة' : '⚠️ تم رفض الصورة وتم إشعار المصور');
    } catch (e) {
      setNotice(`❌ ${e?.message || 'تعذر تحديث حالة الصورة'}`);
    } finally {
      setMutating(false);
    }
  };

  // Load verification requests on mount and when verification tab is active
  useEffect(() => {
    // Load on initial mount for notification count
    loadVerificationRequests();
  }, []);
  
  useEffect(() => {
    // Reload when switching to verification tab
    if (active === 'verification') {
      loadVerificationRequests();
    }
  }, [active]);

  // Get environment variable status (configured or not)
  const getEnvStatus = (key) => {
    const envMap = {
      'GOOGLE_MAPS_API_KEY': import.meta.env?.VITE_GOOGLE_MAPS_API_KEY,
      'TWILIO_ACCOUNT_SID': import.meta.env?.VITE_TWILIO_ACCOUNT_SID,
      'TWILIO_AUTH_TOKEN': import.meta.env?.VITE_TWILIO_AUTH_TOKEN,
      'TWILIO_WHATSAPP_NUMBER': import.meta.env?.VITE_TWILIO_WHATSAPP_NUMBER,
      'APP_URL': import.meta.env?.VITE_APP_URL,
      'SUPABASE_URL': import.meta.env?.VITE_SUPABASE_URL,
      'SUPABASE_ANON_KEY': import.meta.env?.VITE_SUPABASE_ANON_KEY,
      'SUPABASE_SERVICE_ROLE_KEY': 'configured-via-edge-function',
    };
    return !!envMap[key];
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'google': '🗺️ Google Services',
      'twilio': '📱 Twilio / WhatsApp',
      'app': '⚙️ إعدادات التطبيق',
      'general': '📋 عام'
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'google': 'Map',
      'twilio': 'MessageCircle',
      'app': 'Settings',
      'general': 'FileText'
    };
    return icons[category] || 'Key';
  };
  // ============ End App Settings Functions ============

  // ============ Admin Delivery Override Functions ============
  const loadPendingDeliveryBookings = async () => {
    try {
      // Get completed bookings that haven't been delivered yet
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_date,
          booking_time,
          photography_type,
          city,
          customer_id,
          photographer_id,
          status,
          delivery_status
        `)
        .eq('status', 'completed')
        .or('delivery_status.is.null,delivery_status.eq.pending')
        .order('booking_date', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Get user info
      const userIds = [...new Set([
        ...data.map(b => b.customer_id).filter(Boolean),
        ...data.map(b => b.photographer_id).filter(Boolean)
      ])];

      let usersMap = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('id, full_name, avatar_url')
          .in('id', userIds);
        users?.forEach(u => { usersMap[u.id] = u; });
      }

      const enriched = data.map(b => ({
        ...b,
        customer: usersMap[b.customer_id] || null,
        photographer: usersMap[b.photographer_id] || null
      }));

      setPendingDeliveryBookings(enriched);
    } catch (error) {
      console.error('Error loading pending delivery bookings:', error);
      setNotice('فشل تحميل الحجوزات المعلقة');
    }
  };

  const handleAdminFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newFiles = files.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type.startsWith('image/') ? 'image' : 'video',
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setAdminUploadFiles(prev => [...prev, ...newFiles]);
  };

  const handleAdminRemoveFile = (fileId) => {
    setAdminUploadFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== fileId);
    });
  };

  // ─── عرض ملفات التسليم للمشرف (signed URLs مؤقتة لمدة ساعة) ───────────────
  const handleViewDeliveryFiles = async (delivery) => {
    setSelectedDeliveryForFiles(delivery);
    setDeliveryFiles([]);
    setLightboxFile(null);
    setShowDeliveryFilesModal(true);
    setLoadingDeliveryFiles(true);
    try {
      const { data: files, error } = await supabase
        .from('delivery_files')
        .select('id, file_name, file_path, file_type, file_size_bytes, mime_type, thumbnail_path, sort_order')
        .eq('delivery_id', delivery.id)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      if (!files || files.length === 0) { setDeliveryFiles([]); return; }

      // توليد signed URLs مؤقتة (TTL = 1 ساعة) لكل ملف
      const filesWithUrls = await Promise.all(
        files.map(async (file) => {
          try {
            const { data: urlData } = await db.storage
              .from('deliveries')
              .createSignedUrl(file.file_path, 3600);
            let thumbnailUrl = null;
            if (file.thumbnail_path) {
              const { data: thumbData } = await db.storage
                .from('deliveries')
                .createSignedUrl(file.thumbnail_path, 3600);
              thumbnailUrl = thumbData?.signedUrl || null;
            }
            return { ...file, signedUrl: urlData?.signedUrl || null, thumbnailUrl };
          } catch {
            return { ...file, signedUrl: null, thumbnailUrl: null };
          }
        })
      );
      setDeliveryFiles(filesWithUrls);
    } catch (err) {
      console.error('Failed to load delivery files:', err);
      setDeliveryFiles([]);
    } finally {
      setLoadingDeliveryFiles(false);
    }
  };

  const handleAdminUploadAndDeliver = async () => {
    if (!adminDeliveryModal.bookingId || adminUploadFiles.length === 0) {
      setNotice('يرجى اختيار حجز وإضافة ملفات');
      return;
    }

    setAdminUploading(true);
    setAdminUploadProgress(0);

    try {
      const bookingId = adminDeliveryModal.bookingId;
      // البحث في القوائم المحملة أولاً، ثم جلب مباشر من DB كـ fallback
      let booking = pendingDeliveryBookings.find(b => b.id === bookingId) || bookings.find(b => b.id === bookingId);
      if (!booking) {
        const { data: fetchedBooking } = await supabase
          .from('bookings')
          .select('id, customer_id, photographer_id, assigned_photographer_id')
          .eq('id', bookingId)
          .single();
        booking = fetchedBooking;
      }
      // الدمج مع الـ meta المحفوظة من الجدول (customerId من صف التسليم)
      const customerId = adminDeliveryModal._deliveryMeta?.customerId || booking?.customer_id;

      if (!booking && !customerId) {
        throw new Error('الحجز غير موجود');
      }

      // Create or get delivery record
      let deliveryId;
      const { data: existingDelivery } = await supabase
        .from('booking_deliveries')
        .select('id')
        .eq('booking_id', bookingId)
        .single();

      if (existingDelivery) {
        deliveryId = existingDelivery.id;
      } else {
        const { data: newDelivery, error: createError } = await supabase
          .from('booking_deliveries')
          .insert({
            booking_id: bookingId,
            photographer_id: booking.photographer_id,
            customer_id: booking.customer_id,
            status: 'uploading',
            total_photos: adminUploadFiles.filter(f => f.type === 'image').length,
            total_videos: adminUploadFiles.filter(f => f.type === 'video').length
          })
          .select('id')
          .single();
        
        if (createError) throw createError;
        deliveryId = newDelivery.id;
      }

      // Upload files
      const totalFiles = adminUploadFiles.length;
      let uploadedCount = 0;

      for (const fileData of adminUploadFiles) {
        const fileExt = fileData.name.split('.').pop();
        const fileName = `admin/${bookingId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

        const { error: uploadError } = await db.storage
          .from('deliveries')
          .upload(fileName, fileData.file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          continue;
        }

        // لا نستخدم getPublicUrl (رابط دائم غير آمن) — Signed URLs تُولَّد عند العرض
        await db.from('delivery_files').insert({
          delivery_id: deliveryId,
          file_name: fileData.name,
          file_type: fileData.type,
          file_path: fileName,
          file_size_bytes: fileData.size
        });

        uploadedCount++;
        setAdminUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      }

      // Update delivery status to delivered
      await supabase
        .from('booking_deliveries')
        .update({
          status: 'delivered',
          delivered_at: new Date().toISOString(),
          total_photos: adminUploadFiles.filter(f => f.type === 'image').length,
          total_videos: adminUploadFiles.filter(f => f.type === 'video').length
        })
        .eq('id', deliveryId);

      // Update booking delivery status
      await supabase
        .from('bookings')
        .update({ delivery_status: 'delivered', status: 'completed' })
        .eq('id', bookingId);

      // 🔔 إشعار فوري للعميل: صوره جاهزة
      if (customerId) {
        try {
          const { notifyCustomerFilesDelivered } = await import('../../services/notificationService');
          notifyCustomerFilesDelivered(customerId, bookingId);
        } catch (_) {}
      }

      // Clear and close
      setAdminUploadFiles([]);
      setAdminDeliveryModal({ open: false, mode: null, bookingId: null, _deliveryMeta: null });
      setNotice('✅ تم رفع الملفات وتسليمها للعميل بنجاح — وصل إشعار للعميل');
      
      // Refresh data
      setRefreshKey(k => k + 1);

    } catch (error) {
      console.error('Admin upload error:', error);
      setNotice('فشل رفع الملفات: ' + error.message);
    } finally {
      setAdminUploading(false);
      setAdminUploadProgress(0);
    }
  };

  // Load pending bookings when modal opens
  useEffect(() => {
    if (adminDeliveryModal.open && adminDeliveryModal.mode === 'pending_list') {
      loadPendingDeliveryBookings();
    }
  }, [adminDeliveryModal.open, adminDeliveryModal.mode]);
  // ============ End Admin Delivery Override Functions ============

  const filteredBookings = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (bookings || []).filter((b) => {
      const matchesStatus = statusFilter === "all" ? true : (b.status || "") === statusFilter;
      if (!matchesStatus) return false;
      if (!s) return true;
      const hay = [
        b.id,
        b.city,
        b.photography_type,
        b.status,
        b.customer?.full_name,
        b.customer?.email,
        b.customer?.phone,
        b.photographer?.full_name,
        b.photographer?.email,
        b.location,
        b.payment_transaction_id
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
  }, [bookings, search, statusFilter]);

  const onTab = (key) => {
    navigate(`/admin?tab=${key}`);
  };

  const navigateToCustomerBookings = (customer) => {
    const q = customer.full_name || customer.email || customer.phone || String(customer.id || '').slice(0, 8);
    setSearch(q);
    setStatusFilter('all');
    setShowUserModal(false);
    onTab('bookings');
  };

  const navigateToCustomerSupport = (customer) => {
    const q = customer.full_name || customer.email || '';
    setTicketFilter((prev) => ({ ...prev, search: q, status: 'all' }));
    setShowUserModal(false);
    onTab('support');
    if (!tickets.length) loadTickets();
  };

  const openCustomerBookingFromModal = (booking) => {
    setShowUserModal(false);
    setSelectedBooking(booking);
    setShowBookingModal(true);
    setSearch(String(booking.id || '').slice(0, 8));
    onTab('bookings');
  };

  const openCustomerTicketFromModal = async (ticket) => {
    setShowUserModal(false);
    onTab('support');
    if (!tickets.length) await loadTickets();
    await openTicketDetails(ticket);
  };

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed") return <Badge tone="green">مكتمل</Badge>;
    if (s === "paid_confirmed") return <Badge tone="blue">مدفوع ومؤكد</Badge>;
    if (s === "awaiting_payment") return <Badge tone="amber">في انتظار الدفع</Badge>;
    if (s === "pending_photographer_confirmation") return <Badge tone="amber">في انتظار تأكيد المصور</Badge>;
    if (s === "declined") return <Badge tone="red">مرفوض</Badge>;
    if (s === "expired") return <Badge tone="slate">منتهي</Badge>;
    if (s === "cancelled_by_customer" || s === "cancelled_by_admin") return <Badge tone="red">ملغي</Badge>;
    return <Badge tone="slate">{status || "—"}</Badge>;
  };

  // ========== HELPER FUNCTIONS FOR FORMATTING ==========
  
  // Format money with English numbers
  const money = (n) => {
    const v = Number(n || 0);
    return `${v.toLocaleString("en-US")} ر.س`;
  };
  
  // Format date to Gregorian with English numbers (DD/MM/YYYY)
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    } catch {
      return "—";
    }
  };
  
  // Format time to 12-hour format (e.g., 2:30 PM)
  const formatTime = (timeStr) => {
    if (!timeStr) return "—";
    try {
      // Handle HH:MM format
      const [hours, minutes] = timeStr.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return timeStr;
      
      const period = hours >= 12 ? 'م' : 'ص';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeStr;
    }
  };
  
  // Format datetime to full string
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "—";
      const dateFormatted = date.toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      const timeFormatted = date.toLocaleTimeString("ar-SA", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });
      return `${dateFormatted} - ${timeFormatted}`;
    } catch {
      return "—";
    }
  };

  const formatSessionDuration = (startedAt, endedAt) => {
    if (!startedAt || !endedAt) return null;
    const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    const totalMin = Math.max(1, Math.round(ms / 60000));
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h <= 0) return { text: `${m} دقيقة`, minutes: totalMin };
    if (m === 0) return { text: `${h} ساعة`, minutes: totalMin };
    return { text: `${h} ساعة و ${m} دقيقة`, minutes: totalMin };
  };
  
  // Extract structured location from location_details (JSON); plain-text notes return null
  const getLocationDetails = (booking) => {
    if (!booking?.location_details) return null;
    const ld = booking.location_details;
    const isLocObject = (obj) =>
      obj && typeof obj === 'object' && (obj.lat != null || obj.address_text || obj.type);
    if (typeof ld === 'object') return isLocObject(ld) ? ld : null;
    if (typeof ld === 'string') {
      try {
        const parsed = JSON.parse(ld);
        return isLocObject(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const getCustomerNote = (booking) => {
    if (!booking?.location_details) return null;
    const ld = booking.location_details;
    if (typeof ld === 'object') {
      return ld.customer_note || ld.customerNote || null;
    }
    if (typeof ld === 'string') {
      try {
        const parsed = JSON.parse(ld);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed.customer_note || parsed.customerNote || null;
        }
      } catch {
        return ld;
      }
    }
    return null;
  };
  
  // Generate Google Maps link
  const getMapLink = (lat, lng) => {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
              لوحة تحكم المشرف
            </h1>
            <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
              إدارة الحجوزات والمصورين والعملاء والتسعير (MVP)
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refreshData}
              className="px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm flex items-center gap-2"
              title="تحديث البيانات"
            >
              <Icon name="RefreshCw" size={18} color="#0f172a" />
              <span style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تحديث</span>
            </button>
            <Link to="/" className="px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm flex items-center gap-2">
              <Icon name="Home" size={18} color="#0f172a" />
              <span style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الموقع</span>
            </Link>
            <Link to="/admin-master-dashboard" className="px-3 py-2 rounded-xl bg-primary text-white hover:bg-primary/90 text-sm flex items-center gap-2">
              <Icon name="Sparkles" size={18} color="white" />
              <span style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>لوحة متقدمة</span>
            </Link>
          </div>
        </div>

        {/* Layout - القائمة الجانبية قابلة للطي */}
        <div className={`grid grid-cols-1 gap-6 transition-[grid-template-columns] duration-200 ${sidebarCollapsed ? 'lg:grid-cols-[72px_1fr]' : 'lg:grid-cols-[280px_1fr]'}`}>
          {/* Sidebar */}
          <aside className={`bg-white rounded-2xl border border-slate-100 shadow-sm h-fit lg:sticky lg:top-28 transition-all duration-200 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-2' : 'justify-between'} px-2 py-2`}>
              <div className={`flex items-center ${sidebarCollapsed ? 'flex-col' : 'gap-2'}`}>
                <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                  <Icon name="Shield" size={18} color="white" />
                </div>
                {!sidebarCollapsed && (
                  <div>
                    <div className="text-sm font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>لوحة المشرف</div>
                    <div className="text-xs text-emerald-600 font-medium">● مسجّل دخول</div>
                  </div>
                )}
              </div>
              {!sidebarCollapsed && <Badge tone="green">Live</Badge>}
            </div>

            <button
              type="button"
              onClick={() => setSidebarCollapsed((c) => !c)}
              className="mt-2 w-full flex items-center justify-center gap-2 px-2 py-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"
              title={sidebarCollapsed ? 'توسيع القائمة' : 'طي القائمة'}
            >
              <Icon name={sidebarCollapsed ? 'ChevronLeft' : 'ChevronRight'} size={18} />
              {!sidebarCollapsed && <span className="text-xs font-medium" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>طي القائمة</span>}
            </button>
            <button
              type="button"
              onClick={async () => {
                await supabase.auth.signOut();
                navigate('/admin-login', { replace: true });
              }}
              className="mt-1 w-full flex items-center justify-center gap-2 px-2 py-2 rounded-xl hover:bg-red-50 text-red-500 transition-colors"
              title="تسجيل الخروج"
            >
              <Icon name="LogOut" size={18} />
              {!sidebarCollapsed && <span className="text-xs font-medium" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تسجيل الخروج</span>}
            </button>

            <div className="mt-2 space-y-1">
              {TABS.map((t) => {
                const getBadgeCount = () => {
                  switch (t.key) {
                    case 'bookings': return (badgeCounts.bookings || 0) + (badgeCounts.cancellations || 0);
                    case 'deliveries': return badgeCounts.deliveries;
                    case 'photographers': return badgeCounts.photographers;
                    case 'verification': return badgeCounts.verification;
                    case 'reviews': return badgeCounts.reviews;
                    case 'support': return badgeCounts.support;
                    case 'payments': return badgeCounts.payments;
                    default: return 0;
                  }
                };
                const badgeCount = getBadgeCount();
                return (
                  <button
                    key={t.key}
                    onClick={() => onTab(t.key)}
                    title={sidebarCollapsed ? t.label : undefined}
                    className={`w-full flex rounded-xl text-right transition-colors relative ${
                      sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'items-center justify-between px-3 py-2'
                    } ${active === t.key ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-700"}`}
                  >
                    <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
                      <Icon name={t.icon} size={18} color={active === t.key ? "white" : "#334155"} />
                      {!sidebarCollapsed && <span className="text-sm font-semibold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{t.label}</span>}
                    </div>
                    {!sidebarCollapsed && badgeCount > 0 && (
                      <span className={`min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full text-xs font-bold ${
                        active === t.key ? 'bg-white text-slate-900' : 'bg-red-500 text-white animate-pulse'
                      }`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                    {sidebarCollapsed && badgeCount > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" title={`${badgeCount}`} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── Quick links ── */}
            <div className="mt-2 border-t border-slate-100 pt-2 space-y-0.5">
              <button
                onClick={() => navigate('/admin-refunds')}
                title={sidebarCollapsed ? 'إدارة الاستردادات' : undefined}
                className={`w-full flex rounded-xl text-right transition-colors hover:bg-green-50 text-green-700 relative ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'items-center gap-3 px-3 py-2'}`}
              >
                <Icon name="DollarSign" size={18} className="text-green-600 shrink-0" />
                {!sidebarCollapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <span className="text-sm font-semibold" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>إدارة الاستردادات</span>
                    {badgeCounts.refunds > 0 && (
                      <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">{badgeCounts.refunds}</span>
                    )}
                  </div>
                )}
                {sidebarCollapsed && badgeCounts.refunds > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500" />
                )}
              </button>
              <button
                onClick={() => navigate('/admin-photographer-visibility')}
                title={sidebarCollapsed ? 'ظهور المصورين' : undefined}
                className={`w-full flex rounded-xl text-right transition-colors hover:bg-purple-50 text-purple-700 ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'items-center gap-3 px-3 py-2'}`}
              >
                <Icon name="Eye" size={18} className="text-purple-600 shrink-0" />
                {!sidebarCollapsed && (
                  <span className="text-sm font-semibold" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>ظهور المصورين</span>
                )}
              </button>
            </div>

            {!sidebarCollapsed && (
              <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} color="#334155" />
                  <div className="text-xs text-slate-600 leading-6" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    تم تعطيل تسجيل دخول المشرف مؤقتاً (BYPASS) لتجاوز مشكلة إعادة تعيين كلمة المرور.
                    يمكنك إعادة التفعيل لاحقاً عبر متغير البيئة <code className="px-1">VITE_ADMIN_BYPASS_AUTH</code>.
                  </div>
                </div>
              </div>
            )}
          </aside>

          {/* Content Area - Must be wrapped in a single container for grid */}
          <div className="min-w-0">
            {/* Notification Banner for Pending Verifications */}
            {showNotificationBanner && pendingVerificationsCount > 0 && active !== 'verification' && (
              <div className="mb-4 bg-gradient-to-l from-amber-500 to-orange-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Bell" size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-base" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        🔔 طلبات توثيق جديدة!
                      </h3>
                      <p className="text-white/90 text-sm">
                        يوجد <span className="font-bold">{pendingVerificationsCount}</span> مصور{pendingVerificationsCount > 1 ? 'ين' : ''} بانتظار المراجعة
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onTab('verification')}
                      className="px-3 py-1.5 bg-white text-orange-600 rounded-lg font-bold text-sm hover:bg-orange-50 transition-colors flex items-center gap-1.5"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    >
                      <Icon name="Eye" size={14} />
                      مراجعة الآن
                    </button>
                    <button
                      onClick={() => {
                        dismissAllNotifications();
                        setShowNotificationBanner(false);
                      }}
                      className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      title="تجاهل الكل"
                    >
                      <Icon name="X" size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                {/* Individual notification items showing photographer names */}
                {adminNotifications.filter(n => n.type === 'verification_request').length > 0 && (
                  <div className="mt-3 space-y-2 border-t border-white/20 pt-3">
                    {adminNotifications.filter(n => n.type === 'verification_request').slice(0, 5).map(notif => (
                      <div key={notif.id} className="flex items-center justify-between bg-white/15 rounded-xl px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon name="UserCheck" size={16} className="text-white flex-shrink-0" />
                          <p className="text-white text-sm truncate">
                            {notif.related_data?.photographer_name || notif.message || 'طلب توثيق'}
                          </p>
                          <span className="text-white/70 text-xs flex-shrink-0">
                            {notif.created_at && new Date(notif.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markNotificationAsRead(notif.id);
                          }}
                          className="p-1 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                          title="تم القراءة"
                        >
                          <Icon name="Check" size={14} className="text-white" />
                        </button>
                      </div>
                    ))}
                    {adminNotifications.filter(n => n.type === 'verification_request').length > 5 && (
                      <p className="text-white/80 text-xs text-center">
                        +{adminNotifications.filter(n => n.type === 'verification_request').length - 5} إشعارات أخرى
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <section className="space-y-6">
            {loading ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
                <p className="text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>جاري تحميل البيانات…</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="AlertTriangle" size={18} color="#b91c1c" />
                  <div className="font-bold text-red-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تعذر التحميل</div>
                </div>
                <div className="text-sm text-red-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{error}</div>
              </div>
            ) : null}

            {/* Overview */}
            

            {notice ? (
              <div className="bg-slate-900 text-white rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
                <div className="text-sm" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{notice}</div>
                <button onClick={() => setNotice("")} className="text-white/80 hover:text-white">
                  <Icon name="X" size={16} color="white" />
                </button>
              </div>
            ) : null}

{!loading && !error && active === "overview" ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <StatCard title="إجمالي الحجوزات" value={stats.totalBookings} icon="Calendar" hint="كل الحجوزات" tone="purple" />
                  <StatCard title="إجمالي الإيرادات" value={money(stats.totalRevenue)} icon="Wallet" hint="المدفوعات المكتملة" tone="green" />
                  <StatCard title="مصورين نشطين" value={stats.activePhotographers} icon="Camera" hint="role=photographer" tone="blue" />
                  <StatCard title="عملاء" value={stats.totalCustomers} icon="Users" hint="role=customer" tone="amber" />
                </div>

                {/* ── تنبيه الحجوزات المجمّدة ── */}
                {badgeCounts.stuckBookings > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between gap-4" dir="rtl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-xl flex-shrink-0">⚠️</div>
                      <div>
                        <p className="font-bold text-red-800 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                          {badgeCounts.stuckBookings} {badgeCounts.stuckBookings === 1 ? 'حجز يحتاج' : 'حجوزات تحتاج'} تدخلاً
                        </p>
                        <p className="text-xs text-red-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                          جلسات مضت دون توثيق — العميل دفع وينتظر
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onTab('bookings')}
                      className="flex-shrink-0 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-colors"
                      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                    >
                      معالجة الآن
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl border border-slate-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>إجراءات سريعة</h2>
                      <Icon name="Zap" size={18} color="#0f172a" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button onClick={() => onTab("bookings")} className="p-4 rounded-2xl border bg-slate-50 hover:bg-slate-100 text-right">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>مراجعة الحجوزات</div>
                            <div className="text-xs text-slate-600 mt-1">بحث + تصفية الحالة</div>
                          </div>
                          <Icon name="ArrowLeft" size={18} color="#334155" />
                        </div>
                      </button>
                      <Link to="/admin-bookings" className="p-4 rounded-2xl border bg-slate-50 hover:bg-slate-100 text-right">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>إدارة الحجوزات المتقدمة</div>
                            <div className="text-xs text-slate-600 mt-1">عرض شامل + تعديل الحالات</div>
                          </div>
                          <Icon name="ArrowLeft" size={18} color="#334155" />
                        </div>
                      </Link>
                      <button onClick={() => onTab("photographers")} className="p-4 rounded-2xl border bg-slate-50 hover:bg-slate-100 text-right">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>إدارة المصورين</div>
                            <div className="text-xs text-slate-600 mt-1">تعطيل/تفعيل (حسب الأعمدة)</div>
                          </div>
                          <Icon name="ArrowLeft" size={18} color="#334155" />
                        </div>
                      </button>
                      <Link to="/admin-pricing" className="p-4 rounded-2xl border bg-slate-50 hover:bg-slate-100 text-right">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>إدارة قواعد التسعير</div>
                            <div className="text-xs text-slate-600 mt-1">التحكم الكامل في التسعير الذكي</div>
                          </div>
                          <Icon name="ArrowLeft" size={18} color="#334155" />
                        </div>
                      </Link>
                      <button onClick={() => onTab("pricing")} className="p-4 rounded-2xl border bg-slate-50 hover:bg-slate-100 text-right">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>عرض قواعد التسعير</div>
                            <div className="text-xs text-slate-600 mt-1">عرض سريع للقواعد الحالية</div>
                          </div>
                          <Icon name="ArrowLeft" size={18} color="#334155" />
                        </div>
                      </button>
                      <Link to="/admin-analytics-dashboard" className="p-4 rounded-2xl border bg-slate-50 hover:bg-slate-100 text-right">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تحليلات متقدمة</div>
                            <div className="text-xs text-slate-600 mt-1">لوحات جاهزة داخل المشروع</div>
                          </div>
                          <Icon name="ArrowLeft" size={18} color="#334155" />
                        </div>
                      </Link>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-slate-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>آخر الحجوزات</h2>
                      <button onClick={() => onTab("bookings")} className="text-sm text-primary font-semibold hover:underline">عرض الكل</button>
                    </div>

                    {(bookings || []).length === 0 ? (
                      <EmptyState title="لا توجد حجوزات بعد" desc="عند بدء المستخدمين بالحجز ستظهر هنا أحدث الطلبات." />
                    ) : (
                      <div className="space-y-3">
                        {(bookings || []).slice(0, 6).map((b) => {
                          const finalPrice = b.final_price_sar || b.price || 0;
                          const bookingDate = b.booking_date || b.scheduled_at;
                          
                          return (
                          <div 
                            key={b.id} 
                            className="p-4 rounded-2xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => {
                              onTab("bookings");
                              // Scroll to bookings section
                              setTimeout(() => {
                                const element = document.querySelector('[data-booking-id="' + b.id + '"]');
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  element.classList.add('ring-2', 'ring-primary');
                                  setTimeout(() => element.classList.remove('ring-2', 'ring-primary'), 2000);
                                }
                              }, 100);
                            }}
                          >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-bold text-primary font-mono" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                      #{String(b.id).slice(0, 8).toUpperCase()}
                                    </span>
                                    {statusBadge(b.status)}
                                </div>
                                  <div className="text-sm font-semibold text-slate-900 mb-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                    {getPhotographyTypeLabelAr(b.photography_type) || "نوع غير محدد"}
                                </div>
                                  <div className="text-xs text-slate-600 space-y-0.5">
                                    <div className="flex items-center gap-2">
                                      <Icon name="MapPin" size={12} color="#64748b" />
                                      <span>{b.city || "—"}</span>
                              </div>
                                    {b.customer && (
                              <div className="flex items-center gap-2">
                                        <Icon name="User" size={12} color="#64748b" />
                                        <span>{b.customer.full_name || "عميل"}</span>
                              </div>
                                    )}
                                    {bookingDate && (
                                      <div className="flex items-center gap-2">
                                        <Icon name="Calendar" size={12} color="#64748b" />
                                        <span>{formatDate(bookingDate)}</span>
                                        {b.booking_time && <span>• {formatTime(b.booking_time)}</span>}
                            </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                      <Icon name="Clock" size={12} color="#64748b" />
                                      <span>{formatDateTime(b.created_at)}</span>
                          </div>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <Badge tone="purple" className="text-sm font-bold">
                                    {money(finalPrice)}
                                  </Badge>
                                  {b.photographer && (
                                    <div className="text-xs text-slate-600 text-right">
                                      <div className="font-semibold">المصور:</div>
                                      <div className="truncate max-w-[120px]">{b.photographer.full_name}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}

            {/* Bookings - Table View */}
            {!loading && !error && active === "bookings" ? (
              <>
                {/* ── Cancellations + Refunds shortcut ── */}
                {(badgeCounts.cancellations > 0 || badgeCounts.refunds > 0) && (
                  <button
                    onClick={() => navigate('/admin-refunds')}
                    className="w-full flex items-center gap-4 p-4 bg-gradient-to-l from-orange-50 to-amber-50 border border-orange-200 rounded-2xl hover:shadow-md transition-all text-right group"
                  >
                    <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-orange-200 transition-colors">
                      <Icon name="DollarSign" size={22} color="#ea580c" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                          طلبات معلقة بانتظار المعالجة
                        </p>
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                          {(badgeCounts.cancellations || 0) + (badgeCounts.refunds || 0)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {badgeCounts.cancellations > 0 && `${badgeCounts.cancellations} طلب إلغاء عميل`}
                        {badgeCounts.cancellations > 0 && badgeCounts.refunds > 0 && ' · '}
                        {badgeCounts.refunds > 0 && `${badgeCounts.refunds} استرداد انسحاب مصور`}
                        {' — انقر لفتح صفحة إدارة الاستردادات'}
                      </p>
                    </div>
                    <Icon name="ChevronLeft" size={20} color="#ea580c" className="shrink-0" />
                  </button>
                )}


                {/* ── حجوزات تحتاج تدخل (جلسات مضت بدون توثيق) ── */}
                {stuckBookings.length > 0 && (
                  <div className="bg-white rounded-2xl border border-red-200 overflow-hidden" dir="rtl">
                    <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">⚠️</span>
                        <p className="font-bold text-red-800 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                          حجوزات تحتاج تدخلاً — جلسات مضت دون توثيق
                        </p>
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{stuckBookings.length}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {stuckBookings.map(b => (
                        <div key={b.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-lg">#{String(b.id).slice(0,8).toUpperCase()}</span>
                              <span className="text-xs text-slate-600">{b.booking_date}</span>
                              <span className="text-xs text-slate-600">{b.city}</span>
                              <span className="text-xs text-slate-600">{b.photography_type}</span>
                            </div>
                            <div className="flex items-center gap-4 flex-wrap text-sm">
                              <span className="text-slate-700"><span className="text-slate-400 text-xs">العميل: </span><span className="font-semibold">{b.customer_name}</span></span>
                              <span className="text-slate-700"><span className="text-slate-400 text-xs">المصور: </span><span className="font-semibold">{b.photographer_name}</span></span>
                              <span className="text-slate-700"><span className="text-slate-400 text-xs">المبلغ: </span><span className="font-bold text-green-700">{b.amount} ر.س</span></span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              disabled={resolvingStuck === b.id}
                              onClick={async () => {
                                if (!window.confirm(`تأكيد: الجلسة ${b.id.slice(0,8).toUpperCase()} انعقدت فعلاً؟\nسيُنقل الحجز إلى مرحلة رفع الصور.`)) return;
                                setResolvingStuck(b.id);
                                try {
                                  await adminService.setBookingStatus(b.id, 'awaiting_delivery');
                                  setStuckBookings(prev => prev.filter(x => x.id !== b.id));
                                  setBadgeCounts(prev => ({ ...prev, stuckBookings: Math.max(0, prev.stuckBookings - 1) }));
                                  setNotice(`✅ الحجز #${b.id.slice(0,8).toUpperCase()} انتقل لمرحلة رفع الصور`);
                                } catch (e) {
                                  setNotice(e?.message || 'فشل تحديث الحالة');
                                } finally {
                                  setResolvingStuck(null);
                                }
                              }}
                              className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors whitespace-nowrap"
                              style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                            >
                              {resolvingStuck === b.id ? '...' : '✅ الجلسة انعقدت'}
                            </button>
                            <button
                              disabled={resolvingStuck === b.id}
                              onClick={async () => {
                                if (!window.confirm(`تأكيد: الجلسة ${b.id.slice(0,8).toUpperCase()} لم تنعقد؟\nسيُلغى الحجز ويُعاد المبلغ للعميل.`)) return;
                                setResolvingStuck(b.id);
                                try {
                                  await adminService.setBookingStatus(b.id, 'cancelled_by_admin');
                                  setStuckBookings(prev => prev.filter(x => x.id !== b.id));
                                  setBadgeCounts(prev => ({ ...prev, stuckBookings: Math.max(0, prev.stuckBookings - 1) }));
                                  setNotice(`❌ الحجز #${b.id.slice(0,8).toUpperCase()} أُلغي — تذكر معالجة الاسترداد`);
                                } catch (e) {
                                  setNotice(e?.message || 'فشل تحديث الحالة');
                                } finally {
                                  setResolvingStuck(null);
                                }
                              }}
                              className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors whitespace-nowrap"
                              style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                            >
                              {resolvingStuck === b.id ? '...' : '❌ لم تنعقد'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search & Filter Bar */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="relative">
                        <Icon name="Search" size={18} color="#64748b" className="absolute right-3 top-1/2 -translate-y-1/2" />
                        <input
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="ابحث برقم الحجز/المدينة/النوع…"
                          className="w-full md:w-80 pr-10 pl-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                        />
                      </div>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        <option value="all">كل الحالات</option>
                        <option value="pending_photographer_confirmation">في انتظار تأكيد المصور</option>
                        <option value="awaiting_payment">في انتظار الدفع</option>
                        <option value="paid_confirmed">مدفوع ومؤكد</option>
                        <option value="completed">مكتمل</option>
                        <option value="declined">مرفوض</option>
                        <option value="expired">منتهي</option>
                        <option value="cancellation_requested">إلغاء قيد المراجعة</option>
                        <option value="cancelled_by_customer">ملغي من العميل</option>
                        <option value="cancelled_by_photographer">ملغي من المصور</option>
                        <option value="cancelled_by_admin">ملغي من المشرف</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        إجمالي: <span className="font-bold text-slate-900">{filteredBookings.length}</span> حجز
                      </div>
                      <button
                        onClick={refreshData}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="تحديث"
                      >
                        <Icon name="RefreshCw" size={18} color="#64748b" />
                      </button>
                    </div>
                  </div>
                </div>

                {filteredBookings.length === 0 ? (
                  <EmptyState 
                    title={bookings.length === 0 ? "لا توجد حجوزات" : "لا توجد نتائج"} 
                    desc={bookings.length === 0 ? "لم يتم العثور على أي حجوزات في النظام." : "جرّب تغيير كلمات البحث أو تصفية الحالة."} 
                  />
                ) : (
                  <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>رقم الحجز</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الحالة</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>العميل</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>المصور</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>نوع التصوير</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>التاريخ والوقت</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الموقع</th>
                            <th className="px-4 py-3 text-right font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>السعر</th>
                            <th className="px-4 py-3 text-center font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الإجراءات</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredBookings.map((booking) => {
                            const finalPrice = booking.final_price_sar || booking.price || 0;
                            const bookingDate = booking.booking_date || booking.scheduled_at;
                            const locationDetails = getLocationDetails(booking);
                            const mapLink = locationDetails ? getMapLink(locationDetails.lat, locationDetails.lng) : null;
                            
                            return (
                              <tr
                                key={booking.id}
                                className="hover:bg-slate-50 transition-colors"
                                data-booking-id={booking.id}
                              >
                                {/* Booking ID */}
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => {
                                      setSelectedBooking(booking);
                                      setShowBookingModal(true);
                                    }}
                                    className="font-mono text-sm font-bold text-primary hover:underline"
                                  >
                                    #{String(booking.id).slice(0, 8).toUpperCase()}
                                  </button>
                                  <div className="text-xs text-slate-500 mt-1">
                                    {formatDateTime(booking.created_at)}
                                  </div>
                                </td>
                                
                                {/* Status */}
                                <td className="px-4 py-3">
                                  {statusBadge(booking.status)}
                                  {booking.attempt_count > 0 && (
                                    <div className="text-xs text-slate-500 mt-1">
                                      محاولة {booking.attempt_count}/3
                                    </div>
                                  )}
                                </td>
                                
                                {/* Customer */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                      {booking.customer?.avatar_url ? (
                                        <img src={booking.customer.avatar_url} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <Icon name="User" size={14} color="#64748b" />
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="font-semibold text-slate-900 truncate max-w-[120px]">
                                        {booking.customer?.full_name || "غير محدد"}
                                      </div>
                                      {booking.customer?.phone && (
                                        <div className="text-xs text-slate-500 truncate max-w-[120px]" dir="ltr">
                                          {booking.customer.phone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                
                                {/* Photographer */}
                                <td className="px-4 py-3">
                                  {booking.photographer ? (
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {booking.photographer?.avatar_url ? (
                                          <img src={booking.photographer.avatar_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                          <Icon name="Camera" size={14} color="#64748b" />
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="font-semibold text-slate-900 truncate max-w-[120px]">
                                          {booking.photographer.full_name || "غير محدد"}
                                        </div>
                                        {booking.photographer?.phone && (
                                          <div className="text-xs text-slate-500 truncate max-w-[120px]" dir="ltr">
                                            {booking.photographer.phone}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : booking.assigned_photographer_id ? (
                                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">في الانتظار</span>
                                  ) : (
                                    <span className="text-xs text-slate-400">لم يُعيّن</span>
                                  )}
                                </td>
                                
                                {/* Photography Type */}
                                <td className="px-4 py-3">
                                  <div className="font-medium text-slate-900">
                                    {getPhotographyTypeLabelAr(booking.photography_type) || "—"}
                                  </div>
                                  {booking.city && (
                                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                      <Icon name="MapPin" size={10} color="#94a3b8" />
                                      {booking.city}
                                    </div>
                                  )}
                                </td>
                                
                                {/* Date & Time */}
                                <td className="px-4 py-3">
                                  {bookingDate ? (
                                    <div>
                                      <div className="font-medium text-slate-900">
                                        {formatDate(bookingDate)}
                                      </div>
                                      {booking.booking_time && (
                                        <div className="text-xs text-slate-500 mt-1">
                                          {formatTime(booking.booking_time)}
                                        </div>
                                      )}
                                      {booking.duration && (
                                        <div className="text-xs text-slate-400 mt-1">
                                          {booking.duration}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>
                                
                                {/* Location */}
                                <td className="px-4 py-3">
                                  {locationDetails ? (
                                    <div className="max-w-[180px]">
                                      {locationDetails.address_text && (
                                        <div className="text-xs text-slate-700 truncate mb-1" title={locationDetails.address_text}>
                                          {locationDetails.address_text}
                                        </div>
                                      )}
                                      {locationDetails.district && (
                                        <div className="text-xs text-slate-500">
                                          {locationDetails.city || booking.city} - {locationDetails.district}
                                        </div>
                                      )}
                                      {mapLink && (
                                        <a
                                          href={mapLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                                        >
                                          <Icon name="ExternalLink" size={10} color="currentColor" />
                                          الخريطة
                                        </a>
                                      )}
                                    </div>
                                  ) : booking.location ? (
                                    <div className="text-xs text-slate-700 truncate max-w-[180px]" title={booking.location}>
                                      {booking.location}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>
                                
                                {/* Price */}
                                <td className="px-4 py-3">
                                  <div className="font-bold text-slate-900">
                                    {money(finalPrice)}
                                  </div>
                                  {(() => {
                                    const { commission } = getBookingCommissionBreakdown(booking, commissionSettings);
                                    if (!commission) return null;
                                    return (
                                      <div className="text-xs text-primary font-semibold mt-1">
                                        عمولة مشهد: {money(commission)}
                                      </div>
                                    );
                                  })()}
                                </td>
                                
                                {/* Actions */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-1 flex-wrap">
                                    {/* View Details */}
                                    <button
                                      onClick={() => {
                                        setSelectedBooking(booking);
                                        setShowBookingModal(true);
                                      }}
                                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                                      title="عرض التفاصيل"
                                    >
                                      <Icon name="Eye" size={16} color="#334155" />
                                    </button>

                                    {/* No-Show Refund Button */}
                                    {booking.no_show_reported_at &&
                                      booking.status === 'paid_confirmed' &&
                                      booking.payment_status !== 'refund_pending' &&
                                      booking.payment_status !== 'refunded' && (
                                      <button
                                        onClick={() => handleAdminNoShowRefund(booking)}
                                        disabled={!canMutate || mutating}
                                        className="flex items-center gap-1 px-2 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap"
                                        title="غياب المصور — إرسال للاسترداد"
                                        style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                                      >
                                        <Icon name="AlertTriangle" size={12} color="white" />
                                        غياب المصور-استرداد
                                      </button>
                                    )}
                                    
                                    {/* Quick Status Change Dropdown */}
                                    <div className="relative group">
                                      <button
                                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                                        title="تغيير الحالة"
                                        disabled={!canMutate || mutating}
                                      >
                                        <Icon name="MoreVertical" size={16} color="#334155" />
                                      </button>
                                      <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                                        <div className="p-2 space-y-1">
                                          {[
                                            { value: "pending_photographer_confirmation", label: "في انتظار تأكيد المصور", color: "amber" },
                                            { value: "awaiting_payment", label: "في انتظار الدفع", color: "amber" },
                                            { value: "paid_confirmed", label: "مدفوع ومؤكد", color: "blue" },
                                            { value: "session_started", label: "الجلسة جارية", color: "purple" },
                                            { value: "awaiting_delivery", label: "في انتظار رفع الملفات", color: "orange" },
                                            { value: "completed", label: "مكتمل", color: "green" },
                                            { value: "declined", label: "مرفوض", color: "red" },
                                            { value: "expired", label: "منتهي", color: "slate" },
                                            { value: "cancelled_by_admin", label: "إلغاء (مشرف)", color: "red" },
                                          ].map((status) => (
                                            <button
                                              key={status.value}
                                              disabled={!canMutate || mutating || booking.status === status.value}
                                              onClick={async () => {
                                                try {
                                                  setMutating(true);
                                                  await adminService.setBookingStatus(booking.id, status.value);
                                                  setBookings((prev) => (prev || []).map((b) => (b.id === booking.id ? { ...b, status: status.value } : b)));
                                                  setNotice(`تم تحديث حالة الحجز #${String(booking.id).slice(0, 8)}`);
                                                  refreshData();
                                                } catch (e) {
                                                  setNotice(e?.message || "تعذر تحديث حالة الحجز");
                                                } finally {
                                                  setMutating(false);
                                                }
                                              }}
                                              className={`w-full text-right px-3 py-2 rounded-lg text-xs transition-colors ${
                                                booking.status === status.value
                                                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                                  : "hover:bg-slate-50 text-slate-700"
                                              }`}
                                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                                            >
                                              {status.label}
                                              {booking.status === status.value && " ✓"}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Table Footer */}
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                      <div className="text-xs text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        عرض <span className="font-bold">{filteredBookings.length}</span> من <span className="font-bold">{bookings.length}</span> حجز
                      </div>
                      <div className="text-xs text-slate-500">
                        آخر تحديث: {formatDateTime(new Date().toISOString())}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : null}

            {/* Deliveries */}
            {!loading && !error && active === "deliveries" ? (() => {
              // فلترة التسليمات
              const filteredDeliveries = deliveries.filter(d => {
                const q = deliverySearch.trim().toLowerCase();
                const matchSearch = !q || (
                  d.photographer?.full_name?.toLowerCase().includes(q) ||
                  d.customer?.full_name?.toLowerCase().includes(q) ||
                  d.booking?.city?.toLowerCase().includes(q) ||
                  d.booking_id?.toLowerCase().includes(q)
                );
                const matchStatus = deliveryStatusFilter === 'all' || d.status === deliveryStatusFilter;
                return matchSearch && matchStatus;
              });

              // إحصائيات
              const totalCount    = deliveries.length;
              const deliveredCount  = deliveries.filter(d => d.status === 'delivered' || d.status === 'downloaded').length;
              const downloadedCount = deliveries.filter(d => d.status === 'downloaded').length;
              const reviewedCount   = deliveries.filter(d => d.review).length;
              const disputeCount    = deliveries.filter(d => d.dispute).length;

              const formatFileSize = (bytes) => {
                if (!bytes) return '-';
                const k = 1024, s = ['B','KB','MB','GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
              };

              return (
                <>
                  {/* ── إحصائيات سريعة ── */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    {[
                      { label: 'إجمالي التسليمات', value: totalCount, icon: 'Package', color: 'text-primary', bg: 'bg-primary/10' },
                      { label: 'تم التسليم', value: deliveredCount, icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                      { label: 'تم التحميل', value: downloadedCount, icon: 'Download', color: 'text-blue-600', bg: 'bg-blue-50' },
                      { label: 'تم التقييم', value: reviewedCount, icon: 'Star', color: 'text-amber-600', bg: 'bg-amber-50' },
                      { label: 'نزاعات مفتوحة', value: disputeCount, icon: 'AlertTriangle', color: 'text-red-600', bg: 'bg-red-50' },
                    ].map(s => (
                      <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon name={s.icon} size={18} className={s.color} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>{s.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ── خطة الطوارئ ── */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-5 mb-5">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="ShieldAlert" size={20} className="text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-amber-800 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>تسليم طارئ من المشرف</p>
                        <p className="text-xs text-amber-700 mt-0.5" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>في حال تعذّر المصور عن التسليم، يمكنك رفع الملفات مباشرة للعميل.</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setAdminDeliveryModal({ open: true, mode: 'select_booking' })}
                          className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 text-xs font-medium">
                          <Icon name="Upload" size={14} />رفع ملفات لحجز
                        </button>
                        <button onClick={() => setAdminDeliveryModal({ open: true, mode: 'pending_list' })}
                          className="px-3 py-2 bg-white text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors flex items-center gap-2 text-xs font-medium">
                          <Icon name="Clock" size={14} />بانتظار التسليم
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── بحث وفلتر ── */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                      <Icon name="Search" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={deliverySearch}
                        onChange={e => setDeliverySearch(e.target.value)}
                        placeholder="بحث بالاسم أو المدينة أو رقم الحجز..."
                        className="w-full pr-9 pl-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                        style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                      />
                    </div>
                    <select
                      value={deliveryStatusFilter}
                      onChange={e => setDeliveryStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-primary bg-white"
                      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                    >
                      <option value="all">كل الحالات</option>
                      <option value="uploading">جاري الرفع</option>
                      <option value="delivered">تم التسليم</option>
                      <option value="downloaded">تم التحميل</option>
                    </select>
                    <span className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                      {filteredDeliveries.length} من {totalCount}
                    </span>
                  </div>

                  {/* ── جدول التسليمات ── */}
                  {filteredDeliveries.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                      <Icon name="PackageOpen" size={48} className="mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                        {deliveries.length === 0 ? 'لا توجد تسليمات بعد' : 'لا توجد نتائج'}
                      </h3>
                      <p className="text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                        {deliveries.length === 0
                          ? 'ستظهر التسليمات هنا عندما يرفع المصورون ملفاتهم'
                          : 'جرّب تغيير الفلتر أو مصطلح البحث'}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>الحجز</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>المصور</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>العميل</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>الملفات</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>الحالة</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>التقييم</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>نزاع</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>تاريخ التسليم</th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>إجراء</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {filteredDeliveries.map((delivery) => (
                              <tr key={delivery.id} className="hover:bg-slate-50/70 transition-colors">
                                {/* الحجز */}
                                <td className="px-4 py-3">
                                  <div className="font-medium text-slate-900 text-xs">{getPhotographyTypeLabelAr(delivery.booking?.photography_type) || '-'}</div>
                                  <div className="text-[11px] text-slate-400 mt-0.5">{delivery.booking?.city || '-'}</div>
                                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono bg-slate-100 px-1.5 py-0.5 rounded w-fit">#{delivery.booking_id?.slice(0,8)}</div>
                                </td>
                                {/* المصور */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                      {delivery.photographer?.avatar_url
                                        ? <img src={delivery.photographer.avatar_url} alt="" className="w-full h-full object-cover" />
                                        : <Icon name="Camera" size={13} className="text-slate-400" />}
                                    </div>
                                    <span className="text-slate-800 text-xs font-medium">{delivery.photographer?.full_name || '-'}</span>
                                  </div>
                                </td>
                                {/* العميل */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                      {delivery.customer?.avatar_url
                                        ? <img src={delivery.customer.avatar_url} alt="" className="w-full h-full object-cover" />
                                        : <Icon name="User" size={13} className="text-slate-400" />}
                                    </div>
                                    <span className="text-slate-800 text-xs font-medium">{delivery.customer?.full_name || '-'}</span>
                                  </div>
                                </td>
                                {/* الملفات */}
                                <td className="px-4 py-3">
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-2 text-[11px]">
                                      <span className="flex items-center gap-1 text-indigo-600 font-medium">
                                        <Icon name="Image" size={12} />{delivery.total_photos || 0} صورة
                                      </span>
                                      <span className="flex items-center gap-1 text-violet-600 font-medium">
                                        <Icon name="Film" size={12} />{delivery.total_videos || 0} فيديو
                                      </span>
                                    </div>
                                    {delivery.total_size_bytes > 0 && (
                                      <span className="text-[10px] text-slate-400">{formatFileSize(delivery.total_size_bytes)}</span>
                                    )}
                                  </div>
                                </td>
                                {/* الحالة */}
                                <td className="px-4 py-3">
                                  {delivery.status === 'downloaded'
                                    ? <Badge tone="blue">تم التحميل ✓</Badge>
                                    : delivery.status === 'delivered'
                                    ? <Badge tone="green">تم التسليم</Badge>
                                    : delivery.status === 'uploading'
                                    ? <Badge tone="amber">جاري الرفع</Badge>
                                    : <Badge tone="slate">في الانتظار</Badge>}
                                </td>
                                {/* التقييم */}
                                <td className="px-4 py-3">
                                  {delivery.review ? (
                                    <div className="flex items-center gap-1">
                                      {[1,2,3,4,5].map(s => (
                                        <Icon key={s} name="Star" size={12}
                                          className={s <= delivery.review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
                                      ))}
                                      <span className="text-[11px] text-amber-600 font-bold mr-0.5">{delivery.review.rating}</span>
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-slate-300">—</span>
                                  )}
                                </td>
                                {/* نزاع */}
                                <td className="px-4 py-3">
                                  {delivery.dispute ? (
                                    <Badge tone="red">
                                      #{delivery.dispute.ticket_number}
                                    </Badge>
                                  ) : (
                                    <span className="text-[11px] text-slate-300">—</span>
                                  )}
                                </td>
                                {/* تاريخ التسليم */}
                                <td className="px-4 py-3 text-[11px] text-slate-500">
                                  {delivery.delivered_at ? formatDateTime(delivery.delivered_at) : '-'}
                                </td>
                                {/* إجراء */}
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <button
                                      onClick={() => handleViewDeliveryFiles(delivery)}
                                      disabled={!delivery.total_photos && !delivery.total_videos}
                                      title="معاينة الملفات"
                                      className="flex items-center gap-1 px-2.5 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-xs font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <Icon name="Eye" size={13} />معاينة
                                    </button>
                                    {delivery.status !== 'downloaded' && (
                                      <button
                                        onClick={() => setAdminDeliveryModal({
                                          open: true, mode: 'upload',
                                          bookingId: delivery.booking_id,
                                          _deliveryMeta: {
                                            customerId: delivery.customer_id,
                                            photographerName: delivery.photographer?.full_name,
                                            customerName: delivery.customer?.full_name,
                                            bookingType: delivery.booking?.photography_type,
                                          },
                                        })}
                                        title="رفع ملفات بدلاً من المصور"
                                        className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-xs font-medium transition-colors"
                                      >
                                        <Icon name="Upload" size={13} />طارئ
                                      </button>
                                    )}
                                    {delivery.dispute && (
                                      <button
                                        onClick={() => onTab('support')}
                                        title="عرض النزاع في الدعم"
                                        className="flex items-center gap-1 px-2.5 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium transition-colors"
                                      >
                                        <Icon name="AlertCircle" size={13} />نزاع
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              );
            })() : null}

            {/* Photographers */}
            {!loading && !error && active === "photographers" ? (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>المصورون</h2>
                      <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        إجمالي المصورين: <span className="font-bold">{photographers.length}</span>
                        {(photographerFilterSearch || photographerFilterStatus !== 'all') && (
                          <> · النتائج: <span className="font-bold">{filteredPhotographers.length}</span></>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        resetPhotographerWizard();
                        setShowAddPhotographerModal(true);
                      }}
                      className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 text-sm font-bold flex items-center gap-2"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    >
                      <Icon name="Plus" size={18} color="white" />
                      <span>إضافة مصور جديد</span>
                    </button>
                  </div>
                </div>

                {photographers.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3">
                    <span className="text-sm font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>فلتر:</span>
                    <input
                      type="text"
                      value={photographerFilterSearch}
                      onChange={(e) => setPhotographerFilterSearch(e.target.value)}
                      placeholder="الاسم، الهاتف، البريد، أو المدينة..."
                      className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
                      dir="auto"
                    />
                    <select
                      value={photographerFilterStatus}
                      onChange={(e) => setPhotographerFilterStatus(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium"
                    >
                      <option value="all">جميع الحالات</option>
                      <option value="approved">موافق عليهم</option>
                      <option value="pending">قيد المراجعة</option>
                      <option value="suspended">موقوفون</option>
                      <option value="inactive">حسابات معطّلة</option>
                      <option value="unavailable">غير متاحين للحجز</option>
                    </select>
                    {(photographerFilterSearch || photographerFilterStatus !== 'all') && (
                      <button
                        type="button"
                        onClick={() => { setPhotographerFilterSearch(''); setPhotographerFilterStatus('all'); }}
                        className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm"
                      >
                        إلغاء الفلتر
                      </button>
                    )}
                  </div>
                )}

                {photographers.length === 0 ? (
                  <EmptyState title="لا يوجد مصورون" desc="عند تسجيل المصورين سيظهرون هنا." />
                ) : filteredPhotographers.length === 0 ? (
                  <EmptyState title="لا توجد نتائج" desc="جرّب تغيير معايير البحث أو الفلتر." />
                ) : (
                  <Table
                  rowKey="id"
                  columns={[
                    {
                      key: "full_name",
                      title: "المصور",
                      render: (r) => (
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                            {r.avatar_url ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={r.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Icon name="User" size={18} color="#64748b" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 truncate" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{r.full_name || "—"}</div>
                            <div className="text-xs text-slate-600 truncate">{r.phone || r.email || "—"}</div>
                          </div>
                        </div>
                      ),
                    },
                    { key: "city", title: "المدينة", render: (r) => r.city || "—" },
                    { key: "created_at", title: "تاريخ التسجيل", render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { day: "numeric", month: "long", year: "numeric" }) : "—") },
                    {
                      key: "status",
                      title: "حالة الموافقة",
                      render: (r) => {
                        const status = r.status || "pending";
                        if (status === "approved") return <Badge tone="green">موافق عليه</Badge>;
                        if (status === "suspended") return <Badge tone="red">موقوف</Badge>;
                        return <Badge tone="amber">قيد المراجعة</Badge>;
                      },
                    },
                    {
                      key: "is_active",
                      title: "الحالة",
                      render: (r) => {
                        if (r.deleted_by_user) {
                          return <Badge tone="red">محذوف ذاتيًا</Badge>;
                        }
                        return r.is_active === false ? <Badge tone="amber">موقوف</Badge> : <Badge tone="green">نشط</Badge>;
                      },
                    },
                    {
                      key: "availability",
                      title: "القبول للحجز",
                      render: (r) =>
                        r.available_for_booking === false ? <Badge tone="amber">غير متاح</Badge> : <Badge tone="blue">متاح</Badge>,
                    },
                    {
                      key: "actions",
                      title: "إجراءات",
                      render: (r) => {
                        const isUpdating = updatingPhotographer === r.id;
                        const currentStatus = r.status || "pending";
                        const isActive = r.is_active !== false;
                        const isAvailable = r.available_for_booking !== false;
                        
                        return (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* View Details */}
                            <button
                              onClick={() => {
                                setSelectedUser(r);
                                setShowUserModal(true);
                              }}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5"
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              title="عرض تفاصيل المصور"
                            >
                              <Icon name="Eye" size={14} color="white" />
                              تفاصيل
                            </button>
                            
                            {/* Approve Button - Only show if not approved */}
                            {currentStatus !== "approved" && (
                              <button
                                disabled={isUpdating || mutating}
                                onClick={async () => {
                                  try {
                                    setUpdatingPhotographer(r.id);
                                    const result = await managePhotographer(r.id, "approve", null);
                                    // تحديث الحالة المحلية: توثيق الحساب + الموافقة + تفعيل الحجز
                                    setPhotographers((prev) => (prev || []).map((u) => 
                                      u.id === r.id 
                                        ? { 
                                            ...u, 
                                            status: "approved", 
                                            is_active: true,
                                            available_for_booking: true 
                                          } 
                                        : u
                                    ));
                                    setNotice(`✅ تم توثيق حساب المصور والموافقة عليه: ${r.full_name || String(r.id).slice(0, 8)}`);
                                    refreshData();
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || "تعذر الموافقة على المصور"}`);
                                  } finally {
                                    setUpdatingPhotographer(null);
                                  }
                                }}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${
                                  (isUpdating || mutating)
                                    ? "bg-slate-200 text-slate-500 cursor-not-allowed" 
                                    : "bg-green-600 text-white hover:bg-green-700"
                                }`}
                                style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                                title="الموافقة على المصور"
                              >
                                {(isUpdating || mutating) ? (
                                  <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                                ) : (
                                  <Icon name="CheckCircle" size={14} color="white" />
                                )}
                                {(isUpdating || mutating) ? "جاري..." : "موافقة"}
                              </button>
                            )}

                            {/* Reject/Suspend Button - Only show if approved */}
                            {currentStatus === "approved" && (
                              <button
                                disabled={isUpdating || mutating}
                                onClick={async () => {
                                  try {
                                    setUpdatingPhotographer(r.id);
                                    await managePhotographer(r.id, "suspend", null);
                                    setPhotographers((prev) => (prev || []).map((u) => 
                                      u.id === r.id 
                                        ? { ...u, status: "suspended", available_for_booking: false } 
                                        : u
                                    ));
                                    setNotice(`⚠️ تم إيقاف المصور: ${r.full_name || String(r.id).slice(0, 8)}`);
                                    refreshData();
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || "تعذر إيقاف المصور"}`);
                                  } finally {
                                    setUpdatingPhotographer(null);
                                  }
                                }}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${
                                  (isUpdating || mutating)
                                    ? "bg-slate-200 text-slate-500 cursor-not-allowed" 
                                    : "bg-red-600 text-white hover:bg-red-700"
                                }`}
                                style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                                title="إيقاف المصور"
                              >
                                {(isUpdating || mutating) ? (
                                  <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                                ) : (
                                  <Icon name="XCircle" size={14} color="white" />
                                )}
                                {(isUpdating || mutating) ? "جاري..." : "إيقاف"}
                              </button>
                            )}

                            {/* Lift withdrawal suspension */}
                            {r.withdrawal_suspended_until && new Date(r.withdrawal_suspended_until) > new Date() && (
                              <button
                                disabled={isUpdating || mutating}
                                onClick={async () => {
                                  if (!window.confirm('رفع تعليق الانسحاب وإعادة تمكين المصور من قبول الحجوزات؟')) return;
                                  try {
                                    setUpdatingPhotographer(r.id);
                                    await supabase
                                      .from('photographer_profiles')
                                      .update({ withdrawal_suspended_until: null })
                                      .eq('user_id', r.user_id);
                                    setPhotographers(prev => (prev || []).map(u =>
                                      u.id === r.id ? { ...u, withdrawal_suspended_until: null } : u
                                    ));
                                    setNotice(`✅ تم رفع التعليق عن المصور: ${r.full_name || String(r.id).slice(0, 8)}`);
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || 'فشل رفع التعليق'}`);
                                  } finally {
                                    setUpdatingPhotographer(null);
                                  }
                                }}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1.5 disabled:opacity-50"
                                style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                                title="رفع التعليق يدوياً"
                              >
                                <Icon name="ShieldCheck" size={14} color="white" />
                                رفع التعليق
                              </button>
                            )}

                            {/* Toggle Active Status */}
                            <button
                              disabled={isUpdating || mutating}
                              onClick={async () => {
                                try {
                                  setUpdatingPhotographer(r.id);
                                  await managePhotographer(r.id, "toggle_active", !isActive);
                                  setPhotographers((prev) => (prev || []).map((u) => 
                                    u.id === r.id ? { ...u, is_active: !isActive } : u
                                  ));
                                  setNotice(`${!isActive ? "✅ تم تفعيل" : "⚠️ تم إيقاف"} حساب المصور: ${r.full_name || String(r.id).slice(0, 8)}`);
                                  refreshData();
                                } catch (e) {
                                  setNotice(`❌ ${e?.message || "تعذر تحديث حالة المصور"}`);
                                } finally {
                                  setUpdatingPhotographer(null);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${
                                (isUpdating || mutating)
                                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                  : isActive
                                    ? "bg-red-600 text-white hover:bg-red-700"
                                    : "bg-green-600 text-white hover:bg-green-700"
                              }`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              title={isActive ? "إيقاف حساب المصور" : "تفعيل حساب المصور"}
                            >
                              {(isUpdating || mutating) ? (
                                <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                              ) : (
                                isActive ? <Icon name="UserX" size={14} color="white" /> : <Icon name="UserCheck" size={14} color="white" />
                              )}
                              {(isUpdating || mutating) ? "جاري..." : (isActive ? "إيقاف" : "تفعيل")}
                            </button>

                            {/* Toggle Booking Availability - Only show if approved */}
                            {currentStatus === "approved" && (
                              <button
                                disabled={isUpdating || mutating}
                                onClick={async () => {
                                  try {
                                    setUpdatingPhotographer(r.id);
                                    await managePhotographer(r.id, "toggle_availability", !isAvailable);
                                    setPhotographers((prev) => (prev || []).map((u) => 
                                      u.id === r.id ? { ...u, available_for_booking: !isAvailable } : u
                                    ));
                                    setNotice(`${!isAvailable ? "✅ تم جعل المصور متاح" : "⚠️ تم جعل المصور غير متاح"} للحجز`);
                                    refreshData();
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || "تعذر تحديث إتاحة المصور"}`);
                                  } finally {
                                    setUpdatingPhotographer(null);
                                  }
                                }}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${
                                  (isUpdating || mutating)
                                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                    : isAvailable
                                      ? "bg-amber-600 text-white hover:bg-amber-700"
                                      : "bg-slate-900 text-white hover:bg-slate-800"
                                }`}
                                style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                                title={isAvailable ? "منع المصور من قبول الحجوزات" : "السماح للمصور بقبول الحجوزات"}
                              >
                                {(isUpdating || mutating) ? (
                                  <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                                ) : (
                                  isAvailable ? <Icon name="CalendarX" size={14} color="white" /> : <Icon name="CalendarCheck" size={14} color="white" />
                                )}
                                {(isUpdating || mutating) ? "جاري..." : (isAvailable ? "منع الحجز" : "السماح")}
                              </button>
                            )}

                            {/* Delete Photographer - Permanent removal */}
                            <button
                              disabled={isUpdating || mutating}
                              onClick={async () => {
                                if (isUpdating || mutating) return;
                                const confirmed = window.confirm(
                                  `هل أنت متأكد من إزالة هذا المصور نهائياً من المنصة؟\n` +
                                  `سيتم حذف حسابه وجميع بياناته المرتبطة.`
                                );
                                if (!confirmed) return;

                                try {
                                  setUpdatingPhotographer(r.id);
                                  await managePhotographer(r.id, "delete", null);
                                  // إزالة المصور من القائمة محلياً
                                  setPhotographers((prev) => (prev || []).filter((u) => u.id !== r.id));
                                  setNotice(`🗑 تم إزالة المصور نهائياً: ${r.full_name || String(r.id).slice(0, 8)}`);
                                  refreshData();
                                } catch (e) {
                                  setNotice(`❌ ${e?.message || "تعذر إزالة المصور"}`);
                                } finally {
                                  setUpdatingPhotographer(null);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border ${
                                (isUpdating || mutating)
                                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                  : "bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                              }`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              title="إزالة المصور نهائياً من المنصة"
                            >
                              {(isUpdating || mutating) ? (
                                <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                              ) : (
                                <Icon name="Trash2" size={14} color="currentColor" />
                              )}
                              {(isUpdating || mutating) ? "جاري..." : "إزالة"}
                            </button>
                          </div>
                        );
                      },
                    },
                  ]}
                  rows={filteredPhotographers}
                />
                )}
              </>
            ) : null}

            {/* Verification Requests */}
            {!loading && !error && active === "verification" ? (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="ShieldCheck" size={20} className="inline ml-2 text-primary" />
                        التحقق من المصورين
                      </h2>
                      <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        مراجعة وتوثيق حسابات المصورين الجدد
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone="amber">{verificationRequests.filter(v => v.status === 'pending' || v.verification_status === 'pending').length} في الانتظار</Badge>
                      <Badge tone="green">{verificationRequests.filter(v => v.status === 'approved').length} موافق عليه</Badge>
                      <button
                        onClick={loadVerificationRequests}
                        disabled={verificationLoading}
                        className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold flex items-center gap-2"
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        <Icon name={verificationLoading ? "Loader" : "RefreshCw"} size={16} className={verificationLoading ? "animate-spin" : ""} />
                        تحديث
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recent Verification Notifications */}
                {adminNotifications.filter(n => n.type === 'verification_request').length > 0 && (
                  <div className="bg-gradient-to-l from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-blue-900 flex items-center gap-2 text-sm" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="BellRing" size={18} className="text-blue-600" />
                        إشعارات توثيق جديدة ({adminNotifications.filter(n => n.type === 'verification_request').length})
                      </h3>
                      <button
                        onClick={() => {
                          const verificationNotifs = adminNotifications.filter(n => n.type === 'verification_request');
                          verificationNotifs.forEach(n => markNotificationAsRead(n.id));
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1"
                      >
                        <Icon name="CheckCheck" size={14} />
                        تجاهل الكل
                      </button>
                    </div>
                    <div className="space-y-2">
                      {adminNotifications.filter(n => n.type === 'verification_request').map(notif => (
                        <div key={notif.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Icon name="UserCheck" size={18} className="text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-slate-900 truncate" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                {notif.related_data?.photographer_name || 'مصور'}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-slate-400">
                              {notif.created_at && new Date(notif.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <button
                              onClick={() => markNotificationAsRead(notif.id)}
                              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                              title="تم القراءة"
                            >
                              <Icon name="Check" size={14} className="text-green-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Verification Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-700">قيد المراجعة</p>
                        <p className="text-2xl font-bold text-amber-800">{verificationRequests.filter(v => v.status === 'pending' || !v.status).length}</p>
                      </div>
                      <Icon name="Clock" size={24} className="text-amber-500" />
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-700">بانتظار المستندات</p>
                        <p className="text-2xl font-bold text-blue-800">{verificationRequests.filter(v => v.verification_status === 'submitted').length}</p>
                      </div>
                      <Icon name="FileText" size={24} className="text-blue-500" />
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-700">موافق عليهم</p>
                        <p className="text-2xl font-bold text-green-800">{verificationRequests.filter(v => v.status === 'approved').length}</p>
                      </div>
                      <Icon name="CheckCircle" size={24} className="text-green-500" />
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-red-700">مرفوضين</p>
                        <p className="text-2xl font-bold text-red-800">{verificationRequests.filter(v => v.verification_status === 'rejected').length}</p>
                      </div>
                      <Icon name="XCircle" size={24} className="text-red-500" />
                    </div>
                  </div>
                </div>
                {/* شريط تحتاج مراجعة */}
                {(() => {
                  const pendingPortfolioTotal = verificationRequests.reduce((acc, v) => acc + (v.portfolioImages?.filter(i => i.approval_status === 'pending').length || 0), 0);
                  const profileUpdatedCount = verificationRequests.filter(v => {
                    return v.updated_at && v.verification_reviewed_at
                      ? new Date(v.updated_at) > new Date(v.verification_reviewed_at)
                      : v.updated_at && v.status === 'approved'
                        ? new Date(v.updated_at) > new Date(v.created_at)
                        : false;
                  }).length;
                  const total = (pendingPortfolioTotal > 0 ? 1 : 0) + profileUpdatedCount;
                  if (total === 0) return null;
                  return (
                    <div
                      className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mb-4 cursor-pointer hover:bg-amber-100 transition-colors"
                      onClick={() => setVerificationFilterStatus('needs_review')}
                    >
                      <Icon name="AlertTriangle" size={18} className="text-amber-600 shrink-0" />
                      <div className="flex-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <span className="text-sm font-bold text-amber-800">
                          {profileUpdatedCount > 0 && `${profileUpdatedCount} مصور عدّل ملفه`}
                          {profileUpdatedCount > 0 && pendingPortfolioTotal > 0 && ' · '}
                          {pendingPortfolioTotal > 0 && `${pendingPortfolioTotal} صورة معرض بانتظار الموافقة`}
                        </span>
                      </div>
                      <span className="text-xs text-amber-600 font-medium">اضغط للفلترة ←</span>
                    </div>
                  );
                })()}

                {verificationLoading ? (
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                    <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
                    <p className="text-slate-600">جاري تحميل طلبات التحقق...</p>
                  </div>
                ) : verificationRequests.length === 0 ? (
                  <EmptyState title="لا توجد طلبات تحقق" desc="عند تسجيل مصورين جدد ستظهر طلباتهم هنا." />
                ) : (
                  <>
                    {/* فلتر التحقق: بحث + حالة */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3">
                      <span className="text-sm font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>فلتر:</span>
                      <input
                        type="text"
                        value={verificationFilterSearch}
                        onChange={(e) => setVerificationFilterSearch(e.target.value)}
                        placeholder="رقم المصور، الاسم، أو البريد..."
                        className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
                        dir="auto"
                      />
                      <select
                        value={verificationFilterStatus}
                        onChange={(e) => setVerificationFilterStatus(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium"
                      >
                        <option value="all">جميع الحالات</option>
                        <option value="needs_review">⚠️ تحتاج مراجعة</option>
                        <option value="pending">قيد المراجعة</option>
                        <option value="submitted">بانتظار المستندات</option>
                        <option value="approved">موافق عليهم</option>
                        <option value="rejected">مرفوضين</option>
                        <optgroup label="مستند الهوية">
                          <option value="identity_pending">🪪 هوية — قيد المراجعة</option>
                          <option value="identity_expiring_soon">🟡 تنتهي خلال 8–30 يوم</option>
                          <option value="identity_critical">🟠 تنتهي خلال 7 أيام</option>
                          <option value="identity_expired">🔴 هويات منتهية</option>
                          <option value="identity_valid">🟢 هويات سارية</option>
                          <option value="identity_no_expiry">⚪ موثّقة بدون تاريخ</option>
                        </optgroup>
                        <optgroup label="وثيقة العمل الحر">
                          <option value="freelance_pending">📋 وثيقة عمل حر — قيد المراجعة</option>
                          <option value="freelance_expiring_soon">🟡 تنتهي خلال 8–30 يوم</option>
                          <option value="freelance_critical">🟠 تنتهي خلال 7 أيام</option>
                          <option value="freelance_expired">🔴 وثائق منتهية</option>
                          <option value="freelance_valid">🟢 وثائق سارية</option>
                          <option value="freelance_no_expiry">⚪ مقبولة بدون تاريخ</option>
                        </optgroup>
                        <optgroup label="تصريح الدرون">
                          <option value="drone_pending">🛸 تصريح درون — قيد المراجعة</option>
                          <option value="drone_expiring_soon">🟡 ينتهي خلال 8–30 يوم</option>
                          <option value="drone_critical">🟠 ينتهي خلال 7 أيام</option>
                          <option value="drone_expired">🔴 تصاريح منتهية</option>
                          <option value="drone_valid">🟢 تصاريح سارية</option>
                          <option value="drone_no_expiry">⚪ مقبولة بدون تاريخ</option>
                        </optgroup>
                      </select>
                      {(verificationFilterSearch || verificationFilterStatus !== 'all') && (
                        <button
                          type="button"
                          onClick={() => { setVerificationFilterSearch(''); setVerificationFilterStatus('all'); }}
                          className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm"
                        >
                          إلغاء الفلتر
                        </button>
                      )}
                      <span className="text-xs text-slate-500">
                        النتائج: {filteredVerificationRequests.length} من {verificationRequests.length}
                      </span>
                    </div>
                  <div className="space-y-4">
                    {filteredVerificationRequests.map((photographer) => {
                      const pendingPortfolioCount = photographer.portfolioImages?.filter(i => i.approval_status === 'pending').length || 0;
                      const hasProfileUpdate = photographer.updated_at && photographer.verification_reviewed_at
                        ? new Date(photographer.updated_at) > new Date(photographer.verification_reviewed_at)
                        : photographer.updated_at && photographer.status === 'approved'
                          ? new Date(photographer.updated_at) > new Date(photographer.created_at)
                          : false;
                      const needsAttention = pendingPortfolioCount > 0 || hasProfileUpdate;
                      return (
                      <div key={photographer.user_id} className={`bg-white rounded-2xl border p-6 hover:shadow-md transition-shadow ${needsAttention ? 'border-amber-300 shadow-amber-50 shadow-sm' : 'border-slate-100'}`}>
                        {/* شريط تحذير عند وجود بنود تحتاج مراجعة */}
                        {needsAttention && (
                          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                            <Icon name="AlertTriangle" size={15} className="text-amber-600 shrink-0" />
                            <div className="flex flex-wrap gap-2 text-xs font-medium text-amber-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                              {pendingPortfolioCount > 0 && (
                                <span>{pendingPortfolioCount} صورة معرض بانتظار المراجعة</span>
                              )}
                              {pendingPortfolioCount > 0 && hasProfileUpdate && <span className="text-amber-400">·</span>}
                              {hasProfileUpdate && (
                                <span>
                                  عدّل ملفه مؤخراً
                                  {photographer.updated_at && (
                                    <span className="text-amber-600 mr-1">
                                      ({new Date(photographer.updated_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short' })})
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-4">
                          {/* Avatar */}
                          <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                            {photographer.user?.avatar_url ? (
                              <img src={photographer.user.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Icon name="User" size={28} color="#64748b" />
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                #{photographer.display_id != null || photographer.displayId != null ? String(Number(photographer.display_id ?? photographer.displayId)).padStart(6, '0') : '—'}
                              </span>
                              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                {photographer.user?.full_name || "مصور جديد"}
                              </h3>
                              {/* Status Badge */}
                              {photographer.status === 'approved' ? (
                                <Badge tone="green">
                                  <Icon name="CheckCircle" size={12} className="ml-1" />
                                  موافق عليه
                                </Badge>
                              ) : photographer.verification_status === 'rejected' ? (
                                <Badge tone="red">
                                  <Icon name="XCircle" size={12} className="ml-1" />
                                  مرفوض
                                </Badge>
                              ) : (
                                <Badge tone="amber">
                                  <Icon name="Clock" size={12} className="ml-1" />
                                  قيد المراجعة
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-slate-600 mb-3">
                              <div className="flex items-center gap-1">
                                <Icon name="Mail" size={14} className="text-slate-400" />
                                <span className="truncate">{photographer.user?.email || "—"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Icon name="Phone" size={14} className="text-slate-400" />
                                <span>{photographer.user?.phone || "—"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Icon name="MapPin" size={14} className="text-slate-400" />
                                <span>{photographer.city || "—"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Icon name="Calendar" size={14} className="text-slate-400" />
                                <span>{photographer.created_at ? new Date(photographer.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' }) : "—"}</span>
                              </div>
                            </div>
                            
                            {/* حالة التحقق — شارات عرض فقط، القرار في نافذة التفاصيل */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              {[
                                { label: 'الهوية',   ok: !!photographer.identity_verified,   rejected: photographer.identity_reviewed_at && !photographer.identity_verified,   icon: 'User' },
                                { label: 'المعرض',   ok: !!photographer.portfolio_verified,   icon: 'Image' },
                                {
                                  label: 'الشهادات',
                                  ok: photographer.certificates_verified === true,
                                  rejected: photographer.certificates_reviewed_at && !photographer.certificates_verified,
                                  icon: 'Award',
                                },
                                ...(photographerIsIndividual(photographer.entity_type) ? [{
                                  label: 'وثيقة العمل الحر',
                                  ok: photographer.freelance_permit_verified === true,
                                  rejected: photographer.freelance_permit_reviewed_at && !photographer.freelance_permit_verified,
                                  icon: 'Briefcase',
                                }] : []),
                                ...(photographerUsesDrone(photographer.shooting_methods) ? [{
                                  label: 'تصريح الدرون',
                                  ok: photographer.drone_permit_verified === true,
                                  rejected: photographer.drone_permit_reviewed_at && !photographer.drone_permit_verified,
                                  icon: 'Radio',
                                }] : []),
                              ].map(({ label, ok, rejected, icon }) => (
                                <span
                                  key={label}
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium select-none ${
                                    ok ? 'bg-green-100 text-green-700' : rejected ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-400'
                                  }`}
                                >
                                  <Icon name={ok ? 'CheckCircle' : icon} size={12} />
                                  {label}
                                </span>
                              ))}
                              {getIdentityVerificationStatus(photographer) === 'approved'
                                && photographer.identity_document_expires_at && (
                                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${getIdentityExpiryBadgeClass(getIdentityExpiryStatus(photographer.identity_document_expires_at))}`}>
                                  <Icon name="Calendar" size={12} />
                                  هوية: {getIdentityExpiryStatusLabel(getIdentityExpiryStatus(photographer.identity_document_expires_at))}
                                </span>
                              )}
                              {photographerIsIndividual(photographer.entity_type)
                                && getFreelancePermitStatus(photographer) === 'approved'
                                && photographer.freelance_permit_expires_at && (
                                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${getFreelanceExpiryBadgeClass(getFreelanceExpiryStatus(photographer.freelance_permit_expires_at))}`}>
                                  <Icon name="Calendar" size={12} />
                                  عمل حر: {getFreelanceExpiryStatusLabel(getFreelanceExpiryStatus(photographer.freelance_permit_expires_at))}
                                </span>
                              )}
                              {photographerUsesDrone(photographer.shooting_methods)
                                && getDronePermitStatus(photographer) === 'approved'
                                && photographer.drone_permit_expires_at && (
                                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${getDroneExpiryBadgeClass(getDroneExpiryStatus(photographer.drone_permit_expires_at))}`}>
                                  <Icon name="Calendar" size={12} />
                                  درون: {getDroneExpiryStatusLabel(getDroneExpiryStatus(photographer.drone_permit_expires_at))}
                                </span>
                              )}
                            </div>

                            {/* عدد صور المعرض */}
                            {(photographer.portfolioImages && photographer.portfolioImages.length > 0) && (
                              <div className="flex items-center gap-2 flex-wrap mt-1">
                                <span className="flex items-center gap-1 text-xs text-slate-500">
                                  <Icon name="Image" size={12} className="text-slate-400" />
                                  {photographer.portfolioImages.filter(i => i.approval_status === 'approved').length} معتمدة
                                </span>
                                {pendingPortfolioCount > 0 && (
                                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">
                                    <Icon name="Clock" size={12} />
                                    {pendingPortfolioCount} صورة تحتاج مراجعة
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* نوع الحساب (فرد / كيان) */}
                            <div className="mb-3">
                              <p className="text-xs text-slate-500 mb-1">نوع الحساب</p>
                              <p className="text-sm font-medium text-slate-800">
                                {(photographer.entity_type || photographer.entityType) === 'entity' ? 'استوديو' : 'فرد'}
                              </p>
                            </div>
                            
                            {/* Specializations */}
                            {photographer.specializations && photographer.specializations.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {photographer.specializations.slice(0, 5).map((spec, i) => (
                                  <span key={i} className="px-2 py-1 bg-blue-50 text-blue-800 rounded-lg text-xs">
                                    {spec}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Rejection Reason */}
                            {photographer.rejection_reason && (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                                <p className="text-sm text-red-700">
                                  <Icon name="AlertCircle" size={14} className="inline ml-1" />
                                  سبب الرفض: {photographer.rejection_reason}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* زر التفاصيل — الإجراءات كلها داخل النافذة */}
                          <div className="flex flex-col gap-2 shrink-0">
                            <button
                              onClick={() => {
                                setSelectedVerification(photographer);
                                setShowVerificationModal(true);
                              }}
                              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-bold flex items-center gap-2"
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                            >
                              <Icon name="Eye" size={16} />
                              تفاصيل ومراجعة
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  </>
                )}
              </>
            ) : null}

            {/* Reviews Management */}
            {!loading && !error && active === "reviews" ? (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="Star" size={20} className="inline ml-2 text-amber-500" />
                        إدارة التقييمات والمراجعات
                      </h2>
                      <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        عرض وإدارة جميع تقييمات العملاء للمصورين
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone="amber">{reviews.filter(r => r.is_flagged).length} مُبلغ عنها</Badge>
                      <Badge tone="slate">{reviews.filter(r => !r.is_visible).length} مخفية</Badge>
                      <button
                        onClick={loadReviews}
                        disabled={reviewsLoading}
                        className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold flex items-center gap-2"
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        <Icon name={reviewsLoading ? "Loader" : "RefreshCw"} size={16} className={reviewsLoading ? "animate-spin" : ""} />
                        تحديث
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reviews Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-600">إجمالي التقييمات</p>
                        <p className="text-2xl font-bold text-slate-900">{reviews.length}</p>
                      </div>
                      <Icon name="MessageSquare" size={24} className="text-slate-400" />
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-700">متوسط التقييم</p>
                        <p className="text-2xl font-bold text-amber-800">
                          {reviews.length > 0 
                            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) 
                            : '0.0'}
                          <span className="text-sm mr-1">⭐</span>
                        </p>
                      </div>
                      <Icon name="Star" size={24} className="text-amber-500" />
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-700">تقييمات إيجابية (4-5)</p>
                        <p className="text-2xl font-bold text-green-800">{reviews.filter(r => r.rating >= 4).length}</p>
                      </div>
                      <Icon name="ThumbsUp" size={24} className="text-green-500" />
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-red-700">تقييمات سلبية (1-2)</p>
                        <p className="text-2xl font-bold text-red-800">{reviews.filter(r => r.rating <= 2).length}</p>
                      </div>
                      <Icon name="ThumbsDown" size={24} className="text-red-500" />
                    </div>
                  </div>
                </div>

                {reviewsLoading ? (
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                    <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
                    <p className="text-slate-600">جاري تحميل التقييمات...</p>
                  </div>
                ) : reviews.length === 0 ? (
                  <EmptyState title="لا توجد تقييمات" desc="عند إضافة تقييمات من العملاء ستظهر هنا." />
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div 
                        key={review.id} 
                        className={`bg-white rounded-2xl border p-6 transition-shadow hover:shadow-md ${
                          review.is_flagged ? 'border-red-300 bg-red-50/30' : 
                          !review.is_visible ? 'border-slate-300 opacity-60' : 
                          'border-slate-100'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          {/* Customer Avatar */}
                          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                            {review.customer?.avatar_url ? (
                              <img src={review.customer.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Icon name="User" size={20} color="#64748b" />
                            )}
                          </div>
                          
                          {/* Review Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                  {review.customer?.full_name || "عميل"}
                                </span>
                                <span className="text-slate-400">←</span>
                                <span className="text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                  {review.photographer?.full_name || "مصور"}
                                </span>
                              </div>
                              
                              {/* Rating Stars */}
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Icon 
                                    key={star} 
                                    name="Star" 
                                    size={18} 
                                    className={star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} 
                                  />
                                ))}
                                <span className="text-sm text-slate-600 mr-2">({review.rating}/5)</span>
                              </div>
                            </div>
                            
                            {/* Comment */}
                            {review.comment && (
                              <p className="text-slate-700 bg-slate-50 rounded-lg p-3 mb-3" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                "{review.comment}"
                              </p>
                            )}
                            
                            {/* Meta Info */}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Icon name="Calendar" size={12} />
                                {review.created_at ? new Date(review.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                              </span>
                              {review.booking && (
                                <span className="flex items-center gap-1">
                                  <Icon name="Camera" size={12} />
                                  {getPhotographyTypeLabelAr(review.booking.photography_type) || '—'} - {review.booking.city || '—'}
                                </span>
                              )}
                              {review.is_flagged && (
                                <span className="flex items-center gap-1 text-red-600">
                                  <Icon name="Flag" size={12} />
                                  مُبلغ عنه: {review.flag_reason || 'بدون سبب'}
                                </span>
                              )}
                              {!review.is_visible && (
                                <span className="flex items-center gap-1 text-slate-600">
                                  <Icon name="EyeOff" size={12} />
                                  مخفي
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex flex-col gap-2 shrink-0">
                            <button
                              onClick={() => handleToggleReviewVisibility(review.id, review.is_visible)}
                              disabled={mutating}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 ${
                                review.is_visible 
                                  ? 'bg-slate-600 text-white hover:bg-slate-700' 
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                            >
                              <Icon name={review.is_visible ? "EyeOff" : "Eye"} size={14} />
                              {review.is_visible ? 'إخفاء' : 'إظهار'}
                            </button>
                            {!review.is_flagged && (
                              <button
                                onClick={() => {
                                  const reason = prompt('سبب الإبلاغ عن هذا التقييم:');
                                  if (reason) handleFlagReview(review.id, reason);
                                }}
                                disabled={mutating}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1.5 disabled:opacity-50"
                                style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              >
                                <Icon name="Flag" size={14} />
                                إبلاغ
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteReview(review.id)}
                              disabled={mutating}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50"
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                            >
                              <Icon name="Trash2" size={14} />
                              حذف
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}

            {/* Customers */}
            {!loading && !error && active === "customers" ? (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>العملاء</h2>
                      <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        إجمالي العملاء: <span className="font-bold">{customers.length}</span>
                        {(customerFilterSearch || customerFilterStatus !== 'all') && (
                          <> · النتائج: <span className="font-bold">{filteredCustomers.length}</span></>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {customers.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                    {[
                      { key: 'all', label: 'إجمالي العملاء', value: customerStats.total, icon: 'Users', gradient: 'from-blue-500 to-blue-600' },
                      { key: 'active', label: 'نشطون (30 يوم)', value: customerStats.active30d, icon: 'Activity', gradient: 'from-green-500 to-green-600' },
                      { key: 'new', label: 'جدد (7 أيام)', value: customerStats.new7d, icon: 'UserPlus', gradient: 'from-cyan-500 to-cyan-600' },
                      { key: 'suspended', label: 'موقوفون', value: customerStats.suspended, icon: 'UserX', gradient: 'from-red-500 to-red-600' },
                      { key: 'no_bookings', label: 'بدون حجز', value: customerStats.noBookings, icon: 'CalendarX', gradient: 'from-amber-500 to-amber-600' },
                    ].map((card) => (
                      <button
                        key={card.key}
                        type="button"
                        onClick={() => setCustomerFilterStatus(card.key)}
                        className={`rounded-2xl p-4 text-white text-right transition-all bg-gradient-to-br ${card.gradient} ${
                          customerFilterStatus === card.key ? 'ring-4 ring-offset-2 ring-slate-400 scale-[1.02]' : 'hover:shadow-lg hover:scale-[1.01]'
                        }`}
                      >
                        <Icon name={card.icon} size={20} className="opacity-80" />
                        <p className="text-2xl font-bold mt-2">{formatNumber(card.value)}</p>
                        <p className="text-xs opacity-75" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{card.label}</p>
                      </button>
                    ))}
                  </div>
                )}

                {customers.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3">
                    <span className="text-sm font-bold text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>فلتر:</span>
                    <input
                      type="text"
                      value={customerFilterSearch}
                      onChange={(e) => setCustomerFilterSearch(e.target.value)}
                      placeholder="الاسم، الهاتف، أو البريد..."
                      className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
                      dir="auto"
                    />
                    <select
                      value={customerFilterStatus}
                      onChange={(e) => setCustomerFilterStatus(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium"
                    >
                      <option value="all">جميع الحالات</option>
                      <option value="active">نشطون (30 يوم)</option>
                      <option value="new">جدد (7 أيام)</option>
                      <option value="suspended">موقوفون</option>
                      <option value="no_bookings">بدون أي حجز</option>
                    </select>
                    {(customerFilterSearch || customerFilterStatus !== 'all') && (
                      <button
                        type="button"
                        onClick={() => { setCustomerFilterSearch(''); setCustomerFilterStatus('all'); }}
                        className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm"
                      >
                        إلغاء الفلتر
                      </button>
                    )}
                  </div>
                )}

                {customers.length === 0 ? (
                <EmptyState title="لا يوجد عملاء" desc="عند تسجيل العملاء سيظهرون هنا." />
              ) : filteredCustomers.length === 0 ? (
                <EmptyState title="لا توجد نتائج" desc="جرّب تغيير معايير البحث أو الفلتر." />
              ) : (
                <Table
                  rowKey="id"
                  columns={[
                    {
                      key: "full_name",
                      title: "العميل",
                      render: (r) => (
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                            {r.avatar_url ? (
                              <img src={r.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Icon name="User" size={18} color="#64748b" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 truncate" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{r.full_name || "—"}</div>
                            <div className="text-xs text-slate-600 truncate">{r.phone || r.email || "—"}</div>
                          </div>
                        </div>
                      ),
                    },
                    { key: "email", title: "البريد الإلكتروني", render: (r) => r.email || "—" },
                    { key: "phone", title: "الهاتف", render: (r) => r.phone || "—" },
                    { key: "created_at", title: "تاريخ التسجيل", render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { day: "numeric", month: "long", year: "numeric" }) : "—") },
                    {
                      key: "booking_count",
                      title: "عدد الحجوزات",
                      render: (r) => {
                        const activity = getCustomerActivity(r.id);
                        return (
                          <span className="font-bold text-slate-900">{formatNumber(activity.count)}</span>
                        );
                      },
                    },
                    {
                      key: "last_booking",
                      title: "آخر حجز",
                      render: (r) => {
                        const activity = getCustomerActivity(r.id);
                        if (!activity.lastBookingAt) return <span className="text-slate-400">—</span>;
                        return (
                          <span className="text-sm text-slate-700">
                            {formatDate(new Date(activity.lastBookingAt).toISOString())}
                          </span>
                        );
                      },
                    },
                    {
                      key: "total_spend",
                      title: "إجمالي الإنفاق",
                      render: (r) => {
                        const activity = getCustomerActivity(r.id);
                        return activity.totalSpend > 0
                          ? <span className="font-bold text-green-700">{money(activity.totalSpend)}</span>
                          : <span className="text-slate-400">—</span>;
                      },
                    },
                      {
                        key: "is_active",
                        title: "الحالة",
                      render: (r) => {
                        const isActive = r.is_active !== false;
                        return isActive ? <Badge tone="green">نشط</Badge> : <Badge tone="red">موقوف</Badge>;
                      },
                      },
                      {
                        key: "actions",
                        title: "إجراءات",
                      render: (r) => {
                        const isUpdating = updatingCustomer === r.id;
                        const isActive = r.is_active !== false;
                        
                        return (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* View Details */}
                            <button
                              onClick={() => {
                                setSelectedUser(r);
                                setShowUserModal(true);
                              }}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5"
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              title="عرض تفاصيل العميل"
                            >
                              <Icon name="Eye" size={14} color="white" />
                              تفاصيل
                            </button>
                            
                            {/* Toggle Active Status */}
                            <button
                              disabled={isUpdating || mutating}
                              onClick={async () => {
                                try {
                                  setUpdatingCustomer(r.id);
                                  await manageCustomer(r.id, "toggle_active", !isActive);
                                  setCustomers((prev) => (prev || []).map((u) => 
                                    u.id === r.id ? { ...u, is_active: !isActive } : u
                                  ));
                                  setNotice(`${!isActive ? "✅ تم تفعيل" : "⚠️ تم إيقاف"} حساب العميل: ${r.full_name || String(r.id).slice(0, 8)}`);
                                  refreshData();
                                } catch (e) {
                                  setNotice(`❌ ${e?.message || "تعذر تحديث حالة العميل"}`);
                                } finally {
                                  setUpdatingCustomer(null);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${
                                (isUpdating || mutating)
                                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                  : isActive
                                  ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-green-600 text-white hover:bg-green-700"
                              }`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              title={isActive ? "إيقاف حساب العميل" : "تفعيل حساب العميل"}
                            >
                              {(isUpdating || mutating) ? (
                                <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                              ) : (
                                isActive ? <Icon name="UserX" size={14} color="white" /> : <Icon name="UserCheck" size={14} color="white" />
                              )}
                              {(isUpdating || mutating) ? "جاري..." : (isActive ? "إيقاف" : "تفعيل")}
                            </button>

                            <button
                              disabled={isUpdating || mutating}
                              onClick={async () => {
                                const ok = window.confirm(
                                  `حذف العميل «${r.full_name || r.email || r.id}» نهائياً؟\n\nسيتم حذف حساب تسجيل الدخول وبيانات الملف المرتبطة وفق قواعد قاعدة البيانات. لا يمكن التراجع.`
                                );
                                if (!ok) return;
                                try {
                                  setUpdatingCustomer(r.id);
                                  await manageCustomer(r.id, "delete");
                                  setCustomers((prev) => (prev || []).filter((u) => u.id !== r.id));
                                  setNotice(`✅ تم حذف العميل نهائياً: ${r.full_name || String(r.id).slice(0, 8)}`);
                                  refreshData();
                                } catch (e) {
                                  setNotice(`❌ ${e?.message || "تعذر حذف العميل"}`);
                                } finally {
                                  setUpdatingCustomer(null);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${
                                isUpdating || mutating
                                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                                  : "bg-slate-800 text-white hover:bg-slate-900"
                              }`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                              title="حذف نهائي من النظام"
                            >
                              {isUpdating ? (
                                <Icon name="Loader" size={14} color="currentColor" className="animate-spin" />
                              ) : (
                                <Icon name="Trash2" size={14} color="white" />
                              )}
                              {isUpdating ? "جاري..." : "حذف نهائي"}
                            </button>
                          </div>
                        );
                      },
                      },
                  ]}
                  rows={filteredCustomers}
                />
                )}
              </>
            ) : null}

            {/* Support & Complaints */}
            {!loading && !error && active === "support" ? (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="Headphones" size={22} className="text-primary" />
                        نظام الدعم والشكاوى
                      </h2>
                      <p className="text-xs text-slate-600 mt-1">
                        إدارة تذاكر الدعم • الرد على الشكاوى • تتبع الحالة
                      </p>
                    </div>
                    <button
                      onClick={loadTickets}
                      disabled={ticketsLoading}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold flex items-center gap-2"
                    >
                      <Icon name={ticketsLoading ? "Loader" : "RefreshCw"} size={16} className={ticketsLoading ? "animate-spin" : ""} />
                      تحديث
                    </button>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
                    <Icon name="Ticket" size={20} className="opacity-80" />
                    <p className="text-2xl font-bold mt-2">{formatNumber(ticketStats.total)}</p>
                    <p className="text-xs opacity-75">إجمالي التذاكر</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-4 text-white">
                    <Icon name="AlertCircle" size={20} className="opacity-80" />
                    <p className="text-2xl font-bold mt-2">{formatNumber(ticketStats.open)}</p>
                    <p className="text-xs opacity-75">مفتوحة</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-4 text-white">
                    <Icon name="Clock" size={20} className="opacity-80" />
                    <p className="text-2xl font-bold mt-2">{formatNumber(ticketStats.inProgress)}</p>
                    <p className="text-xs opacity-75">قيد المعالجة</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-4 text-white">
                    <Icon name="MessageSquare" size={20} className="opacity-80" />
                    <p className="text-2xl font-bold mt-2">{formatNumber(ticketStats.waitingAdmin)}</p>
                    <p className="text-xs opacity-75">بانتظار الرد</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white">
                    <Icon name="CheckCircle" size={20} className="opacity-80" />
                    <p className="text-2xl font-bold mt-2">{formatNumber(ticketStats.resolved)}</p>
                    <p className="text-xs opacity-75">تم الحل</p>
                  </div>
                  <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-4 text-white">
                    <Icon name="Timer" size={20} className="opacity-80" />
                    <p className="text-2xl font-bold mt-2">{ticketStats.avgResponseTime || 0}</p>
                    <p className="text-xs opacity-75">دقيقة متوسط الرد</p>
                  </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Icon name="Search" size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="بحث برقم التذكرة، الموضوع، اسم المستخدم..."
                          value={ticketFilter.search}
                          onChange={(e) => setTicketFilter(prev => ({ ...prev, search: e.target.value }))}
                          className="w-full pr-10 pl-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-right"
                        />
                      </div>
                    </div>
                    <select
                      value={ticketFilter.status}
                      onChange={(e) => setTicketFilter(prev => ({ ...prev, status: e.target.value }))}
                      className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="all">كل الحالات</option>
                      {TICKET_STATUSES.map(s => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                    <select
                      value={ticketFilter.priority}
                      onChange={(e) => setTicketFilter(prev => ({ ...prev, priority: e.target.value }))}
                      className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="all">كل الأولويات</option>
                      {TICKET_PRIORITIES.map(p => (
                        <option key={p.key} value={p.key}>{p.label}</option>
                      ))}
                    </select>
                    <select
                      value={ticketFilter.category}
                      onChange={(e) => setTicketFilter(prev => ({ ...prev, category: e.target.value }))}
                      className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="all">كل التصنيفات</option>
                      {TICKET_CATEGORIES.map(c => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                    <select
                      value={ticketFilter.userRole}
                      onChange={(e) => setTicketFilter(prev => ({ ...prev, userRole: e.target.value }))}
                      className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="all">كل المستخدمين</option>
                      <option value="customer">عملاء فقط</option>
                      <option value="photographer">مصورون فقط</option>
                    </select>
                    <select
                      value={ticketFilter.ticketType || 'all'}
                      onChange={(e) => setTicketFilter(prev => ({ ...prev, ticketType: e.target.value }))}
                      className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="all">دعم + شكاوى</option>
                      <option value="support">طلبات دعم فقط</option>
                      <option value="complaint">شكاوى فقط</option>
                    </select>
                  </div>
                </div>

                {/* Tickets Table */}
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                  {ticketsLoading ? (
                    <div className="text-center py-12">
                      <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
                      <p className="text-slate-600">جاري تحميل التذاكر...</p>
                    </div>
                  ) : getFilteredTickets().length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Inbox" size={48} className="text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-bold">لا توجد تذاكر</p>
                      <p className="text-sm text-slate-500">ستظهر تذاكر الدعم هنا عند إنشائها</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-100">
                          <tr>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">رقم التذكرة</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">المصدر</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">المستخدم</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">الموضوع</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">التصنيف</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">الأولوية</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">الحالة</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">التاريخ</th>
                            <th className="text-right py-3 px-4 font-bold text-slate-700">إجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredTickets().map(ticket => {
                            const category = TICKET_CATEGORIES.find(c => c.key === ticket.category);
                            const priority = TICKET_PRIORITIES.find(p => p.key === ticket.priority);
                            const status = TICKET_STATUSES.find(s => s.key === ticket.status);
                            
                            return (
                              <tr key={ticket.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => openTicketDetails(ticket)}>
                                <td className="py-3 px-4">
                                  <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                                    {ticket.ticket_number}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex flex-col gap-1">
                                    {(() => {
                                      const role = ticket.user_type || ticket.user_role || ticket.user?.role || 'customer';
                                      return role === 'photographer' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 w-fit">
                                          <Icon name="Camera" size={11} />
                                          مصور
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 w-fit">
                                          <Icon name="User" size={11} />
                                          عميل
                                        </span>
                                      );
                                    })()}
                                    {ticket.ticket_type === 'complaint' ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 w-fit">
                                        <Icon name="AlertCircle" size={11} />
                                        شكوى
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 w-fit">
                                        <Icon name="Headphones" size={11} />
                                        دعم
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="font-bold text-slate-900">{ticket.user?.full_name || ticket.guest_name || 'زائر'}</p>
                                    <p className="text-xs text-slate-500">{ticket.user?.email || ticket.guest_email}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <p className="font-medium text-slate-900 truncate max-w-[200px]">{ticket.subject}</p>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="flex items-center gap-1 text-slate-600">
                                    <Icon name={category?.icon || 'HelpCircle'} size={14} />
                                    {category?.label || ticket.category}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                    priority?.color === 'red' ? 'bg-red-100 text-red-700' :
                                    priority?.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                                    priority?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {priority?.label || ticket.priority}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                    status?.color === 'green' ? 'bg-green-100 text-green-700' :
                                    status?.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                                    status?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                                    status?.color === 'purple' ? 'bg-blue-100 text-blue-800' :
                                    status?.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {status?.label || ticket.status}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-slate-500 text-xs">
                                  {new Date(ticket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  <br />
                                  {new Date(ticket.created_at).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td className="py-3 px-4">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openTicketDetails(ticket); }}
                                    className="px-3 py-1 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary-dark"
                                  >
                                    عرض
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Ticket Details Modal */}
                {showTicketModal && selectedTicket && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                      {/* Modal Header */}
                      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="Ticket" size={20} className="text-primary" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{selectedTicket.ticket_number}</h3>
                            <p className="text-sm text-slate-500">{selectedTicket.subject}</p>
                          </div>
                        </div>
                        <button onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }} className="p-2 hover:bg-slate-200 rounded-xl">
                          <Icon name="X" size={20} />
                        </button>
                      </div>

                      {/* Modal Body */}
                      <div className="flex-1 overflow-y-auto p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Left Side - Conversation */}
                          <div className="lg:col-span-2 space-y-4">
                            {/* Original Message */}
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                                  {(selectedTicket.user?.full_name || selectedTicket.guest_name || 'Z')[0]}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <p className="font-bold">{selectedTicket.user?.full_name || selectedTicket.guest_name || 'زائر'}</p>
                                    <span className="text-xs text-slate-500">
                                      {new Date(selectedTicket.created_at).toLocaleString('ar-SA')}
                                    </span>
                                  </div>
                                  <p className="mt-2 text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                                </div>
                              </div>
                            </div>

                            {/* Replies */}
                            <div className="space-y-3">
                              {ticketReplies.map(reply => (
                                <div 
                                  key={reply.id} 
                                  className={`rounded-xl p-4 ${
                                    reply.is_internal 
                                      ? 'bg-yellow-50 border border-yellow-200' 
                                      : reply.user_id === selectedTicket.user_id
                                        ? 'bg-blue-50 border border-blue-200'
                                        : 'bg-green-50 border border-green-200'
                                  }`}
                                >
                                  {reply.is_internal && (
                                    <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded mb-2 inline-block">
                                      ملاحظة داخلية
                                    </span>
                                  )}
                                  <div className="flex items-start gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                      reply.user_id === selectedTicket.user_id ? 'bg-blue-500' : 'bg-green-500'
                                    }`}>
                                      {(reply.user?.full_name || 'A')[0]}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <p className="font-bold">
                                          {reply.user?.full_name || 'مشرف'}
                                          {reply.user_id !== selectedTicket.user_id && (
                                            <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded mr-2">
                                              فريق الدعم
                                            </span>
                                          )}
                                        </p>
                                        <span className="text-xs text-slate-500">
                                          {new Date(reply.created_at).toLocaleString('ar-SA')}
                                        </span>
                                      </div>
                                      <p className="mt-2 text-slate-700 whitespace-pre-wrap">{reply.message}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Reply Input */}
                            {selectedTicket.status !== 'closed' && (
                              <div className="bg-slate-50 rounded-xl p-4">
                                <textarea
                                  value={ticketReplyText}
                                  onChange={(e) => setTicketReplyText(e.target.value)}
                                  placeholder="اكتب ردك هنا..."
                                  rows={4}
                                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none text-right"
                                  dir="rtl"
                                />
                                <div className="flex items-center justify-between mt-3">
                                  <label className="flex items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={ticketReplyInternal}
                                      onChange={(e) => setTicketReplyInternal(e.target.checked)}
                                      className="rounded"
                                    />
                                    <span className="text-amber-600">ملاحظة داخلية (لن تظهر للمستخدم)</span>
                                  </label>
                                  <button
                                    onClick={sendTicketReply}
                                    disabled={sendingReply || !ticketReplyText.trim()}
                                    className="px-6 py-2 bg-primary text-white rounded-xl font-bold hover:bg-primary-dark flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <Icon name={sendingReply ? "Loader" : "Send"} size={16} className={sendingReply ? "animate-spin" : ""} />
                                    إرسال الرد
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right Side - Ticket Info */}
                          <div className="space-y-4">
                            {/* Status & Actions */}
                            <div className="bg-slate-50 rounded-xl p-4">
                              <h4 className="font-bold text-slate-900 mb-3">حالة التذكرة</h4>
                              <select
                                value={selectedTicket.status}
                                onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm mb-3"
                              >
                                {TICKET_STATUSES.map(s => (
                                  <option key={s.key} value={s.key}>{s.label}</option>
                                ))}
                              </select>
                              
                              <h4 className="font-bold text-slate-900 mb-2 mt-4">الأولوية</h4>
                              <select
                                value={selectedTicket.priority}
                                onChange={(e) => updateTicketPriority(selectedTicket.id, e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                              >
                                {TICKET_PRIORITIES.map(p => (
                                  <option key={p.key} value={p.key}>{p.label}</option>
                                ))}
                              </select>
                            </div>

                            {/* User Info */}
                            <div className="bg-slate-50 rounded-xl p-4">
                              <h4 className="font-bold text-slate-900 mb-3">معلومات المستخدم</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">الاسم:</span>
                                  <span className="font-bold">{selectedTicket.user?.full_name || selectedTicket.guest_name || 'غير معروف'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">البريد:</span>
                                  <span className="font-bold text-xs">{selectedTicket.user?.email || selectedTicket.guest_email || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">الهاتف:</span>
                                  <span className="font-bold">{selectedTicket.user?.phone || selectedTicket.guest_phone || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500">المرسل:</span>
                                  {(() => {
                                    const role = selectedTicket.user_type || selectedTicket.user_role || selectedTicket.user?.role || 'customer';
                                    return role === 'photographer' ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                        <Icon name="Camera" size={11} />
                                        مصور
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                                        <Icon name="User" size={11} />
                                        عميل
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500">نوع الطلب:</span>
                                  {selectedTicket.ticket_type === 'complaint' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                      <Icon name="AlertCircle" size={11} />
                                      شكوى
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                                      <Icon name="Headphones" size={11} />
                                      طلب دعم
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Ticket Details */}
                            <div className="bg-slate-50 rounded-xl p-4">
                              <h4 className="font-bold text-slate-900 mb-3">تفاصيل التذكرة</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">التصنيف:</span>
                                  <span className="font-bold">{TICKET_CATEGORIES.find(c => c.key === selectedTicket.category)?.label}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">تاريخ الإنشاء:</span>
                                  <span className="font-bold">{new Date(selectedTicket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                </div>
                                {selectedTicket.first_response_at && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">أول رد:</span>
                                    <span className="font-bold">{new Date(selectedTicket.first_response_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                  </div>
                                )}
                                {selectedTicket.related_booking_id && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">رقم الحجز:</span>
                                    <span className="font-mono text-xs bg-slate-200 px-2 py-1 rounded">
                                      {selectedTicket.related_booking_id.slice(0, 8)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="space-y-2">
                              {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                                <button
                                  onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                                  className="w-full px-4 py-2 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 flex items-center justify-center gap-2"
                                >
                                  <Icon name="CheckCircle" size={16} />
                                  تم الحل
                                </button>
                              )}
                              {selectedTicket.status !== 'closed' && (
                                <button
                                  onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                                  className="w-full px-4 py-2 bg-slate-500 text-white rounded-xl font-bold hover:bg-slate-600 flex items-center justify-center gap-2"
                                >
                                  <Icon name="XCircle" size={16} />
                                  إغلاق التذكرة
                                </button>
                              )}
                            </div>

                            {/* بطاقة تفاصيل الحجز — تظهر فقط عند وجود related_booking_id */}
                            {selectedTicket.related_booking_id && (
                              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
                                <h4 className="font-bold text-orange-800 text-sm flex items-center gap-2">
                                  <Icon name="BookOpen" size={15} />
                                  تفاصيل الحجز المرتبط
                                </h4>
                                {ticketBookingLoading ? (
                                  <p className="text-xs text-slate-400 animate-pulse">جاري التحميل...</p>
                                ) : ticketBookingDetails ? (
                                  <div className="space-y-1.5 text-xs text-slate-700">
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">رقم الحجز:</span>
                                      <span className="font-mono bg-slate-200 px-2 py-0.5 rounded">{ticketBookingDetails.id?.slice(0, 8)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">الحالة:</span>
                                      <span className="font-bold">{ticketBookingDetails.status}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">المبلغ:</span>
                                      <span className="font-bold">{ticketBookingDetails.total_price ? `${ticketBookingDetails.total_price} ريال` : '—'}</span>
                                    </div>
                                    {ticketBookingDetails.scheduled_at && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">موعد الجلسة:</span>
                                        <span className="font-bold">{new Date(ticketBookingDetails.scheduled_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                      </div>
                                    )}
                                    {ticketBookingDetails.customer && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">العميل:</span>
                                        <span className="font-bold">{ticketBookingDetails.customer.full_name || '—'}</span>
                                      </div>
                                    )}
                                    {ticketBookingDetails.photographer && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">المصور:</span>
                                        <span className="font-bold">{ticketBookingDetails.photographer.full_name || '—'}</span>
                                      </div>
                                    )}
                                    {ticketBookingDetails.delivery && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">حالة التسليم:</span>
                                        <span className="text-green-700 font-bold">✅ تم التسليم</span>
                                      </div>
                                    )}
                                    {ticketBookingDetails.review ? (
                                      <div className="flex justify-between items-center">
                                        <span className="text-slate-500">تقييم العميل:</span>
                                        <span className="font-bold flex items-center gap-1">
                                          {'★'.repeat(ticketBookingDetails.review.rating)}{'☆'.repeat(5 - ticketBookingDetails.review.rating)}
                                          <span className="text-slate-500 font-normal">({ticketBookingDetails.review.rating}/5)</span>
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">تقييم العميل:</span>
                                        <span className="text-slate-400">لم يقيّم بعد</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-red-400">تعذّر تحميل بيانات الحجز</p>
                                )}

                                {/* أزرار قرار النزاع — تظهر فقط للتذاكر ذات النوع complaint */}
                                {selectedTicket.ticket_type === 'complaint' && selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                                  <div className="pt-2 border-t border-orange-200 space-y-2">
                                    <p className="text-xs font-bold text-orange-700">⚖️ قرار النزاع:</p>
                                    <button
                                      disabled={disputeActionLoading}
                                      onClick={() => handleDisputeAction('full_refund')}
                                      className="w-full py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition"
                                    >
                                      <Icon name="RotateCcw" size={13} />
                                      استرداد كامل للعميل
                                    </button>
                                    <button
                                      disabled={disputeActionLoading}
                                      onClick={() => handleDisputeAction('partial_refund')}
                                      className="w-full py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition"
                                    >
                                      <Icon name="Divide" size={13} />
                                      استرداد جزئي
                                    </button>
                                    <button
                                      disabled={disputeActionLoading}
                                      onClick={() => handleDisputeAction('pay_photographer')}
                                      className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition"
                                    >
                                      <Icon name="Banknote" size={13} />
                                      صرف المستحقات للمصور
                                    </button>
                                    {disputeActionLoading && (
                                      <p className="text-xs text-center text-slate-400 animate-pulse">جاري التنفيذ...</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Payments & Commissions */}
            {!loading && !error && active === "payments" ? (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="CreditCard" size={22} className="text-primary" />
                        إدارة المدفوعات والعمولات
                      </h2>
                      <p className="text-xs text-slate-600 mt-1">
                        سجل المدفوعات • نسبة العمولة • التقارير المالية
                      </p>
                    </div>
                    <button
                      onClick={loadPayments}
                      disabled={paymentsLoading}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold flex items-center gap-2"
                    >
                      <Icon name={paymentsLoading ? "Loader" : "RefreshCw"} size={16} className={paymentsLoading ? "animate-spin" : ""} />
                      تحديث
                    </button>
                  </div>
                </div>

                {/* Financial Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="TrendingUp" size={20} className="opacity-80" />
                      <span className="text-xs opacity-75">إجمالي</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(paymentStats?.totalRevenue || 0)}</p>
                    <p className="text-xs opacity-75">ر.س إيرادات</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="BadgePercent" size={20} className="opacity-80" />
                      <span className="text-xs opacity-75">عمولات</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(paymentStats?.totalCommissions || 0)}</p>
                    <p className="text-xs opacity-75">ر.س للمنصة</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="Users" size={20} className="opacity-80" />
                      <span className="text-xs opacity-75">تم صرفه</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(paymentStats?.photographerPayouts || 0)}</p>
                    <p className="text-xs opacity-75">ر.س للمصورين</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="Clock" size={20} className="opacity-80" />
                      <span className="text-xs opacity-75">معلق</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(paymentStats?.pendingPayouts || 0)}</p>
                    <p className="text-xs opacity-75">ر.س بانتظار الصرف</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="Calendar" size={20} className="opacity-80" />
                      <span className="text-xs opacity-75">هذا الشهر</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(paymentStats?.thisMonthRevenue || 0)}</p>
                    <p className="text-xs opacity-75">ر.س إيرادات</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Icon name="Banknote" size={20} className="opacity-80" />
                      <span className="text-xs opacity-75">عمولة الشهر</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(paymentStats?.thisMonthCommissions || 0)}</p>
                    <p className="text-xs opacity-75">ر.س</p>
                  </div>
                </div>

                {/* Sub-tabs: صرف المصورين | سجل المعاملات */}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentsSubTab('payouts')}
                    className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors ${
                      paymentsSubTab === 'payouts'
                        ? 'bg-slate-900 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon name="Landmark" size={16} />
                    صرف المصورين
                    {pendingPayoutGroups.length > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        paymentsSubTab === 'payouts' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {pendingPayoutGroups.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentsSubTab('ledger')}
                    className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors ${
                      paymentsSubTab === 'ledger'
                        ? 'bg-slate-900 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon name="Receipt" size={16} />
                    سجل المعاملات
                  </button>
                </div>

                {/* صرف المصورين — بطاقة لكل مصور */}
                {paymentsSubTab === 'payouts' && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-2xl border border-slate-100 p-4">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="Landmark" size={20} className="text-primary" />
                        مستحقات المصورين — جاهزة للتحويل
                      </h3>
                      <p className="text-xs text-slate-600 mt-1">
                        انسخ IBAN والمبلغ، نفّذ التحويل من تطبيق البنك، ثم اضغط «تأكيد الصرف».
                      </p>
                    </div>

                    {paymentsLoading ? (
                      <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                        <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
                        <p className="text-slate-600">جاري تحميل مستحقات المصورين...</p>
                      </div>
                    ) : pendingPayoutGroups.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                        <Icon name="CheckCircle" size={48} className="text-green-400 mx-auto mb-3" />
                        <p className="text-slate-800 font-bold">لا توجد مستحقات معلّقة</p>
                        <p className="text-sm text-slate-500 mt-1">ستظهر هنا عند وجود حجوزات مدفوعة بانتظار الصرف</p>
                      </div>
                    ) : (
                      pendingPayoutGroups.map((group) => {
                        const bank = group.bank || {};
                        const ibanValid = hasValidSaudiIban(bank.iban);
                        const ibanDisplay = formatIbanForDisplay(bank.iban || '');
                        const isExpanded = expandedPayoutGroups[group.photographerId];

                        return (
                          <div
                            key={group.photographerId}
                            className={`bg-white rounded-2xl border overflow-hidden shadow-sm ${
                              ibanValid ? 'border-slate-200' : 'border-amber-300 bg-amber-50/30'
                            }`}
                            dir="rtl"
                          >
                            <div className="p-4 md:p-5">
                              <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="flex items-center gap-3 min-w-0">
                                  <img
                                    src={group.photographer?.avatar_url || 'https://via.placeholder.com/48'}
                                    alt=""
                                    className="w-12 h-12 rounded-full object-cover shrink-0"
                                  />
                                  <div className="min-w-0 text-start">
                                    <p className="font-bold text-slate-900 truncate">
                                      {group.photographer?.full_name || 'مصور'}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {group.bookings.length} حجز معلّق · {formatNumber(group.totalDue)} ر.س
                                    </p>
                                  </div>
                                </div>
                                <div className="text-start">
                                  <p className="text-xs text-slate-500">المبلغ المستحق</p>
                                  <p className="text-2xl font-bold text-green-600">{formatNumber(group.totalDue)} <span className="text-sm">ر.س</span></p>
                                </div>
                              </div>

                              {!ibanValid ? (
                                <div className="mt-4 flex items-start gap-2 rounded-xl p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                                  <Icon name="AlertTriangle" size={18} className="shrink-0 mt-0.5" />
                                  <p>لم يُدخل المصور IBAN صالحاً — لا يمكن تأكيد الصرف حتى يُكمل بياناته من صفحة المدفوعات.</p>
                                </div>
                              ) : (
                                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    {bank.bank_name && (
                                      <div className="text-start">
                                        <p className="text-slate-500 text-xs">البنك</p>
                                        <p className="font-medium text-slate-900">{bank.bank_name}</p>
                                      </div>
                                    )}
                                    {bank.bank_account_holder_name && (
                                      <div className="text-start">
                                        <p className="text-slate-500 text-xs">صاحب الحساب</p>
                                        <p className="font-medium text-slate-900">{bank.bank_account_holder_name}</p>
                                      </div>
                                    )}
                                    <div className="sm:col-span-2 text-start">
                                      <p className="text-slate-500 text-xs mb-1">IBAN</p>
                                      <p className="font-mono font-semibold text-slate-900 text-sm break-all" dir="ltr">
                                        {ibanDisplay || cleanIban(bank.iban)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 pt-1">
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(cleanIban(bank.iban), 'IBAN')}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      <Icon name="Copy" size={14} />
                                      نسخ IBAN
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(String(group.totalDue), 'المبلغ')}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      <Icon name="Copy" size={14} />
                                      نسخ المبلغ
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(buildPayoutCopyText(group), 'كل البيانات')}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      <Icon name="ClipboardList" size={14} />
                                      نسخ كل البيانات
                                    </button>
                                  </div>
                                </div>
                              )}

                              <div className="mt-4 flex flex-wrap items-center gap-2 justify-between">
                                <button
                                  type="button"
                                  onClick={() => togglePayoutGroupExpanded(group.photographerId)}
                                  className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
                                >
                                  <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                                  {isExpanded ? 'إخفاء الحجوزات' : 'عرض الحجوزات'}
                                </button>
                                <button
                                  type="button"
                                  disabled={!ibanValid || mutating}
                                  onClick={() => handleBatchPayoutPhotographer(group)}
                                  className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Icon name="CheckCircle" size={16} />
                                  تأكيد الصرف
                                </button>
                              </div>

                              {isExpanded && (
                                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                                  {group.bookings.map((b) => (
                                    <div key={b.id} className="flex items-center justify-between gap-3 text-sm py-2 px-3 rounded-lg bg-slate-50">
                                      <div className="min-w-0 text-start">
                                        <p className="font-mono text-xs text-slate-500">#{String(b.bookingId).slice(0, 8).toUpperCase()}</p>
                                        <p className="text-slate-800 truncate">{getPhotographyTypeLabelAr(b.photographyType) || '—'} · {b.city || '—'}</p>
                                      </div>
                                      <p className="font-bold text-green-600 shrink-0">{formatNumber(b.photographerAmount)} ر.س</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {paymentsSubTab === 'ledger' && (
                <>
                {/* Commission Settings */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="Settings" size={20} className="text-primary" />
                    إعدادات العمولة
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        نسبة عمولة المنصة (%)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={commissionSettings?.platformCommissionRate || 15}
                          onChange={(e) => setCommissionSettings(prev => ({
                            ...prev,
                            platformCommissionRate: parseFloat(e.target.value) || 0
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-lg font-bold text-center"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        المبلغ الذي تستقطعه المنصة من كل حجز
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        الحد الأدنى للعمولة (ر.س)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={commissionSettings?.minCommission || 50}
                        onChange={(e) => setCommissionSettings(prev => ({
                          ...prev,
                          minCommission: parseFloat(e.target.value) || 0
                        }))}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        أقل عمولة يتم استقطاعها
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        الحد الأقصى للعمولة (ر.س)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={commissionSettings?.maxCommission || ''}
                        onChange={(e) => setCommissionSettings(prev => ({
                          ...prev,
                          maxCommission: e.target.value ? parseFloat(e.target.value) : null
                        }))}
                        placeholder="بدون حد"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        اتركه فارغاً لعدم وجود حد
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        حد السحب للمصور (ر.س)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={commissionSettings?.payoutThreshold || 500}
                        onChange={(e) => setCommissionSettings(prev => ({
                          ...prev,
                          payoutThreshold: parseFloat(e.target.value) || 0
                        }))}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        الحد الأدنى لطلب السحب
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={saveCommissionSettings}
                      loading={savingCommission}
                      disabled={savingCommission}
                      iconName="Save"
                      iconPosition="right"
                    >
                      حفظ الإعدادات
                    </Button>
                  </div>
                </div>

                {/* Commission Calculator Preview */}
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
                  <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Icon name="Calculator" size={18} className="text-primary" />
                    معاينة حساب العمولة
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">مثال: حجز بـ 500 ر.س</p>
                      <p className="text-sm text-slate-600">
                        العمولة: <span className="font-bold text-primary">{formatNumber(Math.max(500 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                      <p className="text-sm text-slate-600">
                        للمصور: <span className="font-bold text-green-600">{formatNumber(500 - Math.max(500 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">مثال: حجز بـ 1000 ر.س</p>
                      <p className="text-sm text-slate-600">
                        العمولة: <span className="font-bold text-primary">{formatNumber(Math.max(1000 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                      <p className="text-sm text-slate-600">
                        للمصور: <span className="font-bold text-green-600">{formatNumber(1000 - Math.max(1000 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">مثال: حجز بـ 2000 ر.س</p>
                      <p className="text-sm text-slate-600">
                        العمولة: <span className="font-bold text-primary">{formatNumber(Math.max(2000 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                      <p className="text-sm text-slate-600">
                        للمصور: <span className="font-bold text-green-600">{formatNumber(2000 - Math.max(2000 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">مثال: حجز بـ 5000 ر.س</p>
                      <p className="text-sm text-slate-600">
                        العمولة: <span className="font-bold text-primary">{formatNumber(Math.max(5000 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                      <p className="text-sm text-slate-600">
                        للمصور: <span className="font-bold text-green-600">{formatNumber(5000 - Math.max(5000 * ((commissionSettings?.platformCommissionRate || 15) / 100), commissionSettings?.minCommission || 0))} ر.س</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payments Record */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      <Icon name="Receipt" size={20} className="text-primary" />
                      سجل المدفوعات
                    </h3>
                    
                    {/* Filters */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <select
                        value={paymentDateFilter}
                        onChange={(e) => setPaymentDateFilter(e.target.value)}
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="all">كل الفترات</option>
                        <option value="today">اليوم</option>
                        <option value="week">آخر 7 أيام</option>
                        <option value="month">هذا الشهر</option>
                      </select>
                      
                      <select
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="all">كل الحالات</option>
                        <option value="completed">مكتمل</option>
                        <option value="pending">معلق</option>
                        <option value="paid">تم الصرف</option>
                      </select>
                    </div>
                  </div>

                  {paymentsLoading ? (
                    <div className="text-center py-10">
                      <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
                      <p className="text-slate-600">جاري تحميل المدفوعات...</p>
                    </div>
                  ) : getFilteredPayments().length === 0 ? (
                    <div className="text-center py-10">
                      <Icon name="Receipt" size={48} className="text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-bold">لا توجد مدفوعات</p>
                      <p className="text-sm text-slate-500">ستظهر المدفوعات هنا بعد إتمام الحجوزات</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-right py-3 px-2 font-bold text-slate-700">رقم الحجز</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">العميل</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">المصور</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">المبلغ</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">العمولة</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">للمصور</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">البيانات البنكية</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">حالة الصرف</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">التاريخ</th>
                            <th className="text-right py-3 px-2 font-bold text-slate-700">إجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredPayments().map((payment) => (
                            <tr key={payment.id} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="py-3 px-2">
                                <span className="font-mono text-xs text-slate-600">
                                  #{String(payment.bookingId).slice(0, 8).toUpperCase()}
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={payment.customer?.avatar_url || 'https://via.placeholder.com/32'}
                                    alt=""
                                    className="w-7 h-7 rounded-full object-cover"
                                  />
                                  <span className="text-slate-800 text-xs">{payment.customer?.full_name || 'غير معروف'}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={payment.photographer?.avatar_url || 'https://via.placeholder.com/32'}
                                    alt=""
                                    className="w-7 h-7 rounded-full object-cover"
                                  />
                                  <span className="text-slate-800 text-xs">{payment.photographer?.full_name || 'غير معروف'}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2 font-bold text-slate-900">
                                {formatNumber(payment.amount)} ر.س
                              </td>
                              <td className="py-3 px-2 text-primary font-semibold">
                                {formatNumber(payment.commission)} ر.س
                              </td>
                              <td className="py-3 px-2 text-green-600 font-semibold">
                                {formatNumber(payment.photographerAmount)} ر.س
                              </td>
                              <td className="py-3 px-2 text-xs text-slate-600 max-w-[120px] truncate" title={payment.photographerBank?.iban || ''}>
                                {payment.photographerBank?.iban ? (payment.photographerBank.iban.length > 8 ? `${payment.photographerBank.iban.slice(0, 4)}...${payment.photographerBank.iban.slice(-4)}` : payment.photographerBank.iban) : '—'}
                              </td>
                              <td className="py-3 px-2">
                                {payment.payoutStatus === 'paid' ? (
                                  <Badge tone="green">تم الصرف</Badge>
                                ) : payment.payoutStatus === 'refunded' ? (
                                  <Badge tone="red">مُسترد</Badge>
                                ) : payment.payoutStatus === 'pending' ? (
                                  <Badge tone="amber">معلق</Badge>
                                ) : (
                                  <Badge tone="slate">ملغي</Badge>
                                )}
                              </td>
                              <td className="py-3 px-2 text-slate-600 text-xs">
                                {formatDate(payment.date)}
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setSelectedPayment(payment);
                                      setShowPaymentModal(true);
                                    }}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                                    title="عرض التفاصيل"
                                  >
                                    <Icon name="Eye" size={16} />
                                  </button>
                                  {payment.payoutStatus === 'pending' && (
                                    <button
                                      onClick={() => handlePayoutPhotographer(payment)}
                                      disabled={!hasValidSaudiIban(payment.photographerBank?.iban)}
                                      className="p-1.5 rounded-lg hover:bg-green-100 text-green-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                      title={hasValidSaudiIban(payment.photographerBank?.iban) ? 'تأكيد الصرف للمصور' : 'IBAN غير متوفر'}
                                    >
                                      <Icon name="CheckCircle" size={16} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Summary Footer */}
                  {getFilteredPayments().length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-6">
                        <span className="text-slate-600">
                          إجمالي المعروض: <span className="font-bold text-slate-900">{formatNumber(getFilteredPayments().reduce((sum, p) => sum + p.amount, 0))} ر.س</span>
                        </span>
                        <span className="text-slate-600">
                          إجمالي العمولات: <span className="font-bold text-primary">{formatNumber(getFilteredPayments().reduce((sum, p) => sum + p.commission, 0))} ر.س</span>
                        </span>
                      </div>
                      <span className="text-slate-500">
                        عدد العمليات: {formatNumber(getFilteredPayments().length)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Financial Reports */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="BarChart3" size={20} className="text-primary" />
                    التقارير المالية
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Revenue by Photography Type */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <Icon name="Camera" size={16} />
                        الإيرادات حسب نوع التصوير
                      </h4>
                      <div className="space-y-2">
                        {payments && payments.length > 0 ? Object.entries(
                          (payments || []).reduce((acc, p) => {
                            const type = p?.photographyType || 'غير محدد';
                            acc[type] = (acc[type] || 0) + (p?.amount || 0);
                            return acc;
                          }, {})
                        ).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, amount]) => (
                          <div key={type} className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">{type}</span>
                            <span className="font-bold text-slate-900">{formatNumber(amount)} ر.س</span>
                          </div>
                        )) : (
                          <p className="text-sm text-slate-500 text-center py-4">لا توجد بيانات</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Revenue by City */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <Icon name="MapPin" size={16} />
                        الإيرادات حسب المدينة
                      </h4>
                      <div className="space-y-2">
                        {payments && payments.length > 0 ? Object.entries(
                          (payments || []).reduce((acc, p) => {
                            const city = p?.city || 'غير محدد';
                            acc[city] = (acc[city] || 0) + (p?.amount || 0);
                            return acc;
                          }, {})
                        ).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([city, amount]) => (
                          <div key={city} className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">{city}</span>
                            <span className="font-bold text-slate-900">{formatNumber(amount)} ر.س</span>
                          </div>
                        )) : (
                          <p className="text-sm text-slate-500 text-center py-4">لا توجد بيانات</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Top Photographers */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <Icon name="Award" size={16} />
                        أعلى المصورين دخلاً
                      </h4>
                      <div className="space-y-2">
                        {payments && payments.length > 0 ? Object.entries(
                          (payments || []).reduce((acc, p) => {
                            const name = p?.photographer?.full_name || 'غير معروف';
                            acc[name] = (acc[name] || 0) + (p?.photographerAmount || 0);
                            return acc;
                          }, {})
                        ).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, amount], index) => (
                          <div key={name} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                index === 1 ? 'bg-slate-200 text-slate-700' :
                                index === 2 ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {index + 1}
                              </span>
                              <span className="text-sm text-slate-600">{name}</span>
                            </div>
                            <span className="font-bold text-green-600">{formatNumber(amount)} ر.س</span>
                          </div>
                        )) : (
                          <p className="text-sm text-slate-500 text-center py-4">لا توجد بيانات</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Monthly Summary */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <Icon name="CalendarDays" size={16} />
                        ملخص الشهر الحالي
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">عدد الحجوزات المدفوعة</span>
                          <span className="font-bold text-slate-900">
                            {formatNumber((payments || []).filter(p => {
                              const now = new Date();
                              const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                              return new Date(p?.date) >= monthStart;
                            }).length)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">إجمالي الإيرادات</span>
                          <span className="font-bold text-slate-900">{formatNumber(paymentStats?.thisMonthRevenue || 0)} ر.س</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">عمولة المنصة</span>
                          <span className="font-bold text-primary">{formatNumber(paymentStats?.thisMonthCommissions || 0)} ر.س</span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                          <span className="text-sm text-slate-600">صافي المصورين</span>
                          <span className="font-bold text-green-600">
                            {formatNumber((paymentStats?.thisMonthRevenue || 0) - (paymentStats?.thisMonthCommissions || 0))} ر.س
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </>
                )}
              </div>
            ) : null}

            {/* Pricing */}
            {!loading && !error && active === "pricing" ? (
              <div className="space-y-6">
                {/* Important Notice */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 text-center">
                  <Icon name="AlertTriangle" size={48} className="text-amber-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-amber-800 mb-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    ⚠️ تنبيه مهم
                  </h3>
                  <p className="text-amber-700 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    لتعديل مضاعفات المدن والأسعار بشكل صحيح، يرجى استخدام صفحة <strong>التسعير الذكي</strong>
                  </p>
                  <Link
                    to="/admin-pricing"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-600 text-white hover:bg-amber-700 font-bold text-lg transition-colors"
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    <Icon name="Settings" size={20} />
                    افتح صفحة التسعير الذكي
                  </Link>
                </div>

                {pricingRules.length === 0 ? (
                <EmptyState
                  title="لا توجد قواعد تسعير قديمة"
                  desc="استخدم صفحة التسعير الذكي أعلاه لإدارة الأسعار بشكل صحيح."
                  action={null}
                />
              ) : (
                <>
                  <div className="bg-white rounded-2xl border border-slate-100 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>قواعد التسعير</h2>
                        <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          تعديل مباشر (DEV) عبر Service Role — الجدول الحالي: <span className="font-bold">{pricingTable || "غير معروف"}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          disabled={!canMutate || mutating || !pricingTable}
                          onClick={() => {
                            setPricingForm({ id: null, name: '', city: '', photography_type: '', base_price: '', multiplier: '1', is_active: true });
                            setPricingModalOpen(true);
                          }}
                          className={`px-3 py-2 rounded-xl text-sm font-bold ${(!canMutate || mutating || !pricingTable) ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                          style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                        >
                          + إضافة قاعدة
                        </button>
                        <Badge tone="purple">{pricingRules.length.toLocaleString("ar-SA")} قاعدة</Badge>
                      </div>
                    </div>
                  </div>

                  <Table
                    rowKey={(r) => r.id || `${r.city}-${r.photography_type}-${r.name}`}
                    columns={[
                      { key: "name", title: "الاسم", render: (r) => r.name || "—" },
                      { key: "city", title: "المدينة", render: (r) => r.city || "—" },
                      { key: "photography_type", title: "النوع", render: (r) => r.photography_type || "—" },
                      { key: "base_price", title: "سعر أساسي", render: (r) => (r.base_price != null ? money(r.base_price) : "—") },
                      { key: "multiplier", title: "المضاعف", render: (r) => (r.multiplier != null ? <Badge tone="blue">x{r.multiplier}</Badge> : "—") },
                      { key: "is_active", title: "التفعيل", render: (r) => (r.is_active === false ? <Badge tone="red">موقوف</Badge> : <Badge tone="green">مفعّل</Badge>) },
                      {
                        key: "actions",
                        title: "إجراءات",
                        render: (r) => (
                          <div className="flex items-center gap-2">
                            <button
                              disabled={!canMutate || mutating || !pricingTable}
                              onClick={() => {
                                setPricingForm({
                                  id: r.id || null,
                                  name: r.name || '',
                                  city: r.city || '',
                                  photography_type: r.photography_type || '',
                                  base_price: r.base_price ?? '',
                                  multiplier: r.multiplier ?? '1',
                                  is_active: r.is_active !== false,
                                });
                                setPricingModalOpen(true);
                              }}
                              className={`px-3 py-1 rounded-xl text-xs font-bold ${(!canMutate || mutating || !pricingTable) ? "bg-slate-200 text-slate-500" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                            >
                              تعديل
                            </button>
                            <button
                              disabled={!canMutate || mutating || !pricingTable || !r.id}
                              onClick={async () => {
                                if (!r.id) return;
                                try {
                                  setMutating(true);
                                  await adminService.deletePricingRule(pricingTable, r.id);
                                  setPricingRules((prev) => (prev || []).filter((x) => x.id !== r.id));
                                  setNotice("تم حذف قاعدة التسعير");
                                } catch (e) {
                                  setNotice(e?.message || "تعذر حذف القاعدة");
                                } finally {
                                  setMutating(false);
                                }
                              }}
                              className={`px-3 py-1 rounded-xl text-xs font-bold ${(!canMutate || mutating || !pricingTable || !r.id) ? "bg-slate-200 text-slate-500" : "bg-red-600 text-white hover:bg-red-700"}`}
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                            >
                              حذف
                            </button>
                          </div>
                        ),
                      },
                    ]}
                    rows={pricingRules}
                  />
                </>
              )}
              </div>
            ) : null}

            {/* Email Templates */}
            {!loading && !error && active === "email-templates" ? (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="Mail" size={22} className="text-primary" />
                        قوالب البريد الإلكتروني
                      </h2>
                      <p className="text-xs text-slate-600 mt-1">
                        تخصيص رسائل البريد الإلكتروني للعملاء والمصورين
                      </p>
                    </div>
                    <button
                      onClick={loadEmailTemplates}
                      disabled={emailTemplatesLoading}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold flex items-center gap-2"
                    >
                      <Icon name={emailTemplatesLoading ? "Loader" : "RefreshCw"} size={16} className={emailTemplatesLoading ? "animate-spin" : ""} />
                      تحديث
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Categories & Templates List */}
                  <div className="lg:col-span-1 space-y-4">
                    {/* Category Tabs */}
                    <div className="bg-white rounded-2xl border border-slate-100 p-4">
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(EMAIL_TEMPLATES_CONFIG).map(category => (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedTemplateCategory(category);
                              setSelectedTemplate(null);
                            }}
                            className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 ${
                              selectedTemplateCategory === category
                                ? 'bg-primary text-white'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            <Icon name={EMAIL_TEMPLATES_CONFIG[category].icon} size={16} />
                            {EMAIL_TEMPLATES_CONFIG[category].label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Templates List */}
                    <div className="bg-white rounded-2xl border border-slate-100 p-4">
                      <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <Icon name="FileText" size={16} className="text-primary" />
                        القوالب المتاحة
                      </h3>
                      
                      {emailTemplatesLoading ? (
                        <div className="text-center py-6">
                          <Icon name="Loader" size={24} className="animate-spin text-primary mx-auto" />
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                          {EMAIL_TEMPLATES_CONFIG[selectedTemplateCategory]?.templates.map(template => (
                            <button
                              key={template.key}
                              onClick={() => selectTemplate(template.key)}
                              className={`w-full text-right p-3 rounded-xl transition-colors ${
                                selectedTemplate === template.key
                                  ? 'bg-primary/10 border-2 border-primary'
                                  : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                              }`}
                            >
                              <p className="font-bold text-sm text-slate-900">{template.label}</p>
                              <p className="text-xs text-slate-500 mt-1">{template.description}</p>
                              {emailTemplates[template.key] && (
                                <span className="inline-flex items-center gap-1 mt-2 text-xs text-green-600">
                                  <Icon name="Check" size={12} />
                                  مُخصص
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Variables Guide */}
                    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
                      <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <Icon name="Code" size={16} className="text-primary" />
                        المتغيرات المتاحة
                      </h3>
                      <div className="space-y-1 text-xs max-h-[200px] overflow-y-auto">
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{customer_name}}'}</code> - اسم العميل</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{photographer_name}}'}</code> - اسم المصور</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{booking_id}}'}</code> - رقم الحجز</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{booking_date}}'}</code> - تاريخ الحجز</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{booking_time}}'}</code> - وقت الحجز</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{booking_location}}'}</code> - موقع الحجز</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{photography_type}}'}</code> - نوع التصوير</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{total_amount}}'}</code> - المبلغ الإجمالي</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{verification_link}}'}</code> - رابط التحقق</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{reset_link}}'}</code> - رابط استعادة كلمة المرور</p>
                        <p className="text-slate-600"><code className="bg-slate-200 px-1 rounded">{'{{download_link}}'}</code> - رابط التحميل</p>
                      </div>
                    </div>
                  </div>

                  {/* Template Editor */}
                  <div className="lg:col-span-2">
                    {selectedTemplate ? (
                      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div>
                            <h3 className="text-lg font-bold text-slate-900">
                              {EMAIL_TEMPLATES_CONFIG[selectedTemplateCategory]?.templates.find(t => t.key === selectedTemplate)?.label}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {EMAIL_TEMPLATES_CONFIG[selectedTemplateCategory]?.templates.find(t => t.key === selectedTemplate)?.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => setTemplatePreviewMode(!templatePreviewMode)}
                              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 ${
                                templatePreviewMode
                                  ? 'bg-primary text-white'
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              }`}
                            >
                              <Icon name={templatePreviewMode ? "Edit" : "Eye"} size={16} />
                              {templatePreviewMode ? 'تعديل' : 'معاينة'}
                            </button>
                            <button
                              onClick={() => { setTestEmailModalOpen(true); setTestEmailStatus(null); }}
                              className="px-4 py-2 rounded-xl bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center gap-2 hover:bg-emerald-200"
                            >
                              <Icon name="Send" size={16} />
                              إرسال تجريبي
                            </button>
                            <button
                              onClick={() => resetTemplateToDefault(selectedTemplate)}
                              className="px-4 py-2 rounded-xl bg-amber-100 text-amber-700 text-sm font-bold flex items-center gap-2 hover:bg-amber-200"
                            >
                              <Icon name="RotateCcw" size={16} />
                              استعادة الافتراضي
                            </button>
                          </div>
                        </div>

                        {templatePreviewMode ? (
                          /* Preview Mode */
                          <div className="space-y-4">
                            {/* Email subject bar */}
                            <div className="bg-slate-800 rounded-t-xl px-4 py-3 flex items-center gap-3">
                              <div className="flex gap-1.5">
                                <span className="w-3 h-3 rounded-full bg-red-400 inline-block" />
                                <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block" />
                                <span className="w-3 h-3 rounded-full bg-green-400 inline-block" />
                              </div>
                              <div className="flex-1 bg-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300 truncate text-right" dir="rtl">
                                <span className="text-slate-500 ml-2">الموضوع:</span>
                                {getTemplatePreview(editingTemplate.subject)}
                              </div>
                            </div>
                            {/* Rendered email body */}
                            <div className="border border-slate-200 rounded-b-xl overflow-hidden bg-slate-100">
                              <div
                                className="w-full"
                                style={{ minHeight: '300px' }}
                                dangerouslySetInnerHTML={{
                                  __html: (() => {
                                    const previewBody = getTemplatePreview(editingTemplate.body);
                                    const lines = previewBody.split('\n').map(line => {
                                      const t = line.trim();
                                      if (!t) return '<br>';
                                      if (t.startsWith('•') || t.startsWith('-')) return `<li style="margin:4px 0;">${t.replace(/^[•-]\s*/, '')}</li>`;
                                      return `<p style="margin:6px 0;">${t}</p>`;
                                    }).join('');
                                    return `<div dir="rtl" style="font-family:'Segoe UI',Tahoma,sans-serif;background:#f1f5f9;padding:24px 16px;min-height:300px;"><div style="max-width:560px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,.08);"><div style="background:linear-gradient(135deg,#2563eb,#1d4ed8);padding:24px;text-align:center;"><p style="color:rgba(255,255,255,.8);margin:0 0 4px;font-size:12px;">منصة مشهد للتصوير</p><h2 style="color:#fff;margin:0;font-size:18px;">${getTemplatePreview(editingTemplate.subject)}</h2></div><div style="padding:28px 32px;color:#334155;font-size:14px;line-height:1.9;">${lines}</div><div style="background:#f8fafc;padding:16px 32px;text-align:center;border-top:1px solid #e2e8f0;"><p style="color:#94a3b8;font-size:11px;margin:0;">© 2026 منصة مشهد · جميع الحقوق محفوظة</p></div></div></div>`;
                                  })()
                                }}
                              />
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                              <p className="text-xs text-blue-600 flex items-center gap-2">
                                <Icon name="Info" size={14} />
                                معاينة بيانات تجريبية — القيم الفعلية تُستبدل تلقائياً عند الإرسال.
                              </p>
                            </div>
                          </div>
                        ) : (
                          /* Edit Mode */
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                عنوان البريد (Subject)
                              </label>
                              <input
                                type="text"
                                value={editingTemplate.subject}
                                onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-right"
                                placeholder="عنوان البريد الإلكتروني..."
                                dir="rtl"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                نص الرسالة (Body)
                              </label>
                              <textarea
                                value={editingTemplate.body}
                                onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-right font-mono text-sm"
                                rows={15}
                                placeholder="نص الرسالة..."
                                dir="rtl"
                              />
                              <p className="text-xs text-slate-500 mt-2">
                                استخدم المتغيرات مثل {'{{customer_name}}'} لإدراج بيانات ديناميكية
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Save status message */}
                        {templateSaveStatus && (
                          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
                            templateSaveStatus === 'success'
                              ? 'bg-green-50 border border-green-200 text-green-700'
                              : 'bg-red-50 border border-red-200 text-red-700'
                          }`}>
                            <Icon name={templateSaveStatus === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
                            {templateSaveMessage}
                          </div>
                        )}

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                          <button
                            onClick={() => {
                              setSelectedTemplate(null);
                              setEditingTemplate({ subject: '', body: '' });
                              setTemplateSaveStatus(null);
                            }}
                            className="px-6 py-3 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200"
                          >
                            إلغاء
                          </button>
                          <button
                            onClick={() => saveEmailTemplate(selectedTemplate, editingTemplate)}
                            disabled={savingTemplate}
                            className="px-6 py-3 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark flex items-center gap-2 disabled:opacity-50"
                          >
                            <Icon name={savingTemplate ? "Loader" : "Save"} size={18} className={savingTemplate ? "animate-spin" : ""} />
                            {savingTemplate ? 'جاري الحفظ...' : 'حفظ القالب'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* No Template Selected */
                      <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                        <div className="bg-slate-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="MousePointerClick" size={32} className="text-slate-400" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-700 mb-2">اختر قالباً للتعديل</h3>
                        <p className="text-sm text-slate-500">
                          اختر قالباً من القائمة على اليمين لبدء التعديل
                        </p>
                      </div>
                    )}

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
                        <Icon name="User" size={20} className="opacity-80 mb-2" />
                        <p className="text-2xl font-bold">{EMAIL_TEMPLATES_CONFIG.customer.templates.length}</p>
                        <p className="text-xs opacity-75">قوالب العميل</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-4 text-white">
                        <Icon name="Camera" size={20} className="opacity-80 mb-2" />
                        <p className="text-2xl font-bold">{EMAIL_TEMPLATES_CONFIG.photographer.templates.length}</p>
                        <p className="text-xs opacity-75">قوالب المصور</p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-4 text-white">
                        <Icon name="Shield" size={20} className="opacity-80 mb-2" />
                        <p className="text-2xl font-bold">{EMAIL_TEMPLATES_CONFIG.admin.templates.length}</p>
                        <p className="text-xs opacity-75">إشعارات المشرف</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Bulk Notifications */}
            {!loading && !error && active === "bulk-notifications" ? (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="Bell" size={22} className="text-primary" />
                        إرسال إشعارات جماعية
                      </h2>
                      <p className="text-xs text-slate-600 mt-1">
                        إرسال رسائل وإشعارات لمجموعة من المستخدمين
                      </p>
                    </div>
                    <button
                      onClick={loadNotificationHistory}
                      disabled={notificationHistoryLoading}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold flex items-center gap-2"
                    >
                      <Icon name={notificationHistoryLoading ? "Loader" : "RefreshCw"} size={16} className={notificationHistoryLoading ? "animate-spin" : ""} />
                      تحديث
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Compose Notification */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Message Composition */}
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Icon name="Edit3" size={18} className="text-primary" />
                        تكوين الرسالة
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            عنوان الإشعار *
                          </label>
                          <input
                            type="text"
                            value={bulkNotification.title}
                            onChange={(e) => setBulkNotification(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="مثال: تحديث جديد في المنصة!"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-right"
                            dir="rtl"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            نص الرسالة *
                          </label>
                          <textarea
                            value={bulkNotification.message}
                            onChange={(e) => setBulkNotification(prev => ({ ...prev, message: e.target.value }))}
                            placeholder="اكتب نص الرسالة هنا..."
                            rows={5}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-right resize-none"
                            dir="rtl"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {bulkNotification.message.length} حرف
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Recipient Selection */}
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Icon name="Users" size={18} className="text-primary" />
                        المستلمون
                      </h3>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {RECIPIENT_FILTERS.map(filter => (
                          <button
                            key={filter.key}
                            onClick={() => setBulkNotification(prev => ({ ...prev, recipientFilter: filter.key }))}
                            className={`p-4 rounded-xl text-right transition-all ${
                              bulkNotification.recipientFilter === filter.key
                                ? 'bg-primary/10 border-2 border-primary'
                                : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                            }`}
                          >
                            <Icon name={filter.icon} size={20} className={bulkNotification.recipientFilter === filter.key ? 'text-primary' : 'text-slate-500'} />
                            <p className="font-bold text-sm mt-2">{filter.label}</p>
                            <p className="text-xs text-slate-500 mt-1">{filter.description}</p>
                          </button>
                        ))}
                      </div>

                      {/* Recipient Count Badge */}
                      <div className="mt-4 p-4 bg-slate-50 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="Users" size={24} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">عدد المستلمين</p>
                            <p className="text-2xl font-bold text-slate-900">
                              {bulkNotificationLoading ? (
                                <Icon name="Loader" size={24} className="animate-spin" />
                              ) : (
                                formatNumber(recipientCount)
                              )}
                            </p>
                          </div>
                        </div>
                        {bulkNotification.recipientFilter === 'custom' && (
                          <div className="flex flex-col items-end gap-1">
                            {selectedRecipients.length > 0 && (
                              <span className="text-xs text-slate-500">
                                تم تحديد {selectedRecipients.length} مستخدم
                              </span>
                            )}
                            <button
                              onClick={openCustomRecipientsModal}
                              className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                            >
                              <Icon name="UserCheck" size={15} color="white" />
                              {selectedRecipients.length > 0 ? 'تعديل التحديد' : 'اختيار يدوي'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Channels & Schedule */}
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Channels */}
                        <div>
                          <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Icon name="Send" size={18} className="text-primary" />
                            قنوات الإرسال
                          </h3>
                          
                          <div className="space-y-3">
                            <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                              bulkNotification.channels.inApp ? 'bg-green-50 border border-green-200' : 'bg-slate-50'
                            }`}>
                              <input
                                type="checkbox"
                                checked={bulkNotification.channels.inApp}
                                onChange={(e) => setBulkNotification(prev => ({
                                  ...prev,
                                  channels: { ...prev.channels, inApp: e.target.checked }
                                }))}
                                className="w-5 h-5 rounded text-primary"
                              />
                              <Icon name="Bell" size={20} className={bulkNotification.channels.inApp ? 'text-green-600' : 'text-slate-400'} />
                              <div>
                                <p className="font-bold text-sm">إشعار داخلي</p>
                                <p className="text-xs text-slate-500">يظهر في المنصة</p>
                              </div>
                            </label>
                            
                            <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                              bulkNotification.channels.push ? 'bg-purple-50 border border-purple-200' : 'bg-slate-50'
                            }`}>
                              <input
                                type="checkbox"
                                checked={bulkNotification.channels.push}
                                onChange={(e) => setBulkNotification(prev => ({
                                  ...prev,
                                  channels: { ...prev.channels, push: e.target.checked }
                                }))}
                                className="w-5 h-5 rounded text-primary"
                              />
                              <Icon name="Smartphone" size={20} className={bulkNotification.channels.push ? 'text-purple-600' : 'text-slate-400'} />
                              <div>
                                <p className="font-bold text-sm">إشعار Push</p>
                                <p className="text-xs text-slate-500">يصل للهاتف حتى لو التطبيق مغلق</p>
                              </div>
                            </label>

                            <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                              bulkNotification.channels.email ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'
                            }`}>
                              <input
                                type="checkbox"
                                checked={bulkNotification.channels.email}
                                onChange={(e) => setBulkNotification(prev => ({
                                  ...prev,
                                  channels: { ...prev.channels, email: e.target.checked }
                                }))}
                                className="w-5 h-5 rounded text-primary"
                              />
                              <Icon name="Mail" size={20} className={bulkNotification.channels.email ? 'text-blue-600' : 'text-slate-400'} />
                              <div>
                                <p className="font-bold text-sm">بريد إلكتروني</p>
                                <p className="text-xs text-slate-500">إرسال عبر Email</p>
                              </div>
                            </label>
                            
                            <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                              bulkNotification.channels.whatsapp ? 'bg-green-50 border border-green-200' : 'bg-slate-50'
                            }`}>
                              <input
                                type="checkbox"
                                checked={bulkNotification.channels.whatsapp}
                                onChange={(e) => setBulkNotification(prev => ({
                                  ...prev,
                                  channels: { ...prev.channels, whatsapp: e.target.checked }
                                }))}
                                className="w-5 h-5 rounded text-primary"
                              />
                              <Icon name="MessageCircle" size={20} className={bulkNotification.channels.whatsapp ? 'text-green-600' : 'text-slate-400'} />
                              <div>
                                <p className="font-bold text-sm">واتساب</p>
                                <p className="text-xs text-slate-500">إرسال عبر WhatsApp</p>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Schedule */}
                        <div>
                          <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Icon name="Clock" size={18} className="text-primary" />
                            وقت الإرسال
                          </h3>
                          
                          <div className="space-y-3">
                            <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer ${
                              bulkNotification.scheduleType === 'immediate' ? 'bg-primary/10 border border-primary' : 'bg-slate-50'
                            }`}>
                              <input
                                type="radio"
                                name="scheduleType"
                                checked={bulkNotification.scheduleType === 'immediate'}
                                onChange={() => setBulkNotification(prev => ({ ...prev, scheduleType: 'immediate' }))}
                                className="w-5 h-5 text-primary"
                              />
                              <div>
                                <p className="font-bold text-sm">إرسال فوري</p>
                                <p className="text-xs text-slate-500">إرسال الآن مباشرة</p>
                              </div>
                            </label>
                            
                            <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer ${
                              bulkNotification.scheduleType === 'scheduled' ? 'bg-primary/10 border border-primary' : 'bg-slate-50'
                            }`}>
                              <input
                                type="radio"
                                name="scheduleType"
                                checked={bulkNotification.scheduleType === 'scheduled'}
                                onChange={() => setBulkNotification(prev => ({ ...prev, scheduleType: 'scheduled' }))}
                                className="w-5 h-5 text-primary"
                              />
                              <div>
                                <p className="font-bold text-sm">جدولة لاحقة</p>
                                <p className="text-xs text-slate-500">تحديد وقت محدد</p>
                              </div>
                            </label>
                            
                            {bulkNotification.scheduleType === 'scheduled' && (
                              <div className="grid grid-cols-2 gap-3 mt-3">
                                <input
                                  type="date"
                                  value={bulkNotification.scheduledDate}
                                  onChange={(e) => setBulkNotification(prev => ({ ...prev, scheduledDate: e.target.value }))}
                                  min={new Date().toISOString().split('T')[0]}
                                  className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                                <input
                                  type="time"
                                  value={bulkNotification.scheduledTime}
                                  onChange={(e) => setBulkNotification(prev => ({ ...prev, scheduledTime: e.target.value }))}
                                  className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Preview & Send */}
                    <div className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl p-6 text-white">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <h3 className="font-bold text-lg">جاهز للإرسال؟</h3>
                          <p className="text-sm opacity-80">
                            سيتم إرسال الإشعار إلى {formatNumber(recipientCount)} مستلم
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setNotificationPreview(true)}
                            className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl font-bold flex items-center gap-2"
                          >
                            <Icon name="Eye" size={18} />
                            معاينة
                          </button>
                          <button
                            onClick={sendBulkNotification}
                            disabled={sendingNotification || recipientCount === 0 || !bulkNotification.title || !bulkNotification.message}
                            className="px-6 py-3 bg-white text-primary hover:bg-slate-100 rounded-xl font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name={sendingNotification ? "Loader" : "Send"} size={18} className={sendingNotification ? "animate-spin" : ""} />
                            {sendingNotification ? 'جاري الإرسال...' : 'إرسال الآن'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sidebar - History */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl border border-slate-100 p-4 sticky top-4">
                      <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Icon name="History" size={18} className="text-primary" />
                        سجل الإشعارات
                      </h3>
                      
                      {notificationHistoryLoading ? (
                        <div className="text-center py-8">
                          <Icon name="Loader" size={24} className="animate-spin text-primary mx-auto" />
                        </div>
                      ) : notificationHistory.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="Bell" size={32} className="text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">لا توجد إشعارات سابقة</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                          {notificationHistory.map(notification => (
                            <div key={notification.id} className="p-3 bg-slate-50 rounded-xl">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-sm text-slate-900 truncate">{notification.title}</p>
                                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{notification.message}</p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  notification.status === 'sent' ? 'bg-green-100 text-green-700' :
                                  notification.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                  notification.status === 'failed' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {notification.status === 'sent' ? 'تم الإرسال' :
                                   notification.status === 'scheduled' ? 'مجدول' :
                                   notification.status === 'failed' ? 'فشل' :
                                   notification.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Icon name="Users" size={12} />
                                  {formatNumber(notification.recipient_count || 0)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Icon name="Calendar" size={12} />
                                  {new Date(notification.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quick Stats */}
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                        <Icon name="CheckCircle" size={20} className="opacity-80" />
                        <p className="text-xl font-bold mt-2">
                          {formatNumber(notificationHistory.filter(n => n.status === 'sent').length)}
                        </p>
                        <p className="text-xs opacity-75">تم إرسالها</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                        <Icon name="Clock" size={20} className="opacity-80" />
                        <p className="text-xl font-bold mt-2">
                          {formatNumber(notificationHistory.filter(n => n.status === 'scheduled').length)}
                        </p>
                        <p className="text-xs opacity-75">مجدولة</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview Modal */}
                {notificationPreview && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg">معاينة الإشعار</h3>
                        <button onClick={() => setNotificationPreview(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                          <Icon name="X" size={20} />
                        </button>
                      </div>
                      
                      {/* Notification Preview */}
                      <div className="bg-slate-50 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="Bell" size={20} className="text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{bulkNotification.title || 'عنوان الإشعار'}</p>
                            <p className="text-sm text-slate-600 mt-1">{bulkNotification.message || 'نص الرسالة...'}</p>
                            <p className="text-xs text-slate-400 mt-2">الآن</p>
                          </div>
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">المستلمون:</span>
                          <span className="font-bold">{formatNumber(recipientCount)} مستخدم</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">القنوات:</span>
                          <span className="font-bold">
                            {[
                              bulkNotification.channels.inApp && 'داخلي',
                              bulkNotification.channels.push && 'Push',
                              bulkNotification.channels.email && 'بريد',
                              bulkNotification.channels.whatsapp && 'واتساب',
                            ].filter(Boolean).join('، ')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">التوقيت:</span>
                          <span className="font-bold">
                            {bulkNotification.scheduleType === 'immediate' ? 'فوري' : `${bulkNotification.scheduledDate} ${bulkNotification.scheduledTime}`}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => setNotificationPreview(false)}
                          className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
                        >
                          إغلاق
                        </button>
                        <button
                          onClick={() => {
                            setNotificationPreview(false);
                            sendBulkNotification();
                          }}
                          disabled={sendingNotification}
                          className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-dark flex items-center justify-center gap-2"
                        >
                          <Icon name="Send" size={16} />
                          إرسال
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Brand Settings */}
            {!loading && !error && active === "brand-settings" ? (
              <BrandSettingsTab />
            ) : null}

            {/* Pages */}
            {!loading && !error && active === "pages" ? (
              <PagesTab />
            ) : null}

            {/* Settings */}
            {!loading && !error && active === "settings" ? (
              <div className="space-y-6">
                {/* API Keys & Settings Management */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl border-2 border-blue-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <Icon name="Key" size={24} className="text-indigo-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          🔑 إدارة المفاتيح والإعدادات
                        </h2>
                        <p className="text-sm text-slate-600">إدارة مفاتيح API والإعدادات الأساسية للمنصة</p>
                      </div>
                    </div>
                    <button
                      onClick={handleSaveSettings}
                      disabled={savingSettings || settingsLoading}
                      className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:bg-slate-300 disabled:text-slate-500 transition-colors font-medium flex items-center gap-2"
                    >
                      {savingSettings ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <Icon name="Save" size={18} />
                          حفظ التغييرات
                        </>
                      )}
                    </button>
                  </div>

                  {settingsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                      <p className="text-slate-600">جاري تحميل الإعدادات...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Group settings by category */}
                      {['app', 'google', 'twilio'].map(category => {
                        const categorySettings = appSettings.filter(s => s.category === category);
                        if (categorySettings.length === 0) return null;
                        
                        return (
                          <div key={category} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                              <Icon name={getCategoryIcon(category)} size={18} className="text-slate-600" />
                              <h3 className="font-bold text-slate-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                {getCategoryLabel(category)}
                              </h3>
                            </div>
                            <div className="p-5 space-y-4">
                              {categorySettings.map(setting => (
                                <div key={setting.key} className="flex flex-col md:flex-row md:items-center gap-3">
                                  <div className="md:w-1/3">
                                    <label className="text-sm font-medium text-slate-700 block">
                                      {setting.description}
                                    </label>
                                    <code className="text-xs text-slate-500 mt-1 block">{setting.key}</code>
                                  </div>
                                  <div className="md:w-2/3 flex items-center gap-2">
                                    <input
                                      type={setting.is_secret ? 'password' : 'text'}
                                      value={editingSettings[setting.key] || ''}
                                      onChange={(e) => setEditingSettings(prev => ({
                                        ...prev,
                                        [setting.key]: e.target.value
                                      }))}
                                      placeholder={setting.is_secret ? '••••••••' : 'أدخل القيمة...'}
                                      className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all text-sm"
                                      dir="ltr"
                                    />
                                    {/* Show env status */}
                                    <div className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                                      getEnvStatus(setting.key) 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      <Icon name={getEnvStatus(setting.key) ? 'CheckCircle' : 'AlertCircle'} size={14} />
                                      {getEnvStatus(setting.key) ? 'مُعرّف' : 'غير مُعرّف'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Environment Variables Status (Read-only) */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Server" size={20} className="text-slate-600" />
                    <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      حالة متغيرات البيئة (Vercel)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    هذه المتغيرات مُعرّفة في Vercel ولا يمكن تعديلها من هنا. للتعديل، اذهب إلى لوحة Vercel → Settings → Environment Variables
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { key: 'SUPABASE_URL', label: 'Supabase URL', icon: 'Database' },
                      { key: 'SUPABASE_ANON_KEY', label: 'Supabase Anon Key', icon: 'Key' },
                      { key: 'SUPABASE_SERVICE_ROLE_KEY', label: 'Service Role Key', icon: 'Shield' },
                      { key: 'GOOGLE_MAPS_API_KEY', label: 'Google Maps API', icon: 'Map' },
                      { key: 'TWILIO_ACCOUNT_SID', label: 'Twilio Account SID', icon: 'MessageCircle' },
                      { key: 'TWILIO_AUTH_TOKEN', label: 'Twilio Auth Token', icon: 'Lock' },
                    ].map(item => (
                      <div key={item.key} className={`p-3 rounded-xl border flex items-center gap-3 ${
                        getEnvStatus(item.key) 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <Icon name={item.icon} size={18} className={getEnvStatus(item.key) ? 'text-green-600' : 'text-red-500'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.key}</p>
                        </div>
                        <Icon 
                          name={getEnvStatus(item.key) ? 'CheckCircle' : 'XCircle'} 
                          size={20} 
                          className={getEnvStatus(item.key) ? 'text-green-600' : 'text-red-500'} 
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Original Settings Cards */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>إعدادات المنصة (MVP)</h2>
                    <Icon name="Settings" size={18} color="#0f172a" />
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-2xl border bg-slate-50">
                      <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الوصول للوحة المشرف</div>
                      <p className="text-sm text-slate-600 mt-2 leading-6" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        حالياً: الدخول مباشر بدون بريد/كلمة مرور. لاحقاً يمكن إعادة تفعيل الحماية وربط جدول admin_users.
                      </p>
                      <div className="mt-3 text-xs text-slate-600">
                        VITE_ADMIN_BYPASS_AUTH = <code className="px-1">{String(import.meta?.env?.VITE_ADMIN_BYPASS_AUTH ?? "true")}</code>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border bg-slate-50">
                      <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>إشعارات واتساب</div>
                      <p className="text-sm text-slate-600 mt-2 leading-6" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        في الـ MVP: الإشعارات عبر واتساب فقط. يتم ضبط مزود الرسائل لاحقاً (مثل Twilio/WhatsApp Business API) حسب القرار النهائي.
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Badge tone="amber">قيد الإعداد</Badge>
                        <Badge tone="slate">بدون OTP حالياً</Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>روابط سريعة</h3>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <Link to="/admin-analytics-dashboard" className="p-4 rounded-2xl border bg-white hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تحليلات المنصة</div>
                          <div className="text-xs text-slate-600 mt-1">Bookings/Revenue/Performance</div>
                        </div>
                        <Icon name="ArrowLeft" size={18} color="#334155" />
                      </div>
                    </Link>
                    <Link to="/admin-master-dashboard" className="p-4 rounded-2xl border bg-white hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>لوحة الإدارة</div>
                          <div className="text-xs text-slate-600 mt-1">Metrics + Activity feed</div>
                        </div>
                        <Icon name="ArrowLeft" size={18} color="#334155" />
                      </div>
                    </Link>
                    <Link to="/browse-photographers" className="p-4 rounded-2xl border bg-white hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>واجهة الموقع</div>
                          <div className="text-xs text-slate-600 mt-1">تصفح المصورين</div>
                        </div>
                        <Icon name="ArrowLeft" size={18} color="#334155" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
          </div>{/* End Content Area wrapper */}
        </div>

        {/* Test Email Modal */}
        {testEmailModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Icon name="Send" size={18} className="text-emerald-600" />
                  إرسال إيميل تجريبي
                </h3>
                <button onClick={() => { setTestEmailModalOpen(false); setTestEmailStatus(null); }} className="p-2 rounded-xl hover:bg-slate-100">
                  <Icon name="X" size={18} className="text-slate-500" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-slate-600">
                  سيتم إرسال القالب الحالي <strong>"{EMAIL_TEMPLATES_CONFIG[selectedTemplateCategory]?.templates?.find(t => t.key === selectedTemplate)?.label}"</strong> إلى البريد الذي تحدده باستخدام بيانات تجريبية.
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">البريد الإلكتروني</label>
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 text-right"
                    placeholder="example@email.com"
                    dir="ltr"
                  />
                </div>
                {testEmailStatus && (
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
                    testEmailStatus === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    <Icon name={testEmailStatus === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
                    {testEmailStatus === 'success' ? 'تم الإرسال بنجاح! تحقق من صندوق الوارد.' : 'فشل الإرسال. تأكد من إعداد RESEND_API_KEY.'}
                  </div>
                )}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                  <strong>ملاحظة:</strong> يتطلب الإرسال الفعلي تهيئة متغير البيئة <code className="bg-amber-100 px-1 rounded">RESEND_API_KEY</code> في Supabase.
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button onClick={() => { setTestEmailModalOpen(false); setTestEmailStatus(null); }} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 text-sm">
                  إلغاء
                </button>
                <button
                  onClick={sendTestEmail}
                  disabled={sendingTestEmail || !testEmailAddress.trim()}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <Icon name={sendingTestEmail ? "Loader" : "Send"} size={16} className={sendingTestEmail ? "animate-spin" : ""} />
                  {sendingTestEmail ? 'جاري الإرسال...' : 'إرسال'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Recipients Modal */}
        {showCustomRecipientsModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '85vh' }}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                    اختيار المستلمين يدوياً
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {tempSelectedIds.length > 0 ? `تم تحديد ${tempSelectedIds.length} مستخدم` : 'لم يتم تحديد أي مستخدم بعد'}
                  </p>
                </div>
                <button
                  onClick={() => setShowCustomRecipientsModal(false)}
                  className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={18} color="#0f172a" />
                </button>
              </div>

              {/* Search + Select All */}
              <div className="px-6 py-3 border-b border-slate-100 shrink-0 space-y-2">
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={customRecipientsSearch}
                    onChange={(e) => setCustomRecipientsSearch(e.target.value)}
                    placeholder="ابحث بالاسم أو البريد الإلكتروني..."
                    className="w-full pr-9 pl-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                    dir="rtl"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      const filtered = allUsersForCustom.filter(u =>
                        !customRecipientsSearch ||
                        u.full_name?.toLowerCase().includes(customRecipientsSearch.toLowerCase()) ||
                        u.email?.toLowerCase().includes(customRecipientsSearch.toLowerCase())
                      );
                      setTempSelectedIds(prev => {
                        const filteredIds = filtered.map(u => u.id);
                        const allSelected = filteredIds.every(id => prev.includes(id));
                        if (allSelected) {
                          return prev.filter(id => !filteredIds.includes(id));
                        }
                        return [...new Set([...prev, ...filteredIds])];
                      });
                    }}
                    className="text-xs text-primary font-semibold hover:underline"
                  >
                    {(() => {
                      const filtered = allUsersForCustom.filter(u =>
                        !customRecipientsSearch ||
                        u.full_name?.toLowerCase().includes(customRecipientsSearch.toLowerCase()) ||
                        u.email?.toLowerCase().includes(customRecipientsSearch.toLowerCase())
                      );
                      const allSelected = filtered.length > 0 && filtered.every(u => tempSelectedIds.includes(u.id));
                      return allSelected ? 'إلغاء تحديد الكل' : 'تحديد الكل';
                    })()}
                  </button>
                  <span className="text-xs text-slate-400">
                    {allUsersForCustom.filter(u =>
                      !customRecipientsSearch ||
                      u.full_name?.toLowerCase().includes(customRecipientsSearch.toLowerCase()) ||
                      u.email?.toLowerCase().includes(customRecipientsSearch.toLowerCase())
                    ).length} مستخدم
                  </span>
                </div>
              </div>

              {/* Users List */}
              <div className="flex-1 overflow-y-auto px-4 py-3">
                {loadingCustomUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
                      <p className="text-sm text-slate-500">جاري تحميل المستخدمين...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {allUsersForCustom
                      .filter(u =>
                        !customRecipientsSearch ||
                        u.full_name?.toLowerCase().includes(customRecipientsSearch.toLowerCase()) ||
                        u.email?.toLowerCase().includes(customRecipientsSearch.toLowerCase())
                      )
                      .map(user => {
                        const isSelected = tempSelectedIds.includes(user.id);
                        return (
                          <label
                            key={user.id}
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/5 border border-primary/20' : 'hover:bg-slate-50 border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setTempSelectedIds(prev =>
                                  prev.includes(user.id)
                                    ? prev.filter(id => id !== user.id)
                                    : [...prev, user.id]
                                );
                              }}
                              className="w-4 h-4 rounded text-primary shrink-0"
                            />
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Icon name={user.role === 'photographer' ? 'Camera' : 'User'} size={16} className="text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900 truncate">
                                {user.full_name || 'بدون اسم'}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{user.email || '—'}</p>
                            </div>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                              user.role === 'photographer'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {user.role === 'photographer' ? 'مصور' : 'عميل'}
                            </span>
                          </label>
                        );
                      })}
                    {allUsersForCustom.filter(u =>
                      !customRecipientsSearch ||
                      u.full_name?.toLowerCase().includes(customRecipientsSearch.toLowerCase()) ||
                      u.email?.toLowerCase().includes(customRecipientsSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="text-center py-10 text-slate-400 text-sm">
                        لا توجد نتائج للبحث
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
                <button
                  onClick={() => setShowCustomRecipientsModal(false)}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmCustomRecipients}
                  disabled={tempSelectedIds.length === 0}
                  className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  <Icon name="Check" size={15} color="white" />
                  تأكيد تحديد {tempSelectedIds.length > 0 ? `(${tempSelectedIds.length})` : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Modal */}
        {pricingModalOpen ? (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl border border-slate-100 overflow-hidden" dir="rtl">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="font-extrabold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                  {pricingForm?.id ? "تعديل قاعدة تسعير" : "إضافة قاعدة تسعير"}
                </div>
                <button
                  onClick={() => setPricingModalOpen(false)}
                  className="w-9 h-9 rounded-xl hover:bg-slate-50 flex items-center justify-center"
                >
                  <Icon name="X" size={18} color="#0f172a" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الاسم</label>
                    <input
                      value={pricingForm.name}
                      onChange={(e) => setPricingForm((p) => ({ ...p, name: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="مثال: الرياض - زفاف - أساسي"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>المدينة</label>
                    <input
                      value={pricingForm.city}
                      onChange={(e) => setPricingForm((p) => ({ ...p, city: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="الرياض"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>نوع التصوير</label>
                    <input
                      value={pricingForm.photography_type}
                      onChange={(e) => setPricingForm((p) => ({ ...p, photography_type: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="زفاف / منتجات / بورتريه ..."
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>السعر الأساسي</label>
                    <input
                      value={pricingForm.base_price}
                      onChange={(e) => setPricingForm((p) => ({ ...p, base_price: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="1000"
                      inputMode="decimal"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>المضاعف</label>
                    <input
                      value={pricingForm.multiplier}
                      onChange={(e) => setPricingForm((p) => ({ ...p, multiplier: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="1.2"
                      inputMode="decimal"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    />
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={pricingForm.is_active !== false}
                        onChange={(e) => setPricingForm((p) => ({ ...p, is_active: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>مفعّل</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    onClick={() => setPricingModalOpen(false)}
                    className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-bold"
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    إلغاء
                  </button>
                  <button
                    disabled={!canMutate || mutating || !pricingTable}
                    onClick={async () => {
                      try {
                        setMutating(true);
                        const payload = {
                          id: pricingForm.id || undefined,
                          name: pricingForm.name || null,
                          city: pricingForm.city || null,
                          photography_type: pricingForm.photography_type || null,
                          base_price: pricingForm.base_price === '' ? null : Number(pricingForm.base_price),
                          multiplier: pricingForm.multiplier === '' ? null : Number(pricingForm.multiplier),
                          is_active: pricingForm.is_active !== false,
                        };
                        const saved = await adminService.upsertPricingRule(pricingTable, payload);
                        setPricingRules((prev) => {
                          const list = prev || [];
                          const idx = list.findIndex((x) => x.id === saved.id);
                          if (idx >= 0) {
                            const copy = [...list];
                            copy[idx] = saved;
                            return copy;
                          }
                          return [saved, ...list];
                        });
                        setPricingModalOpen(false);
                        setNotice(pricingForm.id ? "تم تحديث قاعدة التسعير" : "تم إضافة قاعدة تسعير جديدة");
                      } catch (e) {
                        setNotice(e?.message || "تعذر حفظ قاعدة التسعير");
                      } finally {
                        setMutating(false);
                      }
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-bold ${(!canMutate || mutating || !pricingTable) ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    حفظ
                  </button>
                </div>

                {!pricingTable ? (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    لا يوجد جدول تسعير متاح. تأكد من إنشاء <code className="px-1">pricing_rules</code> أو <code className="px-1">pricing_settings</code>.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* Booking Details Modal */}
        {/* Booking Details Modal - Enhanced */}
        {showBookingModal && selectedBooking && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowBookingModal(false)}>
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl border border-slate-100 overflow-hidden my-4" dir="rtl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-l from-slate-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon name="Calendar" size={20} color="#7c3aed" />
                  </div>
                  <div>
                    <div className="font-extrabold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      حجز #{String(selectedBooking.id).slice(0, 8).toUpperCase()}
                    </div>
                    <div className="text-xs text-slate-500">
                      أُنشئ في {formatDateTime(selectedBooking.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(selectedBooking.status)}
                  <button
                    onClick={() => setShowBookingModal(false)}
                    className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"
                  >
                    <Icon name="X" size={18} color="#0f172a" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Price Summary */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-600">السعر الإجمالي</div>
                      <div className="text-2xl font-bold text-slate-900">{money(selectedBooking.final_price_sar || selectedBooking.price || 0)}</div>
                    </div>
                    {(() => {
                      const { commission, rate } = getBookingCommissionBreakdown(selectedBooking, commissionSettings);
                      if (!commission) return null;
                      return (
                        <div className="text-left">
                          <div className="text-xs text-slate-500">عمولة مشهد ({rate}%)</div>
                          <div className="text-lg font-bold text-primary">{money(commission)}</div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Customer & Photographer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Customer */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="User" size={16} color="#64748b" />
                      <span className="text-sm font-bold text-slate-700">العميل</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                        {selectedBooking.customer?.avatar_url ? (
                          <img src={selectedBooking.customer.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Icon name="User" size={20} color="#64748b" />
                        )}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900">{selectedBooking.customer?.full_name || "غير محدد"}</div>
                        {selectedBooking.customer?.email && (
                          <div className="text-xs text-slate-600">{selectedBooking.customer.email}</div>
                        )}
                        {selectedBooking.customer?.phone && (
                          <div className="text-xs text-slate-600" dir="ltr">{selectedBooking.customer.phone}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Photographer */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Camera" size={16} color="#64748b" />
                      <span className="text-sm font-bold text-slate-700">المصور</span>
                    </div>
                    {selectedBooking.photographer ? (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                          {selectedBooking.photographer?.avatar_url ? (
                            <img src={selectedBooking.photographer.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Icon name="Camera" size={20} color="#64748b" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-slate-900">{selectedBooking.photographer?.full_name || "غير محدد"}</div>
                          {selectedBooking.photographer?.email && (
                            <div className="text-xs text-slate-600">{selectedBooking.photographer.email}</div>
                          )}
                          {selectedBooking.photographer?.phone && (
                            <div className="text-xs text-slate-600" dir="ltr">{selectedBooking.photographer.phone}</div>
                          )}
                        </div>
                      </div>
                    ) : selectedBooking.assigned_photographer_id ? (
                      <div className="text-amber-600 bg-amber-50 px-3 py-2 rounded-lg text-sm">
                        في انتظار تأكيد المصور
                      </div>
                    ) : (
                      <div className="text-slate-500 text-sm">لم يتم تعيين مصور بعد</div>
                    )}
                  </div>
                </div>
                
                {/* Booking Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">نوع التصوير</div>
                    <div className="font-semibold text-slate-900">{getPhotographyTypeLabelAr(selectedBooking.photography_type) || "—"}</div>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">المدينة</div>
                    <div className="font-semibold text-slate-900">{selectedBooking.city || "—"}</div>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">تاريخ الجلسة</div>
                    <div className="font-semibold text-slate-900">
                      {formatDate(selectedBooking.booking_date || selectedBooking.scheduled_at)}
                    </div>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">وقت الجلسة</div>
                    <div className="font-semibold text-slate-900">
                      {formatTime(selectedBooking.booking_time)}
                    </div>
                  </div>
                  
                  {selectedBooking.duration && (
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">مدة الجلسة المحجوزة</div>
                      <div className="font-semibold text-slate-900">{selectedBooking.duration}</div>
                    </div>
                  )}

                  {(selectedBooking.session_started_at || selectedBooking.session_ended_at) && (
                    <div className="col-span-2 md:col-span-4 bg-purple-50 border border-purple-200 rounded-xl p-4">
                      <div className="text-xs font-bold text-purple-800 mb-3">توثيق الجلسة الميدانية — للنزاعات</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <div className="text-xs text-purple-600 mb-1">بدء التوثيق (PIN)</div>
                          <div className="text-sm font-semibold text-slate-900">
                            {formatDateTime(selectedBooking.session_started_at)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-purple-600 mb-1">إنهاء الجلسة</div>
                          <div className="text-sm font-semibold text-slate-900">
                            {selectedBooking.session_ended_at
                              ? formatDateTime(selectedBooking.session_ended_at)
                              : 'لم تُنهَ بعد'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-purple-600 mb-1">المدة الفعلية</div>
                          <div className="text-sm font-bold text-purple-900">
                            {formatSessionDuration(selectedBooking.session_started_at, selectedBooking.session_ended_at)?.text
                              || (selectedBooking.session_started_at && !selectedBooking.session_ended_at ? 'جارية الآن' : '—')}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-purple-600 mb-1">المدة المحجوزة</div>
                          <div className="text-sm font-semibold text-slate-900">{selectedBooking.duration || '—'}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(() => {
                    const snap = selectedBooking.pricing_snapshot || {};
                    const methods = snap.shootingMethods || (selectedBooking.shooting_method ? [selectedBooking.shooting_method] : snap.shootingMethod ? [snap.shootingMethod] : []);
                    if (!methods.length) return null;
                    return (
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <div className="text-xs text-slate-500 mb-1">وسيلة التصوير</div>
                        <div className="font-semibold text-amber-800">
                          {methods.map(m => getShootingMethodLabelAr(m)).filter(Boolean).join('، ')}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {(selectedBooking.workload_bucket || selectedBooking.pricing_snapshot?.workloadBucket) && (
                    <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">حجم العمل</div>
                      <div className="font-semibold text-green-800">
                        {getWorkloadLabelAr(
                          selectedBooking.photography_type || selectedBooking.service_type,
                          selectedBooking.workload_bucket || selectedBooking.pricing_snapshot?.workloadBucket
                        )}
                      </div>
                    </div>
                  )}

                  {(selectedBooking.shooting_place_type || selectedBooking.pricing_snapshot?.shootingPlaceType) && (
                    <div className="bg-sky-50 border border-sky-100 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">مكان التصوير</div>
                      <div className="font-semibold text-sky-800">
                        {getShootingPlaceTypeLabelAr(selectedBooking.shooting_place_type || selectedBooking.pricing_snapshot?.shootingPlaceType)}
                      </div>
                    </div>
                  )}

                  {selectedBooking.pricing_snapshot?.experienceLevel && (
                    <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">مستوى الخبرة المطلوب</div>
                      <div className="font-semibold text-violet-800">
                        {selectedBooking.pricing_snapshot.experienceLevelLabel || (() => {
                          const map = { certified: 'معتمد', beginner: 'معتمد', intermediate: 'مميز', distinguished: 'مميز', advanced: 'مميز', elite: 'نخبة', expert: 'نخبة' };
                          return map[selectedBooking.pricing_snapshot.experienceLevel] || selectedBooking.pricing_snapshot.experienceLevel;
                        })()}
                      </div>
                    </div>
                  )}

                  {(selectedBooking.pricing_snapshot?.productPhysicalSizeBucket || selectedBooking.pricing_snapshot?.product_physical_size_bucket) && (
                    <div className="bg-teal-50 border border-teal-100 rounded-lg p-3 col-span-2">
                      <div className="text-xs text-slate-500 mb-1">حجم المنتجات</div>
                      <div className="font-semibold text-teal-800">
                        {getProductPhysicalSizeLabelAr(selectedBooking.pricing_snapshot?.productPhysicalSizeBucket || selectedBooking.pricing_snapshot?.product_physical_size_bucket)}
                      </div>
                    </div>
                  )}

                  {(() => {
                    const equip = Array.isArray(selectedBooking.pricing_snapshot?.equipment) ? selectedBooking.pricing_snapshot.equipment : [];
                    if (!equip.length) return null;
                    return (
                      <div className="col-span-2 md:col-span-4 bg-white border border-slate-200 rounded-lg p-3">
                        <div className="text-xs text-slate-500 mb-2">المعدات المطلوبة</div>
                        <div className="flex flex-wrap gap-1.5">
                          {equip.map((item, i) => (
                            <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">{item}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {(() => {
                    const snap = selectedBooking.pricing_snapshot || {};
                    const photosCount = snap.photosCount ?? snap.photos_count;
                    const videosCount = snap.videosCount ?? snap.videos_count;
                    const shortVideosCount = snap.shortVideosCount ?? snap.short_videos_count;
                    const deliverableLabel = snap.deliverableLabel ?? snap.deliverable_label;
                    const hasDeliverables = snap.deliverableMode !== 'unspecified' && (photosCount > 0 || videosCount > 0 || shortVideosCount > 0 || deliverableLabel);
                    if (!hasDeliverables) return null;
                    const label = deliverableLabel || [
                      photosCount > 0 ? `${photosCount} صورة` : null,
                      videosCount > 0 ? `${videosCount} فيديو طويل` : null,
                      shortVideosCount > 0 ? `${shortVideosCount} فيديو قصير` : null,
                    ].filter(Boolean).join(' + ');
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="text-xs text-slate-500 mb-1">عدد المخرجات المطلوبة</div>
                        <div className="font-semibold text-blue-800">{label}</div>
                      </div>
                    );
                  })()}
                  
                  {selectedBooking.attempt_count > 0 && (
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                      <div className="text-xs text-slate-500 mb-1">محاولات التعيين</div>
                      <div className="font-semibold text-slate-900">{selectedBooking.attempt_count} / 3</div>
                    </div>
                  )}
                </div>
                
                {/* Precise Location Section */}
                {(() => {
                  const locDetails = getLocationDetails(selectedBooking);
                  const mapUrl = locDetails ? getMapLink(locDetails.lat, locDetails.lng) : null;
                  
                  return (locDetails || selectedBooking.location) ? (
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="MapPin" size={16} color="#7c3aed" />
                        <span className="text-sm font-bold text-slate-700">الموقع الدقيق للجلسة</span>
                      </div>
                      
                      <div className="space-y-3">
                        {locDetails?.address_text && (
                          <div>
                            <div className="text-xs text-slate-500 mb-1">العنوان</div>
                            <div className="text-sm text-slate-900">{locDetails.address_text}</div>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                          {(locDetails?.city || selectedBooking.city) && (
                            <div>
                              <div className="text-xs text-slate-500 mb-1">المدينة</div>
                              <div className="text-sm font-medium text-slate-900">{locDetails?.city || selectedBooking.city}</div>
                            </div>
                          )}
                          {locDetails?.district && (
                            <div>
                              <div className="text-xs text-slate-500 mb-1">الحي</div>
                              <div className="text-sm font-medium text-slate-900">{locDetails.district}</div>
                            </div>
                          )}
                        </div>
                        
                        {locDetails?.lat && locDetails?.lng && (
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-slate-500">
                              الإحداثيات: {Number(locDetails.lat).toFixed(6)}, {Number(locDetails.lng).toFixed(6)}
                            </div>
                          </div>
                        )}
                        
                        {mapUrl && (
                          <a
                            href={mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
                          >
                            <Icon name="MapPin" size={16} color="white" />
                            فتح في خرائط جوجل
                            <Icon name="ExternalLink" size={14} color="white" />
                          </a>
                        )}
                        
                        {!locDetails && selectedBooking.location && (
                          <div>
                            <div className="text-xs text-slate-500 mb-1">الموقع</div>
                            <div className="text-sm text-slate-900">{selectedBooking.location}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null;
                })()}
                
                {getCustomerNote(selectedBooking) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="FileText" size={16} color="#d97706" />
                      <span className="text-sm font-bold text-amber-700">ملاحظات العميل</span>
                    </div>
                    <div className="text-sm text-amber-900">{getCustomerNote(selectedBooking)}</div>
                  </div>
                )}
                
                {/* Timestamps */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-200">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">تاريخ الإنشاء</div>
                    <div className="text-xs font-medium text-slate-700">{formatDateTime(selectedBooking.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">آخر تحديث</div>
                    <div className="text-xs font-medium text-slate-700">{formatDateTime(selectedBooking.updated_at)}</div>
                  </div>
                  {selectedBooking.hold_expires_at && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">انتهاء الانتظار</div>
                      <div className="text-xs font-medium text-slate-700">{formatDateTime(selectedBooking.hold_expires_at)}</div>
                    </div>
                  )}
                  {selectedBooking.payment_transaction_id && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">رقم المعاملة</div>
                      <div className="text-xs font-mono text-slate-700 truncate">{selectedBooking.payment_transaction_id}</div>
                    </div>
                  )}
                </div>
                
                {/* Admin Debug Section */}
                {(selectedBooking.pricing_snapshot?.workloadMultiplier || selectedBooking.pricing_snapshot?.shootingMethod) && (
                  <details className="border border-slate-200 rounded-lg">
                    <summary className="px-4 py-2 cursor-pointer text-xs font-semibold text-slate-600 hover:bg-slate-50">
                      معلومات تقنية (للمشرف)
                    </summary>
                    <div className="p-4 pt-2 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs border-t border-slate-100">
                      {selectedBooking.pricing_snapshot?.shootingMethod && (
                        <div>
                          <div className="text-slate-500 mb-1">shooting_method</div>
                          <div className="font-mono">{selectedBooking.pricing_snapshot.shootingMethod}</div>
                        </div>
                      )}
                      {selectedBooking.pricing_snapshot?.workloadBucket && (
                        <div>
                          <div className="text-slate-500 mb-1">workload_bucket</div>
                          <div className="font-mono">{selectedBooking.pricing_snapshot.workloadBucket}</div>
                        </div>
                      )}
                      {selectedBooking.pricing_snapshot?.workloadMultiplier && (
                        <div>
                          <div className="text-slate-500 mb-1">workload_multiplier</div>
                          <div className="font-mono">{selectedBooking.pricing_snapshot.workloadMultiplier}</div>
                        </div>
                      )}
                      {selectedBooking.pricing_snapshot?.deliveredPhotosBucket && (
                        <div>
                          <div className="text-slate-500 mb-1">delivered_photos_bucket</div>
                          <div className="font-mono">{selectedBooking.pricing_snapshot.deliveredPhotosBucket}</div>
                        </div>
                      )}
                      <div className="col-span-full">
                        <div className="text-slate-500 mb-1">booking_id</div>
                        <div className="font-mono text-xs break-all">{selectedBooking.id}</div>
                      </div>
                    </div>
                  </details>
                )}
              </div>
              
              {/* Modal Footer - Actions */}
              <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">تغيير الحالة:</span>
                  <select
                    value={bookingEdits?.[selectedBooking.id] ?? selectedBooking.status}
                    disabled={!canMutate || mutating}
                    onChange={(e) => setBookingEdits((prev) => ({ ...(prev || {}), [selectedBooking.id]: e.target.value }))}
                    className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    <option value="pending_photographer_confirmation">في انتظار تأكيد المصور</option>
                    <option value="awaiting_payment">في انتظار الدفع</option>
                    <option value="paid_confirmed">مدفوع ومؤكد</option>
                    <option value="completed">مكتمل</option>
                    <option value="declined">مرفوض</option>
                    <option value="expired">منتهي</option>
                    <option value="cancelled_by_customer">ملغي من العميل</option>
                    <option value="cancelled_by_admin">ملغي من المشرف</option>
                  </select>
                  <button
                    disabled={!canMutate || mutating || (bookingEdits?.[selectedBooking.id] ?? selectedBooking.status) === selectedBooking.status}
                    onClick={async () => {
                      try {
                        setMutating(true);
                        const newStatus = bookingEdits?.[selectedBooking.id] ?? selectedBooking.status;
                        await adminService.setBookingStatus(selectedBooking.id, newStatus);
                        setBookings((prev) => (prev || []).map((b) => (b.id === selectedBooking.id ? { ...b, status: newStatus } : b)));
                        setSelectedBooking((prev) => prev ? { ...prev, status: newStatus } : prev);
                        setNotice(`تم تحديث حالة الحجز #${String(selectedBooking.id).slice(0, 8)}`);
                        refreshData();
                      } catch (e) {
                        setNotice(e?.message || "تعذر تحديث حالة الحجز");
                      } finally {
                        setMutating(false);
                      }
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                      (!canMutate || mutating || (bookingEdits?.[selectedBooking.id] ?? selectedBooking.status) === selectedBooking.status)
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    {mutating ? "جاري الحفظ..." : "حفظ التغيير"}
                  </button>
                </div>
                
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-white text-sm font-semibold transition-colors"
                  style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Photographer Modal */}
        {showAddPhotographerModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setShowAddPhotographerModal(false); resetPhotographerWizard(); }}>
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl border border-slate-100 overflow-hidden my-8" dir="rtl" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <div className="font-extrabold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    تسجيل مصور جديد
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {[{n:1,l:'البيانات'},{n:2,l:'الصور والمعرض'},{n:3,l:'نطاق الخدمة'},{n:4,l:'الهوية'}].map(s => (
                      <div key={s.n} className="flex items-center gap-1">
                        <div className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${photographerWizardStep >= s.n ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>{s.n}</div>
                        <span className={`text-xs hidden sm:inline ${photographerWizardStep >= s.n ? 'text-slate-700 font-medium' : 'text-slate-400'}`} style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{s.l}</span>
                        {s.n < 4 && <div className={`w-5 h-0.5 mx-1 ${photographerWizardStep > s.n ? 'bg-slate-900' : 'bg-slate-200'}`} />}
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => { setShowAddPhotographerModal(false); resetPhotographerWizard(); }}
                  className="w-9 h-9 rounded-xl hover:bg-slate-50 flex items-center justify-center"
                >
                  <Icon name="X" size={18} color="#0f172a" />
                </button>
              </div>

              <div className="p-6 max-h-[80vh] overflow-y-auto">

                {/* ══════════════ الخطوة 1: البيانات ══════════════ */}
                {photographerWizardStep === 1 && (
                  <div className="space-y-6">
                    {/* البيانات الأساسية */}
                    <div>
                      <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <Icon name="User" size={16} color="#0f172a" /> البيانات الأساسية
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-slate-600 mb-1 block" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>الاسم الأول والأخير *</label>
                          <input value={photographerForm.full_name} onChange={(e) => setPhotographerForm(p => ({ ...p, full_name: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="محمد أحمد" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 mb-1 block" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>البريد الإلكتروني *</label>
                          <input type="email" value={photographerForm.email} onChange={(e) => setPhotographerForm(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="photographer@example.com" dir="ltr" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 mb-1 block" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>رقم الجوال * (للدخول لاحقاً)</label>
                          <input type="tel" value={photographerForm.phone} onChange={(e) => setPhotographerForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 15) }))} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="05xxxxxxxx" dir="ltr" inputMode="numeric" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 mb-1 block" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>المدينة *</label>
                          <select value={photographerForm.city} onChange={(e) => setPhotographerForm(p => ({ ...p, city: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40">
                            <option value="">اختر المدينة</option>
                            {['الرياض','جدة','الدمام','الخبر','المدينة المنورة','مكة المكرمة','الطائف','أبها','خميس مشيط','جازان','نجران','الباحة','تبوك','حائل','بريدة','سكاكا'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs text-slate-600 mb-2 block" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>نوع الحساب</label>
                          <div className="flex gap-3">
                            {[{v:'individual',l:'فرد'},{v:'entity',l:'استوديو'}].map(({v,l}) => (
                              <button key={v} type="button" onClick={() => setPhotographerForm(p => ({ ...p, entity_type: v, business_name: v==='individual' ? '' : p.business_name }))} className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${photographerForm.entity_type===v ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`} style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{l}</button>
                            ))}
                          </div>
                        </div>
                        {photographerForm.entity_type === 'entity' && (
                          <div className="md:col-span-2">
                            <label className="text-xs text-slate-600 mb-1 block" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اسم الاستوديو *</label>
                            <input value={photographerForm.business_name} onChange={(e) => setPhotographerForm(p => ({ ...p, business_name: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="استوديو التصوير" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* التخصصات */}
                    <div className="border-t border-slate-100 pt-5">
                      <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><Icon name="Camera" size={16} color="#0f172a" /> التخصصات *</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {ADMIN_WIZARD_SPECIALIZATIONS.map(spec => (
                          <button key={spec.value} type="button" onClick={() => { const cur=photographerForm.specializations||[]; setPhotographerForm(p => ({ ...p, specializations: cur.includes(spec.value) ? cur.filter(s=>s!==spec.value) : [...cur, spec.value] })); }} className={`p-2.5 rounded-xl border text-sm font-medium transition-all ${(photographerForm.specializations||[]).includes(spec.value) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:border-slate-400'}`} style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{spec.label}</button>
                        ))}
                      </div>
                    </div>

                    {/* وسائل التصوير */}
                    <div className="border-t border-slate-100 pt-5">
                      <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><Icon name="Aperture" size={16} color="#0f172a" /> وسائل التصوير *</h3>
                      <p className="text-xs text-slate-500 mb-3" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اختر وسيلة أو أكثر ثم أدخل نوع الجهاز</p>
                      <div className="space-y-3">
                        {[{value:'professional_camera',label:'كاميرا احترافية',placeholder:'مثال: Canon EOS R5, Sony A7 IV'},{value:'mobile_camera',label:'كاميرا جوال',placeholder:'مثال: iPhone 15 Pro, Samsung S24'},{value:'drone',label:'تصوير جوي (درون)',placeholder:'مثال: DJI Mini 4 Pro, Autel Evo'}].map(method => {
                          const sel = (photographerForm.shooting_methods||[]).includes(method.value);
                          return (
                            <div key={method.value}>
                              <button type="button" onClick={() => { if(sel){ const {[method.value]:_,...rest}=photographerForm.shooting_method_models||{}; setPhotographerForm(p=>({...p,shooting_methods:p.shooting_methods.filter(v=>v!==method.value),shooting_method_models:rest})); } else { setPhotographerForm(p=>({...p,shooting_methods:[...(p.shooting_methods||[]),method.value]})); } }} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${sel ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`} style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                                <span>{method.label}</span>
                                {sel && <Icon name="CheckCircle2" size={18} color="var(--color-primary)" />}
                              </button>
                              {sel && <input type="text" value={(photographerForm.shooting_method_models||{})[method.value]||''} onChange={(e) => setPhotographerForm(p=>({...p,shooting_method_models:{...(p.shooting_method_models||{}),[method.value]:e.target.value}}))} placeholder={method.placeholder} className="mt-1.5 w-full px-3 py-2 text-sm rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/40" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* مستوى التعديل */}
                    <div className="border-t border-slate-100 pt-5">
                      <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><Icon name="Wand2" size={16} color="#0f172a" /> مستوى التعديل *</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        {ADMIN_WIZARD_EQUIPMENT_LEVELS.map(eq => {
                          const sel=(photographerForm.equipment_available||[])[0]===eq.value;
                          return <button key={eq.value} type="button" onClick={() => setPhotographerForm(p=>({...p,equipment_available:[eq.value]}))} className={`p-3 rounded-xl border-2 text-right transition-all ${sel ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`} style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><div className="font-semibold text-sm text-slate-800">{eq.label}</div><div className="text-xs text-slate-500 mt-0.5">{eq.desc}</div></button>;
                        })}
                      </div>
                    </div>

                    {/* المعدات المتاحة */}
                    <div className="border-t border-slate-100 pt-5">
                      <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><Icon name="Package" size={16} color="#0f172a" /> المعدات المتاحة *</h3>
                      <div className="flex gap-2 flex-wrap">
                        {['عدسة احترافية','مانع اهتزاز','إضاءة استديو','خلفيات تصوير','مايكروفون'].map(eq => {
                          const sel=(photographerForm.languages||[]).includes(eq);
                          return <button key={eq} type="button" onClick={() => { const cur=photographerForm.languages||[]; setPhotographerForm(p=>({...p,languages:sel?cur.filter(l=>l!==eq):[...cur,eq]})); }} className={`px-3 py-1.5 rounded-full border text-sm transition-all ${sel ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`} style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{eq}</button>;
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* ══════════════ الخطوة 2: الصورة والمعرض ══════════════ */}
                {photographerWizardStep === 2 && (
                  <div className="space-y-6">
                    {/* صورة شخصية */}
                    <div>
                      <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><Icon name="UserCircle" size={16} color="#0f172a" /> الصورة الشخصية *</h3>
                      <p className="text-xs text-slate-500 mb-3" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تُستخدم في ملف المصور وتُحسب لاكتمال الملف الشخصي</p>
                      <div className="flex items-center gap-4">
                        {adminAvatarPreview ? (
                          <div className="relative">
                            <img src={adminAvatarPreview} alt="avatar" className="w-20 h-20 rounded-2xl object-cover border-2 border-primary/30" />
                            <button type="button" onClick={() => { setAdminAvatarFile(null); setAdminAvatarPreview(null); }} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center"><Icon name="X" size={12} color="white" /></button>
                          </div>
                        ) : (
                          <div onClick={() => adminAvatarInputRef.current?.click()} className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-slate-50 transition-all">
                            <Icon name="Plus" size={20} color="#64748b" />
                            <span className="text-xs text-slate-500 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>رفع</span>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-slate-700 font-medium" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اختر صورة شخصية واضحة</p>
                          <p className="text-xs text-slate-400 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>JPEG, PNG, WebP — الحد الأقصى 5MB</p>
                        </div>
                      </div>
                      <input ref={adminAvatarInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f=e.target.files?.[0]; if(!f) return; if(!f.type.startsWith('image/')) return; if(f.size>5*1024*1024) return; setAdminAvatarFile(f); setAdminAvatarPreview(URL.createObjectURL(f)); }} />
                    </div>

                    {/* معرض الأعمال */}
                    <div className="border-t border-slate-100 pt-5">
                      <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}><Icon name="Images" size={16} color="#0f172a" /> معرض الأعمال <span className="text-red-500">*</span> ({adminPortfolioItems.length}/3 على الأقل)</h3>
                      <p className="text-xs text-slate-500 mb-3" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>أضف 3 صور على الأقل لتكتمل نسبة الملف الشخصي — الحد الأقصى 8 صور</p>
                      {adminPortfolioItems.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {adminPortfolioItems.map((item, i) => (
                            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200">
                              <img src={item.preview} alt="" className="w-full h-full object-cover" />
                              <button type="button" onClick={() => { URL.revokeObjectURL(item.preview); setAdminPortfolioItems(prev => prev.filter((_,idx)=>idx!==i)); }} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center"><Icon name="X" size={10} color="white" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      {adminPortfolioItems.length < 8 && (
                        <div onClick={() => adminPortfolioInputRef.current?.click()} className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-slate-50 transition-all">
                          <Icon name="Upload" size={20} color="#94a3b8" className="mx-auto mb-1" />
                          <p className="text-sm text-slate-500" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اختر صور المعرض (يمكن تعدد)</p>
                          <p className="text-xs text-slate-400" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>JPEG, PNG — الحد الأقصى 10MB للصورة</p>
                        </div>
                      )}
                      <input ref={adminPortfolioInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                        const files = Array.from(e.target.files||[]).filter(f => f.type.startsWith('image/') && f.size <= 10*1024*1024);
                        const newItems = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
                        setAdminPortfolioItems(prev => { const combined=[...prev,...newItems]; return combined.length>8 ? combined.slice(0,8) : combined; });
                        if(adminPortfolioInputRef.current) adminPortfolioInputRef.current.value='';
                      }} />
                    </div>
                  </div>
                )}

                {/* ══════════════ الخطوة 3: نطاق الخدمة ══════════════ */}
                {photographerWizardStep === 3 && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 mb-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>نطاق الخدمة</h3>
                      <p className="text-sm text-slate-500" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>حدد المنطقة التي يقدم فيها خدماته — ضمن {photographerForm.city || 'المدينة المحددة'}</p>
                    </div>

                    {/* أزرار تحديد الموقع */}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setAdminMapVisible(v => !v)}
                        className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition flex items-center justify-center gap-2"
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        <Icon name="Map" size={18} color="#0f172a" />
                        {adminMapVisible ? 'إخفاء الخريطة' : 'تحديد على الخريطة'}
                      </button>
                      <button
                        type="button"
                        disabled={adminSavingService}
                        onClick={async () => {
                          if (!isGeolocationSupported()) return;
                          setAdminSavingService(true);
                          try {
                            const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
                            const { latitude: lat, longitude: lng } = pos.coords;
                            const cityCoords = ADMIN_CITY_COORDS[photographerForm.city] || ADMIN_CITY_COORDS['الرياض'];
                            const dist = calculateDistance(Number(cityCoords.lat), Number(cityCoords.lng), lat, lng);
                            if (!isNaN(dist) && dist > 100) return;
                            adminServiceCenterRef.current = { lat, lng };
                            setAdminServiceCenter({ lat, lng });
                            setAdminServiceLabel(photographerForm.city || '');
                            setAdminMapVisible(false);
                          } catch (_) {}
                          finally { setAdminSavingService(false); }
                        }}
                        className="flex-1 py-3 bg-primary/10 text-primary font-semibold rounded-xl hover:bg-primary/20 transition disabled:opacity-50 flex items-center justify-center gap-2"
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        {adminSavingService
                          ? <Icon name="Loader" size={18} color="var(--color-primary)" className="animate-spin" />
                          : <Icon name="Navigation" size={18} color="var(--color-primary)" />}
                        موقعي الحالي
                      </button>
                    </div>

                    {/* الخريطة inline */}
                    {adminMapVisible && (
                      <LocationPickerMap
                        key={`admin-map-${adminServiceCenter.lat || 'default'}`}
                        initialCenter={
                          adminServiceCenter.lat && adminServiceCenter.lng
                            ? { lat: adminServiceCenter.lat, lng: adminServiceCenter.lng }
                            : ADMIN_CITY_COORDS[photographerForm.city] || { lat: 24.7136, lng: 46.6753 }
                        }
                        onLocationSelect={(loc) => {
                          const { lat, lng } = loc;
                          const cityCoords = ADMIN_CITY_COORDS[photographerForm.city] || ADMIN_CITY_COORDS['الرياض'];
                          const dist = calculateDistance(Number(cityCoords.lat), Number(cityCoords.lng), Number(lat), Number(lng));
                          if (!isNaN(dist) && dist > 100) return;
                          adminServiceCenterRef.current = { lat, lng };
                          setAdminServiceCenter({ lat, lng });
                          setAdminServiceLabel(photographerForm.city || '');
                          setAdminMapVisible(false);
                        }}
                        onCancel={() => setAdminMapVisible(false)}
                      />
                    )}

                    {/* بطاقة نجاح الموقع */}
                    {adminServiceCenter.lat && (
                      <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="MapPin" size={20} color="#16a34a" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-green-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تم تحديد الموقع</p>
                          {adminServiceLabel && <p className="text-xs text-green-600 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{adminServiceLabel}</p>}
                        </div>
                      </div>
                    )}

                    {/* شريط نطاق التغطية — يظهر فقط بعد تحديد موقع */}
                    {adminServiceCenter.lat && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          نطاق الخدمة: <span className="text-primary">{adminServiceRadius} كم</span>
                        </label>
                        <input
                          type="range" min={5} max={50} step={5}
                          value={adminServiceRadius}
                          onChange={(e) => setAdminServiceRadius(Number(e.target.value))}
                          className="w-full accent-primary"
                        />
                        <div className="flex justify-between text-xs text-slate-400 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          <span>5 كم</span><span>50 كم</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ══════════════ الخطوة 4: توثيق الهوية ══════════════ */}
                {photographerWizardStep === 4 && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 mb-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>توثيق الهوية</h3>
                      <p className="text-sm text-slate-500" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        ارفع صورة الهوية الوطنية أو جواز السفر. يمكن رفع التصاريح والشهادات المهنية لاحقاً من قسم التحقق في لوحة المصور.
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 flex items-start gap-3">
                      <Icon name="Info" size={18} className="shrink-0 mt-0.5" color="#1d4ed8" />
                      <p style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>التوثيق يتيح ظهور المصور في نتائج البحث بعد مراجعة المشرف. يمكن رفعه الآن أو لاحقاً.</p>
                    </div>

                    {adminIdUploaded ? (
                      <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="CheckCircle" size={22} color="#16a34a" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-green-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تم رفع الهوية بنجاح</p>
                          <p className="text-xs text-green-600 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>سيتم مراجعتها من قِبل المشرف خلال 24-48 ساعة</p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div
                          onClick={() => !adminIdUploading && adminIdInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-xl p-6 text-center transition cursor-pointer ${
                            adminIdUploading ? 'border-primary bg-primary/5 cursor-wait' :
                            adminIdFile ? 'border-primary bg-primary/5' :
                            'border-slate-200 hover:border-primary hover:bg-primary/5'
                          }`}
                        >
                          {adminIdUploading ? (
                            <>
                              <Icon name="Loader" size={36} color="var(--color-primary)" className="mx-auto mb-2 animate-spin" />
                              <p className="text-sm font-medium text-primary" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>جاري رفع الهوية...</p>
                            </>
                          ) : adminIdFile ? (
                            <>
                              <Icon name="FileCheck" size={36} color="var(--color-primary)" className="mx-auto mb-2" />
                              <p className="text-sm font-medium text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{adminIdFile.name}</p>
                              <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اضغط لتغيير الملف</p>
                            </>
                          ) : (
                            <>
                              <Icon name="FileUp" size={36} color="#cbd5e1" className="mx-auto mb-2" />
                              <p className="text-sm font-medium text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اضغط لاختيار ملف الهوية</p>
                              <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>JPEG, PNG, WebP أو PDF — الحد الأقصى 10MB</p>
                            </>
                          )}
                        </div>
                        <input
                          ref={adminIdInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setAdminIdFile(file);
                            await adminUploadIdDocument(file);
                          }}
                        />
                      </div>
                    )}

                    {photographerIsIndividual(photographerForm.entity_type) && (
                      <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Icon name="Briefcase" size={18} color="#4f46e5" className="shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-indigo-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>وثيقة العمل الحر</p>
                            <p className="text-xs text-indigo-600 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>يرجى رفع وثيقة العمل الحر الصادرة من الجهة المختصة</p>
                          </div>
                        </div>

                        {adminFreelancePermitUrl ? (
                          <div className={`flex items-center gap-2 rounded-lg p-3 border ${
                            adminFreelancePermitStatus === 'approved' ? 'bg-green-50 border-green-200' :
                            adminFreelancePermitStatus === 'rejected' ? 'bg-red-50 border-red-200' :
                            'bg-blue-50 border-blue-200'
                          }`}>
                            <Icon
                              name={adminFreelancePermitStatus === 'approved' ? 'CheckCircle' : adminFreelancePermitStatus === 'rejected' ? 'XCircle' : 'Clock'}
                              size={18}
                              color={
                                adminFreelancePermitStatus === 'approved' ? '#16a34a' :
                                adminFreelancePermitStatus === 'rejected' ? '#dc2626' :
                                '#2563eb'
                              }
                              className="shrink-0"
                            />
                            <p className="text-sm font-medium text-slate-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                              {getFreelancePermitStatusLabel(adminFreelancePermitStatus)}
                            </p>
                            <button
                              type="button"
                              onClick={() => !adminFreelanceUploading && adminFreelanceInputRef.current?.click()}
                              className="mr-auto text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 border-0 bg-transparent cursor-pointer"
                              style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                            >
                              استبدال
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => !adminFreelanceUploading && adminFreelanceInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-4 text-center transition cursor-pointer ${
                              adminFreelanceUploading ? 'border-indigo-400 bg-indigo-50 cursor-wait' :
                              adminFreelanceFile ? 'border-indigo-400 bg-indigo-50' :
                              'border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50'
                            }`}
                          >
                            {adminFreelanceUploading ? (
                              <>
                                <Icon name="Loader" size={28} color="#6366f1" className="mx-auto mb-1 animate-spin" />
                                <p className="text-sm font-medium text-indigo-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>جاري رفع الوثيقة...</p>
                              </>
                            ) : adminFreelanceFile ? (
                              <>
                                <Icon name="FileCheck" size={28} color="#6366f1" className="mx-auto mb-1" />
                                <p className="text-sm font-medium text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{adminFreelanceFile.name}</p>
                                <p className="text-xs text-slate-400 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اضغط لتغيير الملف</p>
                              </>
                            ) : (
                              <>
                                <Icon name="FileUp" size={28} color="#a5b4fc" className="mx-auto mb-1" />
                                <p className="text-sm font-medium text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اضغط لرفع وثيقة العمل الحر</p>
                                <p className="text-xs text-slate-400 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>JPEG, PNG, WebP أو PDF — الحد الأقصى 10MB</p>
                              </>
                            )}
                          </div>
                        )}
                        <input
                          ref={adminFreelanceInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setAdminFreelanceFile(file);
                            await adminUploadFreelancePermit(file);
                          }}
                        />
                      </div>
                    )}

                    {(photographerForm.shooting_methods || []).includes('drone') && (
                      <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Icon name="FileText" size={18} color="#d97706" className="shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تصريح الطائرة المسيّرة (درون)</p>
                            <p className="text-xs text-amber-600 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>المصور يستخدم الدرون — يرجى رفع تصريح التشغيل</p>
                          </div>
                        </div>
                        {adminDroneUploaded ? (
                          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                            <Icon name="CheckCircle" size={18} color="#16a34a" className="shrink-0" />
                            <p className="text-sm font-medium text-green-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>تم رفع التصريح بنجاح</p>
                          </div>
                        ) : (
                          <div
                            onClick={() => !adminDroneUploading && adminDroneInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-4 text-center transition cursor-pointer ${
                              adminDroneUploading ? 'border-amber-400 bg-amber-50 cursor-wait' :
                              adminDroneFile ? 'border-amber-400 bg-amber-50' :
                              'border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                            }`}
                          >
                            {adminDroneUploading ? (
                              <>
                                <Icon name="Loader" size={28} color="#d97706" className="mx-auto mb-1 animate-spin" />
                                <p className="text-sm font-medium text-amber-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>جاري رفع التصريح...</p>
                              </>
                            ) : adminDroneFile ? (
                              <>
                                <Icon name="FileCheck" size={28} color="#d97706" className="mx-auto mb-1" />
                                <p className="text-sm font-medium text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>{adminDroneFile.name}</p>
                              </>
                            ) : (
                              <>
                                <Icon name="FileUp" size={28} color="#fcd34d" className="mx-auto mb-1" />
                                <p className="text-sm font-medium text-slate-700" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>اضغط لرفع التصريح</p>
                                <p className="text-xs text-slate-400 mt-0.5" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>JPEG, PNG, WebP أو PDF — الحد الأقصى 10MB</p>
                              </>
                            )}
                          </div>
                        )}
                        <input
                          ref={adminDroneInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setAdminDroneFile(file);
                            await adminUploadDronePermit(file);
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* ── أزرار التنقل بين خطوات الـ wizard ── */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200 gap-2">
                  {/* زر رجوع / إلغاء */}
                  <button
                    type="button"
                    onClick={() => {
                      if (photographerWizardStep === 1) { setShowAddPhotographerModal(false); resetPhotographerWizard(); }
                      else setPhotographerWizardStep(s => s - 1);
                    }}
                    className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-bold flex items-center gap-1"
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    {photographerWizardStep === 1 ? 'إلغاء' : <><Icon name="ChevronRight" size={15} color="#0f172a" /> رجوع</>}
                  </button>

                  {/* زر التالي / حفظ */}
                  {photographerWizardStep === 1 && (
                    <button
                      type="button"
                      disabled={
                        !canMutate || mutating ||
                        !photographerForm.full_name.trim() || !photographerForm.email.trim() || !photographerForm.phone.trim() ||
                        !normalizePhone(photographerForm.phone) ||
                        !photographerForm.city || (photographerForm.specializations||[]).length===0 ||
                        (photographerForm.shooting_methods||[]).length===0 ||
                        (photographerForm.equipment_available||[]).length===0 ||
                        (photographerForm.languages||[]).length===0
                      }
                      onClick={async () => {
                        try {
                          setMutating(true);
                          const normalizedPhone = normalizePhone(photographerForm.phone);
                          if (!normalizedPhone) {
                            setNotice('رقم الجوال غير صالح');
                            return;
                          }
                          // إذا لم يُنشأ الحساب بعد
                          if (!photographerWizardUserId) {
                            const result = await adminService.createPhotographer({
                              full_name: photographerForm.full_name.trim(),
                              email: photographerForm.email.trim().toLowerCase(),
                              phone: normalizedPhone,
                              entity_type: photographerForm.entity_type || 'individual',
                              business_name: photographerForm.entity_type === 'entity' ? photographerForm.business_name : null,
                              city: photographerForm.city,
                              specializations: mapAdminSpecializationsToLabels(photographerForm.specializations),
                              shooting_methods: photographerForm.shooting_methods,
                              shooting_method_models: photographerForm.shooting_method_models || {},
                              equipment_available: mapAdminEquipmentToLabels(photographerForm.equipment_available),
                              languages: photographerForm.languages || [],
                            });
                            setPhotographerWizardUserId(result.user?.id);
                            setPhotographerForm(p => ({ ...p, phone: normalizedPhone }));
                            setNotice('تم إنشاء حساب المصور بنجاح');
                          }
                          setPhotographerWizardStep(2);
                        } catch (e) {
                          setNotice(e?.message || "تعذر إنشاء الحساب");
                        } finally {
                          setMutating(false);
                        }
                      }}
                      className={`px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-1 ${(!canMutate || mutating || !photographerForm.full_name.trim() || !photographerForm.email.trim() || !photographerForm.phone.trim() || !normalizePhone(photographerForm.phone) || !photographerForm.city || (photographerForm.specializations||[]).length===0 || (photographerForm.shooting_methods||[]).length===0 || (photographerForm.equipment_available||[]).length===0 || (photographerForm.languages||[]).length===0) ? 'bg-slate-200 text-slate-400' : 'bg-slate-900 text-white hover:bg-slate-700'}`}
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    >
                      {mutating ? 'جاري الإنشاء...' : <>{photographerWizardUserId ? 'التالي' : 'إنشاء الحساب والتالي'} <Icon name="ChevronLeft" size={15} color="currentColor" /></>}
                    </button>
                  )}

                  {photographerWizardStep === 2 && (
                    <button
                      type="button"
                      disabled={adminUploadingFiles}
                      onClick={async () => {
                        if (adminPortfolioItems.length < 3) { setNotice('يرجى إضافة 3 صور على الأقل للمعرض'); return; }
                        const uid = photographerWizardUserId;
                        if (!uid) { setNotice('خطأ: لم يُنشأ الحساب بعد. عد للخطوة الأولى.'); return; }
                        setAdminUploadingFiles(true);
                        try {
                          const portfolioCategory = mapAdminSpecializationsToLabels(photographerForm.specializations)?.[0]
                            || photographerForm.specializations?.[0]
                            || 'عام';
                          if (adminAvatarFile) {
                            await adminService.uploadPhotographerWizardAvatar({
                              userId: uid,
                              file: adminAvatarFile,
                            });
                          }
                          for (const item of adminPortfolioItems) {
                            await adminService.uploadPhotographerWizardPortfolioItem({
                              userId: uid,
                              file: item.file,
                              category: portfolioCategory,
                            });
                          }
                          setNotice('تم رفع الصور بنجاح');
                          setPhotographerWizardStep(3);
                        } catch (err) {
                          setNotice(err?.message || 'فشل رفع الصور');
                        } finally {
                          setAdminUploadingFiles(false);
                        }
                      }}
                      className={`px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-1 ${adminUploadingFiles ? 'bg-slate-200 text-slate-400' : 'bg-slate-900 text-white hover:bg-slate-700'}`}
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    >
                      {adminUploadingFiles ? 'جاري الرفع...' : <>التالي: نطاق الخدمة <Icon name="ChevronLeft" size={15} color="currentColor" /></>}
                    </button>
                  )}

                  {photographerWizardStep === 3 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPhotographerWizardStep(4)}
                        className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm text-slate-500"
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        تخطي الآن
                      </button>
                      <button
                        type="button"
                        disabled={adminSavingService || !adminServiceCenter.lat}
                        onClick={async () => {
                          const uid = photographerWizardUserId;
                          if (!uid || !adminServiceCenter.lat) return;
                          setAdminSavingService(true);
                          try {
                            await supabase.from('photographer_profiles').update({
                              service_center_lat: adminServiceCenter.lat,
                              service_center_lng: adminServiceCenter.lng,
                              service_radius_km: adminServiceRadius,
                              service_area_label: adminServiceLabel || null,
                              updated_at: new Date().toISOString(),
                            }).eq('user_id', uid);
                            setPhotographerWizardStep(4);
                          } catch (err) {
                            setNotice(err?.message || 'فشل حفظ نطاق الخدمة');
                          } finally {
                            setAdminSavingService(false);
                          }
                        }}
                        className={`px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-1 ${(!adminServiceCenter.lat || adminSavingService) ? 'bg-slate-200 text-slate-400' : 'bg-slate-900 text-white hover:bg-slate-700'}`}
                        style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                      >
                        {adminSavingService ? 'جاري الحفظ...' : <>حفظ والمتابعة <Icon name="ChevronLeft" size={15} color="currentColor" /></>}
                      </button>
                    </div>
                  )}

                  {photographerWizardStep === 4 && (
                    <button
                      type="button"
                      onClick={adminFinishPhotographerWizard}
                      className="px-5 py-2 rounded-xl text-sm font-bold bg-green-600 text-white hover:bg-green-700 flex items-center gap-1"
                      style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    >
                      <Icon name="Check" size={18} color="white" />
                      إنهاء التسجيل
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Delivery Override Modal */}
        {adminDeliveryModal.open && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => {
            setAdminDeliveryModal({ open: false, mode: null, bookingId: null, _deliveryMeta: null });
            setAdminUploadFiles([]);
          }}>
            <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" dir="rtl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Icon name="ShieldAlert" size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      {adminDeliveryModal.mode === 'pending_list' ? 'الحجوزات بانتظار التسليم' :
                       adminDeliveryModal.mode === 'upload' ? 'رفع ملفات بديل للعميل' : 'اختر حجزاً'}
                    </h3>
                    {adminDeliveryModal._deliveryMeta ? (
                      <p className="text-xs text-slate-600">
                        {adminDeliveryModal._deliveryMeta.photographerName || 'مصور'} ←{' '}
                        <span className="font-semibold text-amber-700">{adminDeliveryModal._deliveryMeta.customerName || 'عميل'}</span>
                        {adminDeliveryModal._deliveryMeta.bookingType ? ` · ${adminDeliveryModal._deliveryMeta.bookingType}` : ''}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-600">تسليم بديل من المشرف</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setAdminDeliveryModal({ open: false, mode: null, bookingId: null, _deliveryMeta: null });
                    setAdminUploadFiles([]);
                  }}
                  className="w-9 h-9 rounded-xl hover:bg-amber-100 flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Pending List Mode */}
                {adminDeliveryModal.mode === 'pending_list' && (
                  <div className="space-y-4">
                    {pendingDeliveryBookings.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon name="CheckCircle" size={48} className="mx-auto text-green-400 mb-4" />
                        <p className="text-slate-600" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          جميع الحجوزات تم تسليمها! 🎉
                        </p>
                      </div>
                    ) : (
                      pendingDeliveryBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="p-4 border border-slate-200 rounded-xl hover:border-amber-300 hover:bg-amber-50/50 transition-all cursor-pointer"
                          onClick={() => setAdminDeliveryModal({ open: true, mode: 'upload', bookingId: booking.id })}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                                <Icon name="Camera" size={20} className="text-slate-500" />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{getPhotographyTypeLabelAr(booking.photography_type) || 'تصوير'}</p>
                                <p className="text-xs text-slate-500">{booking.city} • {booking.booking_date}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-left">
                                <p className="text-xs text-slate-500">العميل</p>
                                <p className="text-sm font-medium">{booking.customer?.full_name || '-'}</p>
                              </div>
                              <div className="text-left">
                                <p className="text-xs text-slate-500">المصور</p>
                                <p className="text-sm font-medium">{booking.photographer?.full_name || '-'}</p>
                              </div>
                              <Icon name="ChevronLeft" size={20} className="text-slate-400" />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Select Booking Mode */}
                {adminDeliveryModal.mode === 'select_booking' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      أدخل رقم الحجز أو اختر من القائمة:
                    </p>
                    <input
                      type="text"
                      placeholder="أدخل رقم الحجز (UUID)..."
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        if (value.length >= 8) {
                          setAdminDeliveryModal(prev => ({ ...prev, bookingId: value }));
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (adminDeliveryModal.bookingId) {
                          setAdminDeliveryModal(prev => ({ ...prev, mode: 'upload' }));
                        }
                      }}
                      disabled={!adminDeliveryModal.bookingId}
                      className="w-full py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:bg-slate-200 disabled:text-slate-500 transition-colors font-medium"
                    >
                      متابعة للرفع
                    </button>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                      <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-slate-500">أو</span></div>
                    </div>
                    <button
                      onClick={() => {
                        loadPendingDeliveryBookings();
                        setAdminDeliveryModal(prev => ({ ...prev, mode: 'pending_list' }));
                      }}
                      className="w-full py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-medium text-slate-700"
                    >
                      <Icon name="List" size={18} className="inline ml-2" />
                      عرض قائمة الحجوزات المكتملة
                    </button>
                  </div>
                )}

                {/* Upload Mode */}
                {adminDeliveryModal.mode === 'upload' && adminDeliveryModal.bookingId && (
                  <div className="space-y-6">
                    {/* Booking Info */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">رقم الحجز</p>
                      <p className="font-mono text-sm text-slate-900">{adminDeliveryModal.bookingId}</p>
                    </div>

                    {/* Upload Area */}
                    <div 
                      className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-amber-400 hover:bg-amber-50/30 transition-all cursor-pointer"
                      onClick={() => adminFileInputRef.current?.click()}
                    >
                      <input
                        type="file"
                        ref={adminFileInputRef}
                        className="hidden"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleAdminFileSelect}
                      />
                      <Icon name="Upload" size={40} className="mx-auto text-slate-400 mb-4" />
                      <p className="text-slate-700 font-medium mb-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        اسحب الملفات هنا أو انقر للاختيار
                      </p>
                      <p className="text-xs text-slate-500">يدعم الصور والفيديوهات</p>
                    </div>

                    {/* Selected Files */}
                    {adminUploadFiles.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-slate-700">
                          الملفات المحددة ({adminUploadFiles.length})
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {adminUploadFiles.map((file) => (
                            <div key={file.id} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200">
                              {file.type === 'image' && file.preview ? (
                                <img src={file.preview} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                                  <Icon name="Video" size={24} className="text-white" />
                                </div>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAdminRemoveFile(file.id);
                                }}
                                className="absolute top-1 left-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <Icon name="X" size={14} />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1 text-white text-xs truncate">
                                {file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Upload Progress */}
                    {adminUploading && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">جاري الرفع...</span>
                          <span className="font-medium text-amber-600">{adminUploadProgress}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${adminUploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      onClick={handleAdminUploadAndDeliver}
                      disabled={adminUploadFiles.length === 0 || adminUploading}
                      className="w-full py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl hover:from-amber-700 hover:to-orange-700 disabled:from-slate-300 disabled:to-slate-400 disabled:text-slate-500 transition-all font-bold text-lg flex items-center justify-center gap-2"
                    >
                      {adminUploading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          جاري الرفع والتسليم...
                        </>
                      ) : (
                        <>
                          <Icon name="Send" size={20} />
                          رفع وتسليم للعميل
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowUserModal(false)}>
            <div
              className={`bg-white w-full rounded-2xl shadow-xl border border-slate-100 overflow-hidden my-4 ${
                selectedUser.role === 'photographer' ? 'max-w-xl' : 'max-w-2xl'
              }`}
              dir="rtl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="font-extrabold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                  تفاصيل {selectedUser.role === 'photographer' ? 'المصور' : 'العميل'}
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="w-9 h-9 rounded-xl hover:bg-slate-50 flex items-center justify-center"
                >
                  <Icon name="X" size={18} color="#0f172a" />
                </button>
              </div>
              <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center gap-4">
                  {selectedUser.avatar_url ? (
                    <img src={selectedUser.avatar_url} alt={selectedUser.full_name} className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center">
                      <Icon name="User" size={32} color="#64748b" />
                    </div>
                  )}
                  <div>
                    <div className="font-bold text-lg">{selectedUser.full_name || "—"}</div>
                    <div className="text-sm text-slate-600">{selectedUser.email || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-600 mb-1">الهاتف</div>
                    <div>{selectedUser.phone || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600 mb-1">المدينة</div>
                    <div>{selectedUser.city || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600 mb-1">تاريخ التسجيل</div>
                    <div>{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' }) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600 mb-1">الحالة</div>
                    <div>
                      {selectedUser.deleted_by_user
                        ? <Badge tone="red">محذوف ذاتيًا</Badge>
                        : selectedUser.is_active === false
                          ? <Badge tone="amber">موقوف</Badge>
                          : <Badge tone="green">نشط</Badge>}
                    </div>
                  </div>
                  {selectedUser.role === 'photographer' && (
                    <>
                    <div>
                      <div className="text-xs text-slate-600 mb-1">إتاحة الحجز</div>
                      <div>{selectedUser.available_for_booking === false ? <Badge tone="amber">غير متاح</Badge> : <Badge tone="blue">متاح</Badge>}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 mb-1">سنوات الخبرة</div>
                        <div className="text-sm font-medium">{selectedUser.experience_years || 0} سنة</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 mb-1">مستوى الخبرة</div>
                            <Badge tone={
                              ['elite', 'expert'].includes(selectedUser.experience_level) ? 'purple' :
                              ['distinguished', 'advanced', 'intermediate'].includes(selectedUser.experience_level) ? 'blue' : 'green'
                            }>
                              {selectedUser.experience_level === 'elite' || selectedUser.experience_level === 'expert' ? 'نخبة' :
                               ['distinguished', 'advanced', 'intermediate'].includes(selectedUser.experience_level) ? 'مميز' : 'معتمد'}
                            </Badge>
                      </div>
                    </>
                  )}
                </div>

                {selectedUser.role !== 'photographer' && (() => {
                  const activity = getCustomerActivity(selectedUser.id);
                  const customerBookings = getCustomerBookingsList(selectedUser.id);
                  const customerTickets = getCustomerTicketsList(selectedUser.id);
                  const openTickets = customerTickets.filter((t) => ['open', 'waiting_admin', 'in_progress'].includes(t.status));
                  const pendingRefunds = customerBookings.filter((b) => b.payment_status === 'refund_pending');

                  return (
                    <>
                      <div className="grid grid-cols-3 gap-3 pt-2">
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                          <p className="text-xs text-blue-700 mb-1">الحجوزات</p>
                          <p className="text-xl font-bold text-blue-900">{formatNumber(activity.count)}</p>
                        </div>
                        <div className="bg-green-50 border border-green-100 rounded-xl p-3 text-center">
                          <p className="text-xs text-green-700 mb-1">إجمالي الإنفاق</p>
                          <p className="text-lg font-bold text-green-900">{money(activity.totalSpend)}</p>
                        </div>
                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                          <p className="text-xs text-amber-700 mb-1">تذاكر مفتوحة</p>
                          <p className="text-xl font-bold text-amber-900">{formatNumber(openTickets.length)}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => navigateToCustomerBookings(selectedUser)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5"
                          style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                        >
                          <Icon name="Calendar" size={14} color="white" />
                          عرض حجوزاته
                        </button>
                        <button
                          type="button"
                          onClick={() => navigateToCustomerSupport(selectedUser)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5"
                          style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                        >
                          <Icon name="Headphones" size={14} color="white" />
                          تذاكر الدعم
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowUserModal(false); onTab('bulk-notifications'); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                          style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                        >
                          <Icon name="Bell" size={14} />
                          إشعار جماعي
                        </button>
                        {pendingRefunds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => { setShowUserModal(false); navigate('/admin-refunds'); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-orange-600 text-white hover:bg-orange-700 flex items-center gap-1.5"
                            style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                          >
                            <Icon name="DollarSign" size={14} color="white" />
                            استردادات معلقة ({pendingRefunds.length})
                          </button>
                        )}
                      </div>

                      <div className="border-t border-slate-100 pt-4">
                        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          <Icon name="Calendar" size={16} className="text-primary" />
                          آخر الحجوزات
                        </h3>
                        {customerBookings.length === 0 ? (
                          <p className="text-sm text-slate-500">لا توجد حجوزات مسجّلة.</p>
                        ) : (
                          <div className="space-y-2">
                            {customerBookings.slice(0, 5).map((b) => (
                              <button
                                key={b.id}
                                type="button"
                                onClick={() => openCustomerBookingFromModal(b)}
                                className="w-full flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 hover:border-slate-200 text-right transition-colors"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-bold text-slate-500">#{String(b.id).slice(0, 8).toUpperCase()}</span>
                                    {statusBadge(b.status)}
                                  </div>
                                  <p className="text-xs text-slate-600 mt-1 truncate">
                                    {b.photography_type || '—'} · {b.city || '—'} · {b.booking_date ? formatDate(b.booking_date) : formatDate(b.created_at)}
                                  </p>
                                </div>
                                <div className="text-left shrink-0">
                                  <p className="text-sm font-bold text-green-700">{money(b.final_price_sar ?? b.price)}</p>
                                  <Icon name="ChevronLeft" size={16} className="text-slate-400 mt-1" />
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="border-t border-slate-100 pt-4">
                        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                          <Icon name="Headphones" size={16} className="text-primary" />
                          تذاكر الدعم
                        </h3>
                        {customerTickets.length === 0 ? (
                          <p className="text-sm text-slate-500">لا توجد تذاكر دعم.</p>
                        ) : (
                          <div className="space-y-2">
                            {customerTickets.slice(0, 5).map((t) => {
                              const ticketStatus = TICKET_STATUSES.find((s) => s.key === t.status);
                              return (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => openCustomerTicketFromModal(t)}
                                  className="w-full flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 hover:border-slate-200 text-right transition-colors"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-bold text-slate-700">{t.ticket_number || `#${String(t.id).slice(0, 6)}`}</span>
                                      <Badge tone={ticketStatus?.color || 'slate'}>{ticketStatus?.label || t.status}</Badge>
                                    </div>
                                    <p className="text-xs text-slate-600 mt-1 truncate">{t.subject || '—'}</p>
                                  </div>
                                  <Icon name="ChevronLeft" size={16} className="text-slate-400 shrink-0" />
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Image Preview Modal */}
        {previewPortfolioImage && (
          <div
            className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setPreviewPortfolioImage(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
              dir="rtl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm md:text-base font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                  معاينة صورة المعرض
                </h3>
                <button
                  type="button"
                  onClick={() => setPreviewPortfolioImage(null)}
                  className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
                  aria-label="إغلاق"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
              <div className="flex-1 flex items-center justify-center bg-slate-50 px-4 py-4">
                <img
                  src={previewPortfolioImage.url}
                  alt={previewPortfolioImage.title || 'صورة المعرض'}
                  className="max-h-[70vh] w-auto max-w-full object-contain rounded-xl shadow-md"
                />
              </div>
            </div>
          </div>
        )}

        {/* Verification Details Modal */}
        {showVerificationModal && selectedVerification && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setShowVerificationModal(false); setShowCertificatesRejectInput(false); setCertificatesRejectReason(''); setShowDroneRejectInput(false); setDroneRejectReason(''); setShowFreelanceRejectInput(false); setFreelanceRejectReason(''); setFreelanceExpiryInput(''); setDroneExpiryInput(''); }}>
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl border border-slate-100 overflow-hidden my-4" dir="rtl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-gradient-to-l from-primary/5 to-transparent">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                    {selectedVerification.user?.avatar_url ? (
                      <img src={selectedVerification.user.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Icon name="User" size={32} color="#64748b" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        رقم المصور #{selectedVerification.display_id != null || selectedVerification.displayId != null ? String(Number(selectedVerification.display_id ?? selectedVerification.displayId)).padStart(6, '0') : '—'}
                      </span>
                      <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        {selectedVerification.user?.full_name || "مصور"}
                      </h2>
                    </div>
                    <p className="text-sm text-slate-600">{selectedVerification.user?.email}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {/* Self-deleted badge if applicable */}
                      {selectedVerification.user?.deleted_by_user && (
                        <Badge tone="red">
                          <Icon name="UserX" size={12} className="ml-1" />
                          محذوف ذاتيًا
                        </Badge>
                      )}
                      {/* Status Badge */}
                      {selectedVerification.status === 'approved' ? (
                        <Badge tone="green">
                          <Icon name="CheckCircle" size={12} className="ml-1" />
                          موافق عليه
                        </Badge>
                      ) : selectedVerification.verification_status === 'rejected' ? (
                        <Badge tone="red">
                          <Icon name="XCircle" size={12} className="ml-1" />
                          مرفوض
                        </Badge>
                      ) : selectedVerification.verification_status === 'submitted' ? (
                        <Badge tone="blue">
                          <Icon name="FileText" size={12} className="ml-1" />
                          تم إرسال المستندات
                        </Badge>
                      ) : (
                        <Badge tone="amber">
                          <Icon name="Clock" size={12} className="ml-1" />
                          قيد المراجعة
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowVerificationModal(false)}
                  className="p-2 rounded-xl hover:bg-slate-100"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* تحذير التعديلات الجديدة بعد آخر مراجعة */}
                {(() => {
                  const pendingCount = selectedVerification.portfolioImages?.filter(i => i.approval_status === 'pending').length || 0;
                  const profileUpdated = selectedVerification.updated_at && selectedVerification.verification_reviewed_at
                    ? new Date(selectedVerification.updated_at) > new Date(selectedVerification.verification_reviewed_at)
                    : selectedVerification.updated_at && selectedVerification.status === 'approved'
                      ? new Date(selectedVerification.updated_at) > new Date(selectedVerification.created_at)
                      : false;
                  if (!pendingCount && !profileUpdated) return null;
                  return (
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <Icon name="AlertTriangle" size={20} className="text-amber-600 shrink-0 mt-0.5" />
                      <div style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        <p className="text-sm font-bold text-amber-800 mb-1">هذا المصور يحتاج مراجعة</p>
                        <ul className="text-xs text-amber-700 space-y-0.5">
                          {pendingCount > 0 && (
                            <li>• {pendingCount} صورة في معرض الأعمال بانتظار الموافقة</li>
                          )}
                          {profileUpdated && (
                            <li>
                              • عدّل بيانات ملفه بتاريخ{' '}
                              {new Date(selectedVerification.updated_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                              {selectedVerification.verification_reviewed_at && (
                                <span className="text-amber-600 mr-1">
                                  (آخر مراجعة: {new Date(selectedVerification.verification_reviewed_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })})
                                </span>
                              )}
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  );
                })()}
                {/* حالة التحقق — عرض فقط، القرار في أسفل النافذة */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="ListChecks" size={20} className="inline ml-2 text-primary" />
                    حالة التحقق
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'الهوية',          icon: 'User',  ok: !!selectedVerification.identity_verified,      rejected: selectedVerification.identity_reviewed_at && !selectedVerification.identity_verified },
                      { label: 'معرض الأعمال',     icon: 'Image', ok: !!selectedVerification.portfolio_verified,     rejected: false },
                      {
                        label: 'الشهادات',
                        icon: 'Award',
                        ok: selectedVerification.certificates_verified === true,
                        rejected: selectedVerification.certificates_reviewed_at && !selectedVerification.certificates_verified,
                      },
                      ...(photographerIsIndividual(selectedVerification.entity_type) ? [{
                        label: 'وثيقة العمل الحر',
                        icon: 'Briefcase',
                        ok: selectedVerification.freelance_permit_verified === true,
                        rejected: selectedVerification.freelance_permit_reviewed_at && !selectedVerification.freelance_permit_verified,
                      }] : []),
                      ...(photographerUsesDrone(selectedVerification.shooting_methods) ? [{
                        label: 'تصريح الدرون',
                        icon: 'Radio',
                        ok: selectedVerification.drone_permit_verified === true,
                        rejected: selectedVerification.drone_permit_reviewed_at && !selectedVerification.drone_permit_verified,
                      }] : []),
                    ].map(({ label, icon, ok, rejected }) => (
                      <div
                        key={label}
                        className={`p-3 rounded-xl border-2 ${ok ? 'bg-green-50 border-green-200' : rejected ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${ok ? 'bg-green-500' : rejected ? 'bg-red-500' : 'bg-slate-300'}`}>
                            <Icon name={ok ? 'Check' : icon} size={16} className="text-white" />
                          </div>
                          <div className="text-right min-w-0">
                            <p className="text-sm font-bold text-slate-900">{label}</p>
                            <p className={`text-xs ${ok ? 'text-green-600' : rejected ? 'text-red-500' : 'text-slate-400'}`}>
                              {ok ? '✓ موثّق' : rejected ? 'مرفوض' : 'لم يُراجع'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Identity Document Preview */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="FileText" size={20} className="inline ml-2 text-primary" />
                    مستندات التحقق من الهوية
                    {selectedVerification.identity_verified === true && (
                      <Badge tone="green" className="mr-2">موافق عليها</Badge>
                    )}
                    {selectedVerification.identity_verified === false && selectedVerification.identity_reviewed_at && (
                      <Badge tone="red" className="mr-2">مرفوضة</Badge>
                    )}
                    {photographerHasIdentityDocument(selectedVerification) && !selectedVerification.identity_reviewed_at && (
                      <Badge tone="blue" className="mr-2">قيد المراجعة</Badge>
                    )}
                  </h3>
                  
                  {/* Show identity documents only (exclude drone permits) */}
                  {selectedVerification.identityDocuments?.length > 0 ? (
                    <div className="space-y-4">
                      {selectedVerification.identityDocuments.map((doc, index) => (
                        <div key={index} className="bg-slate-50 rounded-xl p-4">
                          <div className="flex items-start gap-4">
                            {/* Document Preview */}
                            <div className="flex-1">
                              {doc.type?.includes('pdf') || doc.name?.endsWith('.pdf') ? (
                                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                                  <Icon name="FileText" size={64} className="text-red-500 mx-auto mb-3" />
                                  <p className="text-sm text-slate-600 mb-3">مستند PDF</p>
                                  <div className="flex items-center justify-center gap-3">
                                    <a
                                      href={doc.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700"
                                    >
                                      <Icon name="ExternalLink" size={16} />
                                      فتح المستند
                                    </a>
                                    <button
                                      onClick={() => handleDownloadDocument(doc.url, doc.name)}
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700"
                                    >
                                      <Icon name="Download" size={16} />
                                      تحميل
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="relative">
                                  <img
                                    src={doc.url}
                                    alt={`مستند ${index + 1}`}
                                    className="w-full max-h-[500px] object-contain rounded-xl border border-slate-200 bg-white cursor-pointer"
                                    onClick={() => window.open(doc.url, '_blank')}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">خطأ في التحميل</text></svg>';
                                    }}
                                  />
                                  <div className="absolute top-3 left-3 flex gap-2">
                                    <a
                                      href={doc.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-slate-800 rounded-lg text-sm font-medium hover:bg-white flex items-center gap-1.5 shadow-sm"
                                    >
                                      <Icon name="Maximize2" size={14} />
                                      تكبير
                                    </a>
                                    <button
                                      onClick={() => handleDownloadDocument(doc.url, doc.name)}
                                      className="px-3 py-1.5 bg-green-500/90 backdrop-blur-sm text-white rounded-lg text-sm font-medium hover:bg-green-600 flex items-center gap-1.5 shadow-sm"
                                    >
                                      <Icon name="Download" size={14} />
                                      تحميل
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Document Info */}
                            <div className="w-56 shrink-0">
                              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                                <div>
                                  <p className="text-xs text-slate-500">اسم الملف</p>
                                  <p className="font-medium text-slate-800 text-sm truncate" title={doc.name}>
                                    {doc.name}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">نوع المستند</p>
                                  <p className="font-medium text-slate-800">
                                    {doc.type?.includes('pdf') || doc.name?.endsWith('.pdf') ? 'PDF' : 'صورة'}
                                  </p>
                                </div>
                                {doc.uploadedAt && (
                                  <div>
                                    <p className="text-xs text-slate-500">تاريخ الرفع</p>
                                    <p className="font-medium text-slate-800">
                                      {new Date(doc.uploadedAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </p>
                                  </div>
                                )}
                                <div className="pt-2 border-t border-slate-100 space-y-2">
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                  >
                                    <Icon name="ExternalLink" size={14} />
                                    فتح في تاب جديد
                                  </a>
                                  <button
                                    onClick={() => handleDownloadDocument(doc.url, doc.name)}
                                    className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium"
                                  >
                                    <Icon name="Download" size={14} />
                                    تحميل المستند
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : selectedVerification.id_document_url ? (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-start gap-4">
                        {/* Document Preview */}
                        <div className="flex-1">
                          {selectedVerification.id_document_type === 'pdf' ? (
                            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                              <Icon name="FileText" size={64} className="text-red-500 mx-auto mb-3" />
                              <p className="text-sm text-slate-600 mb-3">مستند PDF</p>
                              <div className="flex items-center justify-center gap-3">
                                <a
                                  href={selectedVerification.id_document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700"
                                >
                                  <Icon name="ExternalLink" size={16} />
                                  فتح المستند
                                </a>
                                <button
                                  onClick={() => handleDownloadDocument(selectedVerification.id_document_url, 'identity_document')}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700"
                                >
                                  <Icon name="Download" size={16} />
                                  تحميل
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="relative">
                              <img
                                src={selectedVerification.id_document_url}
                                alt="مستند الهوية"
                                className="w-full max-h-[500px] object-contain rounded-xl border border-slate-200 bg-white cursor-pointer"
                                onClick={() => window.open(selectedVerification.id_document_url, '_blank')}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">خطأ في التحميل</text></svg>';
                                }}
                              />
                              <div className="absolute top-3 left-3 flex gap-2">
                                <a
                                  href={selectedVerification.id_document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-slate-800 rounded-lg text-sm font-medium hover:bg-white flex items-center gap-1.5 shadow-sm"
                                >
                                  <Icon name="Maximize2" size={14} />
                                  تكبير
                                </a>
                                <button
                                  onClick={() => handleDownloadDocument(selectedVerification.id_document_url, 'identity_document')}
                                  className="px-3 py-1.5 bg-green-500/90 backdrop-blur-sm text-white rounded-lg text-sm font-medium hover:bg-green-600 flex items-center gap-1.5 shadow-sm"
                                >
                                  <Icon name="Download" size={14} />
                                  تحميل
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Document Info */}
                        <div className="w-56 shrink-0">
                          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                            <div>
                              <p className="text-xs text-slate-500">نوع المستند</p>
                              <p className="font-medium text-slate-800">
                                {selectedVerification.id_document_type === 'pdf' ? 'PDF' : 'صورة'}
                              </p>
                            </div>
                            {selectedVerification.verification_submitted_at && (
                              <div>
                                <p className="text-xs text-slate-500">تاريخ الإرسال</p>
                                <p className="font-medium text-slate-800">
                                  {new Date(selectedVerification.verification_submitted_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                              </div>
                            )}
                            <div className="pt-2 border-t border-slate-100 space-y-2">
                              <a
                                href={selectedVerification.id_document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                <Icon name="ExternalLink" size={14} />
                                فتح في تاب جديد
                              </a>
                              <button
                                onClick={() => handleDownloadDocument(selectedVerification.id_document_url, 'identity_document')}
                                className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium"
                              >
                                <Icon name="Download" size={14} />
                                تحميل المستند
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                      <Icon name="FileQuestion" size={48} className="text-amber-400 mx-auto mb-3" />
                      <p className="font-bold text-amber-800 mb-1">لم يتم رفع مستندات</p>
                      <p className="text-sm text-amber-700">
                        المصور لم يرسل أي مستندات للتحقق من الهوية بعد
                      </p>
                    </div>
                  )}

                  {/* تاريخ انتهاء مستند الهوية — يضعه المشرف عند القبول */}
                  {((selectedVerification.identityDocuments?.length > 0) || selectedVerification.id_document_url) && (
                    <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                      <label className="block text-sm font-bold text-slate-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                        تاريخ انتهاء مستند الهوية
                        <span className="text-red-500 mr-1">*</span>
                        <span className="text-xs font-normal text-slate-500 mr-2">(يُدخل بعد مراجعة المستند — إلزامي عند القبول)</span>
                      </label>
                      <div className="flex flex-wrap items-end gap-3">
                        <input
                          type="date"
                          value={identityExpiryInput}
                          onChange={(e) => setIdentityExpiryInput(e.target.value)}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-[180px]"
                        />
                        {identityExpiryInput && (
                          <span className={`text-xs font-bold px-2.5 py-1.5 rounded-lg ${getIdentityExpiryBadgeClass(getIdentityExpiryStatus(identityExpiryInput))}`}>
                            {getIdentityExpiryStatusLabel(getIdentityExpiryStatus(identityExpiryInput))}
                            {getIdentityDaysUntilExpiry(identityExpiryInput) !== null && getIdentityDaysUntilExpiry(identityExpiryInput) >= 0 && (
                              <span className="mr-1"> — متبقي {getIdentityDaysUntilExpiry(identityExpiryInput)} يوم</span>
                            )}
                          </span>
                        )}
                        {selectedVerification.identity_verified === true && (
                          <button
                            onClick={() => handleIdentityExpirySave(selectedVerification.user_id, identityExpiryInput)}
                            disabled={mutating || !identityExpiryInput}
                            className="px-4 py-2 bg-slate-600 text-white rounded-xl text-sm font-bold hover:bg-slate-700 disabled:opacity-50"
                          >
                            حفظ التاريخ
                          </button>
                        )}
                      </div>
                      {selectedVerification.identity_verified === true && selectedVerification.identity_document_expires_at && (
                        <div className={`rounded-xl p-3 border ${getIdentityExpiryBadgeClass(getIdentityExpiryStatus(selectedVerification.identity_document_expires_at))}`}>
                          <p className="text-sm font-medium">
                            الحالة الحالية: {getIdentityExpiryStatusLabel(getIdentityExpiryStatus(selectedVerification.identity_document_expires_at))}
                            {' — '}
                            ينتهي {new Date(selectedVerification.identity_document_expires_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                      )}

                      {selectedVerification.identity_rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                          <p className="text-xs text-red-600 font-medium mb-1">سبب الرفض:</p>
                          <p className="text-sm text-red-800">{selectedVerification.identity_rejection_reason}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button
                          onClick={() => handleIdentityApprove(selectedVerification.user_id, identityExpiryInput)}
                          disabled={mutating || selectedVerification.identity_verified === true || !identityExpiryInput}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Check" size={16} />
                          قبول المستند
                        </button>
                        {!showIdentityRejectInput ? (
                          <button
                            onClick={() => setShowIdentityRejectInput(true)}
                            disabled={mutating || (selectedVerification.identity_verified === false && selectedVerification.identity_reviewed_at)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name="X" size={16} />
                            رفض المستند
                          </button>
                        ) : (
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="min-w-[200px]">
                              <label className="block text-xs text-slate-600 mb-1">سبب الرفض (اختياري)</label>
                              <input
                                type="text"
                                value={identityRejectReason}
                                onChange={(e) => setIdentityRejectReason(e.target.value)}
                                placeholder="مثال: المستند غير واضح"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                              />
                            </div>
                            <button
                              onClick={() => handleIdentityReject(selectedVerification.user_id, identityRejectReason.trim() || null)}
                              disabled={mutating}
                              className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700"
                            >
                              تأكيد الرفض
                            </button>
                            <button
                              onClick={() => { setShowIdentityRejectInput(false); setIdentityRejectReason(''); }}
                              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-300"
                            >
                              إلغاء
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* وثيقة العمل الحر — للمصور الفرد فقط */}
                {photographerIsIndividual(selectedVerification.entity_type) && (
                  <div className="mt-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      <Icon name="Briefcase" size={20} className="inline ml-2 text-indigo-600" />
                      وثيقة العمل الحر
                      {selectedVerification.freelance_permit_verified === true && (
                        <Badge tone="green" className="mr-2">موافق عليها</Badge>
                      )}
                      {selectedVerification.freelance_permit_verified === false && selectedVerification.freelance_permit_reviewed_at && (
                        <Badge tone="red" className="mr-2">مرفوضة</Badge>
                      )}
                      {selectedVerification.freelance_permit_url && !selectedVerification.freelance_permit_reviewed_at && (
                        <Badge tone="blue" className="mr-2">قيد المراجعة</Badge>
                      )}
                    </h3>

                    {selectedVerification.freelance_permit_url ? (
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-indigo-900">الوثيقة المرفوعة</p>
                            {selectedVerification.freelance_permit_submitted_at && (
                              <p className="text-xs text-indigo-700 mt-1">
                                تاريخ الرفع: {new Date(selectedVerification.freelance_permit_submitted_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={selectedVerification.freelance_permit_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                            >
                              <Icon name="ExternalLink" size={14} />
                              عرض الوثيقة
                            </a>
                            <button
                              onClick={() => handleDownloadDocument(selectedVerification.freelance_permit_url, 'freelance_permit')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                            >
                              <Icon name="Download" size={14} />
                              تحميل
                            </button>
                          </div>
                        </div>

                        {/* تاريخ الانتهاء — يضعه المشرف بعد مراجعة الوثيقة */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                          <label className="block text-sm font-bold text-slate-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                            تاريخ انتهاء الوثيقة
                            <span className="text-red-500 mr-1">*</span>
                            <span className="text-xs font-normal text-slate-500 mr-2">(يُدخل بعد مراجعة الوثيقة)</span>
                          </label>
                          <div className="flex flex-wrap items-end gap-3">
                            <input
                              type="date"
                              value={freelanceExpiryInput}
                              onChange={(e) => setFreelanceExpiryInput(e.target.value)}
                              className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-[180px]"
                            />
                            {freelanceExpiryInput && (
                              <span className={`text-xs font-bold px-2.5 py-1.5 rounded-lg ${getFreelanceExpiryBadgeClass(getFreelanceExpiryStatus(freelanceExpiryInput))}`}>
                                {getFreelanceExpiryStatusLabel(getFreelanceExpiryStatus(freelanceExpiryInput))}
                                {getDaysUntilExpiry(freelanceExpiryInput) !== null && getDaysUntilExpiry(freelanceExpiryInput) >= 0 && (
                                  <span className="mr-1"> — متبقي {getDaysUntilExpiry(freelanceExpiryInput)} يوم</span>
                                )}
                              </span>
                            )}
                            {selectedVerification.freelance_permit_verified === true && (
                              <button
                                onClick={() => handleFreelanceExpirySave(selectedVerification.user_id, freelanceExpiryInput)}
                                disabled={mutating || !freelanceExpiryInput}
                                className="px-4 py-2 bg-slate-600 text-white rounded-xl text-sm font-bold hover:bg-slate-700 disabled:opacity-50"
                              >
                                حفظ التاريخ
                              </button>
                            )}
                          </div>
                        </div>

                        {selectedVerification.freelance_permit_rejection_reason && (
                          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                            <p className="text-xs text-red-600 font-medium mb-1">سبب الرفض:</p>
                            <p className="text-sm text-red-800">{selectedVerification.freelance_permit_rejection_reason}</p>
                          </div>
                        )}

                        {selectedVerification.freelance_permit_verified === true && selectedVerification.freelance_permit_expires_at && (
                          <div className={`rounded-xl p-3 border ${getFreelanceExpiryBadgeClass(getFreelanceExpiryStatus(selectedVerification.freelance_permit_expires_at))}`}>
                            <p className="text-sm font-medium">
                              الحالة الحالية: {getFreelanceExpiryStatusLabel(getFreelanceExpiryStatus(selectedVerification.freelance_permit_expires_at))}
                              {' — '}
                              تنتهي {new Date(selectedVerification.freelance_permit_expires_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 pt-1">
                          <button
                            onClick={() => handleFreelancePermitApprove(selectedVerification.user_id, freelanceExpiryInput)}
                            disabled={mutating || selectedVerification.freelance_permit_verified === true || !freelanceExpiryInput}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name="Check" size={16} />
                            قبول الوثيقة
                          </button>
                          {!showFreelanceRejectInput ? (
                            <button
                              onClick={() => setShowFreelanceRejectInput(true)}
                              disabled={mutating || (selectedVerification.freelance_permit_verified === false && selectedVerification.freelance_permit_reviewed_at)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Icon name="X" size={16} />
                              رفض الوثيقة
                            </button>
                          ) : (
                            <div className="flex flex-wrap items-end gap-2">
                              <div className="min-w-[200px]">
                                <label className="block text-xs text-slate-600 mb-1">سبب الرفض (اختياري)</label>
                                <input
                                  type="text"
                                  value={freelanceRejectReason}
                                  onChange={(e) => setFreelanceRejectReason(e.target.value)}
                                  placeholder="مثال: الوثيقة غير واضحة"
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                />
                              </div>
                              <button
                                onClick={() => handleFreelancePermitReject(selectedVerification.user_id, freelanceRejectReason.trim() || null)}
                                disabled={mutating}
                                className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700"
                              >
                                تأكيد الرفض
                              </button>
                              <button
                                onClick={() => { setShowFreelanceRejectInput(false); setFreelanceRejectReason(''); }}
                                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-300"
                              >
                                إلغاء
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 text-center">
                        <Icon name="FileQuestion" size={48} className="text-indigo-400 mx-auto mb-3" />
                        <p className="font-bold text-indigo-800 mb-1">لم تُرفع وثيقة العمل الحر بعد</p>
                        <p className="text-sm text-indigo-700">المصور فرد ولم يرفع وثيقة العمل الحر</p>
                      </div>
                    )}
                  </div>
                )}

                {/* تصريح الدرون — قسم مستقل للمراجعة */}
                {photographerUsesDrone(selectedVerification.shooting_methods) && (
                  <div className="mt-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      <Icon name="Radio" size={20} className="inline ml-2 text-amber-600" />
                      تصريح الطائرة المسيّرة (درون)
                      {selectedVerification.drone_permit_verified === true && (
                        <Badge tone="green" className="mr-2">موافق عليه</Badge>
                      )}
                      {selectedVerification.drone_permit_verified === false && selectedVerification.drone_permit_reviewed_at && (
                        <Badge tone="red" className="mr-2">مرفوض</Badge>
                      )}
                      {selectedVerification.drone_permit_url && !selectedVerification.drone_permit_reviewed_at && (
                        <Badge tone="blue" className="mr-2">قيد المراجعة</Badge>
                      )}
                    </h3>

                    {selectedVerification.drone_permit_url ? (
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-amber-900">تصريح مرفوع</p>
                            {selectedVerification.drone_permit_submitted_at && (
                              <p className="text-xs text-amber-700 mt-1">
                                تاريخ الرفع: {new Date(selectedVerification.drone_permit_submitted_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={selectedVerification.drone_permit_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                            >
                              <Icon name="ExternalLink" size={14} />
                              عرض
                            </a>
                            <button
                              onClick={() => handleDownloadDocument(selectedVerification.drone_permit_url, 'drone_permit')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                            >
                              <Icon name="Download" size={14} />
                              تحميل
                            </button>
                          </div>
                        </div>

                        {/* تاريخ الانتهاء — يضعه المشرف بعد مراجعة التصريح */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                          <label className="block text-sm font-bold text-slate-800" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                            تاريخ انتهاء التصريح
                            <span className="text-red-500 mr-1">*</span>
                            <span className="text-xs font-normal text-slate-500 mr-2">(يُدخل بعد مراجعة التصريح)</span>
                          </label>
                          <div className="flex flex-wrap items-end gap-3">
                            <input
                              type="date"
                              value={droneExpiryInput}
                              onChange={(e) => setDroneExpiryInput(e.target.value)}
                              className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-[180px]"
                            />
                            {droneExpiryInput && (
                              <span className={`text-xs font-bold px-2.5 py-1.5 rounded-lg ${getDroneExpiryBadgeClass(getDroneExpiryStatus(droneExpiryInput))}`}>
                                {getDroneExpiryStatusLabel(getDroneExpiryStatus(droneExpiryInput))}
                                {getDroneDaysUntilExpiry(droneExpiryInput) !== null && getDroneDaysUntilExpiry(droneExpiryInput) >= 0 && (
                                  <span className="mr-1"> — متبقي {getDroneDaysUntilExpiry(droneExpiryInput)} يوم</span>
                                )}
                              </span>
                            )}
                            {selectedVerification.drone_permit_verified === true && (
                              <button
                                onClick={() => handleDroneExpirySave(selectedVerification.user_id, droneExpiryInput)}
                                disabled={mutating || !droneExpiryInput}
                                className="px-4 py-2 bg-slate-600 text-white rounded-xl text-sm font-bold hover:bg-slate-700 disabled:opacity-50"
                              >
                                حفظ التاريخ
                              </button>
                            )}
                          </div>
                        </div>

                        {selectedVerification.drone_permit_rejection_reason && (
                          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                            <p className="text-xs text-red-600 font-medium mb-1">سبب الرفض:</p>
                            <p className="text-sm text-red-800">{selectedVerification.drone_permit_rejection_reason}</p>
                          </div>
                        )}

                        {selectedVerification.drone_permit_verified === true && selectedVerification.drone_permit_expires_at && (
                          <div className={`rounded-xl p-3 border ${getDroneExpiryBadgeClass(getDroneExpiryStatus(selectedVerification.drone_permit_expires_at))}`}>
                            <p className="text-sm font-medium">
                              الحالة الحالية: {getDroneExpiryStatusLabel(getDroneExpiryStatus(selectedVerification.drone_permit_expires_at))}
                              {' — '}
                              ينتهي {new Date(selectedVerification.drone_permit_expires_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 pt-1">
                          <button
                            onClick={() => handleDronePermitApprove(selectedVerification.user_id, droneExpiryInput)}
                            disabled={mutating || selectedVerification.drone_permit_verified === true || !droneExpiryInput}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name="Check" size={16} />
                            قبول التصريح
                          </button>
                          {!showDroneRejectInput ? (
                            <button
                              onClick={() => setShowDroneRejectInput(true)}
                              disabled={mutating || (selectedVerification.drone_permit_verified === false && selectedVerification.drone_permit_reviewed_at)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Icon name="X" size={16} />
                              رفض التصريح
                            </button>
                          ) : (
                            <div className="flex flex-wrap items-end gap-2">
                              <div className="min-w-[200px]">
                                <label className="block text-xs text-slate-600 mb-1">سبب الرفض (اختياري)</label>
                                <input
                                  type="text"
                                  value={droneRejectReason}
                                  onChange={(e) => setDroneRejectReason(e.target.value)}
                                  placeholder="مثال: التصريح منتهي الصلاحية"
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                />
                              </div>
                              <button
                                onClick={() => handleDronePermitReject(selectedVerification.user_id, droneRejectReason.trim() || null)}
                                disabled={mutating}
                                className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700"
                              >
                                تأكيد الرفض
                              </button>
                              <button
                                onClick={() => { setShowDroneRejectInput(false); setDroneRejectReason(''); }}
                                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-300"
                              >
                                إلغاء
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                        <Icon name="FileQuestion" size={48} className="text-amber-400 mx-auto mb-3" />
                        <p className="font-bold text-amber-800 mb-1">لم يُرفع تصريح الدرون بعد</p>
                        <p className="text-sm text-amber-700">المصور يستخدم الدرون لكن لم يرفع تصريح التشغيل</p>
                      </div>
                    )}
                  </div>
                )}

                {/* صور المعرض: مراجعة وموافقة/رفض + EXIF لكل صورة (في نافذة التحقق) */}
                {selectedVerification.portfolioImages && selectedVerification.portfolioImages.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      <Icon name="Image" size={20} className="inline ml-2 text-primary" />
                      صور المعرض
                      <Badge tone="blue" className="mr-2">
                        {selectedVerification.portfolioImages.filter(i => i.approval_status === 'pending').length} تحت المراجعة
                      </Badge>
                    </h3>
                    {/* EXIF legend */}
                    <p className="text-xs text-slate-400 mb-4 flex items-center gap-1">
                      <Icon name="Info" size={12} />
                      يُقرأ EXIF تلقائياً من كل صورة ويُقارن بالمعدات المُعلنة من المصور
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {selectedVerification.portfolioImages.map((img) => (
                        <div key={img.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm flex flex-col">
                          {/* Thumbnail */}
                          <div className="relative w-full aspect-square bg-slate-100 shrink-0">
                            <img
                              src={img.image_url}
                              alt={img.title || 'صورة'}
                              className="w-full h-full object-cover"
                            />
                            {/* Status badge */}
                            <span className={`absolute top-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded shadow ${
                              img.approval_status === 'approved' ? 'bg-green-600 text-white' :
                              img.approval_status === 'rejected' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
                            }`}>
                              {img.approval_status === 'approved' ? 'منشورة' :
                               img.approval_status === 'rejected' ? 'مرفوضة' : 'قيد المراجعة'}
                            </span>
                          </div>

                          {/* Info + actions */}
                          <div className="p-2 flex flex-col gap-1.5">
                            {/* EXIF chip */}
                            <ExifChip
                              imageUrl={img.image_url}
                              declaredEquipment={selectedVerification.equipment_available || []}
                            />

                            {/* Action buttons */}
                            <div className="flex gap-1 mt-auto pt-1">
                              {img.approval_status === 'pending' && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handlePortfolioImageApproval(selectedVerification.user_id, img.id, 'approved')}
                                    disabled={mutating}
                                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-[11px] font-medium"
                                    title="موافقة"
                                  >
                                    <Icon name="Check" size={11} /> موافقة
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handlePortfolioImageApproval(selectedVerification.user_id, img.id, 'rejected')}
                                    disabled={mutating}
                                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 text-[11px] font-medium"
                                    title="رفض"
                                  >
                                    <Icon name="X" size={11} /> رفض
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => setPreviewPortfolioImage({ url: img.image_url, title: img.title || 'صورة المعرض' })}
                                className="flex items-center justify-center p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                                title="عرض كبير"
                              >
                                <Icon name="Eye" size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SAFE MODE: Certificates feature - شهادات التصوير (admin approve/reject) */}
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="Award" size={20} className="inline ml-2 text-primary" />
                    شهادات التصوير
                    {selectedVerification.certificates?.length > 0 && (
                      <Badge tone="blue" className="mr-2">
                        {selectedVerification.certificates.length} شهادة
                      </Badge>
                    )}
                    {selectedVerification.certificates_verified === true && (
                      <Badge tone="green" className="mr-2">موافق عليها</Badge>
                    )}
                    {selectedVerification.certificates_verified === false && selectedVerification.certificates_reviewed_at && (
                      <Badge tone="red" className="mr-2">مرفوضة</Badge>
                    )}
                  </h3>
                  {selectedVerification.certificates?.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid gap-3">
                        {(Array.isArray(selectedVerification.certificates) ? selectedVerification.certificates : []).map((cert, index) => {
                          const url = typeof cert === 'string' ? cert : cert?.url;
                          const name = (typeof cert === 'object' && cert?.name) ? cert.name : `شهادة ${index + 1}`;
                          return (
                            <div key={index} className="flex items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                              <div className="min-w-0 flex-1 flex items-center gap-2">
                                <Icon name="FileText" size={18} className="text-slate-500 flex-shrink-0" />
                                <span className="text-sm text-slate-800 truncate">{name}</span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                                  <Icon name="ExternalLink" size={14} />
                                  عرض
                                </a>
                                <button onClick={() => handleDownloadDocument(url, name)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                                  <Icon name="Download" size={14} />
                                  تحميل
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {selectedVerification.certificates_rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                          <p className="text-xs text-red-600 font-medium mb-1">سبب الرفض:</p>
                          <p className="text-sm text-red-800">{selectedVerification.certificates_rejection_reason}</p>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 pt-2">
                        {!showCertificatesRejectInput ? (
                          <button
                            onClick={() => setShowCertificatesRejectInput(true)}
                            disabled={mutating || selectedVerification.certificates_verified === false && selectedVerification.certificates_reviewed_at}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name="X" size={16} />
                            رفض الشهادات
                          </button>
                        ) : (
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="min-w-[200px]">
                              <label className="block text-xs text-slate-600 mb-1">سبب الرفض (اختياري)</label>
                              <input
                                type="text"
                                value={certificatesRejectReason}
                                onChange={(e) => setCertificatesRejectReason(e.target.value)}
                                placeholder="مثال: وثائق غير واضحة"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                              />
                            </div>
                            <button
                              onClick={() => handleCertificatesReject(selectedVerification.user_id, certificatesRejectReason.trim() || null)}
                              disabled={mutating}
                              className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700"
                            >
                              تأكيد الرفض
                            </button>
                            <button
                              onClick={() => { setShowCertificatesRejectInput(false); setCertificatesRejectReason(''); }}
                              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-300"
                            >
                              إلغاء
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-600">لم يرفع المصور أي شهادات تصوير</p>
                    </div>
                  )}
                </div>

                {/* Photographer Details */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="User" size={20} className="inline ml-2 text-primary" />
                    معلومات المصور
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 rounded-xl p-4">
                    <div>
                      <p className="text-xs text-slate-500">البريد الإلكتروني</p>
                      <p className="font-medium text-slate-800">{selectedVerification.user?.email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">رقم الهاتف</p>
                      <p className="font-medium text-slate-800">{selectedVerification.user?.phone || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">المدينة</p>
                      <p className="font-medium text-slate-800">{selectedVerification.city || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">سنوات الخبرة</p>
                      <p className="font-medium text-slate-800">{selectedVerification.experience_years || '0'} سنة</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">اسم النشاط التجاري</p>
                      <p className="font-medium text-slate-800 text-sm">{selectedVerification.business_name || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">التخصصات</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Array.isArray(selectedVerification.specializations) && selectedVerification.specializations.length > 0 ? (
                          selectedVerification.specializations.map((spec, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                              {spec}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">وسائل التصوير</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {Array.isArray(selectedVerification.shooting_methods) && selectedVerification.shooting_methods.length > 0 ? (
                          selectedVerification.shooting_methods.map((method, i) => {
                            const label =
                              method === 'professional_camera' ? 'كاميرا احترافية'
                              : method === 'mobile_camera'     ? 'كاميرا جوال'
                              : method === 'drone'             ? 'تصوير جوي (درون)'
                              : method;
                            const model = selectedVerification.shooting_method_models?.[method];
                            return (
                              <div key={i} className="flex flex-col gap-0.5">
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                  {label}
                                </span>
                                {model && (
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                    {model}
                                  </span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">مستوى التعديل</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Array.isArray(selectedVerification.equipment_available) && selectedVerification.equipment_available.length > 0 ? (
                          selectedVerification.equipment_available.map((equip, i) => (
                            <span key={i} className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs">
                              {equip}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">المعدات المتاحة</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Array.isArray(selectedVerification.languages) && selectedVerification.languages.length > 0 ? (
                          selectedVerification.languages.map((lang, i) => (
                            <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-800 rounded text-xs">
                              {lang}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">نوع الحساب</p>
                      <p className="font-medium text-slate-800 text-sm">
                        {(selectedVerification.entity_type || selectedVerification.entityType) === 'entity' ? 'استوديو' : 'فرد'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rejection Reason (if rejected) */}
                {selectedVerification.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-800 mb-1">سبب الرفض السابق</p>
                        <p className="text-sm text-red-700">{selectedVerification.rejection_reason}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Notes Input */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                    <Icon name="MessageSquare" size={20} className="inline ml-2 text-primary" />
                    ملاحظات للمصور
                  </h3>
                  <textarea
                    id="verification-notes"
                    placeholder="أضف ملاحظات للمصور (اختياري)..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
                    rows={3}
                    defaultValue={selectedVerification.verification_notes || ''}
                  />
                </div>

                {/* إجراءات الإدارة المتقدمة */}
                {(() => {
                  const uid = selectedVerification.user_id;
                  const isUpdating = updatingVerificationUserId === uid || mutating;
                  const currentStatus = selectedVerification.status;
                  const isActive = selectedVerification.user?.is_active !== false;
                  const isAvailable = selectedVerification.available_for_booking !== false;
                  const rawLevel = selectedVerification.experience_level;
                  const experienceLevel = DB_TO_UI_EXPERIENCE_LEVEL[rawLevel] || rawLevel || 'certified';
                  const updateModal = (updates) => {
                    setVerificationRequests((prev) => prev.map((p) => p.user_id === uid ? { ...p, ...updates } : p));
                    setSelectedVerification((prev) => prev ? { ...prev, ...updates } : null);
                  };
                  return (
                    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                      <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                        <Icon name="Settings" size={16} className="text-slate-500" />
                        إجراءات الإدارة
                      </h3>
                      <div className="space-y-3">
                        {/* حالة الحساب */}
                        <div>
                          <p className="text-xs text-slate-500 mb-1.5">حالة الحساب</p>
                          <div className="flex flex-wrap gap-2">
                            {currentStatus !== 'approved' && (
                              <button
                                disabled={isUpdating}
                                onClick={async () => {
                                  try {
                                    setUpdatingVerificationUserId(uid);
                                    await managePhotographer(uid, 'approve', null);
                                    updateModal({ status: 'approved', verification_status: 'approved', available_for_booking: true });
                                    setNotice(`✅ تمت الموافقة على المصور: ${selectedVerification.user?.full_name || String(uid).slice(0, 8)}`);
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || 'تعذر الموافقة'}`);
                                  } finally { setUpdatingVerificationUserId(null); }
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${isUpdating ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                              >
                                {isUpdating ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="CheckCircle" size={12} />}
                                موافقة (إدارة)
                              </button>
                            )}
                            {currentStatus === 'approved' && (
                              <button
                                disabled={isUpdating}
                                onClick={async () => {
                                  try {
                                    setUpdatingVerificationUserId(uid);
                                    await managePhotographer(uid, 'suspend', null);
                                    updateModal({ status: 'suspended', available_for_booking: false });
                                    setNotice(`⚠️ تم إيقاف المصور: ${selectedVerification.user?.full_name || String(uid).slice(0, 8)}`);
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || 'تعذر الإيقاف'}`);
                                  } finally { setUpdatingVerificationUserId(null); }
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${isUpdating ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
                                style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                              >
                                {isUpdating ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="PauseCircle" size={12} />}
                                إيقاف
                              </button>
                            )}
                            <button
                              disabled={isUpdating}
                              onClick={async () => {
                                try {
                                  setUpdatingVerificationUserId(uid);
                                  await managePhotographer(uid, 'toggle_active', !isActive);
                                  const updUser = { ...selectedVerification.user, is_active: !isActive };
                                  updateModal({ user: updUser });
                                  setNotice(`${!isActive ? '✅ تم تفعيل' : '⚠️ تم تعطيل'} حساب المصور`);
                                } catch (e) {
                                  setNotice(`❌ ${e?.message || 'تعذر التحديث'}`);
                                } finally { setUpdatingVerificationUserId(null); }
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${isUpdating ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : isActive ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'}`}
                              style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                            >
                              {isUpdating ? <Icon name="Loader" size={12} className="animate-spin" /> : isActive ? <Icon name="UserX" size={12} /> : <Icon name="UserCheck" size={12} />}
                              {isActive ? 'تعطيل الحساب' : 'تفعيل الحساب'}
                            </button>
                            {currentStatus === 'approved' && (
                              <button
                                disabled={isUpdating}
                                onClick={async () => {
                                  try {
                                    setUpdatingVerificationUserId(uid);
                                    await managePhotographer(uid, 'toggle_availability', !isAvailable);
                                    updateModal({ available_for_booking: !isAvailable });
                                    setNotice(`${!isAvailable ? '✅ متاح' : '⚠️ غير متاح'} للحجز`);
                                  } catch (e) {
                                    setNotice(`❌ ${e?.message || 'تعذر التحديث'}`);
                                  } finally { setUpdatingVerificationUserId(null); }
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${isUpdating ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : isAvailable ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                                style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                              >
                                {isUpdating ? <Icon name="Loader" size={12} className="animate-spin" /> : isAvailable ? <Icon name="CalendarX" size={12} /> : <Icon name="CalendarCheck" size={12} />}
                                {isAvailable ? 'منع الحجز' : 'السماح بالحجز'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* مستوى الخبرة */}
                        <div>
                          <p className="text-xs text-slate-500 mb-1.5">مستوى الخبرة</p>
                          <div className="flex flex-wrap gap-1.5">
                            {['certified', 'distinguished', 'elite'].map((opt) => {
                              const isCurrent = opt === experienceLevel;
                              const label = UI_EXPERIENCE_LEVEL_LABELS_AR[opt] || opt;
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  disabled={isUpdating}
                                  onClick={async () => {
                                    if (opt === experienceLevel) return;
                                    try {
                                      setUpdatingVerificationUserId(uid);
                                      const dbLevel = UI_TO_DB_EXPERIENCE_LEVEL[opt] || opt;
                                      const result = await managePhotographer(uid, 'update_experience_level', dbLevel);
                                      const apiLevel = result?.experience_level;
                                      const updatedLevel = apiLevel ? (DB_TO_UI_EXPERIENCE_LEVEL[apiLevel] || apiLevel) : opt;
                                      updateModal({ experience_level: updatedLevel });
                                      setNotice(`✅ تم تحديث مستوى الخبرة إلى: ${UI_EXPERIENCE_LEVEL_LABELS_AR[updatedLevel] || updatedLevel}`);
                                    } catch (e) {
                                      setNotice(`❌ ${e?.message || 'تعذر التحديث'}`);
                                    } finally { setUpdatingVerificationUserId(null); }
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-bold ${isUpdating ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : isCurrent ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                                  style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* إزالة نهائية */}
                        <div className="pt-2 border-t border-slate-200">
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={async () => {
                              const confirmed = window.confirm('هل أنت متأكد من إزالة هذا المصور نهائياً من المنصة؟\nسيتم حذف حسابه وجميع بياناته المرتبطة.');
                              if (!confirmed) return;
                              try {
                                setUpdatingVerificationUserId(uid);
                                await managePhotographer(uid, 'delete', null);
                                setVerificationRequests((prev) => prev.filter((p) => p.user_id !== uid));
                                setShowVerificationModal(false);
                                setSelectedVerification(null);
                                setNotice(`🗑 تم إزالة المصور نهائياً: ${selectedVerification.user?.full_name || String(uid).slice(0, 8)}`);
                              } catch (e) {
                                setNotice(`❌ ${e?.message || 'تعذر الإزالة'}`);
                              } finally { setUpdatingVerificationUserId(null); }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 border ${isUpdating ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300'}`}
                            style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                          >
                            {isUpdating ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="Trash2" size={12} />}
                            {isUpdating ? 'جاري...' : 'إزالة المصور نهائياً'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-between p-6 border-t border-slate-100 bg-slate-50">
                <button
                  onClick={() => setShowVerificationModal(false)}
                  className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm font-bold"
                  style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                >
                  إغلاق
                </button>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      const notes = document.getElementById('verification-notes')?.value;
                      handleVerificationAction(selectedVerification.user_id, 'request_docs', notes);
                    }}
                    disabled={mutating}
                    className="px-4 py-2 rounded-xl bg-amber-600 text-white hover:bg-amber-700 text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    <Icon name="FileText" size={16} />
                    طلب مستندات إضافية
                  </button>
                  <button
                    onClick={() => openRejectionPanel(selectedVerification.user_id)}
                    disabled={mutating}
                    className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                  >
                    <Icon name="XCircle" size={16} />
                    رفض مع ذكر السبب
                  </button>
                  <button
                    onClick={() => handleVerificationAction(selectedVerification.user_id, 'approve')}
                    disabled={
                      mutating
                      || selectedVerification.status === 'approved'
                      || !photographerAccountActivationRequirementsMet(selectedVerification)
                    }
                    className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50 ${
                      selectedVerification.status === 'approved'
                        ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}
                    title={selectedVerification.status !== 'approved' ? (getIdentityActivationBlockReason(selectedVerification) || undefined) : undefined}
                  >
                    <Icon name="CheckCircle" size={16} />
                    {selectedVerification.status === 'approved' ? 'الحساب مُفعَّل ✓' : 'تفعيل الحساب'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── نافذة الرفض الموحّدة بأسباب متعددة ── */}
        {showRejectionPanel && (
          <div
            className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowRejectionPanel(false)}
          >
            <div
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-100"
              dir="rtl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                    <Icon name="XCircle" size={18} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                      رفض طلب التوثيق
                    </h3>
                    <p className="text-xs text-slate-500">اختر سبباً أو أكثر ثم أرسل</p>
                  </div>
                </div>
                <button onClick={() => setShowRejectionPanel(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                  <Icon name="X" size={18} className="text-slate-500" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">أسباب الرفض</p>
                {REJECTION_REASONS.map((reason) => {
                  const checked = selectedRejectionReasons.includes(reason);
                  return (
                    <label
                      key={reason}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                        checked ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-red-600 shrink-0"
                        checked={checked}
                        onChange={() => toggleRejectionReason(reason)}
                      />
                      <span className="text-sm text-slate-800 leading-snug">{reason}</span>
                    </label>
                  );
                })}

                {/* ملاحظة حرة */}
                <div className="pt-1">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">ملاحظة إضافية (اختياري)</label>
                  <textarea
                    rows={2}
                    value={customRejectionNote}
                    onChange={(e) => setCustomRejectionNote(e.target.value)}
                    placeholder="أي تفاصيل إضافية للمصور..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-200 resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                <button
                  onClick={() => setShowRejectionPanel(false)}
                  className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm font-bold"
                  style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                >
                  إلغاء
                </button>
                <button
                  onClick={submitRejection}
                  disabled={mutating || (selectedRejectionReasons.length === 0 && !customRejectionNote.trim())}
                  className="px-5 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-sm font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                >
                  <Icon name="Send" size={15} />
                  إرسال الرفض
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payout Confirm Modal */}
        {payoutConfirmModal && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => !mutating && setPayoutConfirmModal(null)}>
            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl border border-slate-100 overflow-hidden" dir="rtl" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                  <Icon name="Landmark" size={22} className="text-green-600" />
                  تأكيد صرف للمصور
                </h3>
                <p className="text-sm text-slate-600 mt-2">
                  تأكد أنك نفّذت التحويل البنكي يدوياً قبل الضغط على تأكيد.
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-500">المصور</span>
                    <span className="font-bold text-slate-900">{payoutConfirmModal.photographer?.full_name || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-500">المبلغ</span>
                    <span className="font-bold text-green-600">{formatNumber(payoutConfirmModal.totalAmount)} ر.س</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-500">عدد الحجوزات</span>
                    <span className="font-medium text-slate-900">{payoutConfirmModal.bookingIds?.length || 0}</span>
                  </div>
                  {payoutConfirmModal.bank?.iban && (
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-slate-500 text-xs mb-1">IBAN</p>
                      <p className="font-mono text-slate-900 text-xs break-all" dir="ltr">
                        {formatIbanForDisplay(payoutConfirmModal.bank.iban)}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    رقم مرجع التحويل (اختياري)
                  </label>
                  <input
                    type="text"
                    value={payoutTransferRef}
                    onChange={(e) => setPayoutTransferRef(e.target.value)}
                    placeholder="مثال: TRF-20260606-1234"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 font-mono"
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-100 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setPayoutConfirmModal(null)}
                  disabled={mutating}
                  className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm font-bold disabled:opacity-50"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={executePayoutConfirm}
                  disabled={mutating}
                  className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                >
                  <Icon name={mutating ? 'Loader' : 'CheckCircle'} size={16} className={mutating ? 'animate-spin' : ''} />
                  {mutating ? 'جاري التأكيد...' : 'تأكيد الصرف'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Details Modal */}
        {showPaymentModal && selectedPayment && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowPaymentModal(false)}>
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl border border-slate-100 overflow-hidden my-4" dir="rtl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-gradient-to-l from-green-500/10 to-transparent">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Icon name="Receipt" size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                      تفاصيل الدفعة
                    </h3>
                    <p className="text-sm text-slate-600">
                      حجز #{String(selectedPayment.bookingId).slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center"
                >
                  <Icon name="X" size={20} color="#334155" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Amount Breakdown */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-slate-700 mb-4">تفصيل المبلغ</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">إجمالي الحجز</span>
                      <span className="font-bold text-slate-900 text-lg">{formatNumber(selectedPayment.amount)} ر.س</span>
                    </div>
                    <div className="flex items-center justify-between text-primary">
                      <span>عمولة المنصة ({commissionSettings.platformCommissionRate}%)</span>
                      <span className="font-bold">- {formatNumber(selectedPayment.commission)} ر.س</span>
                    </div>
                    <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
                      <span className="text-slate-600">صافي المصور</span>
                      <span className="font-bold text-green-600 text-lg">{formatNumber(selectedPayment.photographerAmount)} ر.س</span>
                    </div>
                  </div>
                </div>

                {/* Customer & Photographer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-slate-700 mb-3">العميل</h4>
                    <div className="flex items-center gap-3">
                      <img
                        src={selectedPayment.customer?.avatar_url || 'https://via.placeholder.com/48'}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-bold text-slate-900">{selectedPayment.customer?.full_name || 'غير معروف'}</p>
                        <p className="text-xs text-slate-600">{selectedPayment.customer?.email || ''}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-slate-700 mb-3">المصور</h4>
                    <div className="flex items-center gap-3">
                      <img
                        src={selectedPayment.photographer?.avatar_url || 'https://via.placeholder.com/48'}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-bold text-slate-900">{selectedPayment.photographer?.full_name || 'غير معروف'}</p>
                        <p className="text-xs text-slate-600">{selectedPayment.photographer?.email || ''}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booking Details */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-slate-700 mb-3">تفاصيل الحجز</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">تاريخ الحجز</p>
                      <p className="font-medium text-slate-900">{formatDate(selectedPayment.bookingDate)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">نوع التصوير</p>
                      <p className="font-medium text-slate-900">{getPhotographyTypeLabelAr(selectedPayment.photographyType) || 'غير محدد'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">المدينة</p>
                      <p className="font-medium text-slate-900">{selectedPayment.city || 'غير محدد'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">تاريخ الدفع</p>
                      <p className="font-medium text-slate-900">{formatDate(selectedPayment.date)}</p>
                    </div>
                  </div>
                </div>

                {/* معلومات التحويل للمصور */}
                {hasValidSaudiIban(selectedPayment.photographerBank?.iban) ? (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h4 className="text-sm font-bold text-slate-700">معلومات التحويل للمصور</h4>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(cleanIban(selectedPayment.photographerBank.iban), 'IBAN')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 hover:bg-slate-50"
                      >
                        <Icon name="Copy" size={12} />
                        نسخ IBAN
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {selectedPayment.photographerBank.bank_name && (
                        <div>
                          <p className="text-slate-500">اسم البنك</p>
                          <p className="font-medium text-slate-900">{selectedPayment.photographerBank.bank_name}</p>
                        </div>
                      )}
                      {selectedPayment.photographerBank.bank_account_holder_name && (
                        <div>
                          <p className="text-slate-500">صاحب الحساب</p>
                          <p className="font-medium text-slate-900">{selectedPayment.photographerBank.bank_account_holder_name}</p>
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <p className="text-slate-500">الآيبان (IBAN)</p>
                        <p className="font-medium text-slate-900 font-mono" dir="ltr">
                          {formatIbanForDisplay(selectedPayment.photographerBank.iban)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-xl p-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                    <Icon name="AlertTriangle" size={18} className="shrink-0 mt-0.5" />
                    <p>لم يُدخل المصور IBAN صالحاً — لا يمكن تأكيد الصرف حتى يُكمل بياناته.</p>
                  </div>
                )}

                {selectedPayment.payoutStatus === 'paid' && selectedPayment.transferRef && (
                  <div className="bg-green-50 rounded-xl p-3 border border-green-100 text-sm">
                    <p className="text-slate-500 text-xs">مرجع التحويل البنكي</p>
                    <p className="font-medium text-slate-900 font-mono" dir="ltr">{selectedPayment.transferRef}</p>
                  </div>
                )}

                {/* Status */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-slate-600">حالة الحجز: </span>
                    {selectedPayment.status === 'completed' ? (
                      <Badge tone="green">مكتمل</Badge>
                    ) : (
                      <Badge tone="amber">جاري</Badge>
                    )}
                  </div>
                  <div>
                    <span className="text-sm text-slate-600">حالة الصرف: </span>
                    {selectedPayment.payoutStatus === 'paid' ? (
                      <Badge tone="green">تم الصرف</Badge>
                    ) : (
                      <Badge tone="amber">معلق</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-slate-100 bg-slate-50">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm font-bold"
                >
                  إغلاق
                </button>
                
                {selectedPayment.payoutStatus === 'pending' && (
                  <button
                    onClick={() => handlePayoutPhotographer(selectedPayment)}
                    disabled={mutating || !hasValidSaudiIban(selectedPayment.photographerBank?.iban)}
                    className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!hasValidSaudiIban(selectedPayment.photographerBank?.iban) ? 'IBAN غير متوفر' : undefined}
                  >
                    <Icon name="CheckCircle" size={16} />
                    تأكيد صرف المبلغ للمصور
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          مودال عرض ملفات التسليم — للمشرف فقط (Signed URLs مؤقتة)
      ══════════════════════════════════════════════════════════════════ */}
      {showDeliveryFilesModal && selectedDeliveryForFiles && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowDeliveryFilesModal(false); setLightboxFile(null); } }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" dir="rtl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                  ملفات التسليم
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedDeliveryForFiles.photographer?.full_name || '-'}
                  {' → '}
                  {selectedDeliveryForFiles.customer?.full_name || '-'}
                  {' · '}
                  <span className="text-amber-600 font-medium">الروابط تنتهي خلال ساعة</span>
                </p>
              </div>
              <button
                onClick={() => { setShowDeliveryFilesModal(false); setLightboxFile(null); }}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingDeliveryFiles ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-slate-500 text-sm">جاري تحميل الملفات...</p>
                </div>
              ) : deliveryFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
                  <Icon name="ImageOff" size={48} />
                  <p className="text-sm">لا توجد ملفات لهذا التسليم</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-4">
                    {deliveryFiles.length} ملف ·
                    {' '}
                    {deliveryFiles.filter(f => f.file_type === 'image').length} صورة ·
                    {' '}
                    {deliveryFiles.filter(f => f.file_type === 'video').length} فيديو
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {deliveryFiles.map((file) => (
                      <div
                        key={file.id}
                        className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200 cursor-pointer hover:border-primary transition-all"
                        onClick={() => setLightboxFile(file)}
                      >
                        {file.file_type === 'image' && file.signedUrl ? (
                          <img
                            src={file.thumbnailUrl || file.signedUrl}
                            alt={file.file_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : file.file_type === 'video' ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-800 gap-2">
                            {file.thumbnailUrl ? (
                              <img src={file.thumbnailUrl} alt="" className="w-full h-full object-cover absolute inset-0" />
                            ) : null}
                            <div className="relative z-10 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                              <Icon name="Play" size={20} className="text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <Icon name="File" size={32} />
                          </div>
                        )}
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end p-2 opacity-0 group-hover:opacity-100">
                          <span className="text-white text-xs font-medium truncate w-full bg-black/40 rounded px-1.5 py-0.5">
                            {file.file_name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Lightbox */}
          {lightboxFile && (
            <div
              className="fixed inset-0 z-60 flex items-center justify-center bg-black/90"
              onClick={() => setLightboxFile(null)}
            >
              <button
                className="absolute top-4 left-4 text-white/70 hover:text-white p-2"
                onClick={() => setLightboxFile(null)}
              >
                <Icon name="X" size={28} />
              </button>
              <button
                className="absolute top-4 right-4 text-white/70 hover:text-white p-2 flex items-center gap-2 text-sm"
                onClick={(e) => { e.stopPropagation(); window.open(lightboxFile.signedUrl, '_blank'); }}
              >
                <Icon name="Download" size={18} />
                تحميل
              </button>
              {lightboxFile.file_type === 'video' ? (
                <video
                  src={lightboxFile.signedUrl}
                  controls
                  autoPlay
                  className="max-w-full max-h-full rounded-xl"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <img
                  src={lightboxFile.signedUrl}
                  alt={lightboxFile.file_name}
                  className="max-w-full max-h-full object-contain rounded-xl"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <p className="absolute bottom-4 text-white/60 text-sm">{lightboxFile.file_name}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
